export type Maybe<T> = T | null;
export type InputMaybe<T> = Maybe<T>;
/** All built-in and custom scalars, mapped to their actual values */
export type Scalars = {
  ID: { input: string; output: string; }
  String: { input: string; output: string; }
  Boolean: { input: boolean; output: boolean; }
  Int: { input: number; output: number; }
  Float: { input: number; output: number; }
  JSONObject: { input: unknown; output: unknown; }
};

export type AchDetails = {
  __typename?: 'ACHDetails';
  accountType?: Maybe<Scalars['String']['output']>;
  bankName?: Maybe<Scalars['String']['output']>;
  lastFour?: Maybe<Scalars['String']['output']>;
  plaidEnabled?: Maybe<Scalars['Boolean']['output']>;
  plaidMask?: Maybe<Scalars['String']['output']>;
  routingNumber?: Maybe<Scalars['String']['output']>;
};

export type AchDetailsInput = {
  accountNumber: Scalars['String']['input'];
  accountType: Scalars['String']['input'];
  routingNumber: Scalars['String']['input'];
};

export enum AccountApprovalStatus {
  ApplicationSubmitted = 'APPLICATION_SUBMITTED',
  Approved = 'APPROVED',
  Deleted = 'DELETED',
  Pending = 'PENDING',
  PendingRfi = 'PENDING_RFI',
  PendingRfiReview = 'PENDING_RFI_REVIEW',
  Rejected = 'REJECTED'
}

export type AccountInfo = {
  __typename?: 'AccountInfo';
  adminNotes?: Maybe<AdminNotes>;
  approvalStatus?: Maybe<AccountApprovalStatus>;
  bankAccounts?: Maybe<Array<Maybe<BankAccount>>>;
  bannerKey?: Maybe<Scalars['String']['output']>;
  /**
   * The Xero expense account a bill made in Nickel is booked to when Nickel
   * creates it in Xero. Null chartOfAccountId means the org has not chosen one,
   * and no bill made in Nickel is created in Xero. chartOfAccountId is Xero's
   * account id, cleared when the org disconnects from Xero or makes a new Xero
   * connection. Null for an org not on Xero. Change it with
   * updateBillExpenseChartOfAccount.
   */
  billExpenseChartOfAccount?: Maybe<FeeChartOfAccount>;
  conductorEndUserId?: Maybe<Scalars['String']['output']>;
  customAPCardRateBips?: Maybe<Scalars['Int']['output']>;
  customARCardRateBips?: Maybe<Scalars['Int']['output']>;
  customDomain?: Maybe<Scalars['String']['output']>;
  /**
   * The QuickBooks Online or Xero expense account this org's Nickel fees are
   * recorded to — the AP delivery fees and the AR card-processing fee. Null
   * chartOfAccountId means the org has not chosen one. QuickBooks postings then
   * find-or-create an account named "Nickel Fees"; Xero postings use the expense
   * account named "Nickel Fees", which the merchant creates in Xero; Nickel
   * asks for it when it is missing. Null for an org on neither. For Xero,
   * chartOfAccountId is Xero's account id, and is cleared when the org
   * disconnects from Xero or makes a new Xero connection. Change it with
   * updateFeeChartOfAccount.
   */
  feeChartOfAccount?: Maybe<FeeChartOfAccount>;
  hasOwnerInfo?: Maybe<Scalars['Boolean']['output']>;
  id?: Maybe<Scalars['String']['output']>;
  /**
   * The Xero income account an invoice made in Nickel is booked to when Nickel
   * creates it in Xero. Null chartOfAccountId means the org has not chosen one,
   * and no invoice made in Nickel is created in Xero. chartOfAccountId is Xero's
   * account id, cleared when the org disconnects from Xero or makes a new Xero
   * connection. Null for an org not on Xero. Change it with
   * updateInvoiceSalesChartOfAccount.
   */
  invoiceSalesChartOfAccount?: Maybe<FeeChartOfAccount>;
  lightPath?: Maybe<LightPath>;
  lobMerchantAddressId?: Maybe<Scalars['String']['output']>;
  logoKey?: Maybe<Scalars['String']['output']>;
  notificationSettings?: Maybe<NotificationSettings>;
  onboardingInfo?: Maybe<Scalars['String']['output']>;
  organizationId?: Maybe<Scalars['String']['output']>;
  paymentSettings: PaymentSettings;
  qbCompanyInfo?: Maybe<QuickbooksCompanyInfo>;
  quickbooksCompanyId?: Maybe<Scalars['String']['output']>;
  quickbooksPreferences?: Maybe<QuickbooksPreferences>;
  stagedSubscription?: Maybe<StagedSubscriptionResponse>;
  subdomain?: Maybe<Scalars['String']['output']>;
  subscription?: Maybe<SubscriptionResponse>;
  threeDSEnabled?: Maybe<Scalars['Boolean']['output']>;
  tier?: Maybe<OrganizationTier>;
  /**
   * The Xero account a Nickel payment applied to no invoice is credited to: it
   * is recorded as Receive Money into the "Nickel Clearing" account, so the
   * payout that carries it can be recorded too. Null chartOfAccountId means the
   * org has not chosen one, and such payments are not recorded. chartOfAccountId
   * is Xero's account id, cleared when the org disconnects from Xero or makes a
   * new Xero connection. Null for an org not on Xero. Change it with
   * updateUnappliedPaymentChartOfAccount.
   */
  unappliedPaymentChartOfAccount?: Maybe<FeeChartOfAccount>;
  webhookUrl?: Maybe<Scalars['String']['output']>;
  /**
   * Set while the organization is connected to Xero, null otherwise. The Xero
   * counterpart of quickbooksCompanyId and conductorEndUserId: an opaque Nickel
   * id, not Xero's.
   */
  xeroConnectionId?: Maybe<Scalars['String']['output']>;
  /**
   * The state of the Xero connection xeroConnectionId names, null when there is
   * none. NEEDS_REAUTH means Rutter has lost Xero's authorization and nothing
   * syncs until the merchant signs in again through xeroConnectionReconnect.
   */
  xeroConnectionStatus?: Maybe<AccountingConnectionStatus>;
  /**
   * True while the Xero connection is connected but Rutter has not finished its
   * first sync, so nothing has been imported yet. False once it has, and null
   * when there is no Xero connection.
   */
  xeroConnectionSyncing?: Maybe<Scalars['Boolean']['output']>;
};

/**
 * An action a client can withhold from its accounting firm
 * (Organization.accountantRestrictions). PAYOUT_BANK: changing the bank account
 * payouts land in. VENDOR_BANK_DETAILS: adding, changing, removing or
 * requesting the accounts and addresses vendors are paid at. REFUNDS:
 * refunding a customer's payment.
 * PAY_BILLS: paying a bill, approving one for payment, or paying without
 * approval. CANCEL_BILL_PAYMENTS: cancelling a bill payment or stopping a
 * check. APPROVAL_RULES: creating, changing or removing approval rules.
 */
export enum AccountantAccessGroup {
  ApprovalRules = 'APPROVAL_RULES',
  CancelBillPayments = 'CANCEL_BILL_PAYMENTS',
  PayoutBank = 'PAYOUT_BANK',
  PayBills = 'PAY_BILLS',
  Refunds = 'REFUNDS',
  VendorBankDetails = 'VENDOR_BANK_DETAILS'
}

/**
 * One client organization of an accounting firm, as the admin org page lists
 * them. approvalStatus is the client's own OrganizationInfo.approvalStatus.
 */
export type AccountantClient = {
  __typename?: 'AccountantClient';
  approvalStatus?: Maybe<AccountApprovalStatus>;
  id: Scalars['String']['output'];
  name: Scalars['String']['output'];
};

export type AccountantOrganization = {
  __typename?: 'AccountantOrganization';
  organization?: Maybe<Organization>;
  user?: Maybe<User>;
};

export type AccountingAddress = {
  __typename?: 'AccountingAddress';
  city?: Maybe<Scalars['String']['output']>;
  line1?: Maybe<Scalars['String']['output']>;
  line2?: Maybe<Scalars['String']['output']>;
  postalCode?: Maybe<Scalars['String']['output']>;
  region?: Maybe<Scalars['String']['output']>;
  type?: Maybe<Scalars['String']['output']>;
};

export enum AccountingConnectionStatus {
  Connected = 'CONNECTED',
  Disconnected = 'DISCONNECTED',
  NeedsReauth = 'NEEDS_REAUTH',
  Pending = 'PENDING'
}

export type AccountingCustomer = {
  __typename?: 'AccountingCustomer';
  addresses?: Maybe<Array<Maybe<AccountingAddress>>>;
  customerName?: Maybe<Scalars['String']['output']>;
  emailAddress?: Maybe<Scalars['String']['output']>;
  id?: Maybe<Scalars['String']['output']>;
};

export enum AccountingItemType {
  Bill = 'BILL',
  Customer = 'CUSTOMER',
  Invoice = 'INVOICE',
  Vendor = 'VENDOR'
}

export enum AccountingSource {
  Nickel = 'NICKEL',
  QuickbooksDesktop = 'QUICKBOOKS_DESKTOP',
  QuickbooksOnline = 'QUICKBOOKS_ONLINE',
  Xero = 'XERO'
}

export type AccountingSyncParams = {
  __typename?: 'AccountingSyncParams';
  itemCount?: Maybe<Scalars['Int']['output']>;
  itemId?: Maybe<Scalars['String']['output']>;
  itemType?: Maybe<Scalars['String']['output']>;
  syncType: Scalars['String']['output'];
  updatedSince?: Maybe<Scalars['String']['output']>;
};

export type AccountingSyncStatusResponse = {
  __typename?: 'AccountingSyncStatusResponse';
  error?: Maybe<Scalars['String']['output']>;
  startedAt?: Maybe<Scalars['Float']['output']>;
  steps: AccountingSyncStepStatus;
  syncParams?: Maybe<AccountingSyncParams>;
  syncing: Scalars['Boolean']['output'];
};

export type AccountingSyncStepStatus = {
  __typename?: 'AccountingSyncStepStatus';
  bills: Scalars['String']['output'];
  companyInfo: Scalars['String']['output'];
  customers: Scalars['String']['output'];
  invoices: Scalars['String']['output'];
  vendors: Scalars['String']['output'];
};

export enum AccountingTransactionType {
  /** One entry per bill made in Nickel that the push could not create, change or void in Xero; sourceId is the bill. */
  Bill = 'BILL',
  BillPayment = 'BILL_PAYMENT',
  /** One entry per invoice made in Nickel that the push could not create, change or void in Xero; sourceId is the invoice. */
  Invoice = 'INVOICE',
  /** One entry per Xero invoice whose payments the push could not converge; sourceId is the invoice. */
  InvoiceApplications = 'INVOICE_APPLICATIONS',
  InvoicePayment = 'INVOICE_PAYMENT',
  NickelFeesJournalEntry = 'NICKEL_FEES_JOURNAL_ENTRY',
  /** One entry per Nickel payment applied to no invoice whose Xero Receive Money the push could not converge; sourceId is the payment. */
  UnappliedPaymentDeposit = 'UNAPPLIED_PAYMENT_DEPOSIT'
}

/**
 * Whether the caller can take an action on a record right now. Advice: the
 * mutation checks again when it runs, since the record can change in between.
 */
export type ActionAvailability = {
  __typename?: 'ActionAvailability';
  action: ActionKind;
  allowed: Scalars['Boolean']['output'];
  /**
   * When `allowed` is false and the refusal lifts by itself: the time it lifts,
   * in epoch milliseconds (an ACH refund after its wait). Null for any other
   * refusal and for an allowed action.
   */
  availableAt?: Maybe<Scalars['Float']['output']>;
  /**
   * Why not, when `allowed` is false: a message for the merchant, always with a
   * `code`. `PERMISSION_REQUIRED` when the caller's role lacks the action.
   */
  refusal?: Maybe<Error>;
  /**
   * Whether one of the organization's approval rules will hold the action for an
   * approver when it is taken now: false when `allowed` is false and for an
   * action no rule covers. Null when the record cannot tell, as for
   * `SAVED_METHOD_CHARGE`, whose rules turn on the invoice charged.
   */
  requiresApproval?: Maybe<Scalars['Boolean']['output']>;
};

/**
 * Something a merchant can do to a record, as the record's `availableActions`
 * reports it. Each is one mutation: an action no mutation performs is never
 * listed.
 */
export enum ActionKind {
  /** Delete a bill: `deleteBill`. */
  BillDelete = 'BILL_DELETE',
  /** Pay a bill: `addBillPayable`, or the bill's row in `batchBillPay`. */
  BillPay = 'BILL_PAY',
  /** Cancel a bill payment: `cancelBillPayment`. */
  BillPaymentCancel = 'BILL_PAYMENT_CANCEL',
  /** Record money paid to the vendor outside Nickel against a bill: `recordExternalBillPayment`. */
  BillRecordExternalPayment = 'BILL_RECORD_EXTERNAL_PAYMENT',
  /** Edit a bill's amount, due date, reference, note or documents: `updateBill`. */
  BillUpdate = 'BILL_UPDATE',
  /** Apply a Nickel Banking check deposit to an invoice: one row of `applyNickelBalanceDepositsToInvoices`. */
  CheckDepositApplyToInvoice = 'CHECK_DEPOSIT_APPLY_TO_INVOICE',
  /** Void a bill payment recorded as made outside Nickel: `voidExternalBillPayment`. */
  ExternalBillPaymentVoid = 'EXTERNAL_BILL_PAYMENT_VOID',
  /** Void a recorded invoice payment, or unapply a deposit from the invoice: `voidExternalInvoicePayment`. */
  ExternalInvoicePaymentVoid = 'EXTERNAL_INVOICE_PAYMENT_VOID',
  /** Cancel an invoice's scheduled send before it goes out: `cancelInvoiceSend`. */
  InvoiceCancelScheduledSend = 'INVOICE_CANCEL_SCHEDULED_SEND',
  /** Record money the customer paid outside Nickel against an invoice: `recordExternalInvoicePayment`. */
  InvoiceRecordExternalPayment = 'INVOICE_RECORD_EXTERNAL_PAYMENT',
  /** Remove an invoice, or archive it when money is recorded against it: `removeQuestionForm`. */
  InvoiceRemove = 'INVOICE_REMOVE',
  /** Set the date an invoice is first sent: `scheduleInvoiceSend`. */
  InvoiceScheduleSend = 'INVOICE_SCHEDULE_SEND',
  /** Send an invoice's payment link to its recipients: `sendPaymentLinkToRecipients`. */
  InvoiceSend = 'INVOICE_SEND',
  /** Deposit a check into a Nickel Banking account: `createNickelBalanceCheckDeposit`. */
  NickelBalanceCheckDeposit = 'NICKEL_BALANCE_CHECK_DEPOSIT',
  /** Approve a paper check written on a Nickel Banking account: `payNickelBalanceInboundCheck`. */
  NickelBalanceInboundCheckPay = 'NICKEL_BALANCE_INBOUND_CHECK_PAY',
  /** Return a paper check written on a Nickel Banking account: `returnNickelBalanceInboundCheck`. */
  NickelBalanceInboundCheckReturn = 'NICKEL_BALANCE_INBOUND_CHECK_RETURN',
  /** Cancel a payment whose ACH debit is still in flight: `refundPayment`. */
  PaymentCancel = 'PAYMENT_CANCEL',
  /** Refund a settled payment, in full or in part: `refundPayment`. */
  PaymentRefund = 'PAYMENT_REFUND',
  /** Cancel a recurring bill series: `deactivateRecurringBill`. */
  RecurringBillCancel = 'RECURRING_BILL_CANCEL',
  /** Cancel a recurring invoice series: `deactivateRecurringInvoice`. */
  RecurringInvoiceCancel = 'RECURRING_INVOICE_CANCEL',
  /** Edit a recurring invoice's schedule, amount or details: `updateRecurringInvoice`. */
  RecurringInvoiceUpdate = 'RECURRING_INVOICE_UPDATE',
  /** Remove one of the customer's saved payment methods: `deleteRetailCustomerPaymentMethod`. */
  RetailCustomerDeletePaymentMethod = 'RETAIL_CUSTOMER_DELETE_PAYMENT_METHOD',
  /**
   * Email the customer a request to save a payment method:
   * `requestCustomerDebitAuthorization`, or one request of
   * `batchRequestCustomerDebitAuthorizations`.
   */
  RetailCustomerRequestDebitAuthorization = 'RETAIL_CUSTOMER_REQUEST_DEBIT_AUTHORIZATION',
  /**
   * Save a bank account the customer signed a debit authorization for:
   * `createRetailCustomerACHDebitAuthorization`, or one row of
   * `batchCreateRetailCustomerACHDebitAuthorizations`.
   */
  RetailCustomerSaveAchDebitAuthorization = 'RETAIL_CUSTOMER_SAVE_ACH_DEBIT_AUTHORIZATION',
  /** Edit a customer's name, emails, phone numbers or payment settings: `updateRetailCustomer`. */
  RetailCustomerUpdate = 'RETAIL_CUSTOMER_UPDATE',
  /** Charge a customer's saved payment method for invoices: `batchAutoDebit`. */
  SavedMethodCharge = 'SAVED_METHOD_CHARGE',
  /** Cancel a scheduled bill payment before it runs: `cancelScheduledPayment`. */
  ScheduledBillPaymentCancel = 'SCHEDULED_BILL_PAYMENT_CANCEL',
  /** Remove a vendor: `removeVendor`. */
  VendorRemove = 'VENDOR_REMOVE'
}

export type ActivateNickelBalanceCardResponse = {
  __typename?: 'ActivateNickelBalanceCardResponse';
  card?: Maybe<IssuedCard>;
  error?: Maybe<Error>;
};

export type ActivityNote = {
  __typename?: 'ActivityNote';
  flagged?: Maybe<Scalars['Boolean']['output']>;
  note?: Maybe<Scalars['String']['output']>;
  reconciled?: Maybe<Scalars['Boolean']['output']>;
};

export enum ActivityStatus {
  Cancelled = 'CANCELLED',
  Completed = 'COMPLETED',
  Delivered = 'DELIVERED',
  Failed = 'FAILED',
  Pending = 'PENDING',
  Rejected = 'REJECTED',
  Scheduled = 'SCHEDULED',
  Sent = 'SENT'
}

export type AddActivityNoteResponse = {
  __typename?: 'AddActivityNoteResponse';
  activityNote?: Maybe<ActivityNote>;
  error?: Maybe<Error>;
};

export type AddBillPayableResponse = {
  __typename?: 'AddBillPayableResponse';
  billPayable?: Maybe<BillPayable>;
  error?: Maybe<Error>;
};

export type AddPlatformPartnerWebhookPayload = {
  __typename?: 'AddPlatformPartnerWebhookPayload';
  error?: Maybe<Error>;
  partner?: Maybe<PlatformPartnerAdmin>;
  secret?: Maybe<Scalars['String']['output']>;
  webhookId?: Maybe<Scalars['ID']['output']>;
};

export type AddRetailCustomerResponse = {
  __typename?: 'AddRetailCustomerResponse';
  error?: Maybe<Error>;
  retailCustomer?: Maybe<RetailCustomer>;
};

export type AddSubscriptionResponse = {
  __typename?: 'AddSubscriptionResponse';
  error?: Maybe<Error>;
  subscription?: Maybe<SubscriptionDetail>;
};

export type AddVendorResponse = {
  __typename?: 'AddVendorResponse';
  error?: Maybe<Error>;
  vendor?: Maybe<Vendor>;
};

export type AddressDetails = {
  __typename?: 'AddressDetails';
  city?: Maybe<Scalars['String']['output']>;
  country?: Maybe<Scalars['String']['output']>;
  state?: Maybe<Scalars['String']['output']>;
  street1?: Maybe<Scalars['String']['output']>;
  street2?: Maybe<Scalars['String']['output']>;
  zip?: Maybe<Scalars['String']['output']>;
};

export type AddressDetailsInput = {
  city?: InputMaybe<Scalars['String']['input']>;
  country?: InputMaybe<Scalars['String']['input']>;
  state?: InputMaybe<Scalars['String']['input']>;
  street1?: InputMaybe<Scalars['String']['input']>;
  street2?: InputMaybe<Scalars['String']['input']>;
  zip?: InputMaybe<Scalars['String']['input']>;
};

export type AdminNickelBalanceAccount = {
  __typename?: 'AdminNickelBalanceAccount';
  accounts: Array<AdminNickelBalanceAccountRow>;
  applicationSubmittedAt?: Maybe<Scalars['String']['output']>;
  balanceCents?: Maybe<Scalars['Float']['output']>;
  employees: Array<User>;
  organizationId: Scalars['ID']['output'];
  organizationName: Scalars['String']['output'];
  processingBalanceCents?: Maybe<Scalars['Float']['output']>;
  reviewedAt?: Maybe<Scalars['String']['output']>;
  status: NickelBalanceAccountStatus;
};

export type AdminNickelBalanceAccountRow = {
  __typename?: 'AdminNickelBalanceAccountRow';
  balanceCents: Scalars['Float']['output'];
  id: Scalars['ID']['output'];
  nickname?: Maybe<Scalars['String']['output']>;
};

export type AdminNickelBalanceAccountsResponse = {
  __typename?: 'AdminNickelBalanceAccountsResponse';
  accounts: Array<AdminNickelBalanceAccount>;
  error?: Maybe<Error>;
  totalAccounts: Scalars['Int']['output'];
  totalBalanceCents: Scalars['Float']['output'];
  totalProcessingCents: Scalars['Float']['output'];
  totalResults: Scalars['Int']['output'];
};

/**
 * Row order for `adminNickelBalanceAccounts`. BALANCE sorts on the summed
 * balance over the org's AVAILABLE accounts; orgs with no account sort last in
 * both directions.
 */
export enum AdminNickelBalanceAccountsSort {
  BalanceAsc = 'BALANCE_ASC',
  BalanceDesc = 'BALANCE_DESC',
  OpenedAsc = 'OPENED_ASC',
  OpenedDesc = 'OPENED_DESC'
}

export type AdminNote = {
  __typename?: 'AdminNote';
  note: Scalars['String']['output'];
  timestamp: Scalars['String']['output'];
  writer: Scalars['String']['output'];
};

export type AdminNotes = {
  __typename?: 'AdminNotes';
  aiApprovalSummaries?: Maybe<Array<AiApprovalSummaryEntry>>;
  notes: Array<AdminNote>;
};

/**
 * The merchant's business address as updateOrganizationAdminControls reads and
 * writes it: the street/street2/city/state/zip keys of the org's onboarding
 * info, the one copy invoices, receipts, check return addresses and Nickel
 * Balance provisioning all read. A part that is not on file is null.
 */
export type AdminOrganizationAddress = {
  __typename?: 'AdminOrganizationAddress';
  city?: Maybe<Scalars['String']['output']>;
  /** Canonical two-letter US state. */
  state?: Maybe<Scalars['String']['output']>;
  street?: Maybe<Scalars['String']['output']>;
  street2?: Maybe<Scalars['String']['output']>;
  zip?: Maybe<Scalars['String']['output']>;
};

/**
 * A complete US business address for updateOrganizationAdminControls. Every
 * part is trimmed; an empty street2 clears it.
 */
export type AdminOrganizationAddressInput = {
  city: Scalars['String']['input'];
  /** A state name or two-letter abbreviation; stored as the abbreviation. */
  state: Scalars['String']['input'];
  street: Scalars['String']['input'];
  street2?: InputMaybe<Scalars['String']['input']>;
  /** Five digits, or ZIP+4. */
  zip: Scalars['String']['input'];
};

/**
 * The two roles an admin may switch an organization between. CUSTOMER exists
 * on the database enum but nothing sets or reads it, so it is not offered.
 */
export enum AdminOrganizationRole {
  AccountingFirm = 'ACCOUNTING_FIRM',
  Retailer = 'RETAILER'
}

export type AdminPennyChatOrganization = {
  __typename?: 'AdminPennyChatOrganization';
  id: Scalars['ID']['output'];
  name: Scalars['String']['output'];
};

export type AdminPennyChatsResponse = {
  __typename?: 'AdminPennyChatsResponse';
  conversations: Array<AdminPennyConversationListItem>;
  error?: Maybe<Error>;
  organizations: Array<AdminPennyChatOrganization>;
};

export type AdminPennyConversationListItem = {
  __typename?: 'AdminPennyConversationListItem';
  channel: PennyChannel;
  chatStatus: PennyChatStatus;
  createdAt: Scalars['Float']['output'];
  id: Scalars['ID']['output'];
  lastMessageAt: Scalars['Float']['output'];
  organizationId: Scalars['ID']['output'];
  organizationName: Scalars['String']['output'];
  pendingPlanCount: Scalars['Int']['output'];
  preview: Scalars['String']['output'];
  subject: Scalars['String']['output'];
  userEmail?: Maybe<Scalars['String']['output']>;
  userName: Scalars['String']['output'];
};

export type AdminPennyConversationResponse = {
  __typename?: 'AdminPennyConversationResponse';
  conversation?: Maybe<PennyConversationDetails>;
  error?: Maybe<Error>;
  organizationId?: Maybe<Scalars['ID']['output']>;
  organizationName?: Maybe<Scalars['String']['output']>;
  userEmail?: Maybe<Scalars['String']['output']>;
  userName?: Maybe<Scalars['String']['output']>;
};

export type AdminSendPennyReplyResponse = {
  __typename?: 'AdminSendPennyReplyResponse';
  error?: Maybe<Error>;
  message?: Maybe<PennyMessage>;
};

export type AiApprovalSummaryEntry = {
  __typename?: 'AiApprovalSummaryEntry';
  confidence: Scalars['String']['output'];
  decision: Scalars['String']['output'];
  estimatedCostCents?: Maybe<Scalars['Int']['output']>;
  /** Who that call goes to; null unless the decision asks for a call. */
  followUpCall?: Maybe<UnderwritingFollowUpCallPlan>;
  /**
   * What the underwriter wants asked on a call to the applicant. Empty unless
   * the decision is REQUEST_MORE_INFORMATION
   * (docs/underwriting/follow-up-calls.md).
   */
  followUpQuestions: Array<UnderwritingFollowUpQuestion>;
  inputTokens?: Maybe<Scalars['Int']['output']>;
  keyFacts?: Maybe<KeyFacts>;
  outputTokens?: Maybe<Scalars['Int']['output']>;
  /** Whether the run had no bank data to read. */
  plaidDataUnavailable?: Maybe<Scalars['Boolean']['output']>;
  /** CURRENT_APPROVAL_SUMMARY_VERSION when the run was made. */
  promptVersion?: Maybe<Scalars['Int']['output']>;
  recommendedControls?: Maybe<RecommendedControls>;
  researchTruncated?: Maybe<Scalars['Boolean']['output']>;
  riskRating?: Maybe<Scalars['Float']['output']>;
  /** The UnderwritingRun this entry records; a follow-up call names it. */
  runId?: Maybe<Scalars['String']['output']>;
  scorecard: Scorecard;
  summary: Scalars['String']['output'];
  timestamp: Scalars['String']['output'];
  /**
   * What started the run: application_submitted, plaid_link, admin,
   * follow_up_call, auto_approve_confirmation or backfill. Null on runs older
   * than the field.
   */
  trigger?: Maybe<Scalars['String']['output']>;
  /** The trigger's specifics: which bank was linked, who regenerated. Null when the trigger says it all. */
  triggerReason?: Maybe<Scalars['String']['output']>;
  webSearchFindings?: Maybe<WebSearchFindings>;
};

export type AllPaymentMethodsResponse = {
  __typename?: 'AllPaymentMethodsResponse';
  error?: Maybe<Error>;
  paymentMethods?: Maybe<Array<PaymentMethod>>;
};

export type AlloyApplicationOverridesInput = {
  businessName?: InputMaybe<Scalars['String']['input']>;
  city?: InputMaybe<Scalars['String']['input']>;
  contactPhoneNumber?: InputMaybe<Scalars['String']['input']>;
  dateOfBirth?: InputMaybe<Scalars['String']['input']>;
  ownerCity?: InputMaybe<Scalars['String']['input']>;
  ownerFirstName?: InputMaybe<Scalars['String']['input']>;
  ownerLastName?: InputMaybe<Scalars['String']['input']>;
  ownerPhone?: InputMaybe<Scalars['String']['input']>;
  ownerSSN?: InputMaybe<Scalars['String']['input']>;
  ownerState?: InputMaybe<Scalars['String']['input']>;
  ownerStreet?: InputMaybe<Scalars['String']['input']>;
  ownerZip?: InputMaybe<Scalars['String']['input']>;
  state?: InputMaybe<Scalars['String']['input']>;
  street?: InputMaybe<Scalars['String']['input']>;
  taxId?: InputMaybe<Scalars['String']['input']>;
  website?: InputMaybe<Scalars['String']['input']>;
  zip?: InputMaybe<Scalars['String']['input']>;
};

export type AlloyJourney = {
  __typename?: 'AlloyJourney';
  alloyUrl?: Maybe<Scalars['String']['output']>;
  completeOutcome?: Maybe<Scalars['String']['output']>;
  journeyApplicationStatus?: Maybe<Scalars['String']['output']>;
  journeyApplicationToken?: Maybe<Scalars['String']['output']>;
};

export type AnswerForm = {
  __typename?: 'AnswerForm';
  completedAt?: Maybe<Scalars['Float']['output']>;
  createdAt: Scalars['Float']['output'];
  id: Scalars['String']['output'];
  schema: Scalars['String']['output'];
  updatedAt: Scalars['Float']['output'];
};

export type AnswerFormResponse = {
  __typename?: 'AnswerFormResponse';
  answerForm?: Maybe<AnswerForm>;
  error?: Maybe<Error>;
};

export type AnswerFormsResponse = {
  __typename?: 'AnswerFormsResponse';
  answerForms?: Maybe<Array<BaseAnswerForm>>;
  error?: Maybe<Error>;
  totalResults?: Maybe<Scalars['Int']['output']>;
};

export type ApiFile = {
  __typename?: 'ApiFile';
  createdAt: Scalars['String']['output'];
  id: Scalars['String']['output'];
  key: Scalars['String']['output'];
  type: ApiFileType;
  url: Scalars['String']['output'];
};

export type ApiFileResponse = {
  __typename?: 'ApiFileResponse';
  error?: Maybe<Error>;
  file?: Maybe<ApiFile>;
};

export enum ApiFileType {
  AchDebitAuthorizationPdf = 'ACH_DEBIT_AUTHORIZATION_PDF',
  CreditCardAuthorizationPdf = 'CREDIT_CARD_AUTHORIZATION_PDF',
  InvoicePdf = 'INVOICE_PDF'
}

export type ApiToken = {
  __typename?: 'ApiToken';
  createdAt: Scalars['String']['output'];
  createdBy?: Maybe<User>;
  id: Scalars['ID']['output'];
  lastUsedAt?: Maybe<Scalars['String']['output']>;
  name?: Maybe<Scalars['String']['output']>;
  oauthClient?: Maybe<OAuthClientInfo>;
  revokedAt?: Maybe<Scalars['String']['output']>;
  scopes: Array<ApiTokenScope>;
  workspaceRole?: Maybe<WorkspaceRole>;
};

export enum ApiTokenScope {
  Fdx = 'FDX',
  Mcp = 'MCP',
  RestApi = 'REST_API'
}

export type ApiTokensResponse = {
  __typename?: 'ApiTokensResponse';
  apiTokens: Array<ApiToken>;
  error?: Maybe<Error>;
};

export type ApplyNickelBalanceDepositsToInvoicesResponse = {
  __typename?: 'ApplyNickelBalanceDepositsToInvoicesResponse';
  /** One per row, in the order the rows were sent. Empty when refused or only validated. */
  applications: Array<RecordExternalInvoicePaymentResponse>;
  error?: Maybe<Error>;
};

export type Approval = {
  __typename?: 'Approval';
  /**
   * Who may decide the request and how far each of its approval rules has got.
   * Null only on a request decided under a legacy approval policy.
   */
  approvalRequest?: Maybe<ApprovalRequestState>;
  billDueDate?: Maybe<Scalars['Float']['output']>;
  billPayable?: Maybe<BillPayable>;
  createdAt?: Maybe<Scalars['Float']['output']>;
  createdBy?: Maybe<User>;
  id: Scalars['String']['output'];
  paymentMethod?: Maybe<PaymentMethod>;
  payoutAccount?: Maybe<ApprovalPayoutAccount>;
  recurringBill?: Maybe<RecurringBill>;
  status?: Maybe<ApprovalStatus>;
  submittedAmountInCents?: Maybe<Scalars['Int']['output']>;
  /** Null unless the request's bill is inside the caller's bill view; `payoutAccount` names the account to everyone the request is shown to. */
  vendorPayoutMethod?: Maybe<VendorPayoutMethod>;
};

/** What an approval rule gates; mirrors Prisma's ApprovalAction. */
export enum ApprovalAction {
  BillPay = 'BILL_PAY',
  InvoiceChargeSavedMethod = 'INVOICE_CHARGE_SAVED_METHOD',
  InvoiceSend = 'INVOICE_SEND'
}

/**
 * Who an approver entry asks for: one named member (USER), `quantity` members
 * of a workspace role (ROLE), or `quantity` members of any role that may approve
 * the action (ANY_APPROVER).
 */
export enum ApprovalApproverKind {
  AnyApprover = 'ANY_APPROVER',
  Role = 'ROLE',
  User = 'USER'
}

export type ApprovalApproverOption = {
  __typename?: 'ApprovalApproverOption';
  id: Scalars['ID']['output'];
  name: Scalars['String']['output'];
};

/**
 * Who the editor may offer as an approver for one action: only members who can
 * sign in and whose role may approve it, and only roles that may approve it.
 * Anyone else would be refused on save, so they are never offered. Empty for an
 * action no role can approve yet.
 */
export type ApprovalApproverOptions = {
  __typename?: 'ApprovalApproverOptions';
  action: ApprovalAction;
  roles: Array<ApprovalApproverOption>;
  users: Array<ApprovalApproverOption>;
};

export enum ApprovalDecisionValue {
  Approved = 'APPROVED',
  Rejected = 'REJECTED'
}

/**
 * The vendor account an approval request pays into, without its numbers. It has
 * no id, so a client cache stores it inside its approval and never merges it
 * into the VendorPayoutMethod it summarizes.
 */
export type ApprovalPayoutAccount = {
  __typename?: 'ApprovalPayoutAccount';
  /** The last four of the account number (the Plaid mask when linked through Plaid), or of a wire's IBAN or local account number; null for a check. */
  accountNumberLastFour?: Maybe<Scalars['String']['output']>;
  bankName?: Maybe<Scalars['String']['output']>;
  /** With `state`, where a check is mailed; the street is left out. */
  city?: Maybe<Scalars['String']['output']>;
  state?: Maybe<Scalars['String']['output']>;
  type: VendorPayoutMethodType;
};

export type ApprovalRequestDecision = {
  __typename?: 'ApprovalRequestDecision';
  createdAt: Scalars['Float']['output'];
  decision: ApprovalDecisionValue;
  userId: Scalars['ID']['output'];
};

/**
 * A rule-routed request as the approval rules see it. Every rule must be
 * satisfied, by distinct people within a rule; one rejection rejects it.
 */
export type ApprovalRequestState = {
  __typename?: 'ApprovalRequestState';
  decisions: Array<ApprovalRequestDecision>;
  rules: Array<ApprovalRuleProgress>;
  /** Whether the caller may approve or reject it now (changeApprovalStatus agrees). */
  viewerCanDecide: Scalars['Boolean']['output'];
};

export enum ApprovalRequestStatus {
  Approved = 'APPROVED',
  /** Withdrawn by a member. */
  Canceled = 'CANCELED',
  /** Approved, but the send or charge then failed. */
  Failed = 'FAILED',
  Pending = 'PENDING',
  Rejected = 'REJECTED',
  /**
   * Closed without running its action, because the invoice changed, was
   * archived or was sent another way while it waited, or a newer request
   * replaced it; outcomeMessage says which.
   */
  Voided = 'VOIDED'
}

/**
 * Why `checkApproval` routes a payment to approval. The org's approval rules
 * decide whoever sends the payment, so a matching rule is the only reason.
 */
export enum ApprovalRouteReason {
  /** An approval rule (its amount range or vendors) matched this payment. */
  PolicyCondition = 'POLICY_CONDITION'
}

/**
 * A conditional approval rule. Every matching rule applies, and opens a request
 * even when the requester could approve it; amounts read
 * `minAmountCents < amount <= maxAmountCents` ("over" / "up to").
 */
export type ApprovalRule = {
  __typename?: 'ApprovalRule';
  action: ApprovalAction;
  active: Scalars['Boolean']['output'];
  approvers: Array<ApprovalRuleApprover>;
  excludeRequestor: Scalars['Boolean']['output'];
  /** The current version's id; changes with every save. */
  id: Scalars['ID']['output'];
  /** Stable across saves: what the editor saves and archives a rule by. */
  lineageId: Scalars['ID']['output'];
  maxAmountCents?: Maybe<Scalars['Int']['output']>;
  minAmountCents?: Maybe<Scalars['Int']['output']>;
  position: Scalars['Int']['output'];
  problems: Array<ApprovalRuleProblem>;
  /** Customers an invoice rule is limited to; empty means any customer. */
  retailCustomerIds: Array<Scalars['ID']['output']>;
  /** Vendors a bill rule is limited to; empty means any vendor. */
  vendorIds: Array<Scalars['ID']['output']>;
  version: Scalars['Int']['output'];
};

export type ApprovalRuleApprover = {
  __typename?: 'ApprovalRuleApprover';
  kind: ApprovalApproverKind;
  /** How many distinct people the entry needs; always 1 for USER. */
  quantity: Scalars['Int']['output'];
  /** Set for USER. */
  userId?: Maybe<Scalars['ID']['output']>;
  /** Set for ROLE. */
  workspaceRoleId?: Maybe<Scalars['ID']['output']>;
};

export type ApprovalRuleApproverInput = {
  kind: ApprovalApproverKind;
  /** Defaults to 1; ignored for USER. */
  quantity?: InputMaybe<Scalars['Int']['input']>;
  userId?: InputMaybe<Scalars['ID']['input']>;
  workspaceRoleId?: InputMaybe<Scalars['ID']['input']>;
};

export type ApprovalRuleInput = {
  action: ApprovalAction;
  active?: InputMaybe<Scalars['Boolean']['input']>;
  approvers: Array<ApprovalRuleApproverInput>;
  excludeRequestor?: InputMaybe<Scalars['Boolean']['input']>;
  /** Omit to create a rule; set it to save a new version of that rule. */
  lineageId?: InputMaybe<Scalars['ID']['input']>;
  maxAmountCents?: InputMaybe<Scalars['Int']['input']>;
  minAmountCents?: InputMaybe<Scalars['Int']['input']>;
  /** Omit to place a new rule last, or to keep an existing rule where it is. */
  position?: InputMaybe<Scalars['Int']['input']>;
  retailCustomerIds?: InputMaybe<Array<Scalars['ID']['input']>>;
  vendorIds?: InputMaybe<Array<Scalars['ID']['input']>>;
};

/** Something about rules taken together: an overlap or a duplicate. */
export type ApprovalRuleIssue = {
  __typename?: 'ApprovalRuleIssue';
  code: Scalars['String']['output'];
  message: Scalars['String']['output'];
  /** Lineage ids of the rules involved. */
  ruleIds: Array<Scalars['ID']['output']>;
};

/**
 * Something about one rule found by checking it against the team as it is now.
 * A `blocking` problem is refused on save (a person who can no longer sign in, a
 * role without the approve permission); the rest are warnings that a change to
 * the team would fix (a role with too few members today).
 */
export type ApprovalRuleProblem = {
  __typename?: 'ApprovalRuleProblem';
  /** The approver entry it concerns, by position; null for the whole rule. */
  approverIndex?: Maybe<Scalars['Int']['output']>;
  blocking: Scalars['Boolean']['output'];
  code: Scalars['String']['output'];
  message: Scalars['String']['output'];
};

export type ApprovalRuleProgress = {
  __typename?: 'ApprovalRuleProgress';
  /** The rule version the request was made under. */
  ruleId: Scalars['ID']['output'];
  satisfied: Scalars['Boolean']['output'];
  seats: Array<ApprovalSeat>;
};

/**
 * The org's active approval rules, for every action, as plain sentences that
 * each name what they gate. Readable by any member; the editor's own read
 * (approvalRules) is the workflow managers'.
 */
export type ApprovalRuleSummariesResponse = {
  __typename?: 'ApprovalRuleSummariesResponse';
  error?: Maybe<Error>;
  summaries: Array<Scalars['String']['output']>;
  /** Whether the org has ever saved an approval rule, archived ones included. */
  usesApprovalRules: Scalars['Boolean']['output'];
};

export type ApprovalRulesResponse = {
  __typename?: 'ApprovalRulesResponse';
  approverOptions: Array<ApprovalApproverOptions>;
  error?: Maybe<Error>;
  issues: Array<ApprovalRuleIssue>;
  rules: Array<ApprovalRule>;
};

/** One approver entry's seat on a rule, and who fills it so far. */
export type ApprovalSeat = {
  __typename?: 'ApprovalSeat';
  /** The member whose approval fills the seat; null while it is open. */
  filledByUserId?: Maybe<Scalars['ID']['output']>;
  kind: ApprovalApproverKind;
  /** Set for a USER seat. */
  userId?: Maybe<Scalars['ID']['output']>;
  /** Set for a ROLE seat. */
  workspaceRoleId?: Maybe<Scalars['ID']['output']>;
};

/** Bill-approval outcomes; mirrors Prisma's BillApprovalStatus exactly. */
export enum ApprovalStatus {
  Approved = 'APPROVED',
  Deleted = 'DELETED',
  /** Approved, but the payment it fired could not be made. */
  Failed = 'FAILED',
  Pending = 'PENDING',
  Rejected = 'REJECTED'
}

export type ApprovalSummary = {
  __typename?: 'ApprovalSummary';
  confidence: Scalars['String']['output'];
  decision: Scalars['String']['output'];
  hardBlockers: Array<Scalars['String']['output']>;
  keyFacts?: Maybe<KeyFacts>;
  recommendedControls?: Maybe<RecommendedControls>;
  riskFlags: Array<Scalars['String']['output']>;
  scorecard: Scorecard;
  summary: Scalars['String']['output'];
};

export type ApprovalSummaryResponse = {
  __typename?: 'ApprovalSummaryResponse';
  approvalSummary?: Maybe<ApprovalSummary>;
  error?: Maybe<Error>;
};

export type ApprovalsResponse = {
  __typename?: 'ApprovalsResponse';
  approvals?: Maybe<Array<Approval>>;
  error?: Maybe<Error>;
  totalResults?: Maybe<Scalars['Int']['output']>;
};

export type ApproveOAuthAuthorizationResponse = {
  __typename?: 'ApproveOAuthAuthorizationResponse';
  error?: Maybe<Error>;
  redirectUrl?: Maybe<Scalars['String']['output']>;
};

export enum ArAgingBucket {
  Current = 'CURRENT',
  Days_1_30 = 'DAYS_1_30',
  Days_31_60 = 'DAYS_31_60',
  Days_61_90 = 'DAYS_61_90',
  Days_90Plus = 'DAYS_90_PLUS'
}

export type ArAgingBucketSummary = {
  __typename?: 'ArAgingBucketSummary';
  bucket: ArAgingBucket;
  count: Scalars['Int']['output'];
  totalCents: Scalars['Int']['output'];
};

export type ArAgingInvoice = {
  __typename?: 'ArAgingInvoice';
  bucket: ArAgingBucket;
  daysPastDue: Scalars['Int']['output'];
  dueDate?: Maybe<Scalars['Float']['output']>;
  invoiceName: Scalars['String']['output'];
  issueDate?: Maybe<Scalars['Float']['output']>;
  outstandingCents: Scalars['Int']['output'];
  questionFormId: Scalars['String']['output'];
};

export type ArAgingReport = {
  __typename?: 'ArAgingReport';
  asOfDate: Scalars['Float']['output'];
  buckets: Array<ArAgingBucketSummary>;
  invoices: Array<ArAgingInvoice>;
  retailCustomerId: Scalars['String']['output'];
  totalOutstandingCents: Scalars['Int']['output'];
  totalPastDueCents: Scalars['Int']['output'];
};

export type ArAgingReportResponse = {
  __typename?: 'ArAgingReportResponse';
  error?: Maybe<Error>;
  report?: Maybe<ArAgingReport>;
};

/**
 * A debit memo: an amount added to what an invoice asks for after it was
 * issued — an NSF fee after a bounced check, a finance charge, shipping added
 * to the order. The mirror of ExternalArPayment, which brings the balance down:
 * a live memo is included in its invoice's requestedAmount. Voided memos are
 * kept for audit and count toward nothing. See
 * docs/external-payments/debit-memos.md.
 */
export type ArDebitMemo = {
  __typename?: 'ArDebitMemo';
  amountInCents: Scalars['Int']['output'];
  createdAt: Scalars['Float']['output'];
  /** Shown to the customer on the payment page next to the amount. */
  description: Scalars['String']['output'];
  /**
   * The caller's own id for the transaction this memo represents — the
   * idempotency reference passed to recordInvoiceDebitMemo. Unique per invoice
   * among live memos; voiding releases it.
   */
  externalReferenceId?: Maybe<Scalars['String']['output']>;
  id: Scalars['String']['output'];
  questionForm?: Maybe<QuestionForm>;
  /**
   * Team member who recorded it. An org-scoped API token resolves to its owning
   * user; null means no authenticated user, or one since deleted.
   */
  recordedByUser?: Maybe<User>;
  voidedAt?: Maybe<Scalars['Float']['output']>;
};

export type ArchiveApprovalRuleResponse = {
  __typename?: 'ArchiveApprovalRuleResponse';
  archived?: Maybe<Scalars['Boolean']['output']>;
  error?: Maybe<Error>;
};

export type Attachment = {
  __typename?: 'Attachment';
  contentType?: Maybe<Scalars['String']['output']>;
  dateCreated?: Maybe<Scalars['String']['output']>;
  fileSize?: Maybe<Scalars['Int']['output']>;
  id?: Maybe<Scalars['String']['output']>;
  includeWhenSent?: Maybe<Scalars['Boolean']['output']>;
  modifiedDate?: Maybe<Scalars['String']['output']>;
  name?: Maybe<Scalars['String']['output']>;
  sourceModifiedDate?: Maybe<Scalars['String']['output']>;
};

export type AuthPayload = {
  __typename?: 'AuthPayload';
  /**
   * Every method the user could receive a code through. Populated whenever a
   * second factor is owed. When it holds more than one method, no code has been
   * sent yet and twoFactorMethod is null: the client must ask the user to pick,
   * then call login again with that choice.
   */
  availableTwoFactorMethods?: Maybe<Array<TwoFactorMethod>>;
  /**
   * Present when WorkOS refused the password because the email's organization
   * requires SSO, and Nickel found (or just repaired) the SSO route for its
   * domain. The client should offer SSO for this email, as it does when
   * enterpriseSsoForEmail returns a match. No other field is set.
   */
  enterpriseSso?: Maybe<EnterpriseSsoInfo>;
  error?: Maybe<Error>;
  id?: Maybe<Scalars['ID']['output']>;
  /**
   * Present when WorkOS Radar challenged the login instead of completing it. A
   * one-time code was emailed to the address that logged in. Hand this token and
   * that code to completeRadarChallenge to finish the same login. No other field
   * is set. Grants nothing on its own.
   */
  radarChallengeToken?: Maybe<Scalars['String']['output']>;
  token?: Maybe<Scalars['String']['output']>;
  /**
   * Present when a text-message code was just sent (twoFactorMethod = PHONE).
   * A short-lived token naming the account that passed the first factor; the
   * client hands it back to verifyTwoFactorSMSAuthCode so the code is checked
   * against THAT account's enrolled number. A phone number is not unique across
   * accounts, so without it a login could resolve to another account sharing
   * the number. Grants nothing on its own.
   */
  twoFactorChallengeToken?: Maybe<Scalars['String']['output']>;
  twoFactorIdentifier?: Maybe<Scalars['String']['output']>;
  twoFactorMethod?: Maybe<TwoFactorMethod>;
  twoFactorRequired?: Maybe<Scalars['Boolean']['output']>;
};

export type AuthorizationRecordLookupResponse = {
  __typename?: 'AuthorizationRecordLookupResponse';
  error?: Maybe<Error>;
  summaries: Array<AuthorizationRecordSummary>;
};

export type AuthorizationRecordPdfResponse = {
  __typename?: 'AuthorizationRecordPdfResponse';
  error?: Maybe<Error>;
  url?: Maybe<Scalars['String']['output']>;
};

export type AuthorizationRecordSummary = {
  __typename?: 'AuthorizationRecordSummary';
  /** In effect: not cancelled, deleted or past its expiration date. */
  active: Scalars['Boolean']['output'];
  /** ISO 8601: when the payment method in force was saved to it. */
  completedAt: Scalars['String']['output'];
  /**
   * Whether the payment method in force came through the customer's request
   * link. A record is issued only for these: a method the merchant entered has
   * no customer authorization for Nickel to attest to.
   */
  completedThroughLink: Scalars['Boolean']['output'];
  /**
   * Whether a consent record (IP address, device, the terms as presented) was
   * kept when the customer completed it. Older authorizations have none.
   */
  consentRecorded: Scalars['Boolean']['output'];
  customerName: Scalars['String']['output'];
  debitAuthorizationId: Scalars['String']['output'];
  merchantName: Scalars['String']['output'];
  organizationId: Scalars['String']['output'];
  paymentCount: Scalars['Int']['output'];
  paymentMethod: Scalars['String']['output'];
  /** ISO 8601: when the merchant's request was issued; null when there was none. */
  requestedAt?: Maybe<Scalars['String']['output']>;
};

export type AutoDebitInput = {
  debitAuthorizationId: Scalars['String']['input'];
  questionFormAndWithdrawalDateInput: Array<QuestionFormPaymentAndWithdrawalDateInput>;
};

export type BackfillRemindersResponse = {
  __typename?: 'BackfillRemindersResponse';
  error?: Maybe<Error>;
  scheduledCount?: Maybe<Scalars['Int']['output']>;
};

export type BankAccount = {
  __typename?: 'BankAccount';
  balance?: Maybe<Scalars['String']['output']>;
  bankName?: Maybe<Scalars['String']['output']>;
  /** The QuickBooks chart-of-account this bank account posts to. */
  chartOfAccountId?: Maybe<Scalars['String']['output']>;
  default?: Maybe<Scalars['Boolean']['output']>;
  holderName?: Maybe<Scalars['String']['output']>;
  id: Scalars['String']['output'];
  lastFour?: Maybe<Scalars['String']['output']>;
  lastTwo?: Maybe<Scalars['String']['output']>;
  plaidMask?: Maybe<Scalars['String']['output']>;
  routingNumber?: Maybe<Scalars['String']['output']>;
  type?: Maybe<Scalars['String']['output']>;
};

export type BankRoutingNumber = {
  __typename?: 'BankRoutingNumber';
  bankName?: Maybe<Scalars['String']['output']>;
  bankRoutingNumber?: Maybe<Scalars['String']['output']>;
};

export type BankRoutingNumberResponse = {
  __typename?: 'BankRoutingNumberResponse';
  bankRoutingNumber?: Maybe<BankRoutingNumber>;
  error?: Maybe<Error>;
};

/**
 * Where a payer pushes an ACH credit, domestic wire, RTP or FedNow payment to
 * pay an invoice: the credit-only account number minted for it on Nickel's AR
 * pay-in account (docs/bank-transfer-payin.md). The number is the invoice reference, so no
 * memo is needed.
 */
export type BankTransferInstructions = {
  __typename?: 'BankTransferInstructions';
  accountHolderAddress?: Maybe<Scalars['String']['output']>;
  accountHolderName: Scalars['String']['output'];
  accountNumber: Scalars['String']['output'];
  accountType: Scalars['String']['output'];
  bankAddress: Scalars['String']['output'];
  bankName: Scalars['String']['output'];
  routingNumber: Scalars['String']['output'];
};

export enum BankTransferRail {
  AchCredit = 'ACH_CREDIT',
  Fednow = 'FEDNOW',
  Rtp = 'RTP',
  Wire = 'WIRE'
}

export type BaseAnswerForm = {
  __typename?: 'BaseAnswerForm';
  completedAt?: Maybe<Scalars['Float']['output']>;
  id: Scalars['String']['output'];
  payment?: Maybe<Payment>;
};

export type BatchAutoDebitInput = {
  autoDebitInput: Array<AutoDebitInput>;
  idempotencyKey?: InputMaybe<Scalars['String']['input']>;
};

export type BatchAutoDebitResponse = {
  __typename?: 'BatchAutoDebitResponse';
  error?: Maybe<Error>;
  questionFormErrors?: Maybe<Array<QuestionFormError>>;
  results: Array<BatchAutoDebitResult>;
};

export type BatchAutoDebitResult = {
  __typename?: 'BatchAutoDebitResult';
  /**
   * Set when an approval rule held the charge: no payment was made, and the
   * customer is charged once the request is approved. Charging the invoice
   * again while it waits returns the same request.
   */
  heldForApproval?: Maybe<InvoiceApprovalRequest>;
  payment?: Maybe<Payment>;
  questionForm: QuestionForm;
};

export type BatchBillPayInput = {
  arrivalDateEpochMillis?: InputMaybe<Scalars['Float']['input']>;
  billId: Scalars['String']['input'];
  /**
   * Pay a Nickel Balance-funded ACH bill to the vendor instantly (RTP or
   * FedNow) at no fee, when the vendor's bank and the org qualify. Absent or
   * false sends it by standard ACH.
   */
  instantPayoutRequested?: InputMaybe<Scalars['Boolean']['input']>;
  merchantPaymentMethodId: Scalars['String']['input'];
  notificationEmails: Array<Scalars['String']['input']>;
  vendorId: Scalars['String']['input'];
  vendorMemo?: InputMaybe<Scalars['String']['input']>;
  vendorPayoutMethodId: Scalars['String']['input'];
  withdrawalDateEpochMillis?: InputMaybe<Scalars['Float']['input']>;
};

export type BatchBillPayResponse = {
  __typename?: 'BatchBillPayResponse';
  billErrors: Array<BillError>;
  bills: Array<Bill>;
  error?: Maybe<Error>;
};

/**
 * One customer's bank account to save, with the customer's signed
 * authorization.
 */
export type BatchCreateRetailCustomerAchDebitAuthorizationInput = {
  accountNumber: Scalars['String']['input'];
  /** checking or savings. */
  accountType: Scalars['String']['input'];
  /** The account is already saved on another customer, and that is intended. */
  acknowledgeSharedAccount?: InputMaybe<Scalars['Boolean']['input']>;
  /**
   * Added to the customer's emails only when the customer has none, Nickel's or
   * its accounting integration's: a payment method cannot be saved without one.
   * Ignored for a customer that has an email. Sending it needs
   * get_paid.customers.edit.
   */
  email?: InputMaybe<Scalars['String']['input']>;
  /**
   * The customer's signed authorization document: files from prepareFileUpload
   * (or Penny attachments). At least one; each must belong to the caller's
   * organization.
   */
  fileIds: Array<Scalars['ID']['input']>;
  retailCustomerId: Scalars['String']['input'];
  routingNumber: Scalars['String']['input'];
};

/**
 * One row of batchCreateRetailCustomerACHDebitAuthorizations, in the order the
 * rows were sent. Exactly one of three things happened: the account was saved
 * (`debitAuthorization`), the customer already had this exact account live so
 * nothing was written (`alreadySaved`, with that authorization), or the row
 * failed (`error`) and nothing was saved for it.
 */
export type BatchCreateRetailCustomerAchDebitAuthorizationResult = {
  __typename?: 'BatchCreateRetailCustomerACHDebitAuthorizationResult';
  alreadySaved: Scalars['Boolean']['output'];
  debitAuthorization?: Maybe<DebitAuthorization>;
  /** The row's email was added to a customer that had none. Can be true on a failed row. */
  emailAdded: Scalars['Boolean']['output'];
  error?: Maybe<Error>;
  retailCustomerId: Scalars['String']['output'];
};

export type BatchCreateRetailCustomerAchDebitAuthorizationsInput = {
  /** At most 25 rows, one per customer. */
  authorizations: Array<BatchCreateRetailCustomerAchDebitAuthorizationInput>;
};

export type BatchCreateRetailCustomerAchDebitAuthorizationsResponse = {
  __typename?: 'BatchCreateRetailCustomerACHDebitAuthorizationsResponse';
  error?: Maybe<Error>;
  results: Array<BatchCreateRetailCustomerAchDebitAuthorizationResult>;
};

export type BatchRequestCustomerDebitAuthorizationRequestInput = {
  emails: Array<Scalars['String']['input']>;
  expirationDateEpochMillis?: InputMaybe<Scalars['Float']['input']>;
  memo?: InputMaybe<Scalars['String']['input']>;
  paymentMethodTypes?: InputMaybe<Array<DebitAuthorizationMethodType>>;
  retailCustomerId: Scalars['String']['input'];
  transactionLimitCents?: InputMaybe<Scalars['Int']['input']>;
};

export type BatchRequestCustomerDebitAuthorizationResult = {
  __typename?: 'BatchRequestCustomerDebitAuthorizationResult';
  debitAuthorization?: Maybe<DebitAuthorization>;
  error?: Maybe<Error>;
  retailCustomerId: Scalars['String']['output'];
  url?: Maybe<Scalars['String']['output']>;
};

export type BatchRequestCustomerDebitAuthorizationsInput = {
  /**
   * Optional idempotency key. When supplied, a retry within ~4 hours that
   * passes the same key short-circuits to the prior result instead of sending
   * another email or soft-deleting the existing pending DebitAuthorization.
   */
  idempotencyKey?: InputMaybe<Scalars['String']['input']>;
  requests: Array<BatchRequestCustomerDebitAuthorizationRequestInput>;
};

export type BatchRequestCustomerDebitAuthorizationsResponse = {
  __typename?: 'BatchRequestCustomerDebitAuthorizationsResponse';
  error?: Maybe<Error>;
  results: Array<BatchRequestCustomerDebitAuthorizationResult>;
};

export type BatchRequestVendorDeliveryDetailsInput = {
  /**
   * Optional idempotency key. When supplied, a retry within ~4 hours that
   * passes the same key short-circuits to the prior result instead of sending
   * another delivery-details request email.
   */
  idempotencyKey?: InputMaybe<Scalars['String']['input']>;
  requests: Array<BatchRequestVendorDeliveryDetailsRequestInput>;
};

export type BatchRequestVendorDeliveryDetailsRequestInput = {
  /**
   * Require ACH: true blocks CHECK for the vendor (blockedPayoutMethods), false
   * lifts the block. Omit or null to keep the vendor's saved setting.
   */
  achOnly?: InputMaybe<Scalars['Boolean']['input']>;
  emails: Array<Scalars['String']['input']>;
  plaidRequired?: InputMaybe<Scalars['Boolean']['input']>;
  taxRequested?: InputMaybe<Scalars['Boolean']['input']>;
  vendorId: Scalars['String']['input'];
  wireEnabled?: InputMaybe<Scalars['Boolean']['input']>;
};

export type BatchRequestVendorDeliveryDetailsResponse = {
  __typename?: 'BatchRequestVendorDeliveryDetailsResponse';
  error?: Maybe<Error>;
  results: Array<BatchRequestVendorDeliveryDetailsResult>;
};

export type BatchRequestVendorDeliveryDetailsResult = {
  __typename?: 'BatchRequestVendorDeliveryDetailsResult';
  error?: Maybe<Error>;
  /**
   * The vendor's self-service link, the one emailed to them. Null unless the
   * caller holds bill_pay.vendors.manage_payment_accounts,
   * bill_pay.vendors.edit_check_addresses and
   * bill_pay.vendors.edit_international_wire_info: the link's token sets the
   * vendor's bank account, check address and wire details on its own.
   */
  url?: Maybe<Scalars['String']['output']>;
  vendorId: Scalars['String']['output'];
};

export type BatchSendArAgingReportEmailItemInput = {
  recipientEmails: Array<Scalars['String']['input']>;
  retailCustomerId: Scalars['String']['input'];
};

export type BatchSendArAgingReportEmailsResponse = {
  __typename?: 'BatchSendArAgingReportEmailsResponse';
  eligibleCustomerCount: Scalars['Int']['output'];
  error?: Maybe<Error>;
  jobId?: Maybe<Scalars['String']['output']>;
  queuedRetailCustomerIds?: Maybe<Array<Scalars['String']['output']>>;
  skippedNoEmailCount: Scalars['Int']['output'];
  skippedNoEmailRetailCustomerIds?: Maybe<Array<Scalars['String']['output']>>;
};

export type Bill = {
  __typename?: 'Bill';
  accountingSource?: Maybe<AccountingSource>;
  amountDueInCents?: Maybe<Scalars['Int']['output']>;
  attachments?: Maybe<Array<Attachment>>;
  /**
   * What the caller can do to this bill right now, and why not. Always a list;
   * nullable only so clients typed against the whole `Bill` need not select it.
   */
  availableActions?: Maybe<Array<ActionAvailability>>;
  billData?: Maybe<BillData>;
  billPayables?: Maybe<Array<BillPayable>>;
  createdBy?: Maybe<User>;
  createdSource?: Maybe<BillCreationSource>;
  documents?: Maybe<Array<Scalars['String']['output']>>;
  /**
   * Total raw bytes of `documents` — same contract and same per-document S3 HEAD
   * cost as QuestionForm.documentsTotalSizeBytes.
   */
  documentsTotalSizeBytes?: Maybe<Scalars['Int']['output']>;
  externalId?: Maybe<Scalars['String']['output']>;
  id: Scalars['String']['output'];
  internalNote?: Maybe<Scalars['String']['output']>;
  lastSyncedAt?: Maybe<Scalars['Float']['output']>;
  modifiedDate?: Maybe<Scalars['String']['output']>;
  organizationId?: Maybe<Scalars['String']['output']>;
  /**
   * When the bill was paid in full, as epoch milliseconds — the latest of its
   * payment records (an external payment's merchant-stated date, a Nickel
   * payment's initiation), and null until the bill is fully paid or when it was
   * paid entirely in the accounting system. The value the Paid-on column shows
   * and the `bills(orderBy: "paidOn")` sort orders by; mirrors
   * QuestionForm.completedAt on the AR side.
   */
  paidAt?: Maybe<Scalars['Float']['output']>;
  reference?: Maybe<Scalars['String']['output']>;
  tags?: Maybe<Array<Tag>>;
  totalAmountInCents?: Maybe<Scalars['Int']['output']>;
  vendor?: Maybe<Vendor>;
};

export type BillAndApprovalPolicy = {
  __typename?: 'BillAndApprovalPolicy';
  bill: Bill;
  /** Whether paying this bill opens an approval request. */
  requiresApproval: Scalars['Boolean']['output'];
  /**
   * Why this bill's payment is routed to approval; null when it is not, or when
   * its vendor could not be resolved. As `CheckApprovalResponse.routedBecause`.
   */
  routedBecause?: Maybe<ApprovalRouteReason>;
};

/**
 * Provenance of a bill.
 *
 * MERCHANT_API covers apps/rest and external agents on the MCP server alike:
 * both present an org-scoped token, which resolves to its owning user, so the
 * accompanying createdBy is the token's owner rather than someone who typed
 * anything. Bills penny created before PENNY existed also carry it.
 *
 * PENNY means penny created it while createdBy was talking to it.
 *
 * RECURRING_SERIES rows are issued by the recurring sweep with no one acting at
 * issue time; their createdBy is the user who set the series up.
 *
 * ACCOUNTING_SYNC deliberately does not name which integration — accountingSource
 * already does.
 *
 * ACCOUNTANT means a member of the merchant's accounting firm acted inside the
 * merchant's workspace, in an accountant session or with a token minted in one.
 * createdBy names them only as the merchant sees its firm's people (an id and
 * an email), and is null once they have left the firm.
 */
export enum BillCreationSource {
  Accountant = 'ACCOUNTANT',
  AccountingSync = 'ACCOUNTING_SYNC',
  MerchantApi = 'MERCHANT_API',
  MerchantCsvImport = 'MERCHANT_CSV_IMPORT',
  MerchantDashboard = 'MERCHANT_DASHBOARD',
  NickelAdmin = 'NICKEL_ADMIN',
  Penny = 'PENNY',
  RecurringSeries = 'RECURRING_SERIES',
  System = 'SYSTEM'
}

/** Column mapping for a bill CSV bulk import (0-based column indices). */
export type BillCsvColumnMappingInput = {
  /** 0-based index of the amount column (dollars; parsed leniently, e.g. "$1,250.00"). */
  amount: Scalars['Int']['input'];
  /** 0-based index of the description column, if present (falls back to the invoice number). */
  description?: InputMaybe<Scalars['Int']['input']>;
  /** 0-based index of the due-date column (parsed leniently; ISO YYYY-MM-DD preferred). */
  dueDate: Scalars['Int']['input'];
  /** 0-based index of the invoice-number column, if present (also used to de-duplicate against existing bills). */
  invoiceNumber?: InputMaybe<Scalars['Int']['input']>;
  /** 0-based index of the column holding the vendor's name. The vendor must already exist; unmatched rows are reported as failed. */
  vendorName: Scalars['Int']['input'];
};

export type BillData = {
  __typename?: 'BillData';
  amountDue?: Maybe<Scalars['Float']['output']>;
  currency?: Maybe<Scalars['String']['output']>;
  dueDate?: Maybe<Scalars['String']['output']>;
  issueDate?: Maybe<Scalars['String']['output']>;
  lastSyncedAt?: Maybe<Scalars['Float']['output']>;
  lineItems?: Maybe<Array<LineItem>>;
  modifiedDate?: Maybe<Scalars['String']['output']>;
  reference?: Maybe<Scalars['String']['output']>;
  /** Lowercased copy of reference, written by every bill writer alongside it; exposed so clients matching case-insensitively can fall back when a drifted row lacks reference. */
  referenceLowercase?: Maybe<Scalars['String']['output']>;
  sourceModifiedDate?: Maybe<Scalars['String']['output']>;
  status?: Maybe<Scalars['String']['output']>;
  subTotal?: Maybe<Scalars['Float']['output']>;
  supplierRef?: Maybe<SupplierRef>;
  taxAmount?: Maybe<Scalars['Float']['output']>;
  totalAmount?: Maybe<Scalars['Float']['output']>;
};

/**
 * What we could read off an uploaded bill, for the bill form to prefill. Every
 * field is nullable and null is the common case, not an error: a field the
 * document does not show, or shows illegibly, returns null so the merchant types
 * it, which is strictly better than a confident wrong guess.
 *
 * A suggestion, never a fact. The merchant confirms these values before the bill
 * is saved, so nothing here may be treated as authoritative by a caller.
 */
export type BillDocumentParse = {
  __typename?: 'BillDocumentParse';
  /** The amount due now, in cents. Null when the bill is not in US dollars. */
  amountCents?: Maybe<Scalars['Int']['output']>;
  /** The invoice or bill number the vendor printed. */
  billNumber?: Maybe<Scalars['String']['output']>;
  /**
   * The due date as YYYY-MM-DD: printed on the bill, or the invoice date plus
   * printed payment terms ("Net 30").
   */
  dueDate?: Maybe<Scalars['String']['output']>;
  /** A few words on what the bill is for, such as "October grounds maintenance". */
  memo?: Maybe<Scalars['String']['output']>;
  /**
   * The business that issued the bill, as printed. The form uses it to warn when
   * it differs from the vendor the merchant chose; it never picks the vendor.
   */
  vendorName?: Maybe<Scalars['String']['output']>;
};

export type BillError = {
  __typename?: 'BillError';
  bill: Bill;
  error: Error;
};

export type BillPayable = {
  __typename?: 'BillPayable';
  amountDueCents: Scalars['Int']['output'];
  amountPaidCents: Scalars['Int']['output'];
  approvals?: Maybe<Array<Approval>>;
  /**
   * What the caller can do to this bill payable right now, and why not. Always a list;
   * nullable only so clients typed against the whole `BillPayable` need not select it.
   */
  availableActions?: Maybe<Array<ActionAvailability>>;
  bill?: Maybe<Bill>;
  billPayments?: Maybe<Array<BillPayment>>;
  /**
   * Who paid or scheduled this payable. Set from the acting session at create
   * time, so on a SCHEDULED payable — executed days later by a worker with no
   * request context — this is the only record of who queued it. Once executed,
   * the resulting BillPayment's paymentAttempts carry the same identity.
   *
   * Null when no authenticated user was on the request, when the recorded user
   * has since been deleted, and on payables created before the column existed;
   * read null as "unknown", not "system". An org-scoped API token is NOT such a
   * case — it resolves to its owning user (ApiToken.userId is non-nullable), and
   * that is the person recorded here.
   *
   * Unlike Bill.createdBy this is NOT scoped to the reading org: an accounting
   * firm acting on the merchant's behalf is recorded as itself, and hiding it
   * would leave the merchant unable to see who scheduled their own payment.
   * Nickel staff never land here — under admin impersonation the acting session
   * resolves to the merchant user, not the admin.
   */
  createdBy?: Maybe<User>;
  currentOnRecurringBill?: Maybe<RecurringBill>;
  dueDate?: Maybe<Scalars['Float']['output']>;
  /**
   * Estimated UTC calendar date (YYYY-MM-DD) the funds reach the vendor, for a
   * SCHEDULED payable: withdrawalDate plus the delivery rail's transit time —
   * the pre-execution sibling of BillPayment.expectedDeliveryDate, sharing its
   * business-day math. Recomputed with the org's CURRENT delivery speeds rather
   * than persisted at schedule time, so it can shift if the org's plan tier
   * changes after scheduling. Null for non-SCHEDULED payables and when a method
   * needed for the math is missing.
   */
  expectedDeliveryDate?: Maybe<Scalars['String']['output']>;
  /** Merchant-stated date the external money moved (epoch ms). EXTERNAL only. */
  externalPaidAt?: Maybe<Scalars['Float']['output']>;
  id: Scalars['String']['output'];
  kind: BillPayableKind;
  merchantPaymentMethod?: Maybe<PaymentMethod>;
  openBalance?: Maybe<Scalars['Int']['output']>;
  /**
   * Team member who recorded the external payment — kind, not this field, carries
   * provenance. EXTERNAL only.
   *
   * An org-scoped API token does NOT produce a null here: it resolves to its
   * owning user (ApiToken.userId is non-nullable), which is who getIntendedUser
   * returns. Null means no authenticated user was on the request at all, or that
   * the recorded user has since been deleted.
   */
  recordedByUser?: Maybe<User>;
  status?: Maybe<Scalars['String']['output']>;
  type?: Maybe<Scalars['String']['output']>;
  vendor: Vendor;
  vendorMemo?: Maybe<Scalars['String']['output']>;
  /** Null outside the caller's bill view. */
  vendorPayoutMethod?: Maybe<VendorPayoutMethod>;
  withdrawalDate?: Maybe<Scalars['Float']['output']>;
};

/**
 * Provenance of a BillPayable (docs/external-payments/design.md §4.1): NICKEL
 * payables move money through Nickel rails; EXTERNAL payables are the
 * record-based "mark bill as paid" — status PAID with no
 * BillPayment/Charge/VendorPayout, voidable as the undo.
 */
export enum BillPayableKind {
  External = 'EXTERNAL',
  Nickel = 'NICKEL',
  VendorSite = 'VENDOR_SITE'
}

export type BillPayableResponse = {
  __typename?: 'BillPayableResponse';
  billPayable?: Maybe<BillPayable>;
  error?: Maybe<Error>;
};

export type BillPayablesResponse = {
  __typename?: 'BillPayablesResponse';
  error?: Maybe<Error>;
  invoices?: Maybe<Array<BillPayable>>;
  totalResults?: Maybe<Scalars['Int']['output']>;
};

export type BillPayment = {
  __typename?: 'BillPayment';
  achVendorReturn?: Maybe<VendorReturn>;
  activity?: Maybe<PaymentActivity>;
  amountInCents?: Maybe<Scalars['Int']['output']>;
  /**
   * What the caller can do to this bill payment right now, and why not. Always a list;
   * nullable only so clients typed against the whole `BillPayment` need not select it.
   */
  availableActions?: Maybe<Array<ActionAvailability>>;
  billPayable: BillPayable;
  charges?: Maybe<Array<Charge>>;
  createdAt?: Maybe<Scalars['Float']['output']>;
  /**
   * Expected date (YYYY-MM-DD) the funds land in the vendor's account — the
   * same projection the vendor-facing tracking page shows (see
   * computeExpectedDelivery). Prefers the vendor payout's actual settlement
   * date once paid and its provider-cached estimate while in flight, falling
   * back to business-day math from the delivery rail, funding payment method,
   * and the org's plan tier. Null when the payment or its payout FAILED
   * (nothing will be delivered) or when the inputs to an estimate are missing.
   */
  expectedDeliveryDate?: Maybe<Scalars['String']['output']>;
  externalPaymentIntentId?: Maybe<Scalars['String']['output']>;
  id: Scalars['String']['output'];
  paymentAttempts?: Maybe<Array<PaymentAttempt>>;
  paymentMethod?: Maybe<PaymentMethod>;
  /**
   * Absolute URL to download this bill payment's receipt PDF, generated on
   * demand by GET /api/pdf. Sessionless: the URL carries a token signed for this
   * bill payment, so it can be shared directly (e.g. quoted in a Penny email
   * reply). Null until the payment is submitted (CREATED), when it FAILED
   * (including cancellations), and for a caller outside its organization.
   */
  pdfUrl?: Maybe<Scalars['String']['output']>;
  platformFeeInCents?: Maybe<Scalars['Int']['output']>;
  status?: Maybe<Scalars['String']['output']>;
  submittedAmountInCents?: Maybe<Scalars['Int']['output']>;
  updatedAt?: Maybe<Scalars['Float']['output']>;
  /** Null outside the caller's bill view. */
  vendorPayoutMethod?: Maybe<VendorPayoutMethod>;
};

export type BillPaymentResponse = {
  __typename?: 'BillPaymentResponse';
  billPayment?: Maybe<BillPayment>;
  error?: Maybe<Error>;
};

export type BillResponse = {
  __typename?: 'BillResponse';
  bill?: Maybe<Bill>;
  error?: Maybe<Error>;
};

export type BillingDetails = {
  __typename?: 'BillingDetails';
  address?: Maybe<AddressDetails>;
  companyName?: Maybe<Scalars['String']['output']>;
  email?: Maybe<Scalars['String']['output']>;
  name?: Maybe<Scalars['String']['output']>;
};

export type BillingDetailsInput = {
  address?: InputMaybe<AddressDetailsInput>;
  email?: InputMaybe<Scalars['String']['input']>;
  name?: InputMaybe<Scalars['String']['input']>;
  phone?: InputMaybe<Scalars['String']['input']>;
};

export type BillsResponse = {
  __typename?: 'BillsResponse';
  bills?: Maybe<Array<Bill>>;
  error?: Maybe<Error>;
  totalResults?: Maybe<Scalars['Int']['output']>;
};

/** One field of a bulk-import column mapping, resolved against the file's header row. */
export type BulkImportMappedColumn = {
  __typename?: 'BulkImportMappedColumn';
  /** The 0-based column index that was passed for the field. */
  columnIndex: Scalars['Int']['output'];
  /** The mapping field name as passed (e.g. vendorName, amount, description). */
  field: Scalars['String']['output'];
  /** The header cell at that index, or null for a headerless file or an index past the header row. */
  header?: Maybe<Scalars['String']['output']>;
};

/**
 * Result of a CSV bulk import. The *Count fields are authoritative totals; the
 * skipped/failed detail arrays are capped (large imports report counts only).
 */
export type BulkImportResponse = {
  __typename?: 'BulkImportResponse';
  createdCount: Scalars['Int']['output'];
  /** Names of the first few created records, for a human-readable summary reply. */
  createdSample: Array<Scalars['String']['output']>;
  error?: Maybe<Error>;
  failed: Array<BulkImportRowOutcome>;
  failedCount: Scalars['Int']['output'];
  /**
   * The caller's column mapping echoed back with the header text each index
   * landed on, so the mapping can be checked against the file's own headers
   * after the import has read it (e.g. field "description" on column 25 whose
   * header is "InvoiceAmountPaid" is a miscount).
   */
  mappedColumns: Array<BulkImportMappedColumn>;
  /** How many CREATED rows have a due date already in the past (they show as overdue immediately). Only the invoice importer computes this; other importers return 0. */
  pastDueCount: Scalars['Int']['output'];
  skipped: Array<BulkImportRowOutcome>;
  skippedCount: Scalars['Int']['output'];
};

/** One imported row that was skipped (already on file) or failed (could not be created). */
export type BulkImportRowOutcome = {
  __typename?: 'BulkImportRowOutcome';
  /** Human-readable identifier for the row (name / email / invoice number). */
  identifier?: Maybe<Scalars['String']['output']>;
  /** Why the row was skipped or failed. */
  reason: Scalars['String']['output'];
  /** The row number a spreadsheet app shows for this row: 1-based, counting the header row and any blank rows above it. */
  rowNumber: Scalars['Int']['output'];
};

export type BuyerNotificationSetting = {
  __typename?: 'BuyerNotificationSetting';
  buyerPaymentFailed?: Maybe<NotificationSetting>;
  receipt?: Maybe<NotificationSetting>;
  refundCompleted?: Maybe<NotificationSetting>;
  smsPaymentLinks?: Maybe<NotificationSetting>;
};

export type CancelBillPaymentResponse = {
  __typename?: 'CancelBillPaymentResponse';
  billPayment?: Maybe<BillPayment>;
  error?: Maybe<Error>;
};

export type CancelCustomDomainVerificationResponse = {
  __typename?: 'CancelCustomDomainVerificationResponse';
  cancelledDomain?: Maybe<Scalars['String']['output']>;
  error?: Maybe<Error>;
};

export type CancelRetailCustomerDebitAuthorizationResponse = {
  __typename?: 'CancelRetailCustomerDebitAuthorizationResponse';
  error?: Maybe<Error>;
};

export type CancelScheduledInvoicePaymentResponse = {
  __typename?: 'CancelScheduledInvoicePaymentResponse';
  error?: Maybe<Error>;
  questionForm?: Maybe<QuestionForm>;
};

export type CancelScheduledPaymentResponse = {
  __typename?: 'CancelScheduledPaymentResponse';
  billPayable?: Maybe<BillPayable>;
  error?: Maybe<Error>;
};

export type CancelScheduledPlanChangeResponse = {
  __typename?: 'CancelScheduledPlanChangeResponse';
  error?: Maybe<Error>;
};

export type CardBillingAddressInput = {
  city?: InputMaybe<Scalars['String']['input']>;
  state?: InputMaybe<Scalars['String']['input']>;
  streetAddress?: InputMaybe<Scalars['String']['input']>;
  zip?: InputMaybe<Scalars['String']['input']>;
};

/** A card network, as 3-D Secure routing decides which processor can authenticate it. */
export enum CardBrand {
  Amex = 'AMEX',
  Discover = 'DISCOVER',
  Mastercard = 'MASTERCARD',
  Visa = 'VISA'
}

export type CardDetails = {
  __typename?: 'CardDetails';
  brand?: Maybe<Scalars['String']['output']>;
  expMonth?: Maybe<Scalars['String']['output']>;
  expYear?: Maybe<Scalars['String']['output']>;
  fundingType?: Maybe<Scalars['String']['output']>;
  holderName?: Maybe<Scalars['String']['output']>;
  last4?: Maybe<Scalars['String']['output']>;
};

export type CardDetailsInput = {
  /**
   * External 3DS at autopay enrollment on a 3DS org (the Basis Theory frame
   * opened this session for the token intent). The server runs a non-payment
   * `add-card` authentication before vaulting — the frame equivalent of the
   * legacy $0 3DS verify.
   */
  basisTheoryThreeDSSessionId?: InputMaybe<Scalars['String']['input']>;
  /**
   * Required for a card. Token-intent id from the Basis Theory frame. The
   * server vaults the card in Basis Theory and mints the processor twin;
   * `provider` still names the downstream processor.
   */
  basisTheoryTokenIntentId?: InputMaybe<Scalars['String']['input']>;
  /** Not accepted: a processor nonce is refused unless `basisTheoryTokenIntentId` is set. */
  nonce?: InputMaybe<Scalars['String']['input']>;
  /**
   * The payer's browser block for a Basis Theory-captured card on Airwallex
   * (see `confirmPayment.payerDevice`): the save-time PaymentConsent is
   * verified on it. Ignored for Braintree.
   */
  payerDevice?: InputMaybe<CardPayerDeviceInput>;
  provider: Scalars['String']['input'];
  /**
   * Second call after the issuer's challenge ran in the frame: the server reads
   * the challenge result instead of authenticating again.
   */
  threeDSChallengeCompleted?: InputMaybe<Scalars['Boolean']['input']>;
};

export type CardDetailsTokenResponse = {
  __typename?: 'CardDetailsTokenResponse';
  error?: Maybe<Error>;
  /** The token was minted in Increase's sandbox, which the elements must be told. */
  sandbox?: Maybe<Scalars['Boolean']['output']>;
  /**
   * Short-lived (1 hour) token for `@increasebank/card-elements`, scoped to
   * the one card. The browser exchanges it with Increase directly; the PAN
   * never touches Nickel.
   */
  token?: Maybe<Scalars['String']['output']>;
};

/**
 * What the payer's browser reports about itself when a bill is paid by card
 * right now. Forwarded (with the request's ip + user-agent, read server-side)
 * as Airwallex `device_data` so their risk engine can score the
 * customer-initiated charge. Optional and advisory; never used for scheduled
 * or approval-executed charges, which have no payer present.
 */
export type CardPayerDeviceInput = {
  /**
   * The `data-order-session-id` the dashboard handed Airwallex's
   * device-fingerprint script for this checkout attempt. Forwarded verbatim as
   * `device_data.device_id`, which is how Airwallex ties the fingerprint the
   * script captured to this charge. 1–128 chars of [A-Za-z0-9_-], per their
   * spec; anything else is dropped rather than sent.
   */
  deviceId?: InputMaybe<Scalars['String']['input']>;
  language?: InputMaybe<Scalars['String']['input']>;
  screenColorDepth?: InputMaybe<Scalars['Int']['input']>;
  screenHeight?: InputMaybe<Scalars['Int']['input']>;
  screenWidth?: InputMaybe<Scalars['Int']['input']>;
  timezone?: InputMaybe<Scalars['String']['input']>;
};

/** One Increase-enforced spending limit on a card. With several, the most restrictive matching limit wins at authorization. */
export type CardSpendingLimit = {
  __typename?: 'CardSpendingLimit';
  amountCents: Scalars['Int']['output'];
  id: Scalars['ID']['output'];
  interval: CardSpendingLimitInterval;
};

export type CardSpendingLimitInput = {
  amountCents: Scalars['Int']['input'];
  interval: CardSpendingLimitInterval;
};

/**
 * The window an Increase-enforced card spending limit applies to. Increase's
 * names and clock: per_day resets at midnight UTC, per_month on the 1st of the
 * month at midnight UTC, all_time over the life of the card.
 */
export enum CardSpendingLimitInterval {
  AllTime = 'all_time',
  PerDay = 'per_day',
  PerMonth = 'per_month'
}

export type CardTransaction = {
  __typename?: 'CardTransaction';
  amountCents: Scalars['Int']['output'];
  createdAt: Scalars['Float']['output'];
  currency: Scalars['String']['output'];
  /**
   * Why a `declined` transaction was declined. Null on every other status, and
   * on a decline recorded before its reason was stored.
   */
  decline?: Maybe<CardTransactionDecline>;
  description?: Maybe<Scalars['String']['output']>;
  id: Scalars['ID']['output'];
  merchantName?: Maybe<Scalars['String']['output']>;
  status: CardTransactionStatus;
};

export type CardTransactionDecline = {
  __typename?: 'CardTransactionDecline';
  /** Increase's decline reason as sent: cvv2_mismatch, insufficient_funds, ... */
  code: Scalars['String']['output'];
  /** What happened and what to do about it, in a sentence or two. */
  explanation: Scalars['String']['output'];
  /** A few words for display, such as "Wrong security code". */
  label: Scalars['String']['output'];
};

export enum CardTransactionStatus {
  Completed = 'completed',
  Declined = 'declined',
  Pending = 'pending',
  Refunded = 'refunded',
  /**
   * The hold came down without a settlement (voided by the merchant or
   * expired). Nothing booked; `recentTransactions` leaves these out.
   */
  Reversed = 'reversed'
}

export type CashFlowReportSummary = {
  __typename?: 'CashFlowReportSummary';
  cashIncrease?: Maybe<Scalars['Float']['output']>;
  endingCash?: Maybe<Scalars['Float']['output']>;
  netIncome?: Maybe<Scalars['Float']['output']>;
  operatingActivities?: Maybe<Scalars['Float']['output']>;
  operatingAdjustments?: Maybe<OperatingAdjustments>;
};

export type ChangeApprovalResponse = {
  __typename?: 'ChangeApprovalResponse';
  approvals?: Maybe<Array<Approval>>;
  error?: Maybe<Error>;
};

export type ChangePasswordKeepingSessionPayload = {
  __typename?: 'ChangePasswordKeepingSessionPayload';
  /**
   * Replaces the caller's session token, which the change revoked, and expires
   * when it would have. Null when that session had no time left.
   */
  token?: Maybe<Scalars['String']['output']>;
};

export type Charge = {
  __typename?: 'Charge';
  achReturn?: Maybe<Return>;
  achVendorReturn?: Maybe<VendorReturn>;
  amountCapturedInCents?: Maybe<Scalars['Int']['output']>;
  amountRefundedInCents?: Maybe<Scalars['Int']['output']>;
  billPayment?: Maybe<BillPayment>;
  chargeback?: Maybe<Chargeback>;
  createdAt?: Maybe<Scalars['Float']['output']>;
  externalChargeId?: Maybe<Scalars['String']['output']>;
  /**
   * Machine-readable code for the failure `failureMessage` describes — the ACH
   * return code for a returned debit (e.g. "R03" = no account found / account
   * closed), the acquirer's decline code for a card, or one of the internal
   * sentinels the event handlers write ("chargeback", "canceled").
   *
   * Null on a charge that never failed, and on a failed charge whose provider
   * supplied no code. Pair it with `failureMessage` rather than mapping it
   * yourself: the code namespace is per-rail and per-provider, so the same
   * string can mean different things on a card charge and an ACH debit.
   */
  failureCode?: Maybe<Scalars['String']['output']>;
  failureMessage?: Maybe<Scalars['String']['output']>;
  fees?: Maybe<Array<Fee>>;
  id: Scalars['String']['output'];
  payment?: Maybe<Payment>;
  paymentMethod?: Maybe<PaymentMethod>;
  payout?: Maybe<Payout>;
  refunds?: Maybe<Array<Refund>>;
  status?: Maybe<Scalars['String']['output']>;
  vendorPayout?: Maybe<VendorPayout>;
};

export type Chargeback = {
  __typename?: 'Chargeback';
  activity?: Maybe<PaymentActivity>;
  amountCents?: Maybe<Scalars['Int']['output']>;
  /** True when canRespond holds and the rail exposes an accept call (Braintree, Airwallex); Whop acceptance is record-only and still allowed. */
  canAccept: Scalars['Boolean']['output'];
  /**
   * True while the merchant can still act: status OPEN, kind CHARGEBACK,
   * respondBy in the future (or unknown), the org's chargebackReconciliation flag
   * on, and no submission PENDING_REVIEW / APPROVED / SUBMITTED / FAILED. A
   * FAILED submission is ops' to refire (chargebackCli respond), not a merchant
   * retry; only a REJECTED one (or none) reopens the dispute to the merchant.
   */
  canRespond: Scalars['Boolean']['output'];
  charge: Charge;
  createdAt?: Maybe<Scalars['Float']['output']>;
  /** The payer's name as captured on the payment attempt. */
  customerName?: Maybe<Scalars['String']['output']>;
  events: Array<ChargebackEvent>;
  externalChargebackId?: Maybe<Scalars['String']['output']>;
  id: Scalars['String']['output'];
  kind: ChargebackKind;
  network?: Maybe<Scalars['String']['output']>;
  payout?: Maybe<Payout>;
  /** braintree | airwallex | whop */
  provider?: Maybe<Scalars['String']['output']>;
  /** The QuestionForm behind the disputed payment, when there is one (payment links have none). */
  questionForm?: Maybe<QuestionForm>;
  reasonCode?: Maybe<Scalars['String']['output']>;
  /** The processor's own words for the reason, when it sent any. */
  reasonDescription?: Maybe<Scalars['String']['output']>;
  reasonFamily?: Maybe<ChargebackReasonFamily>;
  resolvedAt?: Maybe<Scalars['Float']['output']>;
  respondBy?: Maybe<Scalars['Float']['output']>;
  respondedAt?: Maybe<Scalars['Float']['output']>;
  status: ChargebackStatus;
  /** The latest response, whatever its status; null until the merchant or the system compiles one. */
  submission?: Maybe<ChargebackSubmission>;
  submissions: Array<ChargebackSubmission>;
};

export type ChargebackEvent = {
  __typename?: 'ChargebackEvent';
  createdAt: Scalars['Float']['output'];
  id: Scalars['String']['output'];
  transition: ChargebackTransitionKind;
};

export type ChargebackEvidence = {
  __typename?: 'ChargebackEvidence';
  addedBy: ChargebackEvidenceAddedBy;
  createdAt: Scalars['Float']['output'];
  /** Short-lived presigned GET; null when the row has no file (a note-only entry). */
  downloadUrl?: Maybe<Scalars['String']['output']>;
  filename?: Maybe<Scalars['String']['output']>;
  id: Scalars['String']['output'];
  mimeType?: Maybe<Scalars['String']['output']>;
  note?: Maybe<Scalars['String']['output']>;
  sizeBytes?: Maybe<Scalars['Int']['output']>;
  submittedAt?: Maybe<Scalars['Float']['output']>;
  type: ChargebackEvidenceType;
};

export enum ChargebackEvidenceAddedBy {
  Merchant = 'MERCHANT',
  Ops = 'OPS',
  System = 'SYSTEM'
}

export enum ChargebackEvidenceType {
  Authorization = 'AUTHORIZATION',
  Correspondence = 'CORRESPONDENCE',
  DeliveryProof = 'DELIVERY_PROOF',
  Invoice = 'INVOICE',
  Other = 'OTHER',
  RefundReceipt = 'REFUND_RECEIPT',
  Statement = 'STATEMENT'
}

export type ChargebackEvidenceUploadInput = {
  filename: Scalars['String']['input'];
  mimeType: Scalars['String']['input'];
  sizeBytes: Scalars['Int']['input'];
  type?: InputMaybe<ChargebackEvidenceType>;
  /** The key returned by prepareChargebackEvidenceUpload after the browser PUT the bytes. */
  uploadKey: Scalars['String']['input'];
};

/**
 * CHARGEBACK is a network dispute; AUTO_REFUND is Whop refunding instead of
 * letting an alert become one; INQUIRY is a retrieval request / RFI the system
 * answers on the merchant's behalf and the Disputes page never lists.
 */
export enum ChargebackKind {
  AutoRefund = 'AUTO_REFUND',
  Chargeback = 'CHARGEBACK',
  Inquiry = 'INQUIRY'
}

export enum ChargebackReasonFamily {
  CreditNotProcessed = 'CREDIT_NOT_PROCESSED',
  Duplicate = 'DUPLICATE',
  Fraud = 'FRAUD',
  NotAsDescribed = 'NOT_AS_DESCRIBED',
  NotReceived = 'NOT_RECEIVED',
  Other = 'OTHER'
}

export type ChargebackResponse = {
  __typename?: 'ChargebackResponse';
  chargeback?: Maybe<Chargeback>;
  error?: Maybe<Error>;
};

export enum ChargebackStatus {
  Accepted = 'ACCEPTED',
  Expired = 'EXPIRED',
  Lost = 'LOST',
  Open = 'OPEN',
  UnderReview = 'UNDER_REVIEW',
  Won = 'WON'
}

export type ChargebackSubmission = {
  __typename?: 'ChargebackSubmission';
  createdAt: Scalars['Float']['output'];
  evidence: Array<ChargebackEvidence>;
  id: Scalars['String']['output'];
  note?: Maybe<Scalars['String']['output']>;
  /** Ops' reason when REJECTED, or the responder failure when FAILED; shown to the merchant only for REJECTED. */
  reviewNote?: Maybe<Scalars['String']['output']>;
  reviewedAt?: Maybe<Scalars['Float']['output']>;
  status: ChargebackSubmissionStatus;
  submittedAt?: Maybe<Scalars['Float']['output']>;
};

export enum ChargebackSubmissionStatus {
  Approved = 'APPROVED',
  Failed = 'FAILED',
  PendingReview = 'PENDING_REVIEW',
  Rejected = 'REJECTED',
  Submitted = 'SUBMITTED'
}

export enum ChargebackTransitionKind {
  Accepted = 'ACCEPTED',
  AutoRefunded = 'AUTO_REFUNDED',
  Expired = 'EXPIRED',
  Inquiry = 'INQUIRY',
  Lost = 'LOST',
  Opened = 'OPENED',
  UnderReview = 'UNDER_REVIEW',
  Won = 'WON'
}

export type ChargebackWin = {
  __typename?: 'ChargebackWin';
  activity?: Maybe<PaymentActivity>;
  amountCents?: Maybe<Scalars['Int']['output']>;
  chargeback?: Maybe<Chargeback>;
  id?: Maybe<Scalars['String']['output']>;
  payout?: Maybe<Payout>;
};

/**
 * The Disputes page: an org's chargebacks of kind CHARGEBACK, newest first.
 * Inquiries and auto-refunds are ops-only and never listed here.
 */
export type ChargebacksResponse = {
  __typename?: 'ChargebacksResponse';
  chargebacks: Array<Chargeback>;
  error?: Maybe<Error>;
  /** Chargebacks the merchant can still act on (canRespond). */
  openCount: Scalars['Int']['output'];
};

export type ChartOfAccount = {
  __typename?: 'ChartOfAccount';
  description?: Maybe<Scalars['String']['output']>;
  fullyQualifiedCategory?: Maybe<Scalars['String']['output']>;
  fullyQualifiedName?: Maybe<Scalars['String']['output']>;
  id?: Maybe<Scalars['String']['output']>;
  name?: Maybe<Scalars['String']['output']>;
  nominalCode?: Maybe<Scalars['String']['output']>;
  status?: Maybe<Scalars['String']['output']>;
  type?: Maybe<Scalars['String']['output']>;
};

export type ChartOfAccountsResponse = {
  __typename?: 'ChartOfAccountsResponse';
  chartOfAccounts?: Maybe<Array<ChartOfAccount>>;
  error?: Maybe<Error>;
  totalResults?: Maybe<Scalars['Int']['output']>;
};

export type Check = {
  __typename?: 'Check';
  checkNumber?: Maybe<Scalars['String']['output']>;
  id: Scalars['String']['output'];
};

export type CheckApprovalInput = {
  billId: Scalars['String']['input'];
  paymentMethodId: Scalars['String']['input'];
};

export type CheckApprovalResponse = {
  __typename?: 'CheckApprovalResponse';
  /**
   * The approval rules this payment matches, each as one plain sentence ("For
   * Bills from any vendor over $400.00, require approval from any Admin to
   * pay"); empty when none does.
   */
  approvalRuleSummaries?: Maybe<Array<Scalars['String']['output']>>;
  error?: Maybe<Error>;
  /** Whether this payment opens an approval request; null on a refusal. */
  requiresApproval?: Maybe<Scalars['Boolean']['output']>;
  /** Why the payment is routed to approval; null when it is not. */
  routedBecause?: Maybe<ApprovalRouteReason>;
};

export type CheckApprovalsResponse = {
  __typename?: 'CheckApprovalsResponse';
  billAndApprovalPolicies: Array<BillAndApprovalPolicy>;
  error?: Maybe<Error>;
};

export type CheckCustomDomainAvailabilityResponse = {
  __typename?: 'CheckCustomDomainAvailabilityResponse';
  error?: Maybe<Error>;
  subdomainClaimed?: Maybe<Scalars['Boolean']['output']>;
};

/**
 * What we could read off the front of an uploaded check, for the deposit form to
 * prefill. Every field is nullable and null is the common case, not an error:
 * an unreadable amount or a check with no printed payer block returns null so
 * the merchant types it, which is strictly better than a confident wrong guess.
 *
 * A suggestion, never a fact. The merchant confirms these values before the
 * deposit is sent, and the amount they confirm is both what the bank is asked to
 * deposit and what the settlement-risk engine compares against the bank's own
 * parse — so nothing here may be treated as authoritative by a caller.
 */
export type CheckImageParse = {
  __typename?: 'CheckImageParse';
  /** The amount in cents, or null when it could not be read confidently. */
  amountCents?: Maybe<Scalars['Int']['output']>;
  /** Who wrote the check, or null when the payer block is absent or illegible. */
  payerName?: Maybe<Scalars['String']['output']>;
};

export enum CheckImageSide {
  Back = 'BACK',
  Front = 'FRONT'
}

export enum CheckStatus {
  Canceled = 'canceled',
  Delivered = 'delivered',
  Deposited = 'deposited',
  Issued = 'issued',
  Mailed = 'mailed',
  Rejected = 'rejected',
  Returned = 'returned',
  Stopped = 'stopped'
}

export enum CheckStopDisposition {
  Refund = 'REFUND',
  ReissueCheck = 'REISSUE_CHECK',
  ResendAch = 'RESEND_ACH'
}

export type ClientAccessChange = {
  canAccess: Scalars['Boolean']['input'];
  userId: Scalars['ID']['input'];
};

/**
 * How an accounting firm decides who may enter its clients. ALL_MEMBERS: every
 * active member enters every client. LISTED: a member enters the clients a role
 * manager ticked them for; the firm's role managers (team_org.team.manage_roles)
 * enter every client.
 */
export enum ClientAccessMode {
  AllMembers = 'ALL_MEMBERS',
  Listed = 'LISTED'
}

export type ClientAccessModePayload = {
  __typename?: 'ClientAccessModePayload';
  error?: Maybe<Error>;
  mode?: Maybe<ClientAccessMode>;
};

export type ClientAccessPage = {
  __typename?: 'ClientAccessPage';
  client?: Maybe<FirmClient>;
  error?: Maybe<Error>;
  mode?: Maybe<ClientAccessMode>;
  /** Every active member of the firm. */
  rows?: Maybe<Array<ClientAccessRow>>;
};

export type ClientAccessRow = {
  __typename?: 'ClientAccessRow';
  /** Always true in an all-members firm, and for a role manager. */
  canAccess: Scalars['Boolean']['output'];
  /** When the row was written, in epoch milliseconds. */
  grantedAt?: Maybe<Scalars['Float']['output']>;
  /**
   * Who ticked the box. Null for the rows written when the firm switched to
   * LISTED, and for the row a new client's creator gets.
   */
  grantedBy?: Maybe<User>;
  /** Holds team_org.team.manage_roles, so always has access: the box is locked. */
  managesClientAccess: Scalars['Boolean']['output'];
  member: User;
};

export type CloseNickelBalanceAccountResponse = {
  __typename?: 'CloseNickelBalanceAccountResponse';
  error?: Maybe<Error>;
  organization?: Maybe<Organization>;
};

export type CloseNickelBalanceBankAccountResponse = {
  __typename?: 'CloseNickelBalanceBankAccountResponse';
  /** The closed account (its last view); the accounts listing no longer returns it. */
  account?: Maybe<NickelBalanceAccount>;
  error?: Maybe<Error>;
};

export type ConductorAuthenticateResponse = {
  __typename?: 'ConductorAuthenticateResponse';
  authFlowUrl?: Maybe<Scalars['String']['output']>;
  error?: Maybe<Error>;
};

export type ConfirmPaymentResponse = {
  __typename?: 'ConfirmPaymentResponse';
  attachedPaymentMethodId?: Maybe<Scalars['String']['output']>;
  error?: Maybe<Error>;
  /**
   * Set — with `payment` null and no `error` — when a debit-authorization
   * charge was held by the merchant's approval rules: nothing was charged, and
   * the customer is charged once the request is approved.
   */
  heldForApproval?: Maybe<InvoiceApprovalRequest>;
  payment?: Maybe<Payment>;
  scheduledWithdrawalDate?: Maybe<Scalars['Float']['output']>;
  /**
   * Set — with `payment` null and no `error` — when the issuer requires a
   * challenge; see ThreeDSChallenge.
   */
  threeDSChallenge?: Maybe<ThreeDsChallenge>;
};

/** A connector Nickel offers, from the registry in @nickeltechnology/connectors. */
export type ConnectorCatalogEntry = {
  __typename?: 'ConnectorCatalogEntry';
  /** What a connection lets Penny do, as the connect card says it. */
  can: Array<Scalars['String']['output']>;
  /** What a connection never lets Penny do. */
  cannot: Array<Scalars['String']['output']>;
  /** A connector id, such as `pandadoc`. */
  id: Scalars['String']['output'];
  name: Scalars['String']['output'];
  scopes: Array<ConnectorScope>;
};

export type ConnectorCatalogResponse = {
  __typename?: 'ConnectorCatalogResponse';
  connectors?: Maybe<Array<ConnectorCatalogEntry>>;
  error?: Maybe<Error>;
};

/**
 * An organization's or a member's account at a connector's service, read only
 * through the gateway. Never carries a credential.
 */
export type ConnectorConnection = {
  __typename?: 'ConnectorConnection';
  /** The account as the service names it. */
  accountLabel?: Maybe<Scalars['String']['output']>;
  /** The capability ids the gateway allows on this connection. */
  capabilities: Array<Scalars['String']['output']>;
  /** The member who connected it. */
  connectedByName?: Maybe<Scalars['String']['output']>;
  connectorId: Scalars['String']['output'];
  createdAt: Scalars['String']['output'];
  disconnectedAt?: Maybe<Scalars['String']['output']>;
  id: Scalars['ID']['output'];
  lastCheckedAt?: Maybe<Scalars['String']['output']>;
  /** The provider's last reported problem, while it applies. */
  lastError?: Maybe<Scalars['String']['output']>;
  lastUsedAt?: Maybe<Scalars['String']['output']>;
  scope: ConnectorScope;
  status: ConnectorConnectionStatus;
};

export enum ConnectorConnectionStatus {
  Active = 'ACTIVE',
  /** The person has a Connect Link and has not finished it. */
  Connecting = 'CONNECTING',
  Disconnected = 'DISCONNECTED',
  /** The provider refused the credential; someone must connect again. */
  NeedsReconnect = 'NEEDS_RECONNECT'
}

export type ConnectorConnectionsResponse = {
  __typename?: 'ConnectorConnectionsResponse';
  connections?: Maybe<Array<ConnectorConnection>>;
  error?: Maybe<Error>;
};

export type ConnectorReadResponse = {
  __typename?: 'ConnectorReadResponse';
  capabilityId?: Maybe<Scalars['String']['output']>;
  connectorId?: Maybe<Scalars['String']['output']>;
  /** `business`, `personal_data` or `may_contain_phi` (design.md § 5). */
  dataClass?: Maybe<Scalars['String']['output']>;
  error?: Maybe<Error>;
  /** The gateway's refusal code, beside `error`, when the call did not run or failed. */
  errorCode?: Maybe<Scalars['String']['output']>;
  /** The capability's declared output; every undeclared field was dropped. */
  output?: Maybe<Scalars['JSONObject']['output']>;
};

/**
 * Who a Penny connector connection belongs to (docs/penny-connectors/design.md):
 * one member (`PERSONAL`) or the whole organization (`WORKSPACE`).
 */
export enum ConnectorScope {
  Personal = 'PERSONAL',
  Workspace = 'WORKSPACE'
}

export type ControlPennyComputerResponse = {
  __typename?: 'ControlPennyComputerResponse';
  error?: Maybe<Error>;
  sent?: Maybe<Scalars['Boolean']['output']>;
};

export type CreateAccountingClientResponse = {
  __typename?: 'CreateAccountingClientResponse';
  error?: Maybe<Error>;
  organization?: Maybe<Organization>;
  token?: Maybe<Scalars['String']['output']>;
};

export type CreateApiTokenResponse = {
  __typename?: 'CreateApiTokenResponse';
  apiToken?: Maybe<ApiToken>;
  error?: Maybe<Error>;
  token?: Maybe<Scalars['String']['output']>;
};

export type CreateApprovalResponse = {
  __typename?: 'CreateApprovalResponse';
  approval?: Maybe<Approval>;
  error?: Maybe<Error>;
};

export type CreateMerchantAchPaymentMethodWithPlaidResponse = {
  __typename?: 'CreateMerchantACHPaymentMethodWithPlaidResponse';
  error?: Maybe<Error>;
  paymentMethod?: Maybe<PaymentMethod>;
};

export type CreateMerchantCreditCardResponse = {
  __typename?: 'CreateMerchantCreditCardResponse';
  error?: Maybe<Error>;
  paymentMethod?: Maybe<PaymentMethod>;
};

export type CreateNickelBalanceAccountResponse = {
  __typename?: 'CreateNickelBalanceAccountResponse';
  account?: Maybe<NickelBalanceAccount>;
  error?: Maybe<Error>;
};

export type CreateNickelBalanceCardInput = {
  /** The account the card spends from; omitted = the org's oldest provisioned account (the display default). */
  balanceAccountId?: InputMaybe<Scalars['ID']['input']>;
  /** Optional. Virtual cards default to the business address from Nickel Balance onboarding; physical cards to the shipping address. */
  billingAddress?: InputMaybe<NickelBalanceCardBillingAddressInput>;
  /** Defaults to virtual. */
  cardType?: InputMaybe<IssuedCardType>;
  /** Physical cards: printed on the plastic. Both required. */
  cardholderFirstName?: InputMaybe<Scalars['String']['input']>;
  cardholderLastName?: InputMaybe<Scalars['String']['input']>;
  /** Required for virtual cards; physical cards default it to "{first} {last}". */
  cardholderName?: InputMaybe<Scalars['String']['input']>;
  idempotencyKey: Scalars['String']['input'];
  nickname?: InputMaybe<Scalars['String']['input']>;
  /** Physical cards: where the plastic is mailed (USPS). Required; PO boxes are refused. */
  shippingAddress?: InputMaybe<NickelBalanceCardShippingAddressInput>;
  /**
   * Optional Increase-enforced limits, at most one per interval. Omit (or send
   * []) for no limit: spend is then gated at authorization by the account's
   * balance, so a card can be created before it's funded.
   */
  spendingLimits?: InputMaybe<Array<CardSpendingLimitInput>>;
};

export type CreateNickelBalanceCardResponse = {
  __typename?: 'CreateNickelBalanceCardResponse';
  card?: Maybe<IssuedCard>;
  error?: Maybe<Error>;
};

export type CreateNickelBalanceCheckDepositResponse = {
  __typename?: 'CreateNickelBalanceCheckDepositResponse';
  checkDeposit?: Maybe<NickelBalanceCheckDeposit>;
  error?: Maybe<Error>;
};

export type CreateNickelBalanceDepositResponse = {
  __typename?: 'CreateNickelBalanceDepositResponse';
  error?: Maybe<Error>;
  transfer?: Maybe<NickelBalanceTransfer>;
};

export type CreateNickelBalanceInternalTransferResponse = {
  __typename?: 'CreateNickelBalanceInternalTransferResponse';
  error?: Maybe<Error>;
  transfer?: Maybe<NickelBalanceTransfer>;
};

export type CreateNickelBalanceWithdrawalResponse = {
  __typename?: 'CreateNickelBalanceWithdrawalResponse';
  error?: Maybe<Error>;
  transfer?: Maybe<NickelBalanceTransfer>;
};

export type CreatePaymentResponse = {
  __typename?: 'CreatePaymentResponse';
  error?: Maybe<Error>;
  organizationInfo?: Maybe<OrganizationInfo>;
  payment?: Maybe<Payment>;
};

export type CreatePayoutMethodWithPlaidResponse = {
  __typename?: 'CreatePayoutMethodWithPlaidResponse';
  error?: Maybe<Error>;
  plaidData?: Maybe<Scalars['String']['output']>;
};

export type CreateRecurringBillResponse = {
  __typename?: 'CreateRecurringBillResponse';
  error?: Maybe<Error>;
  recurringBill?: Maybe<RecurringBill>;
};

export type CreateRecurringInvoiceResponse = {
  __typename?: 'CreateRecurringInvoiceResponse';
  error?: Maybe<Error>;
  recurringInvoice?: Maybe<RecurringInvoice>;
};

export type CreateRetailCustomerAchDebitAuthorizationResponse = {
  __typename?: 'CreateRetailCustomerACHDebitAuthorizationResponse';
  debitAuthorization?: Maybe<DebitAuthorization>;
  error?: Maybe<Error>;
};

export type CreateRetailCustomerCreditCardDebitAuthorizationResponse = {
  __typename?: 'CreateRetailCustomerCreditCardDebitAuthorizationResponse';
  debitAuthorization?: Maybe<DebitAuthorization>;
  error?: Maybe<Error>;
};

export type CreateWebhookResponse = {
  __typename?: 'CreateWebhookResponse';
  error?: Maybe<Error>;
  webhook?: Maybe<Webhook>;
};

/**
 * Whether one merchant category also emails the person who created the invoice
 * (merchant.<category>.notifyCreator), by its merchant catalog key; only the
 * categories the catalog marks creatorOptIn take it. Turning it on for a
 * category the org has switched off switches the category back on.
 */
export type CreatorNotificationInput = {
  key: Scalars['String']['input'];
  notifyCreator: Scalars['Boolean']['input'];
};

export enum CreditCardFeeMode {
  Gross = 'GROSS',
  Net = 'NET'
}

export enum CustomDomainVerificationState {
  Active = 'ACTIVE',
  Completed = 'COMPLETED',
  Delayed = 'DELAYED',
  Failed = 'FAILED',
  Unknown = 'UNKNOWN',
  Waiting = 'WAITING'
}

export type CustomDomainVerificationStatus = {
  __typename?: 'CustomDomainVerificationStatus';
  attemptsMade: Scalars['Int']['output'];
  domain?: Maybe<Scalars['String']['output']>;
  failedReason?: Maybe<Scalars['String']['output']>;
  state: CustomDomainVerificationState;
};

export type CustomDomainVerificationStatusResponse = {
  __typename?: 'CustomDomainVerificationStatusResponse';
  error?: Maybe<Error>;
  status?: Maybe<CustomDomainVerificationStatus>;
};

export type Customer = {
  __typename?: 'Customer';
  companyName?: Maybe<Scalars['String']['output']>;
  customerName?: Maybe<Scalars['String']['output']>;
  emailAddress?: Maybe<Scalars['String']['output']>;
  id?: Maybe<Scalars['String']['output']>;
};

/**
 * The Customer column of the matrix: the buyer's own switch for a customer
 * category (buyer.<category>.enabled in notificationSettings).
 */
export type CustomerNotificationInput = {
  enabled: Scalars['Boolean']['input'];
  key: Scalars['String']['input'];
};

/**
 * Where customers' replies to one customer email go, by its customer catalog
 * key (buyer.<category>.emails): the whole list, replaced. Any address may be
 * added and the list may be emptied; the receipt is not sent while its list is
 * empty.
 */
export type CustomerReplyToInput = {
  emails: Array<Scalars['String']['input']>;
  key: Scalars['String']['input'];
};

export type DeactivateNickelAccountResponse = {
  __typename?: 'DeactivateNickelAccountResponse';
  error?: Maybe<Error>;
};

export type DeactivateRecurringBillResponse = {
  __typename?: 'DeactivateRecurringBillResponse';
  error?: Maybe<Error>;
  recurringBill?: Maybe<RecurringBill>;
};

export type DeactivateRecurringInvoiceResponse = {
  __typename?: 'DeactivateRecurringInvoiceResponse';
  error?: Maybe<Error>;
  recurringInvoice?: Maybe<RecurringInvoice>;
};

export type DebitAuthorization = {
  __typename?: 'DebitAuthorization';
  active: Scalars['Boolean']['output'];
  /** What the caller can do with this saved payment method right now, and why not. */
  availableActions: Array<ActionAvailability>;
  createdBy?: Maybe<User>;
  createdSource?: Maybe<DebitAuthorizationSource>;
  deleted: Scalars['Boolean']['output'];
  expiresAt?: Maybe<Scalars['Float']['output']>;
  id: Scalars['String']['output'];
  linkToken?: Maybe<Scalars['String']['output']>;
  methodTypes?: Maybe<Array<DebitAuthorizationMethodType>>;
  paymentMethod?: Maybe<PaymentMethod>;
  requestEmails?: Maybe<Array<RequestEmail>>;
  requestedBy?: Maybe<User>;
  retailCustomer?: Maybe<RetailCustomer>;
  transactionLimitInCents?: Maybe<Scalars['Int']['output']>;
  url?: Maybe<Scalars['String']['output']>;
};

/**
 * What the authorization page showed the customer and when they accepted each
 * term, as the page reports it. It is kept with the consent record and not
 * verified by the server.
 */
export type DebitAuthorizationConsentInput = {
  disclosures?: InputMaybe<Array<DebitAuthorizationDisclosureInput>>;
  terms?: InputMaybe<Array<DebitAuthorizationTermInput>>;
  termsUrl?: InputMaybe<Scalars['String']['input']>;
  /** When the page first showed the request, ISO 8601. */
  viewedAt?: InputMaybe<Scalars['String']['input']>;
};

export type DebitAuthorizationDisclosureInput = {
  heading: Scalars['String']['input'];
  text: Scalars['String']['input'];
};

export enum DebitAuthorizationMethodType {
  Ach = 'ACH',
  CreditCard = 'CREDIT_CARD'
}

export type DebitAuthorizationResponse = {
  __typename?: 'DebitAuthorizationResponse';
  debitAuthorization?: Maybe<DebitAuthorization>;
  error?: Maybe<Error>;
};

/**
 * Provenance of a customer's saved payment details — the AR mirror of
 * VendorPayoutMethodSource.
 *
 * CUSTOMER_SELF_SERVE means the details were entered through the customer's
 * request-email link. That link is also visible to the merchant on the
 * dashboard, so it names the form used, not provably the customer.
 *
 * MERCHANT_API covers apps/rest and external agents on the MCP server alike:
 * both present an org-scoped token, which resolves to its owning user, so the
 * accompanying createdBy is the token's owner rather than someone who typed
 * anything. Details penny entered before PENNY existed also carry it.
 *
 * PENNY means penny entered them while createdBy was talking to it.
 *
 * ACCOUNTANT means a member of the merchant's accounting firm acted inside the
 * merchant's workspace, in an accountant session or with a token minted in one.
 * createdBy names them only as the merchant sees its firm's people (an id and
 * an email), and is null once they have left the firm.
 */
export enum DebitAuthorizationSource {
  Accountant = 'ACCOUNTANT',
  CustomerSelfServe = 'CUSTOMER_SELF_SERVE',
  MerchantApi = 'MERCHANT_API',
  MerchantDashboard = 'MERCHANT_DASHBOARD',
  NickelAdmin = 'NICKEL_ADMIN',
  Penny = 'PENNY',
  System = 'SYSTEM'
}

export type DebitAuthorizationTermInput = {
  /** When the customer accepted this term, ISO 8601. */
  acceptedAt?: InputMaybe<Scalars['String']['input']>;
  label: Scalars['String']['input'];
  statement: Scalars['String']['input'];
  value: Scalars['String']['input'];
};

export type DecideMcpActionsResponse = {
  __typename?: 'DecideMcpActionsResponse';
  error?: Maybe<Error>;
  results?: Maybe<Array<McpActionDecisionResult>>;
};

export type DecideNickelBalanceInboundDebitResponse = {
  __typename?: 'DecideNickelBalanceInboundDebitResponse';
  /** The rule `alwaysAllowCompany` created or found; null otherwise. */
  allowRule?: Maybe<NickelBalanceDebitAllowRule>;
  error?: Maybe<Error>;
  review?: Maybe<NickelBalanceInboundDebitReview>;
};

export type DeleteBillResponse = {
  __typename?: 'DeleteBillResponse';
  error?: Maybe<Error>;
};

export type DeleteReferralInviteResponse = {
  __typename?: 'DeleteReferralInviteResponse';
  error?: Maybe<Error>;
  success?: Maybe<Scalars['Boolean']['output']>;
};

export type DeleteReminderRuleResponse = {
  __typename?: 'DeleteReminderRuleResponse';
  error?: Maybe<Error>;
  success?: Maybe<Scalars['Boolean']['output']>;
};

export type DeleteVendorPayoutMethodResponse = {
  __typename?: 'DeleteVendorPayoutMethodResponse';
  error?: Maybe<Error>;
  vendor?: Maybe<Vendor>;
};

export type DeleteWebhookResponse = {
  __typename?: 'DeleteWebhookResponse';
  error?: Maybe<Error>;
  success?: Maybe<Scalars['Boolean']['output']>;
};

export type DeleteWorkspaceRoleResponse = {
  __typename?: 'DeleteWorkspaceRoleResponse';
  error?: Maybe<Error>;
};

/**
 * Internal-only reasons an admin can attach when denying an organization's
 * application. Recorded on the org's onboarding info, the #signups Slack
 * message, and PostHog — never shown to the merchant.
 */
export enum DenialReason {
  AlloyDenied = 'ALLOY_DENIED',
  FailedRfi = 'FAILED_RFI',
  HeimdallInsight = 'HEIMDALL_INSIGHT',
  InformationMismatch = 'INFORMATION_MISMATCH',
  InsufficientBalance = 'INSUFFICIENT_BALANCE',
  InsufficientHistory = 'INSUFFICIENT_HISTORY',
  /**
   * The business is too small, or otherwise not one Nickel is built for (the AI
   * underwriter's BUSINESS FIT dimension). A judgment on fit, not on risk.
   */
  NotAFit = 'NOT_A_FIT',
  Other = 'OTHER',
  /**
   * The signup is a payer (or payee) of an existing Nickel merchant who did not
   * need an account at all. Unlike every other reason it changes what the
   * applicant receives: the closed-account email replaces the generic denial.
   */
  PayerNotMerchant = 'PAYER_NOT_MERCHANT'
}

/** The wallet a card was provisioned into — Increase's token_requestor. */
export enum DigitalWallet {
  ApplePay = 'apple_pay',
  GooglePay = 'google_pay',
  SamsungPay = 'samsung_pay',
  Unknown = 'unknown'
}

/** Increase's DigitalWalletToken.status. inactive = provisioning started, passcode not yet entered. */
export enum DigitalWalletTokenStatus {
  Active = 'active',
  Deactivated = 'deactivated',
  Declined = 'declined',
  Inactive = 'inactive',
  Suspended = 'suspended'
}

export type DisableNickelBalanceCardResponse = {
  __typename?: 'DisableNickelBalanceCardResponse';
  card?: Maybe<IssuedCard>;
  error?: Maybe<Error>;
};

export type DisconnectAccountingIntegrationResponse = {
  __typename?: 'DisconnectAccountingIntegrationResponse';
  error?: Maybe<Error>;
  organization?: Maybe<Organization>;
};

export type DisconnectConnectorConnectionResponse = {
  __typename?: 'DisconnectConnectorConnectionResponse';
  connection?: Maybe<ConnectorConnection>;
  error?: Maybe<Error>;
};

export type DisconnectSocialSsoPayload = {
  __typename?: 'DisconnectSocialSsoPayload';
  error?: Maybe<Error>;
};

export type EditFollowUpQuestionsInput = {
  organizationId: Scalars['String']['input'];
  /** Up to five, asked in this order. Null goes back to the AI underwriter's. */
  questions?: InputMaybe<Array<FollowUpQuestionInput>>;
  /** Ignored: staff's questions belong to the org, not a summary. */
  underwritingRunId?: InputMaybe<Scalars['String']['input']>;
};

export type EditFollowUpQuestionsPayload = {
  __typename?: 'EditFollowUpQuestionsPayload';
  /** The questions calls now ask; null when they are the AI underwriter's again. */
  edit?: Maybe<FollowUpQuestionEdit>;
  error?: Maybe<Error>;
};

/**
 * The caller org's enterprise SSO configuration, for the Settings security surface.
 * Readable by any org member (so the page can show everyone the real SSO state);
 * only the SSO mutations require the admin role.
 * `domains` reflects WorkOS's live per-domain verification state (so a failed
 * domain claim is visible, not silently stuck), and is null when WorkOS can't be
 * reached — distinct from an empty list, which means no domains are registered.
 */
export type EnterpriseSsoConfig = {
  __typename?: 'EnterpriseSsoConfig';
  defaultRole: UserRole;
  domains?: Maybe<Array<EnterpriseSsoDomainStatus>>;
  enforced: Scalars['Boolean']['output'];
  /**
   * The org's SSO policy, derived once server-side from status and enforced.
   * SSO_ONLY: SSO is the only sign-in method for the org's members on its
   * domains. EXCEPTION: SSO is connected and support has allowed passwords
   * alongside it. PENDING and INACTIVE: no connection routes logins yet.
   */
  policy: EnterpriseSsoPolicy;
  status: EnterpriseSsoConnectionStatus;
  workosOrganizationId: Scalars['String']['output'];
};

export type EnterpriseSsoConfigPayload = {
  __typename?: 'EnterpriseSsoConfigPayload';
  /**
   * WorkOS Admin Portal link for the next setup step. Present on a successful
   * create. Expires in minutes: follow it, never store it.
   */
  adminPortalUrl?: Maybe<Scalars['String']['output']>;
  error?: Maybe<Error>;
  workosOrganizationId?: Maybe<Scalars['String']['output']>;
};

export enum EnterpriseSsoConnectionStatus {
  Active = 'ACTIVE',
  Inactive = 'INACTIVE',
  Pending = 'PENDING'
}

export enum EnterpriseSsoDomainState {
  Failed = 'FAILED',
  Pending = 'PENDING',
  Verified = 'VERIFIED'
}

export type EnterpriseSsoDomainStatus = {
  __typename?: 'EnterpriseSsoDomainStatus';
  domain: Scalars['String']['output'];
  state: EnterpriseSsoDomainState;
};

/**
 * Whether an email's domain routes to an enterprise SSO organization. Returned
 * only for domains whose config is ACTIVE, so the login UI can offer "Continue
 * with SSO" and, when `enforced`, hide the password field.
 */
export type EnterpriseSsoInfo = {
  __typename?: 'EnterpriseSsoInfo';
  enforced: Scalars['Boolean']['output'];
  workosOrganizationId: Scalars['String']['output'];
};

export enum EnterpriseSsoPolicy {
  Exception = 'EXCEPTION',
  Inactive = 'INACTIVE',
  Pending = 'PENDING',
  SsoOnly = 'SSO_ONLY'
}

export enum EnterpriseSsoPortalIntent {
  /**
   * Shows the admin the DNS record that proves they own the domain. Comes
   * first: a connection must not activate before the domain it routes.
   */
  DomainVerification = 'DOMAIN_VERIFICATION',
  /** Connects the identity provider. Refused until a domain is verified. */
  Sso = 'SSO'
}

export type EntriTokenResponse = {
  __typename?: 'EntriTokenResponse';
  applicationId?: Maybe<Scalars['String']['output']>;
  error?: Maybe<Error>;
  token?: Maybe<Scalars['String']['output']>;
};

export type Error = {
  __typename?: 'Error';
  code?: Maybe<ErrorCode>;
  message?: Maybe<Scalars['String']['output']>;
  type?: Maybe<Scalars['String']['output']>;
};

/**
 * Why an operation refused, for a client that acts on the reason. `type` is the
 * broad category (REST maps it to an HTTP status) and `message` is copy for a
 * person: neither is stable enough to branch on. Set only where a client branches
 * on it; absent means no client needs to tell this refusal from its type.
 */
export enum ErrorCode {
  /** The payment's ACH debit settled too recently to be refunded yet. */
  AchRefundTooEarly = 'ACH_REFUND_TOO_EARLY',
  /** The bill's delivery method cannot reach the vendor by the requested arrival date. */
  ArrivalDateUnachievable = 'ARRIVAL_DATE_UNACHIEVABLE',
  /** The bill is paid, or a payment for its full amount is in progress. */
  BillAlreadyPaid = 'BILL_ALREADY_PAID',
  /** No bill with that id in the organization. */
  BillNotFound = 'BILL_NOT_FOUND',
  /** The bill has no remaining balance to record a payment against. */
  BillNoRemainingBalance = 'BILL_NO_REMAINING_BALANCE',
  /** The bill payable is not a scheduled payment. */
  BillPayableNotScheduled = 'BILL_PAYABLE_NOT_SCHEDULED',
  /** The scheduled payment belongs to a recurring bill; deactivate the recurring bill to stop it. */
  BillPayableRecurring = 'BILL_PAYABLE_RECURRING',
  /** The bill payment has gone out, or has already failed or been cancelled. */
  BillPaymentAlreadyProcessed = 'BILL_PAYMENT_ALREADY_PROCESSED',
  /** An approved payment on the bill has not finished; cancel it first, or ask Nickel support to clear it. */
  BillPaymentApprovedUnfinished = 'BILL_PAYMENT_APPROVED_UNFINISHED',
  /** A payment on the bill awaits approval; reject or cancel the request first. */
  BillPaymentAwaitingApproval = 'BILL_PAYMENT_AWAITING_APPROVAL',
  /** A check paid from Nickel Banking is stopped with `stopCheckPayment`, not cancelled. */
  BillPaymentCheckNotCancellable = 'BILL_PAYMENT_CHECK_NOT_CANCELLABLE',
  /** The bill payment was made through Nickel: it is cancelled or disputed, never voided. */
  BillPaymentNotExternal = 'BILL_PAYMENT_NOT_EXTERNAL',
  /** The recorded bill payment has no bill attached. */
  BillPaymentNoBill = 'BILL_PAYMENT_NO_BILL',
  /** The bill payment ran on a retired payment provider and can no longer be cancelled. */
  BillPaymentProviderRetired = 'BILL_PAYMENT_PROVIDER_RETIRED',
  /** The bill has a scheduled payment; cancel it first. */
  BillPaymentScheduled = 'BILL_PAYMENT_SCHEDULED',
  /** A Nickel Banking bill payment is still being made and cannot be cancelled yet. */
  BillPaymentStillProcessing = 'BILL_PAYMENT_STILL_PROCESSING',
  /** The payment needs an approver, under the organization's rules or because the caller's role cannot pay without one, and no one can approve it; nothing was paid. */
  BillPayNoApprover = 'BILL_PAY_NO_APPROVER',
  /** The bill has no linked vendor, so no payment can be recorded against it. */
  BillVendorNotLinked = 'BILL_VENDOR_NOT_LINKED',
  /** The customer has been removed; nothing was changed. */
  CustomerDeleted = 'CUSTOMER_DELETED',
  /** The customer has no email address, which saving a payment method for them needs. */
  CustomerEmailRequired = 'CUSTOMER_EMAIL_REQUIRED',
  /** No customer with that id in the organization. */
  CustomerNotFound = 'CUSTOMER_NOT_FOUND',
  /** Only a check deposited into the organization's own Nickel Banking account, and not already counted by Nickel, applies to an invoice. */
  DepositNotApplicable = 'DEPOSIT_NOT_APPLICABLE',
  /** The bank rejected or returned the deposit, so it has no money to apply. */
  DepositReturned = 'DEPOSIT_RETURNED',
  /** The invoice payment record came from the accounting system, which is where it changes. */
  ExternalPaymentFromAccounting = 'EXTERNAL_PAYMENT_FROM_ACCOUNTING',
  /** The organization is not eligible for this feature. */
  FeatureNotEnabled = 'FEATURE_NOT_ENABLED',
  /** No uploaded file matches an id the operation was given. */
  FileNotFound = 'FILE_NOT_FOUND',
  /** An email address the operation was given is not valid. */
  InvalidEmail = 'INVALID_EMAIL',
  /** The invoice has already been sent to someone, so its first send cannot be scheduled. */
  InvoiceAlreadySent = 'INVOICE_ALREADY_SENT',
  /** The invoice is archived, so it can't accept payments. */
  InvoiceArchived = 'INVOICE_ARCHIVED',
  /** The invoice has payments in the connected accounting system, so it is managed there; nothing was removed. */
  InvoiceHasAccountingPayments = 'INVOICE_HAS_ACCOUNTING_PAYMENTS',
  /** No invoice with that id in the organization. */
  InvoiceNotFound = 'INVOICE_NOT_FOUND',
  /** The invoice is paid, or a charge is scheduled for it, so it is not open to send. */
  InvoiceNotOpen = 'INVOICE_NOT_OPEN',
  /** The invoice has no remaining balance to record or apply a payment against. */
  InvoiceNoRemainingBalance = 'INVOICE_NO_REMAINING_BALANCE',
  /** A reusable payment link has no single balance, so no payment can be recorded or applied against it. */
  InvoiceReusableLink = 'INVOICE_REUSABLE_LINK',
  /** The invoice has no send waiting to go out, so there is nothing to cancel. */
  InvoiceSendNotScheduled = 'INVOICE_SEND_NOT_SCHEDULED',
  /** Sending the invoice needs approval under the organization's rules, and no one on the team can approve it; nothing was sent. */
  InvoiceSendNoApprover = 'INVOICE_SEND_NO_APPROVER',
  /** The Nickel Banking account is closed. */
  NickelBalanceAccountClosed = 'NICKEL_BALANCE_ACCOUNT_CLOSED',
  /** The Nickel Banking account is not yet provisioned. */
  NickelBalanceAccountNotProvisioned = 'NICKEL_BALANCE_ACCOUNT_NOT_PROVISIONED',
  /** The check has already been paid, returned, or resolved by the bank. */
  NickelBalanceInboundCheckDecided = 'NICKEL_BALANCE_INBOUND_CHECK_DECIDED',
  /** The time to return the check has passed; the bank pays it. */
  NickelBalanceInboundCheckReturnWindowClosed = 'NICKEL_BALANCE_INBOUND_CHECK_RETURN_WINDOW_CLOSED',
  /** The payment's ACH debit was cancelled before it settled; there is nothing to refund. */
  PaymentAlreadyCancelled = 'PAYMENT_ALREADY_CANCELLED',
  /** The amount is above the organization's plan payment limit. */
  PaymentLimitExceeded = 'PAYMENT_LIMIT_EXCEEDED',
  /** The payment's ACH debit has already gone to the bank network and can no longer be cancelled. */
  PaymentNotCancellable = 'PAYMENT_NOT_CANCELLABLE',
  /** No payment with that id in the organization. */
  PaymentNotFound = 'PAYMENT_NOT_FOUND',
  /** Nothing on the payment can be refunded: every charge failed, or its provider is no longer supported. */
  PaymentNotRefundable = 'PAYMENT_NOT_REFUNDABLE',
  /** The payment has not settled, so it is cancelled rather than refunded, or it has no charge to refund. */
  PaymentNotSettled = 'PAYMENT_NOT_SETTLED',
  /** The payment has settled, so it is refunded rather than cancelled. */
  PaymentSettled = 'PAYMENT_SETTLED',
  /**
   * The member's role lacks a permission the operation needs. Only the stock
   * refusal carries it: a refusal that states its own reason has no code.
   */
  PermissionRequired = 'PERMISSION_REQUIRED',
  /** The payment was made through a provider Nickel no longer uses. */
  ProviderRetired = 'PROVIDER_RETIRED',
  /** The payment provider could not be reached to check the payment. Retry after a few minutes. */
  ProviderUnavailable = 'PROVIDER_UNAVAILABLE',
  /** QuickBooks refused to create the record, and nothing was written in Nickel or QuickBooks. */
  QuickbooksCreateRejected = 'QUICKBOOKS_CREATE_REJECTED',
  /** The invoice number is longer than QuickBooks accepts; checked before anything was written. */
  QuickbooksInvoiceNumberTooLong = 'QUICKBOOKS_INVOICE_NUMBER_TOO_LONG',
  /** The organization syncs to QuickBooks, so the record needs QuickBooks lines. */
  QuickbooksLinesRequired = 'QUICKBOOKS_LINES_REQUIRED',
  /** The organization's QuickBooks connection is missing; nothing was written. */
  QuickbooksNotConnected = 'QUICKBOOKS_NOT_CONNECTED',
  /** The change was saved in Nickel but QuickBooks was not updated, so the two now differ. */
  QuickbooksUpdateDiverged = 'QUICKBOOKS_UPDATE_DIVERGED',
  /** The recurring bill was saved but could not be activated; it exists and will not run. */
  RecurringActivationFailed = 'RECURRING_ACTIVATION_FAILED',
  /** The recurring series has already ended or been cancelled, so there is nothing to cancel. */
  RecurringAlreadyEnded = 'RECURRING_ALREADY_ENDED',
  /** The recurring invoice has ended or been cancelled and can no longer be edited. */
  RecurringNotEditable = 'RECURRING_NOT_EDITABLE',
  /** A recurring invoice's QuickBooks lines are missing, non-positive or do not add up to its amount. */
  RecurringQuickbooksLinesInvalid = 'RECURRING_QUICKBOOKS_LINES_INVALID',
  /** An active recurring invoice's start date cannot change. */
  RecurringStartDateLocked = 'RECURRING_START_DATE_LOCKED',
  /** A refund of the payment is already pending, so its ACH debit can no longer be cancelled. */
  RefundAlreadyPending = 'REFUND_ALREADY_PENDING',
  /** Another refund of the payment is still being processed. Retry after a few minutes. */
  RefundInProgress = 'REFUND_IN_PROGRESS',
  /** The customer's saved payment method can't be charged until the customer authorizes a payment method again. */
  SavedMethodNotChargeable = 'SAVED_METHOD_NOT_CHARGEABLE',
  /** The member has used the hourly upload budget; nothing was created. Retry after a few minutes. */
  UploadRateLimited = 'UPLOAD_RATE_LIMITED',
  /** The vendor came from the organization's accounting software, so it is removed there, not in Nickel. */
  VendorLinkedToAccounting = 'VENDOR_LINKED_TO_ACCOUNTING',
  /** No vendor with that id in the organization. */
  VendorNotFound = 'VENDOR_NOT_FOUND',
  /** The vendor has no active payout method with that id. */
  VendorPayoutMethodNotFound = 'VENDOR_PAYOUT_METHOD_NOT_FOUND',
  /**
   * The wire was refused before anything moved because the bank account's
   * routing number cannot receive Fedwire. Supply the bank's wire routing
   * number with `setPayoutMethodWireAccountDetails`, then send the wire again.
   */
  WireRoutingNumberRequired = 'WIRE_ROUTING_NUMBER_REQUIRED',
  /** The requested withdrawal date has already passed. */
  WithdrawalDateInPast = 'WITHDRAWAL_DATE_IN_PAST'
}

export type ExecuteTilledAchPaymentResponse = {
  __typename?: 'ExecuteTilledACHPaymentResponse';
  error?: Maybe<Error>;
  paymentMethod?: Maybe<PaymentMethod>;
};

export type ExistingUserResponse = {
  __typename?: 'ExistingUserResponse';
  error?: Maybe<Error>;
  existingUser?: Maybe<Scalars['Boolean']['output']>;
};

/**
 * An external payment recorded against an invoice: the merchant's durable
 * assertion that money satisfied (part of) the requested amount outside
 * Nickel's rails — the record behind "mark as paid". Voided records are kept
 * for audit and count toward nothing. See docs/external-payments/design.md.
 */
export type ExternalArPayment = {
  __typename?: 'ExternalArPayment';
  amountInCents: Scalars['Int']['output'];
  /**
   * What the caller can do to this record right now, and why not. Always a list;
   * nullable only so clients typed against the whole `ExternalArPayment` need not select it.
   */
  availableActions?: Maybe<Array<ActionAvailability>>;
  createdAt: Scalars['Float']['output'];
  /**
   * The caller's own id for the money this record represents — the
   * idempotency reference passed to recordExternalInvoicePayment
   * (docs/external-payments/design.md §3.6). Null on records made from the
   * dashboard or materialized by the system. Unique per invoice among live
   * records; voiding releases it.
   */
  externalReferenceId?: Maybe<Scalars['String']['output']>;
  id: Scalars['String']['output'];
  /**
   * The anchor (docs/external-payments/check-deposit-application.md §3): the
   * Nickel Balance money movement this record was paid from, when Nickel
   * observed the money — a check deposit in v1, reached through the transfer.
   * Null for "paid outside Nickel". An anchored record follows its transfer:
   * when the bank rejects or returns the deposit the system voids the record
   * (voidedAt set, the invoice reopened), so a voided anchored record with no
   * merchant actor on its activity note is a bounced or short-accepted check.
   */
  nickelBalanceTransfer?: Maybe<NickelBalanceTransfer>;
  /** Merchant-stated date the money moved (epoch ms). */
  paidAt: Scalars['Float']['output'];
  /**
   * The invoice the record satisfies (part of). Lets activity-feed rows link
   * back to the invoice and name its customer.
   */
  questionForm?: Maybe<QuestionForm>;
  /**
   * Team member who recorded it — source, not this field, carries provenance.
   *
   * An org-scoped API token does NOT produce a null here: it resolves to its
   * owning user (ApiToken.userId is non-nullable), which is who getIntendedUser
   * returns. Null means no authenticated user was on the request at all, or that
   * the recorded user has since been deleted.
   */
  recordedByUser?: Maybe<User>;
  source: ExternalArPaymentSource;
  /**
   * Why a voided record was voided, read off its audit activity note: null for
   * a merchant undo (and for a live record), DEPOSIT_RETURNED / DEPOSIT_REJECTED
   * when the bank reversed or refused the anchoring deposit (§4.3),
   * AMOUNT_ADJUSTED when the bank accepted less than was applied and the newest
   * applications were trimmed (§4.4).
   */
  voidReason?: Maybe<Scalars['String']['output']>;
  voidedAt?: Maybe<Scalars['Float']['output']>;
};

export enum ExternalArPaymentSource {
  Accounting = 'ACCOUNTING',
  Backfill = 'BACKFILL',
  Merchant = 'MERCHANT'
}

export type Fee = {
  __typename?: 'Fee';
  amountInCents: Scalars['Float']['output'];
  charge?: Maybe<Charge>;
  createdAt?: Maybe<Scalars['Float']['output']>;
  currency?: Maybe<Scalars['String']['output']>;
  description?: Maybe<Scalars['String']['output']>;
  id: Scalars['String']['output'];
  payout?: Maybe<Payout>;
  type?: Maybe<Scalars['String']['output']>;
  updatedAt?: Maybe<Scalars['Float']['output']>;
};

/**
 * See AccountInfo.feeChartOfAccount. chartOfAccountId is null while the org has
 * made no choice; chartOfAccountName alone can be null if the live QuickBooks
 * or Xero lookup fails, or if the chosen account no longer exists (in which case
 * postings fall back to the "Nickel Fees" account).
 */
export type FeeChartOfAccount = {
  __typename?: 'FeeChartOfAccount';
  chartOfAccountId?: Maybe<Scalars['String']['output']>;
  chartOfAccountName?: Maybe<Scalars['String']['output']>;
};

export type FinishPennyWebVisitResponse = {
  __typename?: 'FinishPennyWebVisitResponse';
  error?: Maybe<Error>;
  finished?: Maybe<Scalars['Boolean']['output']>;
};

/**
 * A client as the firm's Clients page lists it: listing fields only, never the
 * client's Organization.
 */
export type FirmClient = {
  __typename?: 'FirmClient';
  bannerKey?: Maybe<Scalars['String']['output']>;
  /**
   * Whether the caller can enter it now. False when the firm lists access and the
   * caller holds no row for it, or when it has no active member to enter as.
   */
  canEnter: Scalars['Boolean']['output'];
  id: Scalars['ID']['output'];
  logoKey?: Maybe<Scalars['String']['output']>;
  name: Scalars['String']['output'];
  subdomain?: Maybe<Scalars['String']['output']>;
};

export type FirmClientsPayload = {
  __typename?: 'FirmClientsPayload';
  /**
   * In a listed firm, a member who does not manage client access gets only the
   * clients they may enter.
   */
  clients?: Maybe<Array<FirmClient>>;
  error?: Maybe<Error>;
};

export enum FollowUpCallCaller {
  Agent = 'AGENT',
  Staff = 'STAFF'
}

/**
 * A follow-up call the applicant can take to have their application reviewed
 * sooner (docs/underwriting/follow-up-calls.md §5).
 */
export type FollowUpCallOffer = {
  __typename?: 'FollowUpCallOffer';
  /** The phone on the application, which the assistant calls, E.164. */
  applicantPhoneNumber: Scalars['String']['output'];
  /** What the applicant agrees to by asking for the call. */
  consentText: Scalars['String']['output'];
  /** Nickel's number, which a missed call can be returned on, E.164. */
  nickelPhoneNumber: Scalars['String']['output'];
  /** How many questions the assistant asks. */
  questionCount: Scalars['Int']['output'];
  status: FollowUpCallOfferStatus;
};

export type FollowUpCallOfferPayload = {
  __typename?: 'FollowUpCallOfferPayload';
  error?: Maybe<Error>;
  /** Null when there is no call to offer this application. */
  offer?: Maybe<FollowUpCallOffer>;
};

export enum FollowUpCallOfferStatus {
  /** The applicant can ask to be called. */
  Available = 'AVAILABLE',
  /** The assistant is calling them, or they are on the call. */
  Calling = 'CALLING',
  /** The applicant took the call; the review team has their answers. */
  Completed = 'COMPLETED',
  /** The last call went unanswered. They can ask again, or call Nickel back. */
  Missed = 'MISSED'
}

/** An applicant's agreement, in their dashboard, to be called now. */
export type FollowUpCallOptIn = {
  __typename?: 'FollowUpCallOptIn';
  /** Who agreed; null for a user since deleted. */
  agreedByName?: Maybe<Scalars['String']['output']>;
  /** The words they agreed to. */
  consentText: Scalars['String']['output'];
  createdAt: Scalars['String']['output'];
  ipAddress?: Maybe<Scalars['String']['output']>;
  /** The phone they agreed to be called at. */
  phoneNumber: Scalars['String']['output'];
};

export enum FollowUpCallOutcome {
  Answered = 'ANSWERED',
  Declined = 'DECLINED',
  NoAnswer = 'NO_ANSWER',
  WrongNumber = 'WRONG_NUMBER'
}

/** Where a pending application's follow-up call stands, for the review queue. */
export enum FollowUpCallReview {
  /** The run that read the call lands in the auto-approve range. A reviewer decides; nothing approved it. */
  Qualifies = 'QUALIFIES',
  /** The applicant answered a follow-up call, so is reviewed sooner. */
  TookCall = 'TOOK_CALL'
}

export enum FollowUpCallTarget {
  ApplicationPhone = 'APPLICATION_PHONE',
  BankRecordPhone = 'BANK_RECORD_PHONE'
}

/**
 * Questions staff wrote for the org, in place of the AI underwriter's. Every
 * call about the org asks these: a reviewer's, the Phone Agent's, and a call back.
 */
export type FollowUpQuestionEdit = {
  __typename?: 'FollowUpQuestionEdit';
  editedAt: Scalars['String']['output'];
  /** Who wrote them; null for a user since deleted. */
  editedByName?: Maybe<Scalars['String']['output']>;
  id: Scalars['String']['output'];
  questions: Array<UnderwritingFollowUpQuestion>;
  /** The org's latest summary; empty before the AI underwriter's first run. */
  underwritingRunId: Scalars['String']['output'];
};

export type FollowUpQuestionInput = {
  /** For the reviewer only: the answer that settles it either way. */
  lookFor?: InputMaybe<Scalars['String']['input']>;
  /** Read aloud, word for word, to the person called. */
  question: Scalars['String']['input'];
  /** For the reviewer only: the finding the question tests. */
  reason?: InputMaybe<Scalars['String']['input']>;
};

export type FollowUpVoiceCall = {
  __typename?: 'FollowUpVoiceCall';
  createdAt: Scalars['String']['output'];
  direction: FollowUpVoiceCallDirection;
  endedAt?: Maybe<Scalars['String']['output']>;
  failureReason?: Maybe<Scalars['String']['output']>;
  id: Scalars['String']['output'];
  /** The applicant's request for this call, when they asked for it in their dashboard. */
  optIn?: Maybe<FollowUpCallOptIn>;
  phoneNumber: Scalars['String']['output'];
  recordingDurationSec?: Maybe<Scalars['Int']['output']>;
  /** Staff-only path the recording streams from; null until Twilio delivers it. */
  recordingUrl?: Maybe<Scalars['String']['output']>;
  status: FollowUpVoiceCallStatus;
  target: FollowUpCallTarget;
  /** What was said, speaker by speaker; null until the call ends. */
  transcript?: Maybe<Scalars['String']['output']>;
};

export enum FollowUpVoiceCallDirection {
  /** The applicant called Nickel back after a missed call. */
  Inbound = 'INBOUND',
  /** Nickel's assistant placed the call. */
  Outbound = 'OUTBOUND'
}

export enum FollowUpVoiceCallStatus {
  Completed = 'COMPLETED',
  Failed = 'FAILED',
  InProgress = 'IN_PROGRESS',
  NoAnswer = 'NO_ANSWER',
  Requested = 'REQUESTED',
  Ringing = 'RINGING'
}

export type ForgotPasswordPayload = {
  __typename?: 'ForgotPasswordPayload';
  error?: Maybe<Error>;
};

export type GetClientTokenResponse = {
  __typename?: 'GetClientTokenResponse';
  /**
   * The browser-side Basis Theory public application key (token-intent:create
   * scope only — it can put cards in, never read them out). Render the
   * /basistheory frame with this as init.clientToken and save via
   * basisTheoryTokenIntentId.
   *
   * Returned to every merchant and portal capture surface (dashboard AP modal,
   * portal checkout, portal CustomerAuthorize, dashboard debit-authorization
   * modal). A frame that fails is retried; there is no processor card form to
   * fall back to.
   */
  basisTheoryPublicKey?: Maybe<Scalars['String']['output']>;
  error?: Maybe<Error>;
  /**
   * Downstream card processor ('braintree' | 'airwallex'). Unchanged by the
   * Basis Theory capture path: the save mutation and charge code key on the
   * processor, while the card-entry frame choice keys on basisTheoryPublicKey.
   */
  provider?: Maybe<Scalars['String']['output']>;
};

export type GetLinkTokenResponse = {
  __typename?: 'GetLinkTokenResponse';
  error?: Maybe<Error>;
  linkToken?: Maybe<Scalars['String']['output']>;
};

export enum HistoryInterval {
  Days_30 = 'DAYS_30',
  Days_60 = 'DAYS_60',
  Days_90 = 'DAYS_90',
  Days_180 = 'DAYS_180',
  Days_365 = 'DAYS_365'
}

export type HubspotDealStatus = {
  __typename?: 'HubspotDealStatus';
  organizationId: Scalars['String']['output'];
  /** Open, Won or Lost; the org's open deal wins over a won one, a won one over a lost one. */
  status: Scalars['String']['output'];
};

/**
 * `deals` lists only orgs with a linked deal. `unavailable` is set when the
 * warehouse could not be asked (unconfigured, error, or timeout) — distinct
 * from an empty list, so a caller gating a decision on it can fail closed.
 */
export type HubspotDealStatusesResponse = {
  __typename?: 'HubspotDealStatusesResponse';
  deals: Array<HubspotDealStatus>;
  error?: Maybe<Error>;
  unavailable: Scalars['Boolean']['output'];
};

/**
 * An ACH credit, wire, RTP or FedNow transfer that arrived on an invoice's
 * receiving account number (docs/bank-transfer-payin.md). Listed for the merchant while it is
 * unapplied, so they can apply it to an invoice or return it.
 */
export type InboundBankTransfer = {
  __typename?: 'InboundBankTransfer';
  amountInCents: Scalars['Int']['output'];
  appliedInCents: Scalars['Int']['output'];
  counterpartyName?: Maybe<Scalars['String']['output']>;
  createdAt: Scalars['Float']['output'];
  id: Scalars['String']['output'];
  /** The payment this transfer was applied as, with its invoice. */
  payments: Array<Payment>;
  /** The invoice whose receiving account number the money arrived on. */
  questionForm?: Maybe<QuestionForm>;
  rail: BankTransferRail;
  remittance?: Maybe<Scalars['String']['output']>;
  returnedAt?: Maybe<Scalars['Float']['output']>;
  status: InboundBankTransferStatus;
};

export type InboundBankTransferResponse = {
  __typename?: 'InboundBankTransferResponse';
  error?: Maybe<Error>;
  inboundBankTransfer?: Maybe<InboundBankTransfer>;
};

export enum InboundBankTransferStatus {
  Applied = 'APPLIED',
  Returned = 'RETURNED',
  Unapplied = 'UNAPPLIED'
}

export type InboundBankTransfersResponse = {
  __typename?: 'InboundBankTransfersResponse';
  error?: Maybe<Error>;
  inboundBankTransfers?: Maybe<Array<InboundBankTransfer>>;
};

export type IntegrationConnectionStatusResponse = {
  __typename?: 'IntegrationConnectionStatusResponse';
  connected: Scalars['Boolean']['output'];
  error?: Maybe<Scalars['String']['output']>;
  lastCheckedAt?: Maybe<Scalars['Float']['output']>;
  latencyMs?: Maybe<Scalars['Int']['output']>;
  provider?: Maybe<Scalars['String']['output']>;
};

export type Invite = {
  __typename?: 'Invite';
  email?: Maybe<Scalars['String']['output']>;
  role?: Maybe<UserRole>;
  status?: Maybe<Scalars['String']['output']>;
  url?: Maybe<Scalars['String']['output']>;
  /**
   * The role the joiner lands on when the invite was sent with one; else the
   * org's seeded role for the tier.
   */
  workspaceRoleId?: Maybe<Scalars['ID']['output']>;
};

export type Invoice = {
  __typename?: 'Invoice';
  accountingSource?: Maybe<AccountingSource>;
  amountDue: Scalars['Float']['output'];
  appliedAmount?: Maybe<Scalars['Float']['output']>;
  companyId?: Maybe<Scalars['String']['output']>;
  customerData?: Maybe<AccountingCustomer>;
  customerRef?: Maybe<Customer>;
  discountPercentage?: Maybe<Scalars['Float']['output']>;
  dueDate?: Maybe<Scalars['String']['output']>;
  externalId?: Maybe<Scalars['String']['output']>;
  /** Nickel Invoice row id. Null for report projections that aren't backed by an Invoice row (e.g. QuickBooks underwrite invoices). */
  id?: Maybe<Scalars['String']['output']>;
  invoiceBuffer?: Maybe<Scalars['String']['output']>;
  invoiceNumber?: Maybe<Scalars['String']['output']>;
  issueDate?: Maybe<Scalars['String']['output']>;
  lastSyncedAt?: Maybe<Scalars['Float']['output']>;
  lineItems?: Maybe<Array<LineItem>>;
  modifiedDate?: Maybe<Scalars['String']['output']>;
  note?: Maybe<Scalars['String']['output']>;
  paidOnDate?: Maybe<Scalars['String']['output']>;
  paymentAllocations?: Maybe<Array<PaymentAllocation>>;
  questionForm?: Maybe<QuestionForm>;
  quickbooksCompanyId?: Maybe<Scalars['String']['output']>;
  status?: Maybe<Scalars['String']['output']>;
  subTotal?: Maybe<Scalars['Float']['output']>;
  totalAmount?: Maybe<Scalars['Float']['output']>;
  totalDiscount?: Maybe<Scalars['Float']['output']>;
  totalTaxAmount?: Maybe<Scalars['Float']['output']>;
};

export type InvoiceAllocation = {
  __typename?: 'InvoiceAllocation';
  totalAmount?: Maybe<Scalars['Float']['output']>;
};

export type InvoiceApplicationInput = {
  amountCents?: InputMaybe<Scalars['Int']['input']>;
  questionFormId: Scalars['String']['input'];
};

/**
 * An invoice action an approval rule holds until it is approved: sending the
 * invoice, or charging the customer's saved payment method for it. The action
 * runs on its own once the last approval is in.
 */
export type InvoiceApprovalRequest = {
  __typename?: 'InvoiceApprovalRequest';
  action: ApprovalAction;
  amountCents: Scalars['Int']['output'];
  /** A held charge's date; null charges on approval. */
  chargeDate?: Maybe<Scalars['Float']['output']>;
  createdAt: Scalars['Float']['output'];
  customerName?: Maybe<Scalars['String']['output']>;
  id: Scalars['ID']['output'];
  invoiceName?: Maybe<Scalars['String']['output']>;
  /** Why a VOIDED request closed, or how a FAILED one's send or charge failed. */
  outcomeMessage?: Maybe<Scalars['String']['output']>;
  /** A held charge's method, by label. */
  paymentMethodLabel?: Maybe<Scalars['String']['output']>;
  questionFormId: Scalars['String']['output'];
  /** A held send's recipients. */
  recipientEmails: Array<Scalars['String']['output']>;
  /** Null when no member asked; requestedBySystem then says what did. */
  requestedByName?: Maybe<Scalars['String']['output']>;
  /** What asked when no member did; null when one did. */
  requestedBySystem?: Maybe<InvoiceApprovalSystemRequestor>;
  state: ApprovalRequestState;
  status: ApprovalRequestStatus;
  /** Whether the caller may withdraw it: whoever asked, or a workflow manager. */
  viewerCanCancel: Scalars['Boolean']['output'];
};

export type InvoiceApprovalRequestResponse = {
  __typename?: 'InvoiceApprovalRequestResponse';
  error?: Maybe<Error>;
  request?: Maybe<InvoiceApprovalRequest>;
};

export type InvoiceApprovalRequestsResponse = {
  __typename?: 'InvoiceApprovalRequestsResponse';
  error?: Maybe<Error>;
  requests: Array<InvoiceApprovalRequest>;
};

/** What asks for an invoice send when no member does. */
export enum InvoiceApprovalSystemRequestor {
  /** The QuickBooks sync, emailing an invoice it created. */
  QuickbooksSync = 'QUICKBOOKS_SYNC',
  /** An email-only recurring invoice series, emailing an invoice it issued. */
  RecurringInvoice = 'RECURRING_INVOICE'
}

/**
 * Column mapping for an invoice (payment-link) CSV bulk import (0-based column
 * indices). Imported invoices are not synced to QuickBooks (an org on Xero
 * with invoice writes on gets them created in Xero by the push), active but
 * NOT sent, with the same defaults as dashboard-created invoices:
 * live checkout link, org memo template applied, automated reminders scheduled
 * when the org has them enabled.
 */
export type InvoiceCsvColumnMappingInput = {
  /** 0-based index of the amount column (dollars; parsed leniently, e.g. "$1,250.00"). */
  amount: Scalars['Int']['input'];
  /** 0-based index of the column holding the customer's email, if the file has one — matched (exact, case-insensitive) before falling back to the name. */
  customerEmail?: InputMaybe<Scalars['Int']['input']>;
  /** 0-based index of the column holding the customer's name. The customer must already exist (import customers first); unmatched rows are reported as failed. */
  customerName: Scalars['Int']['input'];
  /** 0-based index of the due-date column, if present. Rows with a blank due date fall back to their invoice date. */
  dueDate?: InputMaybe<Scalars['Int']['input']>;
  /** 0-based index of the invoice/issue-date column (parsed leniently; ISO YYYY-MM-DD preferred). Required — rows without an invoice date fail. */
  invoiceDate: Scalars['Int']['input'];
  /** 0-based index of the customer-facing note/memo column, if present. */
  memo?: InputMaybe<Scalars['Int']['input']>;
  /** 0-based index of the column holding the invoice's name/label (e.g. INV-1042). Also the per-customer de-duplication key. */
  name: Scalars['Int']['input'];
};

/**
 * What we could read off an invoice the merchant made outside Nickel, for the
 * invoice form to prefill. Every field is nullable and null is the common case,
 * not an error: a field the document does not show, or shows illegibly, returns
 * null so the merchant types it, which is strictly better than a confident wrong
 * guess.
 *
 * A suggestion, never a fact. The merchant confirms these values before the
 * invoice is saved, so nothing here may be treated as authoritative by a caller.
 */
export type InvoiceDocumentParse = {
  __typename?: 'InvoiceDocumentParse';
  /** The amount due now, in cents. Null when the invoice is not in US dollars. */
  amountCents?: Maybe<Scalars['Int']['output']>;
  /**
   * Who the invoice is addressed to, as printed. The form uses it to warn when it
   * differs from the customer the merchant chose; it never picks the customer.
   */
  customerName?: Maybe<Scalars['String']['output']>;
  /**
   * The due date as YYYY-MM-DD: printed on the invoice, or the invoice date plus
   * printed payment terms ("Net 30").
   */
  dueDate?: Maybe<Scalars['String']['output']>;
  /** The date the invoice was issued, as YYYY-MM-DD. */
  invoiceDate?: Maybe<Scalars['String']['output']>;
  /** The invoice number printed on the invoice. */
  invoiceNumber?: Maybe<Scalars['String']['output']>;
  /** A few words on what the invoice is for, such as "October grounds maintenance". */
  memo?: Maybe<Scalars['String']['output']>;
};

export type InvoicePayment = {
  __typename?: 'InvoicePayment';
  id?: Maybe<Scalars['String']['output']>;
  paidOnDate?: Maybe<Scalars['String']['output']>;
  totalAmount?: Maybe<Scalars['Float']['output']>;
};

export type InvoiceSummary = {
  __typename?: 'InvoiceSummary';
  activeInvoices?: Maybe<Scalars['Int']['output']>;
  openBalanceInCents?: Maybe<Scalars['Int']['output']>;
  pastDueBalanceInCents?: Maybe<Scalars['Int']['output']>;
  pastDueInvoiceCount?: Maybe<Scalars['Int']['output']>;
  totalPaidInCents?: Maybe<Scalars['Int']['output']>;
};

export type IssuePlatformPartnerKeyPayload = {
  __typename?: 'IssuePlatformPartnerKeyPayload';
  error?: Maybe<Error>;
  key?: Maybe<Scalars['String']['output']>;
  keyId?: Maybe<Scalars['ID']['output']>;
  partner?: Maybe<PlatformPartnerAdmin>;
};

export type IssuedCard = {
  __typename?: 'IssuedCard';
  /** Physical cards: when the merchant activated the plastic. */
  activatedAt?: Maybe<Scalars['Float']['output']>;
  /** The Nickel Balance account the card spends from (multi-account). */
  balanceAccountId: Scalars['ID']['output'];
  /** The billing (AVS) address the card was issued with — what a checkout asks for. Null on a card issued with none. */
  billingAddress?: Maybe<IssuedCardBillingAddress>;
  cardType: IssuedCardType;
  /** Printed on a physical card; null on virtual cards. */
  cardholderFirstName?: Maybe<Scalars['String']['output']>;
  cardholderLastName?: Maybe<Scalars['String']['output']>;
  cardholderName?: Maybe<Scalars['String']['output']>;
  createdAt: Scalars['Float']['output'];
  /** The card's wallet tokens, newest first, every status — active ones are the wallets it is in today. */
  digitalWallets: Array<IssuedCardDigitalWallet>;
  disableReason?: Maybe<Scalars['String']['output']>;
  disabledAt?: Maybe<Scalars['Float']['output']>;
  /** Derived server-side; what the list and detail render. */
  displayStatus: IssuedCardDisplayStatus;
  expirationMonth?: Maybe<Scalars['Int']['output']>;
  expirationYear?: Maybe<Scalars['Int']['output']>;
  id: Scalars['ID']['output'];
  last4?: Maybe<Scalars['String']['output']>;
  nickname?: Maybe<Scalars['String']['output']>;
  recentTransactions: Array<CardTransaction>;
  shipment?: Maybe<IssuedCardShipment>;
  /** Increase-enforced limits, at most one per interval. Empty = gated only by the account's balance at authorization. */
  spendingLimits: Array<CardSpendingLimit>;
  status: IssuedCardStatus;
  /** Lifetime completed spend in cents (sum of completed CardTransaction rows; a refunded payment drops out entirely). */
  totalSpendCents: Scalars['Int']['output'];
};


export type IssuedCardRecentTransactionsArgs = {
  limit?: InputMaybe<Scalars['Int']['input']>;
};

export type IssuedCardBillingAddress = {
  __typename?: 'IssuedCardBillingAddress';
  city: Scalars['String']['output'];
  line1: Scalars['String']['output'];
  line2?: Maybe<Scalars['String']['output']>;
  state: Scalars['String']['output'];
  zip: Scalars['String']['output'];
};

/** A card in a digital wallet — one per device, mirrored from Increase's digital_wallet_token webhooks (docs/nickel-balance/virtual-cards.md § Wallet tokenization). */
export type IssuedCardDigitalWallet = {
  __typename?: 'IssuedCardDigitalWallet';
  /** Increase's decline reason when status is declined (no_verification_method, declined_by_token_requestor, …). */
  declineReason?: Maybe<Scalars['String']['output']>;
  deviceName?: Maybe<Scalars['String']['output']>;
  /** What the wallet reported about the device (mobile_phone, watch, …), and its name (e.g. iPhone). */
  deviceType?: Maybe<Scalars['String']['output']>;
  id: Scalars['ID']['output'];
  status: DigitalWalletTokenStatus;
  /** When the wallet asked for the token. */
  tokenizedAt: Scalars['Float']['output'];
  wallet: DigitalWallet;
};

/**
 * The one status the dashboard shows, folded from status + cardType + shipment
 * (packages/types nickelBalance/debitCards.ts). Virtual cards read requested /
 * active / closed; physical cards walk requested → issued → shipped →
 * delivered → active → closed.
 */
export enum IssuedCardDisplayStatus {
  Active = 'active',
  Closed = 'closed',
  Delivered = 'delivered',
  Issued = 'issued',
  Requested = 'requested',
  Shipped = 'shipped'
}

/** Where a physical card's plastic is (mirror of the Increase PhysicalCard shipment). Null on virtual cards. */
export type IssuedCardShipment = {
  __typename?: 'IssuedCardShipment';
  address: IssuedCardShippingAddress;
  deliveredAt?: Maybe<Scalars['Float']['output']>;
  /** Newest carrier tracking-update category (in_transit, delivered, …). */
  lastTrackingCategory?: Maybe<Scalars['String']['output']>;
  /** Increase shipment method; 'usps' in v1. */
  method: Scalars['String']['output'];
  shippedAt?: Maybe<Scalars['Float']['output']>;
  status?: Maybe<PhysicalCardShipmentStatus>;
  /** Carrier tracking number; null for USPS, which Increase does not track. */
  trackingNumber?: Maybe<Scalars['String']['output']>;
};

export type IssuedCardShippingAddress = {
  __typename?: 'IssuedCardShippingAddress';
  city: Scalars['String']['output'];
  line1: Scalars['String']['output'];
  line2?: Maybe<Scalars['String']['output']>;
  state: Scalars['String']['output'];
  zip: Scalars['String']['output'];
};

export enum IssuedCardStatus {
  Active = 'active',
  Canceled = 'canceled',
  Disabled = 'disabled',
  /** Physical card only: plastic ordered, Card disabled at Increase until the merchant activates it. */
  Inactive = 'inactive',
  Pending = 'pending'
}

export enum IssuedCardType {
  Physical = 'physical',
  Virtual = 'virtual'
}

export type KeyFacts = {
  __typename?: 'KeyFacts';
  averageDailyBalance?: Maybe<Scalars['String']['output']>;
  businessName?: Maybe<Scalars['String']['output']>;
  businessType?: Maybe<Scalars['String']['output']>;
  endingBalance?: Maybe<Scalars['String']['output']>;
  negativeBalanceDays?: Maybe<Scalars['Int']['output']>;
  nsfCount?: Maybe<Scalars['Int']['output']>;
  transactionCount?: Maybe<Scalars['Int']['output']>;
  transactionDateRange?: Maybe<Scalars['String']['output']>;
};

/**
 * Where a merchant stands on the light path: moving a little money on a
 * Plaid-linked bank before its application is approved.
 */
export type LightPath = {
  __typename?: 'LightPath';
  /** Every bank this merchant can be paid out to was linked through Plaid. */
  bankLinked: Scalars['Boolean']['output'];
  /** In the test arm: the limits apply to what this merchant creates. */
  enrolled: Scalars['Boolean']['output'];
  /** The most one invoice, payment link or bill may ask. */
  itemLimitCents: Scalars['Int']['output'];
  /** True once the merchant has paid as many bills as the light path allows. */
  payLimitReached: Scalars['Boolean']['output'];
};

export type LineItem = {
  __typename?: 'LineItem';
  /** QuickBooks expense account (category) id for account-based lines, e.g. a bill line coded to an expense account. */
  accountId?: Maybe<Scalars['String']['output']>;
  /** QuickBooks expense account (category) name for account-based lines. */
  accountName?: Maybe<Scalars['String']['output']>;
  description?: Maybe<Scalars['String']['output']>;
  /** QuickBooks item (product/service) id for item-based lines. */
  itemId?: Maybe<Scalars['String']['output']>;
  /** QuickBooks item (product/service) name for item-based lines. */
  itemName?: Maybe<Scalars['String']['output']>;
  quantity?: Maybe<Scalars['Float']['output']>;
  subTotal?: Maybe<Scalars['Float']['output']>;
  taxAmount?: Maybe<Scalars['Float']['output']>;
  totalAmount?: Maybe<Scalars['Float']['output']>;
  unitAmount?: Maybe<Scalars['Float']['output']>;
};

export type ManualAccountingItemSyncResponse = {
  __typename?: 'ManualAccountingItemSyncResponse';
  error?: Maybe<Error>;
};

export type ManualAccountingPageSyncItemResult = {
  __typename?: 'ManualAccountingPageSyncItemResult';
  error?: Maybe<Error>;
  itemId: Scalars['String']['output'];
};

export type ManualAccountingPageSyncResponse = {
  __typename?: 'ManualAccountingPageSyncResponse';
  error?: Maybe<Error>;
  results?: Maybe<Array<ManualAccountingPageSyncItemResult>>;
};

export type ManualAccountingSyncResponse = {
  __typename?: 'ManualAccountingSyncResponse';
  error?: Maybe<Error>;
};

export type MarkPennyWebsitePaymentNotPaidResponse = {
  __typename?: 'MarkPennyWebsitePaymentNotPaidResponse';
  error?: Maybe<Error>;
  released?: Maybe<Scalars['Boolean']['output']>;
};

/**
 * A destructive tool call an agent made through the Nickel MCP server, waiting
 * for the person who connected the agent to approve it. Once approved, the
 * agent's next call of the same tool with the same arguments runs, once.
 */
export type McpActionApproval = {
  __typename?: 'McpActionApproval';
  /** The tool's arguments as JSON, exactly as they will run. */
  args: Scalars['String']['output'];
  createdAt: Scalars['Float']['output'];
  decidedAt?: Maybe<Scalars['Float']['output']>;
  expiresAt: Scalars['Float']['output'];
  id: Scalars['ID']['output'];
  redeemedAt?: Maybe<Scalars['Float']['output']>;
  /** Names for the customers, vendors and files the arguments name by id. */
  references: Array<McpActionApprovalReference>;
  /** The agent's connection: its OAuth client's name, or the API token's label. */
  requestedBy?: Maybe<Scalars['String']['output']>;
  status: McpActionApprovalStatus;
  /** The tool's display name. */
  title: Scalars['String']['output'];
  /** The tool's name, e.g. refund_payment. */
  tool: Scalars['String']['output'];
  /** The dashboard page where it is approved. */
  url: Scalars['String']['output'];
};

/** The name of a record an approval's arguments name by id. */
export type McpActionApprovalReference = {
  __typename?: 'McpActionApprovalReference';
  id: Scalars['ID']['output'];
  kind: McpActionApprovalReferenceKind;
  label: Scalars['String']['output'];
};

export enum McpActionApprovalReferenceKind {
  Customer = 'CUSTOMER',
  File = 'FILE',
  Vendor = 'VENDOR'
}

export type McpActionApprovalResponse = {
  __typename?: 'McpActionApprovalResponse';
  approval?: Maybe<McpActionApproval>;
  error?: Maybe<Error>;
};

/**
 * Where an agent's tool call waiting for dashboard approval stands. EXPIRED is
 * derived, never stored: a PENDING or APPROVED approval past its expiresAt.
 */
export enum McpActionApprovalStatus {
  Approved = 'APPROVED',
  Declined = 'DECLINED',
  Expired = 'EXPIRED',
  Pending = 'PENDING',
  Redeemed = 'REDEEMED'
}

export type McpActionApprovalSummary = {
  __typename?: 'McpActionApprovalSummary';
  /**
   * True when the caller holds a live OAuth grant for the Nickel MCP, or an
   * agent of theirs asked for approval in the last 30 days.
   */
  hasAgentConnection: Scalars['Boolean']['output'];
  /** Unexpired calls waiting for the caller's decision. */
  waiting: Scalars['Int']['output'];
};

export type McpActionApprovalSummaryResponse = {
  __typename?: 'McpActionApprovalSummaryResponse';
  error?: Maybe<Error>;
  summary?: Maybe<McpActionApprovalSummary>;
};

export type McpActionApprovalsResponse = {
  __typename?: 'McpActionApprovalsResponse';
  approvals?: Maybe<Array<McpActionApproval>>;
  error?: Maybe<Error>;
};

export enum McpActionDecision {
  Approve = 'APPROVE',
  Decline = 'DECLINE'
}

export type McpActionDecisionResult = {
  __typename?: 'McpActionDecisionResult';
  /** Where the approval stands after the call; null when no approval of the caller's has this id. */
  approval?: Maybe<McpActionApproval>;
  /** Why this id was not approved or declined. Absent when it was, or already was. */
  error?: Maybe<Error>;
  id: Scalars['ID']['output'];
};

/**
 * One external user's settings, from the External Users page
 * (docs/workspace-roles/notifications.md §14). Only an external user of the org
 * is accepted: a member owns their own row.
 */
export type MemberNotificationsInput = {
  /**
   * The user's full subscription map; an omitted key unsubscribes it — except
   * one the writer may not pick (the acting Administrator for an external
   * user, the member for an internal one), which is carried through
   * unchanged.
   */
  subscriptions: Array<NotificationSubscriptionInput>;
  userId: Scalars['ID']['input'];
};

export type MerchantNotificationSetting = {
  __typename?: 'MerchantNotificationSetting';
  achStatusUpdate?: Maybe<NotificationSetting>;
  automatedReminderSent?: Maybe<NotificationSetting>;
  /**
   * A bank account added to or updated on the workspace (catalog key
   * merchant.bank_account_changed).
   */
  bankAccountChanged?: Maybe<NotificationSetting>;
  billPaymentFailed?: Maybe<NotificationSetting>;
  billPaymentMade?: Maybe<NotificationSetting>;
  carbonCopyEmail?: Maybe<NotificationSetting>;
  /**
   * A third-party ACH debit posted to a Nickel Banking account (catalog key
   * merchant.debit_posted).
   */
  debitPosted?: Maybe<NotificationSetting>;
  depositReceived?: Maybe<NotificationSetting>;
  /**
   * Someone on the workspace opened a new Nickel Banking account (catalog key
   * merchant.nickel_balance_account_opened).
   */
  nickelBalanceAccountOpened?: Maybe<NotificationSetting>;
  /**
   * Nickel Balance approved / statement ready / withdrawal returned / check
   * deposit rejected / a debit held or blocked by Debit Protection / a posted
   * debit returned (catalog key merchant.nickel_balance_activity).
   */
  nickelBalanceActivity?: Maybe<NotificationSetting>;
  /**
   * A purchase on a Nickel Banking debit card was declined (catalog key
   * merchant.nickel_balance_card_declined).
   */
  nickelBalanceCardDeclined?: Maybe<NotificationSetting>;
  /**
   * Someone on the workspace issued a Nickel Banking debit card (catalog key
   * merchant.nickel_balance_card_issued).
   */
  nickelBalanceCardIssued?: Maybe<NotificationSetting>;
  paymentFailed?: Maybe<NotificationSetting>;
  paymentMade?: Maybe<NotificationSetting>;
  paymentPaid?: Maybe<NotificationSetting>;
  payoutPaid?: Maybe<NotificationSetting>;
  refundCompleted?: Maybe<NotificationSetting>;
  vendorCarbonCopyEmail?: Maybe<NotificationSetting>;
  vendorCheckDelivered?: Maybe<NotificationSetting>;
  /**
   * How each vendor's "You were sent …" receipt email goes out
   * (merchant.vendorPaymentEmails; absent reads PER_PAYMENT). Bills paid
   * together with Batch Bill Pay go to each vendor as one email either way.
   */
  vendorPaymentEmails: VendorPaymentEmailCadence;
};

/**
 * The remaining additional recipients of one merchant category, by its
 * merchant catalog key (merchant.<category>.emails): what the 2026-09-08
 * backfill could not move onto the team, routed beside the subscribed roles
 * until removed. The list only shrinks — an address not already stored on it
 * is refused; a new recipient is a team member (addExternalTeammate).
 */
export type MerchantRecipientsInput = {
  emails: Array<Scalars['String']['input']>;
  key: Scalars['String']['input'];
};

export type Mutation = {
  __typename?: 'Mutation';
  /**
   * Accept the dispute: the merchant concedes. Calls the rail's accept where
   * one exists (Braintree, Airwallex), records ACCEPTED either way. Money does
   * not move — the merchant was debited at open — this only ends the clock.
   * Irreversible. Refused unless canAccept.
   */
  acceptChargeback?: Maybe<ChargebackResponse>;
  /**
   * Activate a physical card once the merchant holds the plastic (shipped or
   * later; refused while the org has an open overdraft). Re-enables the Card
   * at Increase. Idempotent: an active card returns as-is.
   */
  activateNickelBalanceCard?: Maybe<ActivateNickelBalanceCardResponse>;
  addActivityNote?: Maybe<AddActivityNoteResponse>;
  addAdminNote?: Maybe<UpdateAdminNotesResponse>;
  addBill?: Maybe<BillResponse>;
  addBillPayable?: Maybe<AddBillPayableResponse>;
  addExternalTeammate?: Maybe<UpdateTeammateResponse>;
  addNewTeammate?: Maybe<AuthPayload>;
  /**
   * Always allow ACH debits with this Company ID into the account under Debit
   * Protection. Idempotent: an active rule for the same Company ID is returned.
   * `originatorCompanyId` is 1 to 10 characters, stored trimmed and uppercased.
   * Debits already held are not changed.
   */
  addNickelBalanceDebitAllowRule?: Maybe<NickelBalanceDebitAllowRuleResponse>;
  addPlatformPartnerReturnUrl?: Maybe<PlatformPartnerAdminPayload>;
  addPlatformPartnerWebhook?: Maybe<AddPlatformPartnerWebhookPayload>;
  addQuestionForm?: Maybe<QuestionFormResponse>;
  addRetailCustomer?: Maybe<AddRetailCustomerResponse>;
  addSubscription?: Maybe<AddSubscriptionResponse>;
  addVendor?: Maybe<AddVendorResponse>;
  /**
   * Admin-only. Send a reply into any organization's Penny conversation AS
   * Penny: the merchant receives an ordinary Penny reply (an email on an email
   * thread, a chat bubble on a web thread), and the agent sees it — labeled as
   * staff-sent — in its context on later turns. Delivery follows Penny's own
   * rule: the channel of the latest inbound message. Email delivery is
   * asynchronous (the returned message starts QUEUED); web delivery is
   * immediate. Rejected when an email thread has no inbound email to reply to.
   */
  adminSendPennyReply?: Maybe<AdminSendPennyReplyResponse>;
  /**
   * Apply an unapplied inbound bank transfer to one of the organization's open
   * invoices, creating a payment on it for the whole amount.
   */
  applyInboundBankTransfer?: Maybe<InboundBankTransferResponse>;
  /**
   * Apply up to 25 Nickel Banking deposits to invoices at once, all or nothing,
   * in one transaction (docs/external-payments/check-deposit-application.md
   * §4.1, §6.1). Each row writes an ExternalArPayment anchored to its deposit's
   * NickelBalanceTransfer, counted from creation and voided by the system if
   * the bank rejects or returns the deposit; unapply is
   * voidExternalInvoicePayment. A row's amountCents defaults to min(the
   * deposit's unapplied amount, the invoice's remaining balance). Each row
   * takes recordExternalInvoicePayment's invoice guards, plus: the transfer
   * must be an eligible deposit of this org (a check deposit, not rejected or
   * returned, not Nickel-processed money) with unapplied money to cover the
   * amount. Each row sees the rows before it, so two rows on one deposit share
   * its unapplied money and two rows on one invoice share its remaining
   * balance. The record pushes to QuickBooks as a Payment into the "Nickel
   * Balance" bank account rather than the clearing account.
   *
   * A refused row refuses the batch and nothing is written; when there are
   * several rows, the error's message starts "Application N: ", naming the row
   * counting from 1, and a one-row batch's message is the refusal alone.
   *
   * A row's externalReferenceId has recordExternalInvoicePayment's contract,
   * with the deposit held to it as well: a repeat on the same invoice from the
   * same deposit (and the same amountCents, when stated) returns the existing
   * application, and a reference already live on the invoice for outside
   * money or another deposit is refused with a ConflictError. Sending a batch
   * of referenced rows again writes nothing new.
   */
  applyNickelBalanceDepositsToInvoices?: Maybe<ApplyNickelBalanceDepositsToInvoicesResponse>;
  applyTagToEntities?: Maybe<Array<TagAssignmentResponse>>;
  /**
   * Approve an agent's tool call. Only the person whose connection asked may,
   * and only from a dashboard session.
   */
  approveMcpAction?: Maybe<McpActionApprovalResponse>;
  approveOAuthAuthorization?: Maybe<ApproveOAuthAuthorizationResponse>;
  archiveApprovalRule?: Maybe<ArchiveApprovalRuleResponse>;
  backfillRemindersForOpenInvoices?: Maybe<BackfillRemindersResponse>;
  /**
   * Charge invoices against their customers' saved payment methods. One call
   * charges at most 25 invoices, counted across every `autoDebitInput`; a call
   * with more is refused, `validateOnly` included, before anything is read or
   * charged. Split a larger batch into calls of 25 or fewer. An invoice that is
   * already paid, scheduled or a reusable link is reported in
   * `questionFormErrors` and the rest of the call is still charged.
   */
  batchAutoDebit?: Maybe<BatchAutoDebitResponse>;
  /**
   * Pay up to 25 bills at once. Each bill is paid on its own: one that fails is
   * reported in `billErrors` and the others are still paid. A call with more
   * than 25 bills is refused whole; split a longer batch across calls.
   */
  batchBillPay?: Maybe<BatchBillPayResponse>;
  /**
   * Save up to 25 customers' bank accounts at once, each with its signed
   * authorization: createRetailCustomerACHDebitAuthorization per row, with a
   * result per row rather than all-or-nothing. Safe to send again: a row whose
   * customer already has that exact account live is reported as alreadySaved
   * and nothing new is written.
   */
  batchCreateRetailCustomerACHDebitAuthorizations?: Maybe<BatchCreateRetailCustomerAchDebitAuthorizationsResponse>;
  /**
   * Email up to 25 customers a request to authorize a payment method on file,
   * with a result per customer rather than all-or-nothing. A call with more
   * than 25 is refused whole; split a longer list across calls.
   */
  batchRequestCustomerDebitAuthorizations?: Maybe<BatchRequestCustomerDebitAuthorizationsResponse>;
  /**
   * Email up to 25 vendors a request for their delivery details, with a result
   * per vendor rather than all-or-nothing. A call with more than 25 is refused
   * whole; split a longer list across calls.
   */
  batchRequestVendorDeliveryDetails?: Maybe<BatchRequestVendorDeliveryDetailsResponse>;
  batchSendArAgingReportEmails?: Maybe<BatchSendArAgingReportEmailsResponse>;
  becomeUser?: Maybe<AuthPayload>;
  /** Bulk-create bills from a CSV or Excel (.xlsx) attachment. Each row's vendor is resolved by name (must already exist); duplicate bills (same vendor + invoice number) are skipped. sheetName picks the sheet of a multi-sheet workbook (ignored for CSV). Imports are capped at 500 data rows per file. */
  bulkImportBillsFromFile?: Maybe<BulkImportResponse>;
  /** Bulk-create invoices (payment links) from a CSV or Excel (.xlsx) attachment. Each row's customer is resolved by email then normalized name (must already exist); duplicate invoices (same customer + invoice name) are skipped. Blank due dates fall back to the row's invoice date. Invoices are created active but NOT sent, and are not synced to QuickBooks; an org on Xero with invoice writes on gets them created in Xero by the push. sheetName picks the sheet of a multi-sheet workbook (ignored for CSV). Imports are capped at 500 data rows per file. */
  bulkImportInvoicesFromFile?: Maybe<BulkImportResponse>;
  /** Bulk-create retail customers from a CSV or Excel (.xlsx) attachment; existing name/email matches are skipped. sheetName picks the sheet of a multi-sheet workbook (ignored for CSV). Imports are capped at 500 data rows per file. */
  bulkImportRetailCustomersFromFile?: Maybe<BulkImportResponse>;
  /**
   * Bulk-create vendors from a CSV or Excel (.xlsx) file (a Penny attachmentId,
   * or a fileId from prepareFileUpload). Rows whose name or email already match a vendor on
   * file are skipped and reported. The file is read server-side from the
   * sensitive bucket; access is scoped to the caller's organization. For an
   * Excel workbook with multiple data sheets, sheetName picks the sheet to
   * import (ignored for CSV). Imports are capped at 500 data rows per file.
   */
  bulkImportVendorsFromFile?: Maybe<BulkImportResponse>;
  cancelBillPayment?: Maybe<CancelBillPaymentResponse>;
  cancelCustomDomainVerification?: Maybe<CancelCustomDomainVerificationResponse>;
  cancelCustomerDebitAuthorizationRequest?: Maybe<DebitAuthorizationResponse>;
  /** Withdraw a waiting invoice action: whoever asked, or a workflow manager. */
  cancelInvoiceApprovalRequest?: Maybe<InvoiceApprovalRequestResponse>;
  /**
   * Cancels a send that is still waiting to go out, clearing its date. The
   * invoice keeps its saved recipients.
   */
  cancelInvoiceSend?: Maybe<QuestionFormResponse>;
  cancelPennyPlanForDashboard?: Maybe<PennyPendingPlanResponse>;
  cancelRetailCustomerDebitAuthorization?: Maybe<CancelRetailCustomerDebitAuthorizationResponse>;
  cancelScheduledInvoicePayment?: Maybe<CancelScheduledInvoicePaymentResponse>;
  cancelScheduledPayment?: Maybe<CancelScheduledPaymentResponse>;
  /** Undo a scheduled plan change: the next renewal stays on the plan in effect. */
  cancelScheduledPlanChange?: Maybe<CancelScheduledPlanChangeResponse>;
  changeApprovalStatus?: Maybe<ChangeApprovalResponse>;
  /**
   * Sets the caller's password (WorkOS + local hash) and revokes every session.
   * Refused while the account has a social ssoProvider: its session may have
   * been minted by the provider alone, so those accounts set a password through
   * the emailed reset link (forgotPassword → newPassword), which proves mailbox
   * control.
   */
  changePassword?: Maybe<Scalars['String']['output']>;
  /**
   * changePassword, returning a session token for the caller that the
   * revocation spares, so only the caller's other sessions end.
   */
  changePasswordKeepingSession: ChangePasswordKeepingSessionPayload;
  closeNickelBalanceAccount?: Maybe<CloseNickelBalanceAccountResponse>;
  /**
   * Close ONE of the org's Nickel Balance accounts (multi-account; the account
   * page's Settings → Danger Zone). Refused unless the account holds no money
   * — a zero ledger balance, a zero fresh bank balance, nothing in flight — and
   * has no live cards, and unless another open account remains: AR routing
   * that named this account (the org selection, invoice and recurring pins)
   * moves to the oldest remaining account. The bank account is closed at
   * Increase and the row leaves every listing; its history stays.
   */
  closeNickelBalanceBankAccount?: Maybe<CloseNickelBalanceBankAccountResponse>;
  /**
   * Finishes a login that WorkOS Radar challenged. Takes the radarChallengeToken
   * from login and the emailed code. Answers like login: a session token, or a
   * two-factor challenge when the account's only factor is a text message. A
   * passed challenge counts as the email factor, so no second email code is sent.
   * A challenge allows five attempts. After that the answer is
   * RADAR_CHALLENGE_EXPIRED and the user logs in again.
   */
  completeRadarChallenge?: Maybe<AuthPayload>;
  completeSsoEmailVerification: SsoCompleteResult;
  confirmPayment?: Maybe<ConfirmPaymentResponse>;
  consumeSignInLink?: Maybe<AuthPayload>;
  controlPennyComputer?: Maybe<ControlPennyComputerResponse>;
  createACHPaymentMethod?: Maybe<ExecuteTilledAchPaymentResponse>;
  createAccountingClient?: Maybe<CreateAccountingClientResponse>;
  createApiToken?: Maybe<CreateApiTokenResponse>;
  createApproval?: Maybe<CreateApprovalResponse>;
  createCardDetailsToken?: Maybe<CardDetailsTokenResponse>;
  /**
   * Provisions a WorkOS SSO organization for the caller's Nickel org and stores a
   * PENDING OrganizationSsoConfig. Requires `team_org.security.manage_sso`, in a
   * non-accounting-firm org. Returns an Admin Portal link for the next setup
   * step: domain verification, or connecting the identity provider when the
   * domain is already verified.
   */
  createEnterpriseSsoOrganization: EnterpriseSsoConfigPayload;
  createMerchantACHPaymentMethod?: Maybe<ExecuteTilledAchPaymentResponse>;
  createMerchantACHPaymentMethodWithPlaid?: Maybe<CreateMerchantAchPaymentMethodWithPlaidResponse>;
  createMerchantCreditCardPaymentMethod?: Maybe<CreateMerchantCreditCardResponse>;
  /**
   * Open an additional Nickel Balance account for the org (multi-account): a
   * real Increase account under the merchant's entity, nicknamed Nickel-side.
   * Gated on the org being ACTIVE and unfrozen; at most 10 accounts per org.
   * `idempotencyKey` makes a retry resume the same account instead of opening
   * a second one. `chartOfAccount` chooses the account's QuickBooks ledger
   * account — link an existing one or name the new one; omitted, a
   * "Nickel – {nickname}" bank account is opened in the background.
   */
  createNickelBalanceAccount?: Maybe<CreateNickelBalanceAccountResponse>;
  createNickelBalanceCard?: Maybe<CreateNickelBalanceCardResponse>;
  createNickelBalanceCheckDeposit?: Maybe<CreateNickelBalanceCheckDepositResponse>;
  createNickelBalanceDeposit?: Maybe<CreateNickelBalanceDepositResponse>;
  /**
   * Move money between two of the org's own Nickel Balance accounts
   * (multi-account). Settles immediately: one book transfer at the bank,
   * mirrored as a debit on the source and a credit on the destination, with
   * one COMPLETED feed row. Gated on the SOURCE's spendable balance only — no
   * org-wide debt term, so funds can be moved onto an overdrafted account to
   * repay it. `idempotencyKey` is a client-generated key per submission; a
   * retry with the same key returns the same transfer.
   */
  createNickelBalanceInternalTransfer?: Maybe<CreateNickelBalanceInternalTransferResponse>;
  createNickelBalanceWithdrawal?: Maybe<CreateNickelBalanceWithdrawalResponse>;
  createPartnerEnrollment?: Maybe<PartnerEnrollmentPayload>;
  createPartnerEnrollmentLink?: Maybe<PartnerEnrollmentLinkPayload>;
  createPayment?: Maybe<CreatePaymentResponse>;
  createPayoutMethodWithPlaid?: Maybe<CreatePayoutMethodWithPlaidResponse>;
  createPlatformPartner?: Maybe<PlatformPartnerAdminPayload>;
  createRecurringBill?: Maybe<CreateRecurringBillResponse>;
  createRecurringInvoice?: Maybe<CreateRecurringInvoiceResponse>;
  createReminderRule?: Maybe<ReminderRuleResponse>;
  /**
   * Save a bank account on a customer, with the customer's signed authorization
   * document. Refused with a SharedAccountError, naming the other customer, when
   * the same routing + account number is already saved on a different customer
   * of the organization — unless acknowledgeSharedAccount is true. A shared
   * account is legitimate for franchise and multi-location merchants; it is
   * also what saving an account under the wrong customer looks like, and a
   * later batch charge would then debit the wrong bank.
   */
  createRetailCustomerACHDebitAuthorization?: Maybe<CreateRetailCustomerAchDebitAuthorizationResponse>;
  createRetailCustomerCreditCardDebitAuthorization?: Maybe<CreateRetailCustomerCreditCardDebitAuthorizationResponse>;
  createTagForOrg?: Maybe<TagResponse>;
  createWebhook?: Maybe<CreateWebhookResponse>;
  createWorkspaceRole?: Maybe<WorkspaceRoleResponse>;
  deactivateNickelAccount?: Maybe<DeactivateNickelAccountResponse>;
  deactivateRecurringBill?: Maybe<DeactivateRecurringBillResponse>;
  deactivateRecurringInvoice?: Maybe<DeactivateRecurringInvoiceResponse>;
  deattachMerchantPayment?: Maybe<MutationDetachPaymentResponse>;
  /**
   * A payer's Remove on a remember-me method. Requires the method's
   * PaymentMethod.checkoutToken: the external id alone names any payer's method.
   */
  deattachPayment?: Maybe<MutationDetachPaymentResponse>;
  /**
   * Approve or reject a held invoice action. Authority is the request's own
   * rules (ApprovalRequestState.viewerCanDecide agrees). The approval that
   * completes it runs the send or charge; if that then fails, the request is
   * FAILED and `error` says why.
   */
  decideInvoiceApprovalRequest?: Maybe<InvoiceApprovalRequestResponse>;
  /**
   * Approve or decline up to 100 of the caller's agent tool calls at once, each
   * under the same rules as approveMcpAction or declineMcpAction. One result per
   * distinct id, in the order asked; a refused id does not stop the others.
   */
  decideMcpActions?: Maybe<DecideMcpActionsResponse>;
  /**
   * Decide a Debit Protection hold while it is still PENDING_DECISION. ALLOW
   * records the decision and lets the bank accept the debit on its schedule;
   * BLOCK declines it at the bank as R08 (payment stopped). Refused once the
   * hold has left PENDING_DECISION (deadline passed, a teammate decided, or
   * the bank resolved it) — the response's error names the outcome. The same
   * decision repeated is a no-op success. `alwaysAllowCompany` with ALLOW also
   * adds the debit's Company ID to the account's allowed list, in the same
   * write: refused when the debit carries no Company ID, and nothing is added
   * when the decision is refused.
   */
  decideNickelBalanceInboundDebit?: Maybe<DecideNickelBalanceInboundDebitResponse>;
  decidePennyProposal?: Maybe<PennyProposalResponse>;
  /** Decline an agent's tool call. Same caller rule as approveMcpAction. */
  declineMcpAction?: Maybe<McpActionApprovalResponse>;
  declinePennyProposal?: Maybe<PennyProposalResponse>;
  deleteBill?: Maybe<DeleteBillResponse>;
  deleteLegacyWebhook?: Maybe<DeleteWebhookResponse>;
  deleteReferralInvite?: Maybe<DeleteReferralInviteResponse>;
  deleteReminderRule?: Maybe<DeleteReminderRuleResponse>;
  deleteRetailCustomerPaymentMethod?: Maybe<RetailCustomerResponse>;
  deleteVendorPayoutMethod?: Maybe<DeleteVendorPayoutMethodResponse>;
  deleteWebhook?: Maybe<DeleteWebhookResponse>;
  deleteWorkspaceRole?: Maybe<DeleteWorkspaceRoleResponse>;
  disableNickelBalanceCard?: Maybe<DisableNickelBalanceCardResponse>;
  disconnectAccountingIntegration?: Maybe<DisconnectAccountingIntegrationResponse>;
  /**
   * Disconnects a workspace connection: the gateway refuses it at once, and the
   * provider's account is deleted (retried by the hourly sweep when that fails).
   * Disconnecting a disconnected connection retries only the delete. Gated by
   * penny.connectors.manage_workspace.
   */
  disconnectConnectorConnection?: Maybe<DisconnectConnectorConnectionResponse>;
  /**
   * Self-serve SSO disconnect for the caller's org: turns enforcement off, deletes
   * the org's WorkOS SSO connections, and tears down local state (status INACTIVE,
   * routable domains removed) through the same sync path the WorkOS webhook uses.
   * No password-setup email is sent — the domain teardown immediately re-enables
   * the standard forgotPassword flow for teammates who only ever signed in via
   * SSO. The WorkOS organization and its verified domains are kept, so SSO can be
   * re-enabled later without re-proving domain ownership. Requires
   * `team_org.security.manage_sso`, in a configured org.
   */
  disconnectEnterpriseSso: EnterpriseSsoConfig;
  /**
   * Removes the caller's social sign-in (Google / Microsoft / Intuit) so the
   * account logs in by password only. Requires the current password, verified
   * through WorkOS exactly like login: that proves a real password exists
   * (social signups carry only a random placeholder hash) and re-authenticates
   * before a sign-in method is removed. The WorkOS user and its OAuth identity
   * are kept; the social callback refuses accounts without an ssoProvider.
   * Revokes every session in the same write: the sessions being retired are
   * exactly the ones the provider may have minted, and the actor logs back in
   * with the password just proved.
   */
  disconnectSocialSso: DisconnectSocialSsoPayload;
  /**
   * Staff only. Replaces the questions every call about the org asks, or with
   * null questions goes back to the AI underwriter's. Refused while an AI call
   * about the org is in progress.
   */
  editFollowUpQuestions?: Maybe<EditFollowUpQuestionsPayload>;
  /**
   * Verify the enrollment code and save the phone number as the user's SMS 2FA
   * method, switching 2FA on. Also the "change my number" path: enrolling a
   * different number replaces the one on file.
   *
   * On the change-my-number path (a verified number already on file), the caller
   * must also pass currentNumberCode: a code texted to the CURRENT number (via
   * sendTwoFactorVerificationCodeViaSMS). Proving control of the existing number
   * as well as the new one stops a hijacked session from silently re-pointing 2FA
   * to an attacker's phone. First-time enrollment has no number to challenge, so
   * currentNumberCode is not required then.
   */
  enrollSmsTwoFactor?: Maybe<TwoFactorSettingsPayload>;
  /**
   * Enters one of the firm's clients as the client user the API picks, and
   * returns an accountant session.
   */
  enterClientWorkspace: AuthPayload;
  /**
   * Mints the enterprise-SSO authorization URL for an email whose domain routes to
   * an ACTIVE, enforced-or-not SSO org. Signs enterprise state (kind + WorkOS org
   * id) round-tripped through WorkOS and cross-checked in the callback.
   */
  enterpriseSsoAuthorizationUrl: Scalars['String']['output'];
  finishPennyWebVisit?: Maybe<FinishPennyWebVisitResponse>;
  forgotPassword?: Maybe<ForgotPasswordPayload>;
  generateApprovalSummary?: Maybe<ApprovalSummaryResponse>;
  /**
   * Nickel staff: renders the formal Record of Payment Authorization for a
   * completed authorization, keeps the PDF, and returns a short-lived download
   * URL. `timeZone` is an IANA zone from the tool's list (Eastern by default);
   * `attention` names the merchant contact on the cover letter.
   */
  generateAuthorizationRecordPdf: AuthorizationRecordPdfResponse;
  /**
   * Mints a fresh WorkOS Admin Portal link for the caller's org. DOMAIN_VERIFICATION
   * shows the DNS record to add; SSO connects the identity provider, and is
   * refused until WorkOS lists a verified domain, so a connection can never
   * activate before the domain it routes. With no intent, it returns the link
   * for whichever step the org is on. Requires `team_org.security.manage_sso`.
   */
  generateSsoAdminPortalLink: Scalars['String']['output'];
  getLinkToken?: Maybe<GetLinkTokenResponse>;
  heartbeatPennyWebVisit?: Maybe<PennyWebVisitWantedResponse>;
  inviteExternalTeammate?: Maybe<NewTeammateResponse>;
  inviteOwner?: Maybe<NewTeammateResponse>;
  issuePlatformPartnerKey?: Maybe<IssuePlatformPartnerKeyPayload>;
  /**
   * twoFactorMethod is the user's answer to the method picker, sent on a second
   * login call when the first returned more than one availableTwoFactorMethods.
   * Omit it on the first call.
   */
  login?: Maybe<AuthPayload>;
  manualAccountingItemSync?: Maybe<ManualAccountingItemSyncResponse>;
  manualAccountingPageSync?: Maybe<ManualAccountingPageSyncResponse>;
  manualAccountingSync?: Maybe<ManualAccountingSyncResponse>;
  markPennyWebsitePaymentNotPaid?: Maybe<MarkPennyWebsitePaymentNotPaidResponse>;
  newPassword?: Maybe<Scalars['String']['output']>;
  /**
   * Invites by legacy tier (role) or by workspace role (workspaceRoleId: one of
   * the org's roles, carrying nothing the inviter lacks) — exactly one of the
   * two. Needs team_org.team.invite_members.
   */
  newTeammate?: Maybe<NewTeammateResponse>;
  /**
   * The ACH & Wire Instructions sheet for one of the org's Nickel Balance
   * accounts: a Nickel-branded PDF (the statement's chrome) of the account's
   * full routing and account numbers, its legal titling as the beneficiary,
   * and the receiving bank — what a merchant hands to a customer or
   * counterparty who will pay them by ACH or domestic wire. Rendered by
   * Nickel's PDF service from the same live details
   * nickelBalanceDepositInstructions serves (never Increase's own
   * funding-instructions export, which carries Increase's letterhead), stored
   * beside the account's statements, and returned as a time-limited presigned
   * URL (the nickelBalanceStatementDownloadUrl contract). Re-requesting serves
   * the stored sheet until any printed detail changes. Throws when the account
   * is still provisioning or the render failed.
   */
  nickelBalanceAchWireInstructionsUrl: Scalars['String']['output'];
  /**
   * A bank letter (Increase's account verification letter) for one of the
   * org's Nickel Balance accounts: a PDF on the bank's letterhead stating the
   * account holder, the full account and routing numbers, and the balance as
   * of today (Eastern) — what a landlord, payroll provider or counterparty
   * asks for as proof of account. Rendered on demand by Increase, stored
   * beside the account's statements, and returned as a time-limited presigned
   * URL (the nickelBalanceStatementDownloadUrl contract). Re-requesting the
   * same day serves the same letter. Throws when the account is still
   * provisioning, and — retryably — when the render has not finished within
   * the request's wait budget: the retry resumes the same document.
   */
  nickelBalanceBankLetterUrl: Scalars['String']['output'];
  nickelBalanceStatementDownloadUrl: Scalars['String']['output'];
  /**
   * The Nickel Balance page's "Download transactions" export: assembles the
   * org's NB ledger lines posted between startDate and endDate (inclusive
   * calendar dates, YYYY-MM-DD, read on the timeZone's calendar when one is
   * given and as UTC days otherwise), keeps one statement section when given,
   * stores the CSV and returns a time-limited presigned S3 URL for it. Rows are
   * the same statement lines the monthly statement CSV carries
   * (docs/nickel-balance/statements.md §7), assembled on demand over the
   * requested window — the ledger, not the dashboard's current table view.
   * Throws on an invalid window or time zone and when the ledger fails an
   * assembly guard.
   */
  nickelBalanceTransactionsCsvUrl: Scalars['String']['output'];
  /**
   * A voided check for one of the org's Nickel Balance accounts — the "void
   * check" a payroll provider or vendor asks for to set up direct deposit or
   * ACH. Printed by Increase over the account's MICR line with `payer` as the
   * payer block when any of its lines is given, else with the merchant's legal
   * name over its onboarded business address. Same rendering path, storage
   * and URL contract as nickelBalanceBankLetterUrl; a distinct payer block
   * renders a distinct check.
   */
  nickelBalanceVoidedCheckUrl: Scalars['String']['output'];
  onboardingCreatePayoutMethod?: Maybe<OnboardingPayload>;
  /**
   * Read the bill number, due date, memo, amount due and vendor name off an
   * uploaded bill (a PDF or a photo, from prepareFileUpload) so the bill form can
   * prefill them. Costs a model call, so call it once per uploaded file, then
   * pass the same fileId to addBill. Errors only for a file the caller's
   * organization does not own, one not uploaded yet, or one that is not a PDF or
   * an image; a document that cannot be read returns a parse of nulls.
   */
  parseBillDocument?: Maybe<ParseBillDocumentResponse>;
  /**
   * Read the amount and payer name off an already-uploaded check image so the
   * deposit form can prefill them. Costs a model call, so call it once per
   * uploaded front image — typically the moment that upload resolves, while the
   * merchant is still working on the back.
   */
  parseCheckDepositImage?: Maybe<ParseCheckDepositImageResponse>;
  /**
   * Reads the invoice number, invoice date, due date, memo, amount and customer
   * off an uploaded invoice (a PDF or a photo, from prepareFileUpload) so the
   * invoice form can prefill them. Costs a model call, so call it once per
   * uploaded file, then pass the same fileId to addQuestionForm or
   * createRecurringInvoice. Errors only for a file the caller's organization does
   * not own, one not uploaded yet, or one that is not a PDF or an image; a
   * document that cannot be read returns a parse of nulls.
   */
  parseInvoiceDocument?: Maybe<ParseInvoiceDocumentResponse>;
  /**
   * Approve a held paper check. Nothing is sent to the bank: the check is paid
   * at `automaticallyResolvesAt`, as it would be undecided. Refused once the
   * check has left PENDING_DECISION.
   */
  payNickelBalanceInboundCheck?: Maybe<NickelBalanceInboundCheckReviewResponse>;
  portalGetLinkToken?: Maybe<PortalGetLinkTokenResponse>;
  /**
   * Resolution Center (docs/chargebacks/design.md §7). Mint a presigned S3 PUT
   * for one evidence file on a chargeback the caller's org can still respond
   * to. png, jpg, jpeg, pdf only; 4 MB per file (the tightest rail, Braintree).
   */
  prepareChargebackEvidenceUpload?: Maybe<PrepareChargebackEvidenceUploadResponse>;
  prepareCheckDepositUpload?: Maybe<PrepareCheckDepositUploadResponse>;
  /**
   * Mint an upload for a file the caller will hand to another mutation by id:
   * a check photo, a signed debit authorization, a bill's or invoice's source
   * document, a CSV or xlsx to import. Returns the file's id and a presigned
   * URL (valid one hour) to PUT the bytes to; the PUT must carry exactly
   * `contentLength` bytes and should carry `contentType` as its Content-Type.
   * The file belongs to the caller's organization, and the mutations that take
   * file ids read it only for that organization. Accepts PDF, JPEG, PNG, WebP,
   * HEIC, CSV, xlsx, xls, docx, doc, plain text and Outlook .msg, up to 25 MB.
   */
  prepareFileUpload?: Maybe<PrepareFileUploadResponse>;
  /**
   * The payment the payer's checkout of a payment request charges: its open
   * payment, created with its processor intent and Plaid tokens when it has
   * none, so the pay page can load the request without waiting on either.
   * Answers no payment and no error when the request has nothing left to
   * collect. Public, as for getPaymentRequest: the organization + checkout path
   * in the link are the capability.
   */
  preparePaymentRequestCheckout?: Maybe<PaymentResponse>;
  preparePennyAttachmentUpload?: Maybe<PreparePennyAttachmentUploadResponse>;
  preparePennyVisitCard?: Maybe<PreparePennyVisitCardResponse>;
  preparePennyWebVisitCapture?: Maybe<PreparePennyWebVisitCaptureResponse>;
  processUnsyncedTransactions?: Maybe<ProcessUnsyncedTransactionsResponse>;
  /**
   * Admin-only, staging and local development only (BadRequestError in
   * production). Mints an already-APPROVED merchant account from three fields:
   * the organization, its owner user (USER_ADMIN, on the ADMIN workspace role,
   * with a WorkOS identity so the password signs in like any other), a claimed
   * nickelpayments.com subdomain derived from the business name, and a filled-in
   * onboardingInfo so nothing downstream sees a half-finished application. The
   * approval side effects of updateOrganizationStatus (Alloy, PostHog funnel,
   * PartnerStack, the approved email) are deliberately skipped: this is a test
   * account, not a merchant decision.
   */
  provisionAccount?: Maybe<ProvisionAccountResponse>;
  quickbooksTokenCreate?: Maybe<OauthTokenCreateResponse>;
  /**
   * Undo a voluntary cancel while the subscription is still in its paid grace
   * window (canceledAt set, nextBillingDate in the future). Clears canceledAt,
   * cancels the pending period-end downgrade, and re-arms billing for the
   * current term. No new payment method or charge is required.
   */
  reactivateSubscription?: Maybe<ReactivateSubscriptionResponse>;
  recordExternalBillPayment?: Maybe<RecordExternalBillPaymentResponse>;
  recordExternalInvoicePayment?: Maybe<RecordExternalInvoicePaymentResponse>;
  recordInvoiceDebitMemo?: Maybe<RecordInvoiceDebitMemoResponse>;
  /**
   * The caller opened the Nickel Balance application, which only the browser
   * sees, passed on as a journey event for marketing email
   * (docs/journey-events.md § "Nickel Banking application"). Fire-and-forget:
   * it sends nothing for an accountant in a client's workspace or staff acting
   * as a user, and at most one event per member a day.
   */
  recordNickelBankingApplicationOpened?: Maybe<RecordNickelBankingApplicationOpenedResponse>;
  /**
   * A moment in the onboarding application that only the browser sees, sent as a
   * journey event to PostHog, which forwards it to marketing tools.
   * Fire-and-forget: it refuses no member, and sends nothing for an accountant in
   * a client's workspace or staff acting as a user.
   */
  recordOnboardingApplicationEvent?: Maybe<RecordOnboardingApplicationEventResponse>;
  /**
   * Records that an invoice's pay page was opened in the portal — the source of
   * QuestionForm.lastViewedAt / viewCount. Public: the portal has no session, so
   * the organization + checkout path in the link are the capability, as for
   * getPaymentRequest.
   */
  recordPaymentRequestView?: Maybe<RecordPaymentRequestViewResponse>;
  recordPennyVisitPaymentOutcome?: Maybe<RecordPennyVisitPaymentOutcomeResponse>;
  /**
   * Staff only. Records a call that asked an underwriting run's follow-up
   * questions, then queues a run that reads the answers.
   */
  recordUnderwritingFollowUpCall?: Maybe<RecordUnderwritingFollowUpCallPayload>;
  /**
   * Spend this connection's approved approval for `tool` with exactly these
   * `args`. `redeemed` true means the call may run, once.
   */
  redeemMcpActionApproval?: Maybe<RedeemMcpActionApprovalResponse>;
  refundPayment?: Maybe<RefundPaymentResponse>;
  removeDocumentFromQuestionForm?: Maybe<QuestionFormResponse>;
  removeInvite?: Maybe<RemoveInviteResponse>;
  removePlatformPartnerReturnUrl?: Maybe<PlatformPartnerAdminPayload>;
  removePlatformPartnerWebhook?: Maybe<PlatformPartnerAdminPayload>;
  removeQuestionForm?: Maybe<QuestionFormResponse>;
  removeRetailCustomer?: Maybe<RemoveRetailCustomerResponse>;
  removeSubscription?: Maybe<RemoveSubscriptionResponse>;
  removeTagFromOrg?: Maybe<RemoveTagResponse>;
  removeTeammate?: Maybe<RemoveTeammateResponse>;
  removeVendor?: Maybe<RemoveVendorResponse>;
  reorderTagsForOrg?: Maybe<ReorderTagsResponse>;
  requestAlloyApplication?: Maybe<RequestAlloyApplicationResponse>;
  requestCustomerDebitAuthorization?: Maybe<RequestCustomerDebitAuthorizationResponse>;
  /**
   * The applicant agrees to a follow-up call, and Nickel's automated assistant
   * calls the phone on their application now. Records the agreement first.
   * Refused unless followUpCallOffer offers a call, and for a staff or
   * accountant session: only the applicant can agree. Returns the offer as it
   * then stands.
   */
  requestFollowUpCall?: Maybe<FollowUpCallOfferPayload>;
  /**
   * Ask the person who connected this MCP connection to approve a destructive
   * tool call in the dashboard, for an MCP client that cannot ask them itself.
   * MCP-token callers only. `args` is the tool's parsed arguments as JSON.
   * Returns the pending approval, reusing one already waiting for the same call.
   */
  requestMcpActionApproval?: Maybe<McpActionApprovalResponse>;
  requestPennyVisitPaymentApproval?: Maybe<RequestPennyVisitPaymentApprovalResponse>;
  requestSignInLink?: Maybe<RequestSignInLinkPayload>;
  requestVendorDeliveryDetails?: Maybe<RequestVendorDeliveryDetailsResponse>;
  /**
   * Emails the code of a pending Radar challenge again. At most one send per 30
   * seconds per challenge.
   */
  resendRadarChallenge: ResendRadarChallengePayload;
  resendSsoVerificationCode: ResendSsoVerificationCodePayload;
  /**
   * Send an unapplied inbound bank transfer back to the payer (ACH credit: R23;
   * wire: reversal). Refused for RTP and FedNow, which are final once received.
   */
  returnInboundBankTransfer?: Maybe<InboundBankTransferResponse>;
  /**
   * Return a held paper check: the bank declines it and no money leaves the
   * account. Refused once the check has left PENDING_DECISION or its
   * `declineDeadline` has passed.
   */
  returnNickelBalanceInboundCheck?: Maybe<NickelBalanceInboundCheckReviewResponse>;
  revertToAccountingAccount?: Maybe<AuthPayload>;
  /**
   * Admin-only. Approve a PENDING_REVIEW submission (the responder sends it to
   * the processor and the chargeback moves to UNDER_REVIEW) or reject it back
   * to the merchant with a note. A responder failure leaves the submission
   * FAILED with the reason; chargebackCli respond refires it.
   */
  reviewChargebackSubmission?: Maybe<ChargebackResponse>;
  revokeApiToken?: Maybe<RevokeApiTokenResponse>;
  /**
   * Stop always allowing a company: its next debit is held for review again.
   * Debits already allowed are not changed. Idempotent.
   */
  revokeNickelBalanceDebitAllowRule?: Maybe<NickelBalanceDebitAllowRuleResponse>;
  revokePlatformPartnerKey?: Maybe<PlatformPartnerAdminPayload>;
  /**
   * Nickel staff only. Reject every session JWT the named users hold, by
   * stamping `User.sessionsRevokedAt` — the flag the GraphQL context checks on
   * every request (assertSessionNotRevoked).
   *
   * This exists for account takeover, where the sessions must die but the
   * account should survive: the merchant is meant to sign back in after a
   * verified password reset. Before it, the only writer of sessionsRevokedAt
   * was a password change/reset, i.e. only whoever holds the mailbox — which in
   * a takeover is exactly who cannot be trusted. Deactivating the user also
   * ends the session (getUserAndOrganizationFromUserId refuses an inactive
   * row), but it is a bigger hammer: it blocks the real owner from coming back
   * without staff lifting it.
   *
   * `organizationId` is required and scopes the blast radius — a revocation can
   * never cross orgs. Omit `userId` to revoke every member of the org, which is
   * the takeover shape: an attacker who held a session for days may have minted
   * others. The action is appended to the org's admin notes.
   *
   * Revoking does not log anyone out of anything but Nickel, and it does not
   * change passwords — a stolen credential still works until it is rotated.
   */
  revokeSessions?: Maybe<RevokeSessionsResponse>;
  /** Creates an approval rule, or saves a new version of one by lineageId. */
  saveApprovalRule?: Maybe<SaveApprovalRuleResponse>;
  /**
   * Sets the date an invoice is first emailed to its recipients. The API sends
   * at 9am US Eastern that day, routing the send for approval as the caller.
   * Only an unsent, open invoice takes a date; calling it again while the send
   * waits moves the date and replaces the recipients.
   */
  scheduleInvoiceSend?: Maybe<QuestionFormResponse>;
  /**
   * Schedule a move from Pro to Plus for the next renewal. Nothing is charged
   * or refunded now. `nextChargeAcknowledgedCents` is the renewal price the
   * client displayed (from subscriptionDowngradePreview) and must match it.
   */
  scheduleSubscriptionDowngrade?: Maybe<ScheduleSubscriptionDowngradeResponse>;
  /**
   * Selects the org's AR payout destination (Settings → Get Paid): one of the
   * org's `payoutMethods`. Admin-only. A bank method must be live; a Nickel
   * Balance method requires an ACTIVE Nickel Balance and a provisioned account.
   * Existing per-invoice pins are untouched; every unpinned invoice — existing
   * and future — follows the new selection for payouts created after the
   * change. Returns the organization so `payoutMethod` can be re-read.
   */
  selectPayoutMethod?: Maybe<OrganizationResponse>;
  sendArAgingReportEmail?: Maybe<SendArAgingReportEmailResponse>;
  sendNickelVerificationCodeViaEmail?: Maybe<SendNickelVerificationCodeViaEmailResponse>;
  sendPaymentLinkToRecipients?: Maybe<SendPaymentLinkToRecipientsResponse>;
  sendPennyMessage?: Maybe<SendPennyMessageResponse>;
  sendPlatformPartnerTestEvent?: Maybe<PlatformPartnerAdminPayload>;
  sendReferralInvite?: Maybe<SendReferralInviteResponse>;
  sendRegistrationCode?: Maybe<SendNickelVerificationCodeViaEmailResponse>;
  /**
   * Text a code to the number the user just typed, for SMS 2FA enrollment.
   *
   * Distinct from sendTwoFactorVerificationCodeViaSMS, which deliberately ignores
   * its argument once a number is enrolled and always texts the number on file --
   * that keeps a hijacked session from redirecting login codes. Enrollment is the
   * one flow that must reach a NEW number, so it gets its own send.
   */
  sendSmsTwoFactorEnrollmentCode?: Maybe<TwoFactorAuthPayload>;
  sendTestReminder?: Maybe<SendTestReminderResponse>;
  sendTwoFactorVerificationCodeViaSMS?: Maybe<TwoFactorAuthPayload>;
  /**
   * Sets what the org withholds from its accounting firm
   * (Organization.accountantRestrictions): the firm's sessions, and tokens
   * minted in them, then act with the grants of the member they entered as
   * less every permission of each listed group. Takes team_org.team.change_roles
   * or team_org.team.delete_members. Giving a group back needs every permission
   * in it. Refused with no firm linked, from an accountant session, and from an
   * API token.
   */
  setAccountantAccess?: Maybe<SetAccountantAccessResponse>;
  /**
   * LISTED writes every member's row for every client. ALL_MEMBERS deletes them,
   * letting every member enter every client again, and needs proofToken.
   */
  setClientAccessMode: ClientAccessModePayload;
  setCustomDomain?: Maybe<SetCustomDomainResponse>;
  /**
   * Makes one of the org's bill-pay funding methods (a getAllMerchantPaymentMethods
   * row) the default the bill-pay pickers preselect. A Nickel Balance account
   * also takes banking.account.see_balance, an org whose Nickel Balance can
   * fund bills, and an account switched on for bill pay.
   */
  setDefaultBillPaymentMethod?: Maybe<PaymentMethodResponse>;
  /**
   * Turn bill pay on or off for one of the org's Nickel Balance accounts
   * (Settings → Bill Pay). Off: getAllMerchantPaymentMethods stops listing the
   * account and new bill payments from it are refused. Payments already
   * scheduled, awaiting approval or recurring from it keep running until they
   * finish or are cancelled. An account switched off stops being the org's
   * default (setDefaultBillPaymentMethod).
   */
  setNickelBalanceAccountBillPay?: Maybe<UpdateNickelBalanceAccountResponse>;
  /**
   * Change where one of the org's Nickel Balance accounts records in
   * QuickBooks (the account's Settings tab): link a bank account already in
   * the Chart of Accounts, or open a new one under a name. Replaces the current
   * link on the account's payout method (deposits) and payment method (bill
   * payments) together; exactly one input field is required.
   */
  setNickelBalanceAccountChartOfAccount?: Maybe<SetNickelBalanceAccountChartOfAccountResponse>;
  /**
   * Turn paper checks on or off for one of the org's Nickel Banking accounts
   * (the account's Settings tab). On: the account gets its own check-writing
   * account number, and checks written on it are held for review and paid
   * unless returned. Off: the bank declines any check presented on that number
   * afterwards. Turning it back on reuses the same number.
   */
  setNickelBalanceAccountCheckWriting?: Maybe<UpdateNickelBalanceAccountResponse>;
  /**
   * Turn Debit Protection on or off for one of the org's Nickel Balance
   * accounts (the account's Settings tab). On: third-party ACH debits that
   * arrive from now on are held for review. Off: every debit currently held on
   * the account is ALLOWED in the same write (attributed to the caller) — the
   * merchant just said they no longer want debits gated, so none may be
   * blocked by the deadline behind their back.
   */
  setNickelBalanceAccountDebitProtection?: Maybe<UpdateNickelBalanceAccountResponse>;
  /**
   * Admin-only. Sets the interest rate (basis points, 0–200) Nickel offers on
   * Nickel Balance holdings. Omit organizationId to set the global default.
   */
  setNickelBalanceInterestRate?: Maybe<SetNickelBalanceInterestRateResponse>;
  /**
   * Nickel staff: makes an existing organization a client of an accounting firm,
   * moving it off any firm it had, or unlinks it when accountantOrganizationId
   * is null. Access rows the client had under a previous firm are deleted, so a
   * LISTED firm's role managers grant access to the new client themselves. The
   * firm must be an ACCOUNTING_FIRM and the client must not be one. The change
   * is appended to the client's admin notes.
   */
  setOrganizationAccountant?: Maybe<SetOrganizationAccountantResponse>;
  setOrganizationPlaidUpdated?: Maybe<OrganizationResponse>;
  setPayoutMethodWireAccountDetails?: Maybe<SetPayoutMethodWireAccountDetailsResponse>;
  setPayoutMethodWireBeneficiaryName?: Maybe<SetPayoutMethodWireBeneficiaryNameResponse>;
  setPlatformPartnerEnrollmentsPaused?: Maybe<PlatformPartnerAdminPayload>;
  setPlatformPartnerSuspended?: Maybe<PlatformPartnerAdminPayload>;
  setQuestionFormRemindersDisabled?: Maybe<QuestionFormResponse>;
  setTagOnEntity?: Maybe<TagAssignmentResponse>;
  /**
   * Turn one 2FA method on or off, leaving the other alone. Disabling a method
   * drops its record; 2FA stays on while any method survives.
   *
   * PHONE cannot be enabled here -- SMS needs a number to send to, so it is
   * switched on by completing enrollSmsTwoFactor instead.
   *
   * Requires proofToken: a short-lived single-use proof minted by
   * verifyTwoFactorSMSAuthCode / validateEmailVerificationCode, from a factor
   * the user already has enrolled (or, for the very first factor, the method
   * being enabled). Enabling EMAIL while PHONE is enrolled therefore requires
   * passing the SMS challenge, and vice versa.
   */
  setTwoFactorMethodEnabled?: Maybe<TwoFactorSettingsPayload>;
  signUpThroughPartnerEnrollment?: Maybe<SignUpThroughPartnerEnrollmentPayload>;
  signup?: Maybe<SignupPayload>;
  /**
   * Sandbox only: fake the payer's bank pushing an ACH credit, wire, RTP or
   * FedNow transfer to an open invoice's receiving account number, then queue
   * the posted transaction for the worker as a real inbound credit would be. Refuses in production
   * before touching anything. Public: the (organizationId, checkoutPath) pair
   * is the access, as for getPaymentRequest. amountInCents defaults to the
   * invoice's balance due.
   */
  simulateBankTransferCredit?: Maybe<SimulateBankTransferCreditResponse>;
  ssoAuthorizationUrl: Scalars['String']['output'];
  ssoSignup?: Maybe<SignupPayload>;
  /**
   * Exchanges the twoFactorToken minted by /auth/sso/callback (for accounts
   * with two-factor enabled) for a 2FA challenge: sends a code over the chosen
   * or only enrolled method and returns the same challenge shape as login.
   * Never returns a session token — complete the flow with
   * verifyTwoFactorSMSAuthCode / validateEmailVerificationCode, exactly like
   * password login. twoFactorMethod behaves as on login.
   */
  ssoTwoFactorChallenge?: Maybe<AuthPayload>;
  /**
   * Staff only. Has Nickel's Phone Agent call the number about any org and ask
   * its questions (PhoneAgent.questions), framed as an optional request for
   * information; the answers are recorded as a follow-up call when it finishes.
   * Off unless the environment enables it.
   */
  startFollowUpVoiceCall?: Maybe<StartFollowUpVoiceCallPayload>;
  startPartnerEnrollmentSession?: Maybe<StartPartnerEnrollmentSessionPayload>;
  startPennyWebVisit?: Maybe<PennyWebVisitWantedResponse>;
  /**
   * Starts connecting a connector for the whole organization
   * (docs/penny-connectors/design.md): a CONNECTING connection and the
   * provider's Connect Link, where the person enters the credential. It replaces
   * the org's current connection for the connector only once finished. Gated by
   * penny.connectors.manage_workspace and the pennyConnectors flag.
   */
  startWorkspaceConnectorConnection?: Maybe<StartConnectorConnectionResponse>;
  /**
   * Stop payment on an issued vendor check. `disposition` chooses what happens
   * to the captured funds: REFUND (default) unwinds the payment back to the
   * merchant; REISSUE_CHECK cuts a replacement check to the vendor from the
   * same funds (bill stays paid, no refund).
   */
  stopCheckPayment?: Maybe<StopCheckPaymentResponse>;
  /**
   * Deny the dispute: file the merchant's evidence and note as one
   * ChargebackSubmission in PENDING_REVIEW. Nothing reaches the processor
   * until ops approves it (reviewChargebackSubmission); the merchant sees
   * "Denial pending". Refused unless canRespond. Files were PUT to the keys
   * from prepareChargebackEvidenceUpload.
   */
  submitChargebackResponse?: Maybe<ChargebackResponse>;
  /**
   * Open the org's Nickel Balance: the merchant accepted the disclosures and
   * chose how their first account should be set up. Records the acceptance,
   * provisions the org's Increase entity, first account and account number
   * synchronously (nicknamed and configured from `account`), then marks the
   * org ACTIVE — there is no separate review step; the Get Activated approval
   * is the KYB gate, so an org whose activation is not APPROVED is refused.
   * Idempotent while ACTIVE (returns the open account); errors if a previous
   * application was REJECTED or the org's onboarding data could not provision
   * (see nickelBalanceOnboardingReadiness). A provisioning failure returns the
   * error and leaves the status untouched, so the merchant can retry.
   * `account` is optional only for clients predating it: omitted, the first
   * account opens unnamed with the defaults (payout destination selected,
   * Debit Protection off).
   */
  submitNickelBalanceApplication?: Maybe<SubmitNickelBalanceApplicationResponse>;
  submitNickelUserFeedback?: Maybe<SurveyResponse>;
  syncAccountingCustomers?: Maybe<SyncAccountingCustomersResponse>;
  togglePaymentState?: Maybe<ReconcilePaymentResponse>;
  unlinkOrganizationFromAccountant?: Maybe<UnlinkOrganizationFromAccountantResponse>;
  updateAnswerForm?: Maybe<AnswerFormResponse>;
  updateBankAccount?: Maybe<UpdateBankAccountResponse>;
  updateBill?: Maybe<BillResponse>;
  /**
   * Sets the Xero expense account bills made in Nickel are booked to when
   * Nickel creates them in Xero (AccountInfo.billExpenseChartOfAccount). Xero
   * only, and used only while Xero accounts payable is on for the org. The
   * account must be an active expense account, by Xero's id, as listed by
   * chartOfAccounts(accountType: "expense"). Omitting chartOfAccountId (or
   * passing null) clears the choice, and Nickel stops creating bills in Xero.
   */
  updateBillExpenseChartOfAccount?: Maybe<UpdateBillExpenseChartOfAccountResponse>;
  updateBranding?: Maybe<UpdateBrandingResponse>;
  /**
   * One save of the Client Access page. In an all-members firm it first switches
   * the firm to LISTED, writing a row for every active member and every client,
   * then applies the changes. A batch that grants anyone needs proofToken.
   */
  updateClientAccess: ClientAccessPage;
  /**
   * Sets the org-wide account Nickel fee journal entries debit (Settings →
   * Integrations). QuickBooks Online or Xero. Must name an EXPENSE-classified
   * account (for Xero, an active expense account by Xero's id) — a fee debit
   * posted to a bank or income account silently misstates the merchant's books,
   * and this mutation is reachable outside the picker.
   * Omitting chartOfAccountId (or passing null) CLEARS the choice, returning the
   * org to the default "Nickel Fees" account (created by Nickel in QuickBooks
   * Online, by the merchant in Xero).
   */
  updateFeeChartOfAccount?: Maybe<UpdateFeeChartOfAccountResponse>;
  /**
   * Sets the Xero income account invoices made in Nickel are booked to when
   * Nickel creates them in Xero (AccountInfo.invoiceSalesChartOfAccount). Xero
   * only. The account must be an active income account, by Xero's id, as listed
   * by chartOfAccounts(accountType: "invoiceSales"). Omitting chartOfAccountId
   * (or passing null) clears the choice, and Nickel stops creating invoices in
   * Xero.
   */
  updateInvoiceSalesChartOfAccount?: Maybe<UpdateInvoiceSalesChartOfAccountResponse>;
  updateMerchantPaymentMethod?: Maybe<PaymentMethodResponse>;
  /**
   * Your Notifications (notifications.md §13, §14): the caller's own full
   * subscription map, checked against what they may pick; an omitted key
   * unsubscribes it, and a stored subscription they may not pick is carried
   * through unchanged (its row is hidden from them). Any internal member of
   * the org — an external user never signs in.
   */
  updateMyNotifications?: Maybe<MyNotificationsResponse>;
  /** Rename one of the org's Nickel Balance accounts. */
  updateNickelBalanceAccount?: Maybe<UpdateNickelBalanceAccountResponse>;
  /** Edit a card's nickname and/or its spending limits (the account it spends from is fixed for life). */
  updateNickelBalanceCard?: Maybe<UpdateNickelBalanceCardResponse>;
  /**
   * Settings → Notifications, the tab beside the matrix: merges the given
   * defaults into notificationSettings. Gated by manage_notifications like
   * the matrix.
   */
  updateNotificationDefaults?: Maybe<NotificationDefaultsResponse>;
  /**
   * The retired legacy Notifications page's save. Gated by manage_notifications,
   * then always refused with a NotificationSettingsMovedError: every org is on
   * the Team and Your Notifications pages.
   */
  updateNotificationSettings?: Maybe<UpdateNotificationSettingsResponse>;
  updateOnboardingInformation?: Maybe<OnboardingInfoPayload>;
  /**
   * Links a payment method or payout method (and its siblings) to an account in
   * the org's QuickBooks Online or Desktop chart of accounts, or to an active
   * Xero account by Xero's id: a bank account, or a credit card account for a
   * card payment method. A Xero org links a payment method, the account its bill
   * payments are paid from, only with Xero accounts payable on.
   */
  updateOrCreateChartOfAccount?: Maybe<UpdateOrCreateChartOfAccountResponse>;
  /**
   * Admin-only. Edits the org's admin-managed settings: the kill switches —
   * paymentsDisabled (blocks new AR payments and AP bill payments),
   * payoutsDisabled (holds AR settlement and vendor payouts), threeDSEnabled
   * (the org-level 3DS default; a QuestionForm's own threeDSEnabled still wins
   * over it) — plus creditCardEnabled (refused when it would leave the checkout
   * with neither cards nor ACH, the same rule as updatePaymentSettings),
   * brandingDisabled (white-label: hides Nickel branding on the portal and
   * emails, and Nickel's payout offers in vendor bill-paid emails), the
   * display name, and the nickelpayments.com subdomain (normalized
   * and validated exactly as updateSubdomain does; AlreadyTakenError when
   * another org holds it or it is reserved — note existing payment links on the
   * old subdomain stop resolving), the role (RETAILER or ACCOUNTING_FIRM;
   * refused while a firm still has linked clients, or while a would-be firm is
   * itself linked to a firm or has enterprise SSO configured), and the checkout
   * copy: the label and placeholder of the payer's reference input on the
   * portal (OrganizationInfo.checkoutCopy; an empty string restores that
   * default, strings are trimmed and capped at 160 characters), and the
   * business address (validated as the merchant's own onboarding write is: the
   * state must resolve to a US state and neither street line may be a PO Box —
   * plus a well-formed ZIP), and the legal name. Omitted arguments are left
   * unchanged; at least one must be supplied. Every change is appended to the org's admin notes with the acting
   * admin's name.
   */
  updateOrganizationAdminControls?: Maybe<UpdateOrganizationAdminControlsResponse>;
  /**
   * Admin-only. Decides a pending application (APPROVED, REJECTED, PENDING_RFI),
   * or moves a decided org: PENDING_RFI also reopens a REJECTED org, keeping its
   * denial reasons on record; DELETED soft-deletes it from any status (the
   * deactivateNickelAccount tombstone — status flipped, members deactivated,
   * subdomain freed — plus the billing-engine disarm and the pending-payments
   * cancel cascade denial uses), and PENDING flips an APPROVED, REJECTED or
   * DELETED org back to pending. Members a deletion tombstoned are re-invited,
   * not restored. The only refusal is "already in that state": this is the
   * admin side; the merchant-facing deactivation is the gated one. Every
   * transition is appended to the org's admin notes. `denialReasonOther`
   * doubles as the free-text reason on a DELETED transition.
   */
  updateOrganizationStatus?: Maybe<OrganizationResponse>;
  updatePartnerEnrollment?: Maybe<PartnerEnrollmentPayload>;
  updatePayment?: Maybe<UpdatePaymentResponse>;
  updatePaymentSettings?: Maybe<UpdatePaymentSettingsResponse>;
  updatePlaidIdentityVerificationId?: Maybe<UserResponse>;
  updateQuestionForm?: Maybe<QuestionFormResponse>;
  updateRecurringInvoice?: Maybe<RecurringInvoiceResponse>;
  updateReminderRule?: Maybe<ReminderRuleResponse>;
  updateRetailCustomer?: Maybe<UpdateRetailCustomerResponse>;
  updateRetailCustomerPaymentMethod?: Maybe<UpdateRetailCustomerPaymentMethodResponse>;
  updateSubdomain?: Maybe<UpdateSubdomainResponse>;
  updateSubscriptionPaymentMethod?: Maybe<UpdateSubscriptionPaymentMethodResponse>;
  updateTagForOrg?: Maybe<TagResponse>;
  /**
   * Team Notifications, for Administrators (docs/workspace-roles/
   * notifications.md §14), in one transaction: `cells` is the role × category
   * grid as a bulk edit — each change is stored on the role and pushed into the
   * setting of every current internal member of it (on only where the member
   * may pick the category, off always); `members` writes one user's whole map
   * — an external user's (the External Users page), checked against what the
   * acting Administrator may pick, or an internal member's, checked against
   * what that member may pick; `customer` is the buyer switches;
   * `emailRecipients` adds and removes addresses on merchant notifications
   * (§16), adding only where the acting Administrator may pick the
   * notification. Gated by team_org.organization.manage_notifications. `roles`
   * is the retired whole-map shape and is refused.
   */
  updateTeamNotifications?: Maybe<TeamNotificationsResponse>;
  updateTeammate?: Maybe<UpdateTeammateResponse>;
  updateTeammateProfile?: Maybe<UpdateTeammateResponse>;
  /**
   * Sets the Xero account Nickel payments applied to no invoice are credited to
   * (AccountInfo.unappliedPaymentChartOfAccount). Xero only. The account must be
   * an active income or liability account, by Xero's id, as listed by
   * chartOfAccounts(accountType: "unappliedPayment"). Omitting chartOfAccountId
   * (or passing null) clears the choice, and such payments stop being recorded.
   */
  updateUnappliedPaymentChartOfAccount?: Maybe<UpdateUnappliedPaymentChartOfAccountResponse>;
  updateVendor?: Maybe<VendorResponse>;
  updateVendorACHDeliveryMethodWithPlaid?: Maybe<UpdateVendorPayoutMethodResponse>;
  updateVendorCheckPayoutMethod?: Maybe<UpdateVendorPayoutMethodResponse>;
  /**
   * Replaces the vendor's DOMESTIC_WIRE delivery method: a domestic Fedwire
   * paid from the merchant's Nickel Balance with a $20 fee. Merchant sessions
   * only; a vendor link and an API token are refused. routingNumber is the
   * bank's wire ABA, which can differ from its ACH routing number.
   * accountHolderName is sent as the wire's beneficiary and must match the
   * account title (at most 35 characters, no accents): a mismatch is returned
   * after the money has left.
   */
  updateVendorDomesticWirePayoutMethod?: Maybe<UpdateVendorPayoutMethodResponse>;
  updateVendorPayoutMethod?: Maybe<UpdateVendorPayoutMethodResponse>;
  updateVendorWirePayoutMethod?: Maybe<UpdateVendorPayoutMethodResponse>;
  updateWorkspaceRole?: Maybe<WorkspaceRoleResponse>;
  /**
   * Answers the org's open request for information (PENDING_RFI →
   * PENDING_RFI_REVIEW). `s3Urls` are required when a document item is
   * requested; a PRIMARY_BANK_ACCOUNT item is answered by a payout method
   * created since the request (createPayoutMethodWithPlaid or
   * onboardingCreatePayoutMethod with source RFI) and refused without one.
   */
  uploadOrganizationRFIDocuments?: Maybe<OrganizationResponse>;
  validateEmailVerificationCode?: Maybe<ValidateEmailVerificationResponse>;
  /**
   * Checks a texted code. Signed-in callers (step-up) are resolved by session.
   * Login callers should pass the twoFactorChallengeToken from the login /
   * ssoTwoFactorChallenge response, which pins the check to the account that
   * just passed its first factor; `identifier` alone is a legacy fallback for
   * clients loaded before the token existed and picks an arbitrary account when
   * several share one number.
   */
  verifyTwoFactorSMSAuthCode?: Maybe<TwoFactorAuthPayload>;
  voidExternalBillPayment?: Maybe<VoidExternalBillPaymentResponse>;
  voidExternalInvoicePayment?: Maybe<VoidExternalInvoicePaymentResponse>;
  voidInvoiceDebitMemo?: Maybe<VoidInvoiceDebitMemoResponse>;
  /**
   * Finishes a Xero connection from the public_token on the redirect. The token
   * must belong to a connection the caller's organization started.
   */
  xeroConnectionComplete?: Maybe<XeroConnectionCompleteResponse>;
  /**
   * Starts signing the caller's organization in to Xero again, for a connection
   * whose xeroConnectionStatus is NEEDS_REAUTH. Refused for any other status.
   */
  xeroConnectionReconnect?: Maybe<XeroConnectionReconnectResponse>;
  /**
   * Starts connecting the caller's organization to Xero. Nothing is connected
   * until xeroConnectionComplete succeeds. Refused while the organization is on
   * QuickBooks.
   */
  xeroConnectionStart?: Maybe<XeroConnectionStartResponse>;
};


export type MutationAcceptChargebackArgs = {
  chargebackId: Scalars['String']['input'];
};


export type MutationActivateNickelBalanceCardArgs = {
  id: Scalars['ID']['input'];
};


export type MutationAddActivityNoteArgs = {
  activityId: Scalars['String']['input'];
  activitySource?: InputMaybe<PaymentActivitySource>;
  flagged?: InputMaybe<Scalars['Boolean']['input']>;
  note?: InputMaybe<Scalars['String']['input']>;
  reconciled?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationAddAdminNoteArgs = {
  note: Scalars['String']['input'];
  organizationId: Scalars['String']['input'];
};


export type MutationAddBillArgs = {
  amountCents: Scalars['Int']['input'];
  billNumber?: InputMaybe<Scalars['String']['input']>;
  dueDateEpochMillis: Scalars['Float']['input'];
  fileIds?: InputMaybe<Array<Scalars['ID']['input']>>;
  lines?: InputMaybe<QuickbooksLinesInput>;
  reason: Scalars['String']['input'];
  vendorId: Scalars['String']['input'];
};


export type MutationAddBillPayableArgs = {
  amountDueCents: Scalars['Int']['input'];
  billId: Scalars['String']['input'];
  dueDateEpochMillis: Scalars['Float']['input'];
  idempotencyKey?: InputMaybe<Scalars['String']['input']>;
  instantPayoutRequested?: InputMaybe<Scalars['Boolean']['input']>;
  invoiceDateEpochMillis: Scalars['Float']['input'];
  merchantPaymentMethodId: Scalars['String']['input'];
  payerDevice?: InputMaybe<CardPayerDeviceInput>;
  validateOnly?: InputMaybe<Scalars['Boolean']['input']>;
  vendorEmails?: InputMaybe<Array<Scalars['String']['input']>>;
  vendorId: Scalars['String']['input'];
  vendorMemo?: InputMaybe<Scalars['String']['input']>;
  vendorPayoutMethodId: Scalars['String']['input'];
  withdrawalDateEpochMillis?: InputMaybe<Scalars['Float']['input']>;
};


export type MutationAddExternalTeammateArgs = {
  email: Scalars['String']['input'];
  role?: InputMaybe<UserRole>;
};


export type MutationAddNewTeammateArgs = {
  firstName: Scalars['String']['input'];
  lastName: Scalars['String']['input'];
  rawPassword: Scalars['String']['input'];
  token: Scalars['String']['input'];
};


export type MutationAddNickelBalanceDebitAllowRuleArgs = {
  balanceAccountId: Scalars['ID']['input'];
  originatorCompanyId: Scalars['String']['input'];
  originatorCompanyName: Scalars['String']['input'];
};


export type MutationAddPlatformPartnerReturnUrlArgs = {
  partnerId: Scalars['ID']['input'];
  url: Scalars['String']['input'];
};


export type MutationAddPlatformPartnerWebhookArgs = {
  partnerId: Scalars['ID']['input'];
  url: Scalars['String']['input'];
};


export type MutationAddQuestionFormArgs = {
  achEnabled?: InputMaybe<Scalars['Boolean']['input']>;
  bankTransferEnabled?: InputMaybe<Scalars['Boolean']['input']>;
  checkoutPath?: InputMaybe<Scalars['String']['input']>;
  creditCardEnabled?: InputMaybe<Scalars['Boolean']['input']>;
  dueDateEpochMillis?: InputMaybe<Scalars['Float']['input']>;
  feePassthroughPercent?: InputMaybe<Scalars['Int']['input']>;
  fileIds?: InputMaybe<Array<Scalars['ID']['input']>>;
  invoiceDateEpochMillis?: InputMaybe<Scalars['Float']['input']>;
  lines?: InputMaybe<QuickbooksLinesInput>;
  memo?: InputMaybe<Scalars['String']['input']>;
  name?: InputMaybe<Scalars['String']['input']>;
  payoutMethodId?: InputMaybe<Scalars['ID']['input']>;
  recipients?: InputMaybe<Array<Recipient>>;
  requestedAmount?: InputMaybe<Scalars['Int']['input']>;
  retailCustomerId?: InputMaybe<Scalars['String']['input']>;
  schema?: InputMaybe<Scalars['String']['input']>;
  status?: InputMaybe<QuestionFormStatus>;
  syncToQbo?: InputMaybe<Scalars['Boolean']['input']>;
  thumbnail?: InputMaybe<Scalars['String']['input']>;
};


export type MutationAddRetailCustomerArgs = {
  emails?: InputMaybe<Array<Scalars['String']['input']>>;
  name: Scalars['String']['input'];
  phoneNumbers?: InputMaybe<Array<Scalars['String']['input']>>;
  syncToQbo?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationAddSubscriptionArgs = {
  freeTrial?: InputMaybe<Scalars['Boolean']['input']>;
  merchantPaymentMethodId: Scalars['String']['input'];
  planName: Scalars['String']['input'];
  prorationAcknowledgedCents?: InputMaybe<Scalars['Int']['input']>;
  upgradeFeature?: InputMaybe<Scalars['String']['input']>;
};


export type MutationAddVendorArgs = {
  emails?: InputMaybe<Array<Scalars['String']['input']>>;
  name: Scalars['String']['input'];
  syncToQbo?: InputMaybe<Scalars['Boolean']['input']>;
  type?: InputMaybe<VendorType>;
  vendorAddress?: InputMaybe<AddressDetailsInput>;
};


export type MutationAdminSendPennyReplyArgs = {
  body: Scalars['String']['input'];
  conversationId: Scalars['ID']['input'];
};


export type MutationApplyInboundBankTransferArgs = {
  inboundBankTransferId: Scalars['String']['input'];
  questionFormId: Scalars['String']['input'];
};


export type MutationApplyNickelBalanceDepositsToInvoicesArgs = {
  applications: Array<NickelBalanceDepositApplicationInput>;
  validateOnly?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationApplyTagToEntitiesArgs = {
  entityIds: Array<Scalars['String']['input']>;
  on: Scalars['Boolean']['input'];
  tagId: Scalars['String']['input'];
};


export type MutationApproveMcpActionArgs = {
  id: Scalars['ID']['input'];
};


export type MutationApproveOAuthAuthorizationArgs = {
  clientId: Scalars['ID']['input'];
  codeChallenge?: InputMaybe<Scalars['String']['input']>;
  codeChallengeMethod?: InputMaybe<Scalars['String']['input']>;
  redirectUri: Scalars['String']['input'];
  resource?: InputMaybe<Scalars['String']['input']>;
  scope?: InputMaybe<Scalars['String']['input']>;
  state?: InputMaybe<Scalars['String']['input']>;
  twoFactorProofToken?: InputMaybe<Scalars['String']['input']>;
};


export type MutationArchiveApprovalRuleArgs = {
  lineageId: Scalars['ID']['input'];
};


export type MutationBatchAutoDebitArgs = {
  batchAutoDebitInput: BatchAutoDebitInput;
  validateOnly?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationBatchBillPayArgs = {
  batchBillPayInput: Array<BatchBillPayInput>;
  idempotencyKey?: InputMaybe<Scalars['String']['input']>;
  payerDevice?: InputMaybe<CardPayerDeviceInput>;
  validateOnly?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationBatchCreateRetailCustomerAchDebitAuthorizationsArgs = {
  input: BatchCreateRetailCustomerAchDebitAuthorizationsInput;
  validateOnly?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationBatchRequestCustomerDebitAuthorizationsArgs = {
  input: BatchRequestCustomerDebitAuthorizationsInput;
  validateOnly?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationBatchRequestVendorDeliveryDetailsArgs = {
  input: BatchRequestVendorDeliveryDetailsInput;
};


export type MutationBatchSendArAgingReportEmailsArgs = {
  ccMerchant?: InputMaybe<Scalars['Boolean']['input']>;
  customMessage?: InputMaybe<Scalars['String']['input']>;
  items: Array<BatchSendArAgingReportEmailItemInput>;
};


export type MutationBecomeUserArgs = {
  becomeUserId: Scalars['String']['input'];
};


export type MutationBulkImportBillsFromFileArgs = {
  attachmentId?: InputMaybe<Scalars['String']['input']>;
  columnMapping: BillCsvColumnMappingInput;
  fileId?: InputMaybe<Scalars['ID']['input']>;
  hasHeader: Scalars['Boolean']['input'];
  sheetName?: InputMaybe<Scalars['String']['input']>;
};


export type MutationBulkImportInvoicesFromFileArgs = {
  attachmentId?: InputMaybe<Scalars['String']['input']>;
  columnMapping: InvoiceCsvColumnMappingInput;
  fileId?: InputMaybe<Scalars['ID']['input']>;
  hasHeader: Scalars['Boolean']['input'];
  sheetName?: InputMaybe<Scalars['String']['input']>;
};


export type MutationBulkImportRetailCustomersFromFileArgs = {
  attachmentId?: InputMaybe<Scalars['String']['input']>;
  columnMapping: RetailCustomerCsvColumnMappingInput;
  fileId?: InputMaybe<Scalars['ID']['input']>;
  hasHeader: Scalars['Boolean']['input'];
  sheetName?: InputMaybe<Scalars['String']['input']>;
};


export type MutationBulkImportVendorsFromFileArgs = {
  attachmentId?: InputMaybe<Scalars['String']['input']>;
  columnMapping: VendorCsvColumnMappingInput;
  fileId?: InputMaybe<Scalars['ID']['input']>;
  hasHeader: Scalars['Boolean']['input'];
  sheetName?: InputMaybe<Scalars['String']['input']>;
};


export type MutationCancelBillPaymentArgs = {
  billPaymentId: Scalars['String']['input'];
  validateOnly?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationCancelCustomerDebitAuthorizationRequestArgs = {
  debitAuthorizationId: Scalars['String']['input'];
};


export type MutationCancelInvoiceApprovalRequestArgs = {
  requestId: Scalars['ID']['input'];
};


export type MutationCancelInvoiceSendArgs = {
  questionFormId: Scalars['String']['input'];
  validateOnly?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationCancelPennyPlanForDashboardArgs = {
  planId: Scalars['ID']['input'];
};


export type MutationCancelRetailCustomerDebitAuthorizationArgs = {
  deleteAuthorization: Scalars['Boolean']['input'];
  retailCustomerId: Scalars['String']['input'];
  shortId: Scalars['String']['input'];
};


export type MutationCancelScheduledInvoicePaymentArgs = {
  questionFormId: Scalars['String']['input'];
};


export type MutationCancelScheduledPaymentArgs = {
  billPayableId: Scalars['String']['input'];
  validateOnly?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationChangeApprovalStatusArgs = {
  approvalIds?: InputMaybe<Array<Scalars['String']['input']>>;
  status: ApprovalStatus;
};


export type MutationChangePasswordArgs = {
  newPassword: Scalars['String']['input'];
};


export type MutationChangePasswordKeepingSessionArgs = {
  newPassword: Scalars['String']['input'];
};


export type MutationCloseNickelBalanceBankAccountArgs = {
  id: Scalars['ID']['input'];
};


export type MutationCompleteRadarChallengeArgs = {
  challengeToken: Scalars['String']['input'];
  code: Scalars['String']['input'];
};


export type MutationCompleteSsoEmailVerificationArgs = {
  code: Scalars['String']['input'];
  verificationToken: Scalars['String']['input'];
};


export type MutationConfirmPaymentArgs = {
  basisTheoryThreeDSSessionId?: InputMaybe<Scalars['String']['input']>;
  billingAddress?: InputMaybe<CardBillingAddressInput>;
  companyName?: InputMaybe<Scalars['String']['input']>;
  debitAuthorizationId?: InputMaybe<Scalars['String']['input']>;
  email: Scalars['String']['input'];
  fullName?: InputMaybe<Scalars['String']['input']>;
  idempotencyKey?: InputMaybe<Scalars['String']['input']>;
  livenessId?: InputMaybe<Scalars['String']['input']>;
  notes?: InputMaybe<Scalars['String']['input']>;
  payerDevice?: InputMaybe<CardPayerDeviceInput>;
  paymentId: Scalars['String']['input'];
  questionFormId?: InputMaybe<Scalars['String']['input']>;
  recurringInvoiceId?: InputMaybe<Scalars['String']['input']>;
  startDateEpochMillis?: InputMaybe<Scalars['Float']['input']>;
  threeDSChallengeCompleted?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationConsumeSignInLinkArgs = {
  token: Scalars['String']['input'];
};


export type MutationControlPennyComputerArgs = {
  action: PennyComputerAction;
  approvalId?: InputMaybe<Scalars['ID']['input']>;
  conversationId: Scalars['ID']['input'];
  visitId: Scalars['ID']['input'];
};


export type MutationCreateAchPaymentMethodArgs = {
  accountHolderName: Scalars['String']['input'];
  accountNumber?: InputMaybe<Scalars['String']['input']>;
  accountType?: InputMaybe<Scalars['String']['input']>;
  billingDetails?: InputMaybe<BillingDetailsInput>;
  organizationId?: InputMaybe<Scalars['String']['input']>;
  paymentId: Scalars['String']['input'];
  publicToken?: InputMaybe<Scalars['String']['input']>;
  routingNumber?: InputMaybe<Scalars['String']['input']>;
};


export type MutationCreateAccountingClientArgs = {
  businessName: Scalars['String']['input'];
  city?: InputMaybe<Scalars['String']['input']>;
  country?: InputMaybe<Scalars['String']['input']>;
  isApprovedOnSignup?: InputMaybe<Scalars['Boolean']['input']>;
  ownerEmail: Scalars['String']['input'];
  ownerFirstName: Scalars['String']['input'];
  ownerLastName: Scalars['String']['input'];
  paymentUrl?: InputMaybe<Scalars['String']['input']>;
  state?: InputMaybe<Scalars['String']['input']>;
  streetAddress?: InputMaybe<Scalars['String']['input']>;
  zip?: InputMaybe<Scalars['String']['input']>;
};


export type MutationCreateApiTokenArgs = {
  name?: InputMaybe<Scalars['String']['input']>;
  scopes?: InputMaybe<Array<ApiTokenScope>>;
  workspaceRoleId?: InputMaybe<Scalars['ID']['input']>;
};


export type MutationCreateApprovalArgs = {
  billPayableId?: InputMaybe<Scalars['String']['input']>;
  paymentMethodId: Scalars['String']['input'];
  recurringBillId?: InputMaybe<Scalars['String']['input']>;
  vendorPayoutMethodId: Scalars['String']['input'];
};


export type MutationCreateCardDetailsTokenArgs = {
  cardId: Scalars['ID']['input'];
};


export type MutationCreateEnterpriseSsoOrganizationArgs = {
  domain: Scalars['String']['input'];
};


export type MutationCreateMerchantAchPaymentMethodArgs = {
  accountNumber: Scalars['String']['input'];
  accountType: Scalars['String']['input'];
  routingNumber: Scalars['String']['input'];
};


export type MutationCreateMerchantAchPaymentMethodWithPlaidArgs = {
  bankAccountId: Scalars['String']['input'];
  publicToken: Scalars['String']['input'];
};


export type MutationCreateMerchantCreditCardPaymentMethodArgs = {
  basisTheoryTokenIntentId?: InputMaybe<Scalars['String']['input']>;
  billingAddress?: InputMaybe<CardBillingAddressInput>;
  nonce: Scalars['String']['input'];
  payerDevice?: InputMaybe<CardPayerDeviceInput>;
  provider: Scalars['String']['input'];
};


export type MutationCreateNickelBalanceAccountArgs = {
  chartOfAccount?: InputMaybe<NickelBalanceChartOfAccountInput>;
  debitProtectionEnabled?: InputMaybe<Scalars['Boolean']['input']>;
  idempotencyKey: Scalars['String']['input'];
  nickname: Scalars['String']['input'];
};


export type MutationCreateNickelBalanceCardArgs = {
  input: CreateNickelBalanceCardInput;
};


export type MutationCreateNickelBalanceCheckDepositArgs = {
  amountCents: Scalars['Int']['input'];
  applications?: InputMaybe<Array<InvoiceApplicationInput>>;
  backImageFileId?: InputMaybe<Scalars['ID']['input']>;
  backImageKey?: InputMaybe<Scalars['String']['input']>;
  balanceAccountId?: InputMaybe<Scalars['ID']['input']>;
  description?: InputMaybe<Scalars['String']['input']>;
  frontImageFileId?: InputMaybe<Scalars['ID']['input']>;
  frontImageKey?: InputMaybe<Scalars['String']['input']>;
  idempotencyKey?: InputMaybe<Scalars['String']['input']>;
  payerName?: InputMaybe<Scalars['String']['input']>;
  validateOnly?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationCreateNickelBalanceDepositArgs = {
  amountCents: Scalars['Int']['input'];
  balanceAccountId?: InputMaybe<Scalars['ID']['input']>;
  idempotencyKey: Scalars['String']['input'];
  payoutMethodId: Scalars['String']['input'];
  statementDescriptor?: InputMaybe<Scalars['String']['input']>;
};


export type MutationCreateNickelBalanceInternalTransferArgs = {
  amountCents: Scalars['Int']['input'];
  fromBalanceAccountId: Scalars['ID']['input'];
  idempotencyKey: Scalars['String']['input'];
  statementDescriptor?: InputMaybe<Scalars['String']['input']>;
  toBalanceAccountId: Scalars['ID']['input'];
};


export type MutationCreateNickelBalanceWithdrawalArgs = {
  amountCents: Scalars['Int']['input'];
  balanceAccountId?: InputMaybe<Scalars['ID']['input']>;
  idempotencyKey: Scalars['String']['input'];
  method?: InputMaybe<NickelBalanceWithdrawalMethod>;
  payoutMethodId: Scalars['String']['input'];
  statementDescriptor?: InputMaybe<Scalars['String']['input']>;
};


export type MutationCreatePartnerEnrollmentArgs = {
  partnerAccountRef: Scalars['String']['input'];
  prefill: PartnerEnrollmentPrefillInput;
  returnUrl: Scalars['String']['input'];
};


export type MutationCreatePartnerEnrollmentLinkArgs = {
  enrollmentId: Scalars['ID']['input'];
};


export type MutationCreatePaymentArgs = {
  amount: Scalars['Int']['input'];
  createdById?: InputMaybe<Scalars['String']['input']>;
  currency: Scalars['String']['input'];
  organizationId: Scalars['String']['input'];
  questionFormId?: InputMaybe<Scalars['String']['input']>;
};


export type MutationCreatePayoutMethodWithPlaidArgs = {
  bankAccountId: Scalars['String']['input'];
  proofToken?: InputMaybe<Scalars['String']['input']>;
  publicToken: Scalars['String']['input'];
  source?: InputMaybe<PayoutMethodCreationSource>;
};


export type MutationCreatePlatformPartnerArgs = {
  name: Scalars['String']['input'];
};


export type MutationCreateRecurringBillArgs = {
  amountCents: Scalars['Int']['input'];
  endDate?: InputMaybe<Scalars['String']['input']>;
  frequency: RecurringBillFrequency;
  lines?: InputMaybe<QuickbooksLinesInput>;
  paymentMethodId?: InputMaybe<Scalars['String']['input']>;
  paymentMode?: InputMaybe<RecurringBillPaymentMode>;
  reason: Scalars['String']['input'];
  reminderDaysBeforeDue?: InputMaybe<Scalars['Int']['input']>;
  reminderTiming?: InputMaybe<RecurringBillReminderTiming>;
  startDate: Scalars['String']['input'];
  vendorId: Scalars['String']['input'];
  vendorPayoutMethodId?: InputMaybe<Scalars['String']['input']>;
};


export type MutationCreateRecurringInvoiceArgs = {
  achEnabled?: InputMaybe<Scalars['Boolean']['input']>;
  amountCents: Scalars['Int']['input'];
  collectionMethod?: InputMaybe<RecurringInvoiceCollectionMethod>;
  creditCardEnabled?: InputMaybe<Scalars['Boolean']['input']>;
  endDate?: InputMaybe<Scalars['String']['input']>;
  feePassthroughPercent?: InputMaybe<Scalars['Int']['input']>;
  fileIds?: InputMaybe<Array<Scalars['ID']['input']>>;
  frequency: RecurringBillFrequency;
  invoiceNumber?: InputMaybe<Scalars['String']['input']>;
  lines?: InputMaybe<QuickbooksLinesInput>;
  memo?: InputMaybe<Scalars['String']['input']>;
  payoutMethodId?: InputMaybe<Scalars['ID']['input']>;
  retailCustomerId: Scalars['String']['input'];
  schema?: InputMaybe<Scalars['String']['input']>;
  sendDaysBeforeDue?: InputMaybe<Scalars['Int']['input']>;
  sendTiming?: InputMaybe<RecurringInvoiceSendTiming>;
  startDate: Scalars['String']['input'];
  status?: InputMaybe<QuestionFormStatus>;
  thumbnail?: InputMaybe<Scalars['String']['input']>;
};


export type MutationCreateReminderRuleArgs = {
  bodyTemplate: Scalars['String']['input'];
  ccInvoiceContact?: InputMaybe<Scalars['Boolean']['input']>;
  count: Scalars['Int']['input'];
  direction: ReminderDirection;
  isActive?: InputMaybe<Scalars['Boolean']['input']>;
  subjectTemplate: Scalars['String']['input'];
  unit: ReminderUnit;
};


export type MutationCreateRetailCustomerAchDebitAuthorizationArgs = {
  accountNumber: Scalars['String']['input'];
  accountType: Scalars['String']['input'];
  acknowledgeSharedAccount?: InputMaybe<Scalars['Boolean']['input']>;
  expirationDateEpochMillis?: InputMaybe<Scalars['Float']['input']>;
  fileIds?: InputMaybe<Array<Scalars['ID']['input']>>;
  memo?: InputMaybe<Scalars['String']['input']>;
  retailCustomerId: Scalars['String']['input'];
  routingNumber: Scalars['String']['input'];
  transactionLimitCents?: InputMaybe<Scalars['Int']['input']>;
  validateOnly?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationCreateRetailCustomerCreditCardDebitAuthorizationArgs = {
  basisTheoryTokenIntentId?: InputMaybe<Scalars['String']['input']>;
  billingAddress?: InputMaybe<CardBillingAddressInput>;
  expirationDateEpochMillis?: InputMaybe<Scalars['Float']['input']>;
  fileIds?: InputMaybe<Array<Scalars['ID']['input']>>;
  memo?: InputMaybe<Scalars['String']['input']>;
  nonce?: InputMaybe<Scalars['String']['input']>;
  payerDevice?: InputMaybe<CardPayerDeviceInput>;
  provider: Scalars['String']['input'];
  retailCustomerId: Scalars['String']['input'];
  transactionLimitCents?: InputMaybe<Scalars['Int']['input']>;
};


export type MutationCreateTagForOrgArgs = {
  name: Scalars['String']['input'];
  type: TagType;
};


export type MutationCreateWebhookArgs = {
  type?: InputMaybe<WebhookType>;
  url: Scalars['String']['input'];
};


export type MutationCreateWorkspaceRoleArgs = {
  description?: InputMaybe<Scalars['String']['input']>;
  grants: Array<PermissionGrantInput>;
  name: Scalars['String']['input'];
};


export type MutationDeactivateNickelAccountArgs = {
  reason?: InputMaybe<Scalars['String']['input']>;
};


export type MutationDeactivateRecurringBillArgs = {
  recurringBillId: Scalars['String']['input'];
  validateOnly?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationDeactivateRecurringInvoiceArgs = {
  checkoutPath?: InputMaybe<Scalars['String']['input']>;
  organizationId?: InputMaybe<Scalars['String']['input']>;
  recurringInvoiceId: Scalars['String']['input'];
  validateOnly?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationDeattachMerchantPaymentArgs = {
  paymentMethodId: Scalars['String']['input'];
};


export type MutationDeattachPaymentArgs = {
  paymentMethodCheckoutToken?: InputMaybe<Scalars['String']['input']>;
  paymentMethodId: Scalars['String']['input'];
};


export type MutationDecideInvoiceApprovalRequestArgs = {
  decision: ApprovalDecisionValue;
  requestId: Scalars['ID']['input'];
};


export type MutationDecideMcpActionsArgs = {
  decision: McpActionDecision;
  ids: Array<Scalars['ID']['input']>;
};


export type MutationDecideNickelBalanceInboundDebitArgs = {
  alwaysAllowCompany?: InputMaybe<Scalars['Boolean']['input']>;
  decision: NickelBalanceInboundDebitDecision;
  id: Scalars['ID']['input'];
};


export type MutationDecidePennyProposalArgs = {
  optionIndex: Scalars['Int']['input'];
  proposalId: Scalars['ID']['input'];
};


export type MutationDeclineMcpActionArgs = {
  id: Scalars['ID']['input'];
};


export type MutationDeclinePennyProposalArgs = {
  proposalId: Scalars['ID']['input'];
};


export type MutationDeleteBillArgs = {
  billId: Scalars['String']['input'];
  validateOnly?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationDeleteReferralInviteArgs = {
  id: Scalars['ID']['input'];
};


export type MutationDeleteReminderRuleArgs = {
  reminderRuleId: Scalars['String']['input'];
};


export type MutationDeleteRetailCustomerPaymentMethodArgs = {
  paymentMethodId: Scalars['String']['input'];
  validateOnly?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationDeleteVendorPayoutMethodArgs = {
  vendorPayoutMethodId: Scalars['String']['input'];
};


export type MutationDeleteWebhookArgs = {
  id: Scalars['ID']['input'];
};


export type MutationDeleteWorkspaceRoleArgs = {
  id: Scalars['ID']['input'];
};


export type MutationDisableNickelBalanceCardArgs = {
  id: Scalars['ID']['input'];
};


export type MutationDisconnectAccountingIntegrationArgs = {
  organizationId: Scalars['String']['input'];
  reason?: InputMaybe<Scalars['String']['input']>;
};


export type MutationDisconnectConnectorConnectionArgs = {
  connectionId: Scalars['ID']['input'];
};


export type MutationDisconnectSocialSsoArgs = {
  rawPassword: Scalars['String']['input'];
};


export type MutationEditFollowUpQuestionsArgs = {
  input: EditFollowUpQuestionsInput;
};


export type MutationEnrollSmsTwoFactorArgs = {
  code: Scalars['String']['input'];
  currentNumberCode?: InputMaybe<Scalars['String']['input']>;
  phoneNumber: Scalars['String']['input'];
  proofToken?: InputMaybe<Scalars['String']['input']>;
};


export type MutationEnterClientWorkspaceArgs = {
  clientOrganizationId: Scalars['ID']['input'];
};


export type MutationEnterpriseSsoAuthorizationUrlArgs = {
  email: Scalars['String']['input'];
};


export type MutationFinishPennyWebVisitArgs = {
  report: PennyWebVisitReport;
  visitId: Scalars['ID']['input'];
};


export type MutationForgotPasswordArgs = {
  email: Scalars['String']['input'];
};


export type MutationGenerateApprovalSummaryArgs = {
  organizationId: Scalars['String']['input'];
};


export type MutationGenerateAuthorizationRecordPdfArgs = {
  attention?: InputMaybe<Scalars['String']['input']>;
  debitAuthorizationId: Scalars['String']['input'];
  timeZone?: InputMaybe<Scalars['String']['input']>;
};


export type MutationGenerateSsoAdminPortalLinkArgs = {
  intent?: InputMaybe<EnterpriseSsoPortalIntent>;
};


export type MutationGetLinkTokenArgs = {
  identityVerification?: InputMaybe<Scalars['Boolean']['input']>;
  updateMode?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationHeartbeatPennyWebVisitArgs = {
  visitId: Scalars['ID']['input'];
};


export type MutationInviteExternalTeammateArgs = {
  proofToken?: InputMaybe<Scalars['String']['input']>;
  role?: InputMaybe<UserRole>;
  userId: Scalars['String']['input'];
};


export type MutationIssuePlatformPartnerKeyArgs = {
  name?: InputMaybe<Scalars['String']['input']>;
  partnerId: Scalars['ID']['input'];
};


export type MutationLoginArgs = {
  email: Scalars['String']['input'];
  rawPassword: Scalars['String']['input'];
  signalsId?: InputMaybe<Scalars['String']['input']>;
  twoFactorMethod?: InputMaybe<TwoFactorMethod>;
};


export type MutationManualAccountingItemSyncArgs = {
  itemId: Scalars['String']['input'];
  itemType: AccountingItemType;
};


export type MutationManualAccountingPageSyncArgs = {
  itemIds: Array<Scalars['String']['input']>;
  itemType: AccountingItemType;
};


export type MutationManualAccountingSyncArgs = {
  updatedSinceTimestampMillis?: InputMaybe<Scalars['Float']['input']>;
};


export type MutationMarkPennyWebsitePaymentNotPaidArgs = {
  conversationId: Scalars['ID']['input'];
  visitId: Scalars['ID']['input'];
};


export type MutationNewPasswordArgs = {
  newPassword: Scalars['String']['input'];
  token: Scalars['String']['input'];
};


export type MutationNewTeammateArgs = {
  email: Scalars['String']['input'];
  proofToken?: InputMaybe<Scalars['String']['input']>;
  role?: InputMaybe<Scalars['String']['input']>;
  workspaceRoleId?: InputMaybe<Scalars['ID']['input']>;
};


export type MutationNickelBalanceAchWireInstructionsUrlArgs = {
  balanceAccountId: Scalars['ID']['input'];
};


export type MutationNickelBalanceBankLetterUrlArgs = {
  balanceAccountId: Scalars['ID']['input'];
};


export type MutationNickelBalanceStatementDownloadUrlArgs = {
  format?: InputMaybe<NickelBalanceStatementFormat>;
  statementId: Scalars['ID']['input'];
};


export type MutationNickelBalanceTransactionsCsvUrlArgs = {
  balanceAccountId?: InputMaybe<Scalars['ID']['input']>;
  endDate: Scalars['String']['input'];
  section?: InputMaybe<NickelBalanceTransactionsExportSection>;
  startDate: Scalars['String']['input'];
  timeZone?: InputMaybe<Scalars['String']['input']>;
};


export type MutationNickelBalanceVoidedCheckUrlArgs = {
  balanceAccountId: Scalars['ID']['input'];
  payer?: InputMaybe<NickelBalanceVoidedCheckPayerInput>;
};


export type MutationOnboardingCreatePayoutMethodArgs = {
  accountHolder?: InputMaybe<Scalars['String']['input']>;
  accountNumber?: InputMaybe<Scalars['String']['input']>;
  accountType?: InputMaybe<Scalars['String']['input']>;
  manualPlaidThresholdInCents?: InputMaybe<Scalars['Int']['input']>;
  routingNumber?: InputMaybe<Scalars['String']['input']>;
  source?: InputMaybe<PayoutMethodCreationSource>;
};


export type MutationParseBillDocumentArgs = {
  fileId: Scalars['ID']['input'];
};


export type MutationParseCheckDepositImageArgs = {
  frontImageKey: Scalars['String']['input'];
};


export type MutationParseInvoiceDocumentArgs = {
  fileId: Scalars['ID']['input'];
};


export type MutationPayNickelBalanceInboundCheckArgs = {
  id: Scalars['ID']['input'];
  validateOnly?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationPortalGetLinkTokenArgs = {
  userId: Scalars['String']['input'];
};


export type MutationPrepareChargebackEvidenceUploadArgs = {
  chargebackId: Scalars['String']['input'];
  filename: Scalars['String']['input'];
  mimeType: Scalars['String']['input'];
  sizeBytes: Scalars['Int']['input'];
};


export type MutationPrepareCheckDepositUploadArgs = {
  contentLength: Scalars['Int']['input'];
  contentType: Scalars['String']['input'];
  side: CheckImageSide;
};


export type MutationPrepareFileUploadArgs = {
  contentLength: Scalars['Int']['input'];
  contentType: Scalars['String']['input'];
  filename: Scalars['String']['input'];
  type?: InputMaybe<ApiFileType>;
};


export type MutationPreparePaymentRequestCheckoutArgs = {
  organizationId: Scalars['String']['input'];
  paymentRequestId: Scalars['String']['input'];
};


export type MutationPreparePennyAttachmentUploadArgs = {
  filename: Scalars['String']['input'];
  mimeType: Scalars['String']['input'];
  sizeBytes: Scalars['Int']['input'];
};


export type MutationPreparePennyVisitCardArgs = {
  approvalId: Scalars['ID']['input'];
  conversationId: Scalars['ID']['input'];
  visitId: Scalars['ID']['input'];
};


export type MutationPreparePennyWebVisitCaptureArgs = {
  contentLength: Scalars['Int']['input'];
  visitId: Scalars['ID']['input'];
};


export type MutationProcessUnsyncedTransactionsArgs = {
  actions: Array<UnsyncedTransactionAction>;
  transactionType?: InputMaybe<AccountingTransactionType>;
};


export type MutationProvisionAccountArgs = {
  addBankAccount?: InputMaybe<Scalars['Boolean']['input']>;
  businessName: Scalars['String']['input'];
  email: Scalars['String']['input'];
  password: Scalars['String']['input'];
};


export type MutationQuickbooksTokenCreateArgs = {
  code: Scalars['String']['input'];
};


export type MutationRecordExternalBillPaymentArgs = {
  amountCents?: InputMaybe<Scalars['Int']['input']>;
  billId: Scalars['String']['input'];
  paidAt?: InputMaybe<Scalars['Float']['input']>;
  payFromChartOfAccountId?: InputMaybe<Scalars['String']['input']>;
  validateOnly?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationRecordExternalInvoicePaymentArgs = {
  amountCents?: InputMaybe<Scalars['Int']['input']>;
  externalReferenceId?: InputMaybe<Scalars['String']['input']>;
  paidAt?: InputMaybe<Scalars['Float']['input']>;
  questionFormId: Scalars['String']['input'];
  validateOnly?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationRecordInvoiceDebitMemoArgs = {
  amountCents: Scalars['Int']['input'];
  description: Scalars['String']['input'];
  externalReferenceId?: InputMaybe<Scalars['String']['input']>;
  questionFormId: Scalars['String']['input'];
};


export type MutationRecordOnboardingApplicationEventArgs = {
  event: OnboardingApplicationEvent;
};


export type MutationRecordPaymentRequestViewArgs = {
  organizationId: Scalars['String']['input'];
  paymentRequestId: Scalars['String']['input'];
};


export type MutationRecordPennyVisitPaymentOutcomeArgs = {
  approvalId: Scalars['ID']['input'];
  chargedCents?: InputMaybe<Scalars['Int']['input']>;
  confirmationNumber?: InputMaybe<Scalars['String']['input']>;
  conversationId: Scalars['ID']['input'];
  outcome: PennyVisitPaymentOutcome;
};


export type MutationRecordUnderwritingFollowUpCallArgs = {
  input: RecordUnderwritingFollowUpCallInput;
};


export type MutationRedeemMcpActionApprovalArgs = {
  args: Scalars['String']['input'];
  tool: Scalars['String']['input'];
};


export type MutationRefundPaymentArgs = {
  amount: Scalars['Int']['input'];
  fundingPayoutMethodId?: InputMaybe<Scalars['String']['input']>;
  fundingSource?: InputMaybe<RefundFundingRail>;
  lines?: InputMaybe<Array<RefundLineInput>>;
  paymentId: Scalars['String']['input'];
  reason?: InputMaybe<Scalars['String']['input']>;
  validateOnly?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationRemoveDocumentFromQuestionFormArgs = {
  documentIndex: Scalars['Int']['input'];
  questionFormId: Scalars['String']['input'];
};


export type MutationRemoveInviteArgs = {
  email: Scalars['String']['input'];
  organizationId: Scalars['String']['input'];
};


export type MutationRemovePlatformPartnerReturnUrlArgs = {
  partnerId: Scalars['ID']['input'];
  url: Scalars['String']['input'];
};


export type MutationRemovePlatformPartnerWebhookArgs = {
  webhookId: Scalars['ID']['input'];
};


export type MutationRemoveQuestionFormArgs = {
  questionFormId: Scalars['String']['input'];
  validateOnly?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationRemoveRetailCustomerArgs = {
  retailCustomerId: Scalars['String']['input'];
};


export type MutationRemoveTagFromOrgArgs = {
  tagId: Scalars['String']['input'];
};


export type MutationRemoveTeammateArgs = {
  teammateId: Scalars['String']['input'];
};


export type MutationRemoveVendorArgs = {
  validateOnly?: InputMaybe<Scalars['Boolean']['input']>;
  vendorId: Scalars['String']['input'];
};


export type MutationReorderTagsForOrgArgs = {
  orderedTagIds: Array<Scalars['String']['input']>;
  type?: InputMaybe<TagType>;
};


export type MutationRequestAlloyApplicationArgs = {
  organizationId: Scalars['String']['input'];
  overrides?: InputMaybe<AlloyApplicationOverridesInput>;
};


export type MutationRequestCustomerDebitAuthorizationArgs = {
  emails: Array<Scalars['String']['input']>;
  expirationDateEpochMillis?: InputMaybe<Scalars['Float']['input']>;
  memo?: InputMaybe<Scalars['String']['input']>;
  paymentMethodTypes?: InputMaybe<Array<DebitAuthorizationMethodType>>;
  retailCustomerId: Scalars['String']['input'];
  sendEmail?: InputMaybe<Scalars['Boolean']['input']>;
  transactionLimitCents?: InputMaybe<Scalars['Int']['input']>;
  validateOnly?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationRequestMcpActionApprovalArgs = {
  args: Scalars['String']['input'];
  title: Scalars['String']['input'];
  tool: Scalars['String']['input'];
};


export type MutationRequestPennyVisitPaymentApprovalArgs = {
  amountCents: Scalars['Int']['input'];
  billAmountCents: Scalars['Int']['input'];
  billId: Scalars['ID']['input'];
  conversationId: Scalars['ID']['input'];
  raisesApprovalId?: InputMaybe<Scalars['ID']['input']>;
  siteUrl: Scalars['String']['input'];
  visitId: Scalars['ID']['input'];
};


export type MutationRequestVendorDeliveryDetailsArgs = {
  achOnly?: InputMaybe<Scalars['Boolean']['input']>;
  emails?: InputMaybe<Array<Scalars['String']['input']>>;
  plaidRequired?: InputMaybe<Scalars['Boolean']['input']>;
  skipEmail?: InputMaybe<Scalars['Boolean']['input']>;
  taxRequested?: InputMaybe<Scalars['Boolean']['input']>;
  vendorId: Scalars['String']['input'];
  wireEnabled?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationResendRadarChallengeArgs = {
  challengeToken: Scalars['String']['input'];
};


export type MutationResendSsoVerificationCodeArgs = {
  verificationToken: Scalars['String']['input'];
};


export type MutationReturnInboundBankTransferArgs = {
  inboundBankTransferId: Scalars['String']['input'];
};


export type MutationReturnNickelBalanceInboundCheckArgs = {
  id: Scalars['ID']['input'];
  validateOnly?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationRevertToAccountingAccountArgs = {
  admin?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationReviewChargebackSubmissionArgs = {
  approve: Scalars['Boolean']['input'];
  reviewNote?: InputMaybe<Scalars['String']['input']>;
  submissionId: Scalars['String']['input'];
};


export type MutationRevokeApiTokenArgs = {
  id: Scalars['ID']['input'];
};


export type MutationRevokeNickelBalanceDebitAllowRuleArgs = {
  id: Scalars['ID']['input'];
};


export type MutationRevokePlatformPartnerKeyArgs = {
  keyId: Scalars['ID']['input'];
};


export type MutationRevokeSessionsArgs = {
  organizationId: Scalars['String']['input'];
  userId?: InputMaybe<Scalars['String']['input']>;
};


export type MutationSaveApprovalRuleArgs = {
  rule: ApprovalRuleInput;
};


export type MutationScheduleInvoiceSendArgs = {
  questionFormId: Scalars['String']['input'];
  recipients: Array<Recipient>;
  sendOn: Scalars['String']['input'];
  validateOnly?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationScheduleSubscriptionDowngradeArgs = {
  nextChargeAcknowledgedCents: Scalars['Int']['input'];
  planName: Scalars['String']['input'];
};


export type MutationSelectPayoutMethodArgs = {
  payoutMethodId: Scalars['ID']['input'];
};


export type MutationSendArAgingReportEmailArgs = {
  ccMerchant?: InputMaybe<Scalars['Boolean']['input']>;
  customMessage?: InputMaybe<Scalars['String']['input']>;
  recipientEmails: Array<Scalars['String']['input']>;
  retailCustomerId: Scalars['String']['input'];
};


export type MutationSendNickelVerificationCodeViaEmailArgs = {
  email?: InputMaybe<Scalars['String']['input']>;
  paymentMethodId?: InputMaybe<Scalars['String']['input']>;
};


export type MutationSendPaymentLinkToRecipientsArgs = {
  questionFormId: Scalars['String']['input'];
  recipients: Array<Recipient>;
  sendSms?: InputMaybe<Scalars['Boolean']['input']>;
  validateOnly?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationSendPennyMessageArgs = {
  attachments?: InputMaybe<Array<PennyMessageAttachmentInput>>;
  body: Scalars['String']['input'];
  conversationId?: InputMaybe<Scalars['ID']['input']>;
};


export type MutationSendPlatformPartnerTestEventArgs = {
  webhookId: Scalars['ID']['input'];
};


export type MutationSendReferralInviteArgs = {
  email: Scalars['String']['input'];
};


export type MutationSendRegistrationCodeArgs = {
  email: Scalars['String']['input'];
  rawPassword?: InputMaybe<Scalars['String']['input']>;
  signalsId?: InputMaybe<Scalars['String']['input']>;
  turnstileToken: Scalars['String']['input'];
};


export type MutationSendSmsTwoFactorEnrollmentCodeArgs = {
  phoneNumber: Scalars['String']['input'];
};


export type MutationSendTestReminderArgs = {
  questionFormId: Scalars['String']['input'];
  reminderRuleId: Scalars['String']['input'];
};


export type MutationSendTwoFactorVerificationCodeViaSmsArgs = {
  phoneNumber: Scalars['String']['input'];
};


export type MutationSetAccountantAccessArgs = {
  restrictions: Array<AccountantAccessGroup>;
};


export type MutationSetClientAccessModeArgs = {
  mode: ClientAccessMode;
  proofToken?: InputMaybe<Scalars['String']['input']>;
};


export type MutationSetCustomDomainArgs = {
  customDomain?: InputMaybe<Scalars['String']['input']>;
};


export type MutationSetDefaultBillPaymentMethodArgs = {
  paymentMethodId: Scalars['String']['input'];
};


export type MutationSetNickelBalanceAccountBillPayArgs = {
  enabled: Scalars['Boolean']['input'];
  id: Scalars['ID']['input'];
};


export type MutationSetNickelBalanceAccountChartOfAccountArgs = {
  chartOfAccount: NickelBalanceChartOfAccountInput;
  id: Scalars['ID']['input'];
};


export type MutationSetNickelBalanceAccountCheckWritingArgs = {
  enabled: Scalars['Boolean']['input'];
  id: Scalars['ID']['input'];
};


export type MutationSetNickelBalanceAccountDebitProtectionArgs = {
  enabled: Scalars['Boolean']['input'];
  id: Scalars['ID']['input'];
};


export type MutationSetNickelBalanceInterestRateArgs = {
  organizationId?: InputMaybe<Scalars['String']['input']>;
  rateBips: Scalars['Int']['input'];
};


export type MutationSetOrganizationAccountantArgs = {
  accountantOrganizationId?: InputMaybe<Scalars['String']['input']>;
  organizationId: Scalars['String']['input'];
};


export type MutationSetPayoutMethodWireAccountDetailsArgs = {
  accountNumber?: InputMaybe<Scalars['String']['input']>;
  payoutMethodId: Scalars['String']['input'];
  routingNumber: Scalars['String']['input'];
  token?: InputMaybe<Scalars['String']['input']>;
};


export type MutationSetPayoutMethodWireBeneficiaryNameArgs = {
  payoutMethodId: Scalars['String']['input'];
  wireBeneficiaryName: Scalars['String']['input'];
};


export type MutationSetPlatformPartnerEnrollmentsPausedArgs = {
  partnerId: Scalars['ID']['input'];
  paused: Scalars['Boolean']['input'];
};


export type MutationSetPlatformPartnerSuspendedArgs = {
  partnerId: Scalars['ID']['input'];
  reason?: InputMaybe<Scalars['String']['input']>;
  suspended: Scalars['Boolean']['input'];
};


export type MutationSetQuestionFormRemindersDisabledArgs = {
  disabled: Scalars['Boolean']['input'];
  questionFormId: Scalars['String']['input'];
};


export type MutationSetTagOnEntityArgs = {
  entityId: Scalars['String']['input'];
  on: Scalars['Boolean']['input'];
  tagId: Scalars['String']['input'];
};


export type MutationSetTwoFactorMethodEnabledArgs = {
  enabled: Scalars['Boolean']['input'];
  method: TwoFactorMethod;
  proofToken?: InputMaybe<Scalars['String']['input']>;
};


export type MutationSignUpThroughPartnerEnrollmentArgs = {
  businessName?: InputMaybe<Scalars['String']['input']>;
};


export type MutationSignupArgs = {
  companyBannerImage?: InputMaybe<Scalars['String']['input']>;
  companyLogoImage?: InputMaybe<Scalars['String']['input']>;
  email: Scalars['String']['input'];
  firstName: Scalars['String']['input'];
  firstPromoterReferralCode?: InputMaybe<Scalars['String']['input']>;
  isAccountingFirm?: InputMaybe<Scalars['Boolean']['input']>;
  lastName: Scalars['String']['input'];
  name: Scalars['String']['input'];
  onboardingInfo?: InputMaybe<Scalars['String']['input']>;
  paymentUrl?: InputMaybe<Scalars['String']['input']>;
  psPartnerKey?: InputMaybe<Scalars['String']['input']>;
  psXid?: InputMaybe<Scalars['String']['input']>;
  rawPassword: Scalars['String']['input'];
  referralCode?: InputMaybe<Scalars['String']['input']>;
  referralSource?: InputMaybe<Scalars['String']['input']>;
  token: Scalars['String']['input'];
};


export type MutationSimulateBankTransferCreditArgs = {
  amountInCents?: InputMaybe<Scalars['Int']['input']>;
  checkoutPath: Scalars['String']['input'];
  organizationId: Scalars['String']['input'];
  rail: BankTransferRail;
};


export type MutationSsoAuthorizationUrlArgs = {
  email?: InputMaybe<Scalars['String']['input']>;
  intent: Scalars['String']['input'];
  provider: SsoProvider;
};


export type MutationSsoSignupArgs = {
  firstName: Scalars['String']['input'];
  firstPromoterReferralCode?: InputMaybe<Scalars['String']['input']>;
  isAccountingFirm?: InputMaybe<Scalars['Boolean']['input']>;
  lastName: Scalars['String']['input'];
  name: Scalars['String']['input'];
  onboardingInfo?: InputMaybe<Scalars['String']['input']>;
  paymentUrl?: InputMaybe<Scalars['String']['input']>;
  psPartnerKey?: InputMaybe<Scalars['String']['input']>;
  psXid?: InputMaybe<Scalars['String']['input']>;
  referralCode?: InputMaybe<Scalars['String']['input']>;
  referralSource?: InputMaybe<Scalars['String']['input']>;
  registrationToken: Scalars['String']['input'];
};


export type MutationSsoTwoFactorChallengeArgs = {
  twoFactorMethod?: InputMaybe<TwoFactorMethod>;
  twoFactorToken: Scalars['String']['input'];
};


export type MutationStartFollowUpVoiceCallArgs = {
  input: StartFollowUpVoiceCallInput;
};


export type MutationStartPartnerEnrollmentSessionArgs = {
  token: Scalars['String']['input'];
};


export type MutationStartPennyWebVisitArgs = {
  visitId: Scalars['ID']['input'];
};


export type MutationStartWorkspaceConnectorConnectionArgs = {
  connectorId: Scalars['String']['input'];
};


export type MutationStopCheckPaymentArgs = {
  billPaymentId: Scalars['String']['input'];
  disposition?: InputMaybe<CheckStopDisposition>;
};


export type MutationSubmitChargebackResponseArgs = {
  chargebackId: Scalars['String']['input'];
  note?: InputMaybe<Scalars['String']['input']>;
  uploads: Array<ChargebackEvidenceUploadInput>;
};


export type MutationSubmitNickelBalanceApplicationArgs = {
  account?: InputMaybe<NickelBalanceFirstAccountInput>;
};


export type MutationSubmitNickelUserFeedbackArgs = {
  feedback: Scalars['String']['input'];
  rating: Scalars['String']['input'];
};


export type MutationTogglePaymentStateArgs = {
  paymentId: Scalars['String']['input'];
  reconciled: Scalars['Boolean']['input'];
  seen: Scalars['Boolean']['input'];
};


export type MutationUnlinkOrganizationFromAccountantArgs = {
  clientOrgId: Scalars['String']['input'];
};


export type MutationUpdateAnswerFormArgs = {
  answerFormId: Scalars['String']['input'];
  paymentId: Scalars['String']['input'];
  schema: Scalars['String']['input'];
};


export type MutationUpdateBankAccountArgs = {
  accountNumber: Scalars['String']['input'];
  holderName: Scalars['String']['input'];
  manualPlaidThresholdInCents?: InputMaybe<Scalars['Int']['input']>;
  routingNumber: Scalars['String']['input'];
  token: Scalars['String']['input'];
  type: Scalars['String']['input'];
};


export type MutationUpdateBillArgs = {
  amountCents?: InputMaybe<Scalars['Int']['input']>;
  billId: Scalars['String']['input'];
  dueDateEpochMillis?: InputMaybe<Scalars['Float']['input']>;
  fileIds?: InputMaybe<Array<Scalars['ID']['input']>>;
  internalNote?: InputMaybe<Scalars['String']['input']>;
  reference?: InputMaybe<Scalars['String']['input']>;
  validateOnly?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationUpdateBillExpenseChartOfAccountArgs = {
  chartOfAccountId?: InputMaybe<Scalars['String']['input']>;
};


export type MutationUpdateBrandingArgs = {
  companyBannerImage?: InputMaybe<Scalars['String']['input']>;
  companyLogoImage?: InputMaybe<Scalars['String']['input']>;
  removeBanner?: InputMaybe<Scalars['Boolean']['input']>;
  removeLogo?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationUpdateClientAccessArgs = {
  changes: Array<ClientAccessChange>;
  clientOrganizationId: Scalars['ID']['input'];
  proofToken?: InputMaybe<Scalars['String']['input']>;
};


export type MutationUpdateFeeChartOfAccountArgs = {
  chartOfAccountId?: InputMaybe<Scalars['String']['input']>;
};


export type MutationUpdateInvoiceSalesChartOfAccountArgs = {
  chartOfAccountId?: InputMaybe<Scalars['String']['input']>;
};


export type MutationUpdateMerchantPaymentMethodArgs = {
  nickname?: InputMaybe<Scalars['String']['input']>;
  paymentMethodId: Scalars['String']['input'];
};


export type MutationUpdateMyNotificationsArgs = {
  subscriptions: Array<NotificationSubscriptionInput>;
};


export type MutationUpdateNickelBalanceAccountArgs = {
  id: Scalars['ID']['input'];
  nickname: Scalars['String']['input'];
};


export type MutationUpdateNickelBalanceCardArgs = {
  id: Scalars['ID']['input'];
  input: UpdateNickelBalanceCardInput;
};


export type MutationUpdateNotificationDefaultsArgs = {
  input: NotificationDefaultsInput;
};


export type MutationUpdateNotificationSettingsArgs = {
  notificationSettings: Scalars['String']['input'];
};


export type MutationUpdateOnboardingInformationArgs = {
  onboardingInfo: Scalars['String']['input'];
  posthogSessionId?: InputMaybe<Scalars['String']['input']>;
};


export type MutationUpdateOrCreateChartOfAccountArgs = {
  chartOfAccountId?: InputMaybe<Scalars['String']['input']>;
  paymentMethodId?: InputMaybe<Scalars['String']['input']>;
  payoutMethodId?: InputMaybe<Scalars['String']['input']>;
};


export type MutationUpdateOrganizationAdminControlsArgs = {
  address?: InputMaybe<AdminOrganizationAddressInput>;
  brandingDisabled?: InputMaybe<Scalars['Boolean']['input']>;
  creditCardEnabled?: InputMaybe<Scalars['Boolean']['input']>;
  legalName?: InputMaybe<Scalars['String']['input']>;
  name?: InputMaybe<Scalars['String']['input']>;
  orderReferenceLabel?: InputMaybe<Scalars['String']['input']>;
  orderReferencePlaceholder?: InputMaybe<Scalars['String']['input']>;
  organizationId: Scalars['String']['input'];
  paymentsDisabled?: InputMaybe<Scalars['Boolean']['input']>;
  payoutsDisabled?: InputMaybe<Scalars['Boolean']['input']>;
  role?: InputMaybe<AdminOrganizationRole>;
  subdomain?: InputMaybe<Scalars['String']['input']>;
  threeDSEnabled?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationUpdateOrganizationStatusArgs = {
  denialReasonOther?: InputMaybe<Scalars['String']['input']>;
  denialReasons?: InputMaybe<Array<DenialReason>>;
  organizationId: Scalars['String']['input'];
  rfiItems?: InputMaybe<Array<RfiItem>>;
  rfiNote?: InputMaybe<Scalars['String']['input']>;
  status?: InputMaybe<AccountApprovalStatus>;
};


export type MutationUpdatePartnerEnrollmentArgs = {
  id: Scalars['ID']['input'];
  prefill: PartnerEnrollmentPrefillInput;
};


export type MutationUpdatePaymentArgs = {
  basisTheoryTokenIntentId?: InputMaybe<Scalars['String']['input']>;
  checkoutPath?: InputMaybe<Scalars['String']['input']>;
  countryCode?: InputMaybe<Scalars['String']['input']>;
  email?: InputMaybe<Scalars['String']['input']>;
  newAmountInCents?: InputMaybe<Scalars['Int']['input']>;
  paymentId: Scalars['String']['input'];
  paymentMethodCheckoutToken?: InputMaybe<Scalars['String']['input']>;
  paymentMethodId?: InputMaybe<Scalars['String']['input']>;
};


export type MutationUpdatePaymentSettingsArgs = {
  achEnabled: Scalars['Boolean']['input'];
  automaticQbInvoiceEmail?: InputMaybe<Scalars['Boolean']['input']>;
  creditCardEnabled: Scalars['Boolean']['input'];
  feePassthroughPercent: Scalars['Int']['input'];
  invoiceMemoTemplate?: InputMaybe<Scalars['String']['input']>;
  paymentAmountEditable?: InputMaybe<Scalars['Boolean']['input']>;
  showOtherUnpaidInvoices?: InputMaybe<Scalars['Boolean']['input']>;
  twoFactorRequired?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationUpdatePlaidIdentityVerificationIdArgs = {
  plaidIdentityVerificationId?: InputMaybe<Scalars['String']['input']>;
};


export type MutationUpdateQuestionFormArgs = {
  achEnabled?: InputMaybe<Scalars['Boolean']['input']>;
  bankTransferEnabled?: InputMaybe<Scalars['Boolean']['input']>;
  checkoutActive?: InputMaybe<Scalars['Boolean']['input']>;
  checkoutPath?: InputMaybe<Scalars['String']['input']>;
  creditCardEnabled?: InputMaybe<Scalars['Boolean']['input']>;
  dueDateEpochMillis?: InputMaybe<Scalars['Float']['input']>;
  feePassthroughPercent?: InputMaybe<Scalars['Int']['input']>;
  fileIds?: InputMaybe<Array<Scalars['ID']['input']>>;
  invoiceDateEpochMillis?: InputMaybe<Scalars['Float']['input']>;
  memo?: InputMaybe<Scalars['String']['input']>;
  name?: InputMaybe<Scalars['String']['input']>;
  payoutMethodId?: InputMaybe<Scalars['ID']['input']>;
  questionFormId: Scalars['String']['input'];
  recipients?: InputMaybe<Array<Recipient>>;
  requestedAmount?: InputMaybe<Scalars['Int']['input']>;
  retailCustomerId?: InputMaybe<Scalars['String']['input']>;
  schema?: InputMaybe<Scalars['String']['input']>;
  thumbnail?: InputMaybe<Scalars['String']['input']>;
};


export type MutationUpdateRecurringInvoiceArgs = {
  amountCents?: InputMaybe<Scalars['Int']['input']>;
  endDate?: InputMaybe<Scalars['String']['input']>;
  frequency?: InputMaybe<RecurringBillFrequency>;
  invoiceNumber?: InputMaybe<Scalars['String']['input']>;
  lines?: InputMaybe<QuickbooksLinesInput>;
  memo?: InputMaybe<Scalars['String']['input']>;
  nextInvoiceDate?: InputMaybe<Scalars['String']['input']>;
  payoutMethodId?: InputMaybe<Scalars['ID']['input']>;
  recurringInvoiceId: Scalars['String']['input'];
  startDate?: InputMaybe<Scalars['String']['input']>;
  validateOnly?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationUpdateReminderRuleArgs = {
  bodyTemplate?: InputMaybe<Scalars['String']['input']>;
  ccInvoiceContact?: InputMaybe<Scalars['Boolean']['input']>;
  count?: InputMaybe<Scalars['Int']['input']>;
  direction?: InputMaybe<ReminderDirection>;
  isActive?: InputMaybe<Scalars['Boolean']['input']>;
  reminderRuleId: Scalars['String']['input'];
  subjectTemplate?: InputMaybe<Scalars['String']['input']>;
  unit?: InputMaybe<ReminderUnit>;
};


export type MutationUpdateRetailCustomerArgs = {
  achEnabled?: InputMaybe<Scalars['Boolean']['input']>;
  creditCardEnabled?: InputMaybe<Scalars['Boolean']['input']>;
  customFeePassthroughPercent?: InputMaybe<Scalars['Int']['input']>;
  emails?: InputMaybe<Array<Scalars['String']['input']>>;
  integrationEmails?: InputMaybe<Array<Scalars['String']['input']>>;
  name?: InputMaybe<Scalars['String']['input']>;
  phoneNumbers?: InputMaybe<Array<Scalars['String']['input']>>;
  retailCustomerId: Scalars['String']['input'];
  validateOnly?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationUpdateRetailCustomerPaymentMethodArgs = {
  achDetails?: InputMaybe<AchDetailsInput>;
  billingAddress?: InputMaybe<AddressDetailsInput>;
  cardDetails?: InputMaybe<CardDetailsInput>;
  consent?: InputMaybe<DebitAuthorizationConsentInput>;
  customerType?: InputMaybe<Scalars['String']['input']>;
  email?: InputMaybe<Scalars['String']['input']>;
  name?: InputMaybe<Scalars['String']['input']>;
  phoneNumber?: InputMaybe<Scalars['String']['input']>;
  portalOrganizationId: Scalars['String']['input'];
  publicToken?: InputMaybe<Scalars['String']['input']>;
  retailCustomerId: Scalars['String']['input'];
  shortId: Scalars['String']['input'];
  title?: InputMaybe<Scalars['String']['input']>;
};


export type MutationUpdateSubdomainArgs = {
  subdomain: Scalars['String']['input'];
};


export type MutationUpdateSubscriptionPaymentMethodArgs = {
  merchantPaymentMethodId: Scalars['String']['input'];
  subscriptionId: Scalars['String']['input'];
};


export type MutationUpdateTagForOrgArgs = {
  name: Scalars['String']['input'];
  tagId: Scalars['String']['input'];
};


export type MutationUpdateTeamNotificationsArgs = {
  cells?: InputMaybe<Array<NotificationCellInput>>;
  customer?: InputMaybe<Array<CustomerNotificationInput>>;
  emailRecipients?: InputMaybe<Array<NotificationEmailRecipientInput>>;
  members?: InputMaybe<Array<MemberNotificationsInput>>;
  roles?: InputMaybe<Array<WorkspaceRoleNotificationsInput>>;
};


export type MutationUpdateTeammateArgs = {
  proofToken?: InputMaybe<Scalars['String']['input']>;
  role?: InputMaybe<UserRole>;
  userId: Scalars['String']['input'];
  workspaceRoleId?: InputMaybe<Scalars['ID']['input']>;
};


export type MutationUpdateTeammateProfileArgs = {
  email?: InputMaybe<Scalars['String']['input']>;
  firstName?: InputMaybe<Scalars['String']['input']>;
  lastName?: InputMaybe<Scalars['String']['input']>;
  userId: Scalars['String']['input'];
};


export type MutationUpdateUnappliedPaymentChartOfAccountArgs = {
  chartOfAccountId?: InputMaybe<Scalars['String']['input']>;
};


export type MutationUpdateVendorArgs = {
  emails?: InputMaybe<Array<Scalars['String']['input']>>;
  integrationEmails?: InputMaybe<Array<Scalars['String']['input']>>;
  name?: InputMaybe<Scalars['String']['input']>;
  taxId?: InputMaybe<Scalars['String']['input']>;
  token?: InputMaybe<Scalars['String']['input']>;
  type?: InputMaybe<VendorType>;
  vendorAddress?: InputMaybe<AddressDetailsInput>;
  vendorId?: InputMaybe<Scalars['String']['input']>;
};


export type MutationUpdateVendorAchDeliveryMethodWithPlaidArgs = {
  accountHolderName?: InputMaybe<Scalars['String']['input']>;
  bankAccountId: Scalars['String']['input'];
  publicToken: Scalars['String']['input'];
  token: Scalars['String']['input'];
  vendorId: Scalars['String']['input'];
};


export type MutationUpdateVendorCheckPayoutMethodArgs = {
  city: Scalars['String']['input'];
  country?: InputMaybe<Scalars['String']['input']>;
  state: Scalars['String']['input'];
  street: Scalars['String']['input'];
  street2?: InputMaybe<Scalars['String']['input']>;
  token?: InputMaybe<Scalars['String']['input']>;
  vendorId?: InputMaybe<Scalars['String']['input']>;
  zip: Scalars['String']['input'];
};


export type MutationUpdateVendorDomesticWirePayoutMethodArgs = {
  accountHolderName: Scalars['String']['input'];
  accountNumber: Scalars['String']['input'];
  city: Scalars['String']['input'];
  routingNumber: Scalars['String']['input'];
  state: Scalars['String']['input'];
  street: Scalars['String']['input'];
  street2?: InputMaybe<Scalars['String']['input']>;
  vendorId: Scalars['String']['input'];
  zip: Scalars['String']['input'];
};


export type MutationUpdateVendorPayoutMethodArgs = {
  accountHolderName?: InputMaybe<Scalars['String']['input']>;
  accountNumber: Scalars['String']['input'];
  accountType: Scalars['String']['input'];
  routingNumber: Scalars['String']['input'];
  token?: InputMaybe<Scalars['String']['input']>;
  vendorId?: InputMaybe<Scalars['String']['input']>;
};


export type MutationUpdateVendorWirePayoutMethodArgs = {
  accountHolderName?: InputMaybe<Scalars['String']['input']>;
  accountNumber?: InputMaybe<Scalars['String']['input']>;
  bankCode?: InputMaybe<Scalars['String']['input']>;
  canadianAccountNumber?: InputMaybe<Scalars['String']['input']>;
  chineseAccountNumber?: InputMaybe<Scalars['String']['input']>;
  correspondentSwiftCode?: InputMaybe<Scalars['String']['input']>;
  countryCode: Scalars['String']['input'];
  hongKongAccountNumber?: InputMaybe<Scalars['String']['input']>;
  iban?: InputMaybe<Scalars['String']['input']>;
  ifscCode?: InputMaybe<Scalars['String']['input']>;
  indianAccountNumber?: InputMaybe<Scalars['String']['input']>;
  recipientAddress: Scalars['String']['input'];
  recipientAddressLine2?: InputMaybe<Scalars['String']['input']>;
  recipientCity: Scalars['String']['input'];
  recipientState: Scalars['String']['input'];
  recipientZip: Scalars['String']['input'];
  swiftCode: Scalars['String']['input'];
  token?: InputMaybe<Scalars['String']['input']>;
  transitNumber?: InputMaybe<Scalars['String']['input']>;
  vendorId: Scalars['String']['input'];
};


export type MutationUpdateWorkspaceRoleArgs = {
  description?: InputMaybe<Scalars['String']['input']>;
  grants?: InputMaybe<Array<PermissionGrantInput>>;
  id: Scalars['ID']['input'];
  name?: InputMaybe<Scalars['String']['input']>;
  proofToken?: InputMaybe<Scalars['String']['input']>;
};


export type MutationUploadOrganizationRfiDocumentsArgs = {
  explainer?: InputMaybe<Scalars['String']['input']>;
  organizationId: Scalars['String']['input'];
  s3Urls?: InputMaybe<Array<Scalars['String']['input']>>;
};


export type MutationValidateEmailVerificationCodeArgs = {
  code: Scalars['String']['input'];
  email: Scalars['String']['input'];
  login?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationVerifyTwoFactorSmsAuthCodeArgs = {
  challengeToken?: InputMaybe<Scalars['String']['input']>;
  code: Scalars['String']['input'];
  identifier: Scalars['String']['input'];
};


export type MutationVoidExternalBillPaymentArgs = {
  billPayableId: Scalars['String']['input'];
  validateOnly?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationVoidExternalInvoicePaymentArgs = {
  externalPaymentId: Scalars['String']['input'];
  validateOnly?: InputMaybe<Scalars['Boolean']['input']>;
};


export type MutationVoidInvoiceDebitMemoArgs = {
  debitMemoId: Scalars['String']['input'];
};


export type MutationXeroConnectionCompleteArgs = {
  publicToken: Scalars['String']['input'];
};

export type MutationDetachPaymentResponse = {
  __typename?: 'MutationDetachPaymentResponse';
  error?: Maybe<Error>;
};

export type MyNotificationsResponse = {
  __typename?: 'MyNotificationsResponse';
  error?: Maybe<Error>;
  /** The caller's row with the map as written. */
  user?: Maybe<User>;
};

export type NewTeammateResponse = {
  __typename?: 'NewTeammateResponse';
  error?: Maybe<Error>;
  token?: Maybe<Scalars['String']['output']>;
};

/**
 * One of the org's Nickel Balance accounts (multi-account,
 * docs/nickel-balance/architecture.md), listed oldest first. None of them is
 * "the default": AR proceeds follow the org's selected payout method. An
 * org-scoped MUTATION (and nickelBalanceDepositInstructions) that names no
 * `balanceAccountId` targets the oldest provisioned account only until that
 * argument becomes required; the org-scoped READS (nickelBalanceInterestSummary,
 * nickelBalanceAvailableHistory) aggregate every account when it is omitted.
 * `accountNumberLast4`, `routingNumber` and `accountNumber` are null
 * mid-provisioning; the two full numbers are the persisted inbound ACH/wire
 * details (the same nickelBalanceDepositInstructions publishes), so the
 * accounts table can show them per row. `balanceCents` is the cached settled
 * ledger balance (can be negative under overdraft), not a live Increase read.
 */
export type NickelBalanceAccount = {
  __typename?: 'NickelBalanceAccount';
  accountNumber?: Maybe<Scalars['String']['output']>;
  accountNumberLast4?: Maybe<Scalars['String']['output']>;
  /**
   * What the caller can do with this account right now, and why not. Always a list;
   * nullable only so clients typed against the whole `NickelBalanceAccount` need not select it.
   */
  availableActions?: Maybe<Array<ActionAvailability>>;
  balanceCents: Scalars['Int']['output'];
  /**
   * Whether bill pay may draw on this account: getAllMerchantPaymentMethods
   * lists it and new bill payments may use it. On by default; switched by
   * setNickelBalanceAccountBillPay.
   */
  billPayEnabled: Scalars['Boolean']['output'];
  /**
   * The account number to print on the merchant's checks, with `routingNumber`.
   * Null until paper checks are first turned on, on a closed account, or for a
   * caller without `banking.account.see_deposit_instructions`.
   */
  checkWritingAccountNumber?: Maybe<Scalars['String']['output']>;
  /**
   * Paper checks: checks written on `checkWritingAccountNumber` are paid from
   * this account, each held first so a teammate can return it
   * (nickelBalanceInboundCheckReviews / returnNickelBalanceInboundCheck).
   */
  checkWritingEnabled: Scalars['Boolean']['output'];
  /**
   * When the merchant closed the account; null while open. A closed account's
   * `routingNumber` and `accountNumber` are null: they no longer take deposits.
   */
  closedAt?: Maybe<Scalars['Float']['output']>;
  createdAt: Scalars['Float']['output'];
  /**
   * Debit Protection: third-party ACH debits (pulls) against this account are
   * held for the merchant's decision and blocked at a deadline unless allowed
   * (nickelBalanceInboundDebitReviews / decideNickelBalanceInboundDebit).
   */
  debitProtectionEnabled: Scalars['Boolean']['output'];
  id: Scalars['ID']['output'];
  nickname?: Maybe<Scalars['String']['output']>;
  /**
   * Money committed to card swipes Increase has authorized against this
   * account but not posted. `balanceCents` is the settled ledger and does not
   * know about them, so a client that shows what the merchant can actually
   * spend subtracts this from it — the same arithmetic
   * `NickelBalanceInterestSummary.pendingCardHoldCents` drives on the balance
   * card, which is why the two agree for a single-account org.
   *
   * Populated by `nickelBalanceAccounts` and null everywhere else this type is
   * returned (the create/update/close payloads, which answer about one account
   * the caller just acted on and have no reason to price its card holds). A
   * client that selects it there would also write the null over a list row it
   * had already loaded — so don't: select it on the list query only.
   */
  pendingCardHoldCents?: Maybe<Scalars['Float']['output']>;
  /**
   * Where this account records in QuickBooks; null with no QuickBooks
   * integration, until the background sync has linked it, or for a caller
   * without `integrations.accounting.see_chart_of_accounts`.
   */
  quickbooksChartOfAccount?: Maybe<NickelBalanceAccountChartOfAccount>;
  routingNumber?: Maybe<Scalars['String']['output']>;
};

/**
 * The QuickBooks (QBO/QBD) ledger account one of the org's Nickel Balance
 * accounts records into (nickelBalance/chartOfAccount.ts): the link on its
 * own payout method, which its bill-pay payment method shares.
 * `chartOfAccountName` is the name stamped when the link was made and can lag
 * a rename in QuickBooks; the dashboard prefers the live Chart of Accounts
 * listing when it has one.
 */
export type NickelBalanceAccountChartOfAccount = {
  __typename?: 'NickelBalanceAccountChartOfAccount';
  chartOfAccountId: Scalars['String']['output'];
  chartOfAccountName?: Maybe<Scalars['String']['output']>;
};

/**
 * A Nickel Balance account as a row's label alone: enough to file an activity
 * row under the account it belongs to, and nothing a banking key protects.
 * The balance is `nickelBalanceAccounts` (keyed see_balance) and the full
 * routing and account numbers are `nickelBalanceDepositInstructions` (keyed
 * see_deposit_instructions); this type carries neither by construction, so
 * the unkeyed activity feed cannot serve them through it.
 */
export type NickelBalanceAccountRef = {
  __typename?: 'NickelBalanceAccountRef';
  accountNumberLast4?: Maybe<Scalars['String']['output']>;
  id: Scalars['ID']['output'];
  nickname?: Maybe<Scalars['String']['output']>;
};

/**
 * Lifecycle of an org's Nickel Balance account, mirroring the Prisma enum of
 * the same name. Submitting the application (submitNickelBalanceApplication)
 * provisions the Increase resources synchronously and only then writes ACTIVE,
 * so ACTIVE always means the Increase account exists. IN_REVIEW and REJECTED
 * belong to the retired admin-review step: nothing writes them any more.
 */
export enum NickelBalanceAccountStatus {
  Active = 'ACTIVE',
  Closed = 'CLOSED',
  InReview = 'IN_REVIEW',
  None = 'NONE',
  Rejected = 'REJECTED'
}

export type NickelBalanceAvailableHistoryPoint = {
  __typename?: 'NickelBalanceAvailableHistoryPoint';
  /** Balance at that instant. Signed — negative while overdrafted (the chart shows the truth, no flooring). */
  balanceCents: Scalars['Float']['output'];
  date: Scalars['Float']['output'];
};

export type NickelBalanceCardBillingAddressInput = {
  city: Scalars['String']['input'];
  line1: Scalars['String']['input'];
  state: Scalars['String']['input'];
  zip: Scalars['String']['input'];
};

export type NickelBalanceCardShippingAddressInput = {
  city: Scalars['String']['input'];
  line1: Scalars['String']['input'];
  line2?: InputMaybe<Scalars['String']['input']>;
  state: Scalars['String']['input'];
  zip: Scalars['String']['input'];
};

/**
 * How a new Nickel Balance account should appear in the org's QuickBooks Chart
 * of Accounts (createNickelBalanceAccount). Refused unless QuickBooks is
 * connected. Omit it for the default: a new bank account named
 * "Nickel – {nickname}", opened in the background. At most one field.
 */
export type NickelBalanceChartOfAccountInput = {
  /**
   * Link the account to this existing QuickBooks bank account — an id from
   * chartOfAccounts(accountType: "bankAccount") — instead of opening one.
   */
  chartOfAccountId?: InputMaybe<Scalars['String']['input']>;
  /**
   * Open the account's QuickBooks bank account under this name instead of
   * "Nickel – {nickname}" (at most 100 characters).
   */
  chartOfAccountName?: InputMaybe<Scalars['String']['input']>;
};

export type NickelBalanceCheckDeposit = {
  __typename?: 'NickelBalanceCheckDeposit';
  /**
   * Epoch ms of Increase's acceptance: the parsed check credited the account.
   * Acceptance precedes Increase's `submitted` status (and so `submittedAt`) by
   * hours — the item is only "submitted" once it goes to the Check21 network —
   * so a deposit can be accepted and credited while `status` still reads
   * PENDING. Null until accepted, and on legacy rows with no transfer.
   */
  acceptedAt?: Maybe<Scalars['Float']['output']>;
  amountCents: Scalars['Int']['output'];
  /**
   * Every invoice application paid from this deposit, voided ones included
   * (read `voidedAt`; a system void carries `voidReason` on its activity note).
   * Newest first.
   */
  applications: Array<ExternalArPayment>;
  /**
   * What the caller can do with this deposit right now, and why not. Always a list;
   * nullable only so clients typed against the whole `NickelBalanceCheckDeposit` need not select it.
   */
  availableActions?: Maybe<Array<ActionAvailability>>;
  backImageUrl?: Maybe<Scalars['String']['output']>;
  backPreviewUrl?: Maybe<Scalars['String']['output']>;
  backThumbnailUrl?: Maybe<Scalars['String']['output']>;
  /** The account the check is deposited into (multi-account). Deposits made before multi-account were stamped onto the org's then-only account. */
  balanceAccountId: Scalars['ID']['output'];
  createdAt: Scalars['Float']['output'];
  depositedAt?: Maybe<Scalars['Float']['output']>;
  description?: Maybe<Scalars['String']['output']>;
  frontImageUrl?: Maybe<Scalars['String']['output']>;
  frontPreviewUrl?: Maybe<Scalars['String']['output']>;
  /**
   * Presigned URLs for the server-generated derivatives of the check images: the
   * ~320px thumbnail a list draws in a small square, and the ~1024px preview a
   * hover or zoom surface shows. Both are far smaller than the originals above,
   * which are whatever the merchant's camera produced (up to 25MB, unresized).
   *
   * Null until the thumbnail worker has run, and permanently null when it could
   * not rasterize the upload. Treat null as "not available yet", never as "no
   * image" — fall back to frontImageUrl/backImageUrl rather than rendering
   * nothing.
   */
  frontThumbnailUrl?: Maybe<Scalars['String']['output']>;
  /**
   * The instant the funds are, or became, usable: `depositedAt` once DEPOSITED;
   * otherwise the release instant of Nickel's settlement-risk hold when one is
   * stamped. Null when unknown (not yet accepted, or Increase's own hold is in
   * place) and when the funds never arrive (rejected / returned).
   */
  fundsAvailableAt?: Maybe<Scalars['Float']['output']>;
  id: Scalars['ID']['output'];
  payerName?: Maybe<Scalars['String']['output']>;
  rejectedAt?: Maybe<Scalars['Float']['output']>;
  rejectionReason?: Maybe<Scalars['String']['output']>;
  returnReason?: Maybe<Scalars['String']['output']>;
  returnedAt?: Maybe<Scalars['Float']['output']>;
  status: NickelBalanceCheckDepositStatus;
  /**
   * Epoch ms of Increase's Check21 submission of the item. Not an ordering
   * signal relative to the other stamps: acceptance precedes submission by
   * hours, so this commonly lands AFTER depositedAt (an unheld deposit settles
   * at acceptance) and can post-date returnedAt. Use `status` / depositedAt /
   * returnedAt for settlement state.
   */
  submittedAt?: Maybe<Scalars['Float']['output']>;
  /**
   * The NickelBalanceTransfer this deposit books in through — the id a row of
   * `applyNickelBalanceDepositsToInvoices` takes. Null only on legacy rows
   * written before deposits carried a transfer.
   */
  transferId?: Maybe<Scalars['ID']['output']>;
  /**
   * What is left to apply: min(claimed amount, bank-accepted amount) minus the
   * live applications, and zero once the deposit is rejected or returned
   * (docs/external-payments/check-deposit-application.md §4.4).
   */
  unappliedAmountCents: Scalars['Int']['output'];
};

export enum NickelBalanceCheckDepositStatus {
  Deposited = 'DEPOSITED',
  Pending = 'PENDING',
  Rejected = 'REJECTED',
  RequiresAttention = 'REQUIRES_ATTENTION',
  Returned = 'RETURNED',
  Submitted = 'SUBMITTED'
}

/**
 * Which way a cross-org Nickel Balance transfer moved money, from the viewing
 * org's side: OUTBOUND when it sent the funds, INBOUND when it received them.
 */
export enum NickelBalanceCrossOrgDirection {
  Inbound = 'INBOUND',
  Outbound = 'OUTBOUND'
}

/**
 * The other organization on a cross-org Nickel Balance transfer, phrased from
 * the viewing org's side. `counterpartyOrganizationName` is that org's display
 * name; null only when its row can no longer be resolved — clients fall back to
 * the product name rather than to Internal Transfer.
 */
export type NickelBalanceCrossOrgTransfer = {
  __typename?: 'NickelBalanceCrossOrgTransfer';
  counterpartyOrganizationName?: Maybe<Scalars['String']['output']>;
  direction: NickelBalanceCrossOrgDirection;
};

/**
 * A company whose ACH debits a Debit Protection account allows without a hold,
 * matched on the originator's Company ID alone. Applies only to debits that
 * arrive while it is active; adding or revoking one never changes a debit
 * already held. Timestamps are ms since epoch.
 */
export type NickelBalanceDebitAllowRule = {
  __typename?: 'NickelBalanceDebitAllowRule';
  balanceAccountId: Scalars['ID']['output'];
  createdAt: Scalars['Float']['output'];
  createdByName: Scalars['String']['output'];
  id: Scalars['ID']['output'];
  /** Trimmed and uppercased, as matched. */
  originatorCompanyId: Scalars['String']['output'];
  /** The label shown in the allowed-companies list; never matched. */
  originatorCompanyName: Scalars['String']['output'];
  /** Set once revoked; a revoked rule no longer allows anything. */
  revokedAt?: Maybe<Scalars['Float']['output']>;
};

export type NickelBalanceDebitAllowRuleResponse = {
  __typename?: 'NickelBalanceDebitAllowRuleResponse';
  error?: Maybe<Error>;
  rule?: Maybe<NickelBalanceDebitAllowRule>;
};

/**
 * A company that has debited the account, with its most recent debit: one per
 * Company ID. `lastReviewStatus` is the Debit Protection outcome when that
 * debit was held or auto-allowed, and null when it simply posted.
 */
export type NickelBalanceDebitOriginator = {
  __typename?: 'NickelBalanceDebitOriginator';
  lastAmountCents: Scalars['Int']['output'];
  lastDebitAt: Scalars['Float']['output'];
  lastReviewStatus?: Maybe<NickelBalanceInboundDebitReviewStatus>;
  originatorCompanyId: Scalars['String']['output'];
  originatorCompanyName: Scalars['String']['output'];
};

export type NickelBalanceDebitOriginatorSearch = {
  __typename?: 'NickelBalanceDebitOriginatorSearch';
  /** Newest first, at most 50. */
  originators: Array<NickelBalanceDebitOriginator>;
  /** How many matched before the limit. */
  totalCount: Scalars['Int']['output'];
};

/** One row of applyNickelBalanceDepositsToInvoices. */
export type NickelBalanceDepositApplicationInput = {
  /**
   * Defaults to min(the deposit's unapplied amount, the invoice's remaining
   * balance), after the rows before this one.
   */
  amountCents?: InputMaybe<Scalars['Int']['input']>;
  externalReferenceId?: InputMaybe<Scalars['String']['input']>;
  nickelBalanceTransferId: Scalars['ID']['input'];
  questionFormId: Scalars['String']['input'];
};

export type NickelBalanceDepositInstructions = {
  __typename?: 'NickelBalanceDepositInstructions';
  accountName: Scalars['String']['output'];
  accountNumber: Scalars['String']['output'];
  routingNumber: Scalars['String']['output'];
};

export type NickelBalanceDetails = {
  __typename?: 'NickelBalanceDetails';
  balanceAccountId?: Maybe<Scalars['ID']['output']>;
  nickname?: Maybe<Scalars['String']['output']>;
  spendableCents: Scalars['Int']['output'];
};

/**
 * How the merchant wants their FIRST Nickel Balance account opened — the
 * application's "Create your first Nickel bank account" step
 * (submitNickelBalanceApplication). The same choices createNickelBalanceAccount
 * takes for an additional account, plus whether the account becomes the org's
 * AR payout destination.
 */
export type NickelBalanceFirstAccountInput = {
  /**
   * The account's QuickBooks ledger account — link an existing bank account or
   * name the new one. Refused unless QuickBooks is connected; omitted, a
   * "Nickel – {nickname}" bank account is opened in the background.
   */
  chartOfAccount?: InputMaybe<NickelBalanceChartOfAccountInput>;
  /** Open the account with Debit Protection on (default off). */
  debitProtectionEnabled?: InputMaybe<Scalars['Boolean']['input']>;
  /**
   * Select the account as the org's AR payout destination
   * (Organization.payoutMethod) in the same write that activates Nickel
   * Balance — "Set as default for receiving payments". Default true; false
   * leaves the current selection (the linked bank) alone.
   */
  defaultForReceivingPayments?: InputMaybe<Scalars['Boolean']['input']>;
  /** The account's Nickel-side name, what the merchant sees everywhere (1–40 characters). */
  nickname: Scalars['String']['input'];
};

export type NickelBalanceFundingSource = {
  __typename?: 'NickelBalanceFundingSource';
  accountHolderName?: Maybe<Scalars['String']['output']>;
  bankName?: Maybe<Scalars['String']['output']>;
  id: Scalars['ID']['output'];
  lastFour?: Maybe<Scalars['String']['output']>;
  needsWireAccountDetails?: Maybe<Scalars['Boolean']['output']>;
  needsWireBeneficiaryName?: Maybe<Scalars['Boolean']['output']>;
  wireRoutingNumber?: Maybe<Scalars['String']['output']>;
  wireRoutingNumberEditable?: Maybe<Scalars['Boolean']['output']>;
};

/**
 * A paper check written on one of the org's Nickel Banking accounts, held so
 * a teammate can return it before the bank pays it. Amounts are unsigned;
 * timestamps are ms since epoch. `declineDeadline` is the last moment it can
 * be returned.
 */
export type NickelBalanceInboundCheckReview = {
  __typename?: 'NickelBalanceInboundCheckReview';
  amountCents: Scalars['Int']['output'];
  automaticallyResolvesAt: Scalars['Float']['output'];
  availableActions?: Maybe<Array<ActionAvailability>>;
  /** A short-lived link to the image of the back of the check, when the bank sent one. */
  backImageUrl?: Maybe<Scalars['String']['output']>;
  balanceAccountId: Scalars['ID']['output'];
  checkNumber?: Maybe<Scalars['String']['output']>;
  decidedAt?: Maybe<Scalars['Float']['output']>;
  /** The teammate who decided, when a person did. */
  decidedByName?: Maybe<Scalars['String']['output']>;
  declineDeadline: Scalars['Float']['output'];
  /** A short-lived link to the image of the front of the check, when the bank sent one. */
  frontImageUrl?: Maybe<Scalars['String']['output']>;
  heldAt: Scalars['Float']['output'];
  id: Scalars['ID']['output'];
  /**
   * The bank's comparison of the payee on the check with the account holder:
   * `name_matches`, `does_not_match` or `not_evaluated`.
   */
  payeeNameAnalysis?: Maybe<Scalars['String']['output']>;
  status: NickelBalanceInboundCheckReviewStatus;
};

export type NickelBalanceInboundCheckReviewResponse = {
  __typename?: 'NickelBalanceInboundCheckReviewResponse';
  error?: Maybe<Error>;
  review?: Maybe<NickelBalanceInboundCheckReview>;
};

/**
 * PENDING_DECISION: held; paid at `automaticallyResolvesAt` unless returned
 * first. PAID: a teammate approved it. RETURNED: a teammate returned it.
 * PAID_UNREVIEWED: nobody decided and the bank paid it. RESOLVED_EXTERNALLY:
 * the bank resolved it other than as decided (e.g. insufficient funds).
 */
export enum NickelBalanceInboundCheckReviewStatus {
  Paid = 'PAID',
  PaidUnreviewed = 'PAID_UNREVIEWED',
  PendingDecision = 'PENDING_DECISION',
  ResolvedExternally = 'RESOLVED_EXTERNALLY',
  Returned = 'RETURNED'
}

export enum NickelBalanceInboundDebitDecision {
  Allow = 'ALLOW',
  Block = 'BLOCK'
}

/**
 * A third-party ACH debit (pull) that Debit Protection held against one of
 * the org's Nickel Balance accounts, with the merchant's decision on it.
 * Amounts are unsigned; timestamps are ms since epoch. `declineDeadline` is
 * when an undecided hold is blocked — it precedes the bank's own resolution
 * time by a safety margin.
 */
export type NickelBalanceInboundDebitReview = {
  __typename?: 'NickelBalanceInboundDebitReview';
  /** The allowed-company rule an AUTO_ALLOWED debit matched; null otherwise. */
  allowRuleId?: Maybe<Scalars['ID']['output']>;
  amountCents: Scalars['Int']['output'];
  automaticallyResolvesAt: Scalars['Float']['output'];
  balanceAccountId: Scalars['ID']['output'];
  decidedAt?: Maybe<Scalars['Float']['output']>;
  /** The teammate who decided, when a person did; null for system outcomes. */
  decidedByName?: Maybe<Scalars['String']['output']>;
  declineDeadline: Scalars['Float']['output'];
  heldAt: Scalars['Float']['output'];
  id: Scalars['ID']['output'];
  /** The originator's entry description ("AUTOPAY", "TAX PMT"), when set. */
  originatorCompanyEntryDescription?: Maybe<Scalars['String']['output']>;
  /**
   * The ACH originator's Company ID, which allowed-company rules match on.
   * Null when the debit carried none, so it cannot be always-allowed.
   */
  originatorCompanyId?: Maybe<Scalars['String']['output']>;
  /** Who is pulling the funds (the ACH originator's company name). */
  originatorCompanyName: Scalars['String']['output'];
  /** 3-letter SEC code (CCD, PPD, WEB, …), when known. */
  standardEntryClassCode?: Maybe<Scalars['String']['output']>;
  status: NickelBalanceInboundDebitReviewStatus;
  /** The NickelBalanceTransfer the hold is on. */
  transferId: Scalars['ID']['output'];
};

/**
 * Where a Debit Protection hold stands. PENDING_DECISION: held at the bank,
 * blocked at `declineDeadline` unless allowed first. ALLOWED: a teammate
 * allowed it (or turned protection off while it was held); it settles on the
 * bank's schedule. AUTO_ALLOWED: never held — its Company ID was on the
 * account's allowed list when it arrived (`allowRuleId`). BLOCKED: a teammate
 * blocked it. EXPIRED_BLOCKED: blocked automatically — the deadline passed, or
 * it arrived with no time to decide. RESOLVED_EXTERNALLY: the bank resolved it
 * before or against the decision (ops are alerted).
 */
export enum NickelBalanceInboundDebitReviewStatus {
  Allowed = 'ALLOWED',
  AutoAllowed = 'AUTO_ALLOWED',
  Blocked = 'BLOCKED',
  ExpiredBlocked = 'EXPIRED_BLOCKED',
  PendingDecision = 'PENDING_DECISION',
  ResolvedExternally = 'RESOLVED_EXTERNALLY'
}

/**
 * Read-only snapshot for the dashboard's "interest earned" widget and, since
 * 2026-09-10, the balance figures the Balance page renders. `accrued*` is the
 * ledger expectation (independent of the Increase receipt); `paid*` is what's
 * actually been credited from received interest, and lags until the receipt
 * lands.
 *
 * The query is gated on `banking.account.see_balance`, so the interest half —
 * `currentPeriod`, `currentRateBips`, both `accrued*`, both `*Paid*` and
 * `accruedThroughMs` — is null for a caller who does not also hold
 * `banking.account.see_interest`. The balance figures are served either way.
 */
export type NickelBalanceInstantPayout = {
  __typename?: 'NickelBalanceInstantPayout';
  instant: Scalars['Boolean']['output'];
  /** RTP or FEDNOW when instant. */
  rail?: Maybe<Scalars['String']['output']>;
  /**
   * When not instant, why the payment goes by standard ACH, said to the
   * merchant: the vendor's bank, the amount, today's limit or the account's
   * history. Null when instant, and for a reason that is the org's rather than
   * the payment's (`nickelBalancePayoutOffers` answers those) or one there is
   * nothing useful to say about.
   */
  standardReason?: Maybe<Scalars['String']['output']>;
};

export type NickelBalanceInterestSummary = {
  __typename?: 'NickelBalanceInterestSummary';
  accruedThisPeriodCents?: Maybe<Scalars['Float']['output']>;
  /**
   * Epoch ms of the instant `lifetimeAccruedCents` covers through: end (exclusive)
   * of the newest accrual day. Null until the org's first accrual row. The ticker
   * extrapolates from this wall-clock anchor so every page load shows the same
   * value instead of restarting from the last payout. Because the daily job
   * pre-credits its whole run day, this instant is usually in the future — the
   * ticker's negative elapsed term then backs out the not-yet-elapsed remainder.
   */
  accruedThroughMs?: Maybe<Scalars['Float']['output']>;
  /** AVAILABLE balance at query time — lets the widget derive the per-second accrual rate (balance × rateBips / 365·86400·10_000) and tick between snapshots. */
  availableBalanceCents: Scalars['Float']['output'];
  /** 'YYYY-MM' (UTC). Null without `banking.account.see_interest`. */
  currentPeriod?: Maybe<Scalars['String']['output']>;
  /** Rate Nickel pays this merchant today, in bips (≤200). */
  currentRateBips?: Maybe<Scalars['Int']['output']>;
  /**
   * Check-deposit funds under an unexpired Nickel settlement-risk hold at query
   * time — accepted and credited by Increase, but not yet spendable. Already
   * inside availableBalanceCents and NOT inside processingBalanceCents; the
   * balance card subtracts it from Available and adds it to Processing, so
   * Total Balance is unchanged. Also the figure the withdrawal form caps at.
   */
  heldCheckDepositCents: Scalars['Float']['output'];
  /** How many deposits make up heldCheckDepositCents (0 when nothing is held) — lets copy say "available <date>" for one hold and "available by <date>" for several. */
  heldCheckDepositCount: Scalars['Int']['output'];
  /**
   * Epoch ms of the LATEST release instant among those holds — the moment by
   * which all of heldCheckDepositCents is usable. Null when nothing is held.
   */
  heldCheckDepositReleaseAt?: Maybe<Scalars['Float']['output']>;
  /** Expected interest accrued over the org's whole history (same formula as accruedThisPeriodCents, all rows). The dashboard ticker anchors on it. */
  lifetimeAccruedCents?: Maybe<Scalars['Float']['output']>;
  lifetimePaidCents?: Maybe<Scalars['Float']['output']>;
  paidThisPeriodCents?: Maybe<Scalars['Float']['output']>;
  /**
   * Money committed to card swipes Increase is still holding — authorized, not
   * yet settled. The mirror image of heldCheckDepositCents rather than another
   * of it: a held check is INSIDE availableBalanceCents and only unusable,
   * while a card hold was never in it (the balance mirror is settled-only) and
   * is on its way out. So the balance card subtracts this from Available AND
   * from Total, and the same money shows in the transactions table as a
   * Processing card row until it posts — the two are built from the same
   * mirror rows, so the card and the table cannot disagree.
   *
   * Not a gate and not a deduction the ledger knows about: every path that
   * moves money out reads Increase's own available balance, which already nets
   * the hold. This field exists so the merchant's screen agrees with the bank.
   * Org-wide (omitted `balanceAccountId`) = Σ over the org's own accounts;
   * vendor virtual-card wallets are never included.
   */
  pendingCardHoldCents: Scalars['Float']['output'];
  /**
   * Money in motion OUT of the account at query time: PENDING outbound
   * transfers — withdrawals to the merchant's bank (ACH and domestic wire) and
   * native-check reservations — that Increase has not settled. Feeds the
   * balance card's "Pending out" stat. Display only, NOT a deduction: an ACH
   * withdrawal already left availableBalanceCents at intent, a wire or check
   * leaves it when it posts, and NB-funded bill payments settle at intent so
   * they never appear here. Total Balance stays available + processing.
   * Org-wide (omitted `balanceAccountId`) = Σ over the org's own accounts.
   */
  pendingOutboundCents: Scalars['Float']['output'];
  /**
   * In-flight money headed into Nickel Balance at query time: receivables whose
   * activity is still PENDING (valued at the payment's submittedAmountInCents —
   * the figure the transactions feed shows for the row) plus deposits still
   * PENDING on the transfer ledger — merchant ACH pulls and submitted check
   * deposits alike, both of which ride PENDING inbound transfer rows. Feeds the
   * balance card's Processing row; the card derives
   * Total Balance = available + processing.
   *
   * Net of any overdraft: availableBalanceCents floors a negative balance at 0,
   * and the first money in repays it, so this figure is what the in-flight money
   * leaves once it settles (never below 0).
   *
   * Deliberately EXCLUDES check-deposit funds under a Nickel settlement-risk
   * hold: those sit inside availableBalanceCents (the mirror includes the
   * acceptance credit) and this figure is shared with the admin Balance page's
   * cross-merchant totals. They are surfaced separately as
   * heldCheckDepositCents so the merchant's card can move them.
   */
  processingBalanceCents: Scalars['Float']['output'];
};

/**
 * Dry-run of the onboarding-data preconditions Nickel Balance provisioning
 * enforces when it creates the merchant's Increase entity. Lets the dashboard
 * route the merchant back to the Get Activated section that is missing fields
 * BEFORE they set up their first account and accept the NB disclosures, instead
 * of submission failing at provisioning. Mirrors merchantEntityDataProblems on
 * the API.
 */
export type NickelBalanceOnboardingReadiness = {
  __typename?: 'NickelBalanceOnboardingReadiness';
  /** The business-details section (legal name / EIN / address) is missing or invalid. */
  businessDetailsIncomplete: Scalars['Boolean']['output'];
  /** The owner-details section (beneficial owners) is missing or invalid. */
  ownerDetailsIncomplete: Scalars['Boolean']['output'];
  /** Merchant-readable description of each missing or invalid field. */
  problems: Array<Scalars['String']['output']>;
  /** True when onboarding has everything entity provisioning needs. */
  ready: Scalars['Boolean']['output'];
};

export type NickelBalanceOverdraft = {
  __typename?: 'NickelBalanceOverdraft';
  /** The Nickel Balance account carrying the overdraft (multi-account): debt is anchored, covered and repaid per account. */
  accountId: Scalars['ID']['output'];
  amountCents: Scalars['Int']['output'];
  createdAt: Scalars['Float']['output'];
  id: Scalars['ID']['output'];
  remainingCents: Scalars['Int']['output'];
  status: NickelBalanceOverdraftStatus;
};

export enum NickelBalanceOverdraftStatus {
  Open = 'OPEN',
  Repaid = 'REPAID',
  WrittenOff = 'WRITTEN_OFF'
}

/**
 * What the bill-pay flow may offer an org paying from Nickel Balance. True
 * promises nothing: the vendor's bank, the amount and velocity are asked when
 * the bill is paid (`nickelBalanceInstantPayout` for instant payment; the pay
 * mutation refuses a wire).
 */
export type NickelBalancePayoutOffers = {
  __typename?: 'NickelBalancePayoutOffers';
  /** Instant payment: the rollout, the payout source and the org's eligibility. */
  instantPayment: Scalars['Boolean']['output'];
  /** A wire, domestic or international, paid from Nickel Balance: the org's eligibility. */
  wire: Scalars['Boolean']['output'];
};

/**
 * Money Increase is holding against a Nickel Balance account that has not
 * posted to the ledger yet — the local mirror of one Increase
 * pending_transaction.
 *
 * Surfaced on a feed row for exactly one category: `card_authorization`, a
 * live card hold. That is the only in-flight state a merchant has no other
 * record of, and it is the reason a card purchase now appears in the
 * transactions table on the day of the swipe rather than a day or three later
 * when the acquirer settles. Every other pending category Increase holds is
 * already a feed row of its own (a submitted check deposit, an initiated
 * transfer), so it is mirrored but never surfaced here.
 *
 * Temporary by construction: when the settlement posts, the feed row moves
 * onto the `NickelBalanceTransaction` and this field goes null. Render money
 * from whichever of the two resolves — the settled amount can differ from the
 * hold (a partial capture, a tip adjustment), so a client that keeps showing
 * the hold would be showing the wrong figure.
 */
export type NickelBalancePendingTransaction = {
  __typename?: 'NickelBalancePendingTransaction';
  /** The Nickel Balance account the hold sits against (multi-account). */
  accountId: Scalars['ID']['output'];
  /** Signed cents, as Increase reports it: a card hold is negative. */
  amountCents: Scalars['Int']['output'];
  /** The acquirer-reported merchant the card was used at; null when the authorization named nobody. */
  counterpartyName?: Maybe<Scalars['String']['output']>;
  id: Scalars['ID']['output'];
  /** When Increase opened the hold — the instant the row is dated by, the counterpart of a posting's postedAt. */
  pendingAt: Scalars['Float']['output'];
  /** Raw Increase pending_transaction.source.category — `card_authorization` for every row a client sees today. */
  sourceCategory: Scalars['String']['output'];
};

export type NickelBalanceStatement = {
  __typename?: 'NickelBalanceStatement';
  accountLabel: Scalars['String']['output'];
  /** The account this statement describes. Statements cut before multi-account were stamped onto the org's then-only account. */
  balanceAccountId: Scalars['ID']['output'];
  id: Scalars['ID']['output'];
  periodEnd: Scalars['Float']['output'];
  periodStart: Scalars['Float']['output'];
};

export enum NickelBalanceStatementFormat {
  Csv = 'CSV',
  Pdf = 'PDF'
}

export type NickelBalanceTransaction = {
  __typename?: 'NickelBalanceTransaction';
  /** The Nickel Balance account the posting sits on (multi-account). */
  accountId: Scalars['ID']['output'];
  amountCents: Scalars['Int']['output'];
  /**
   * Inbound wires only (sourceCategory inbound_wire_transfer): the debtor's bank
   * account number as the wire carried it. Null for other rails (ACH entries
   * don't identify the originator's account) and when the lookup failed.
   */
  counterpartyAccountNumber?: Maybe<Scalars['String']['output']>;
  /** External party behind the posting (wire sender, ACH originator); null when the rail doesn't identify one. */
  counterpartyName?: Maybe<Scalars['String']['output']>;
  /** Sender-supplied reference (wire remittance information, ACH entry description); null when absent. */
  description?: Maybe<Scalars['String']['output']>;
  externalTransactionId: Scalars['String']['output'];
  id: Scalars['ID']['output'];
  /**
   * The bank behind originatorRoutingNumber per Increase's routing-number
   * directory, as the directory spells it (usually upper-case). Null for rails
   * that identify no bank and when the directory doesn't know the number.
   */
  originatorBankName?: Maybe<Scalars['String']['output']>;
  /**
   * Third-party ACH postings only (sourceCategory inbound_ach_transfer, either
   * direction): the originator's NACHA company ID. Null for other rails.
   */
  originatorCompanyId?: Maybe<Scalars['String']['output']>;
  /**
   * The routing number of the counterparty's bank: the originator's ODFI for
   * third-party ACH, the debtor's bank (else the instructing agent) for inbound
   * wires. Null for other rails.
   */
  originatorRoutingNumber?: Maybe<Scalars['String']['output']>;
  postedAt: Scalars['Float']['output'];
  sourceCategory: Scalars['String']['output'];
  /** Third-party ACH postings only: the three-letter NACHA SEC code (PPD, CCD, WEB, …). Null for other rails. */
  standardEntryClassCode?: Maybe<Scalars['String']['output']>;
  transferId?: Maybe<Scalars['String']['output']>;
};

export enum NickelBalanceTransactionsExportSection {
  CardActivity = 'CARD_ACTIVITY',
  DepositsAndCredits = 'DEPOSITS_AND_CREDITS',
  InterestFeesAndAdjustments = 'INTEREST_FEES_AND_ADJUSTMENTS',
  ReturnsAndReversals = 'RETURNS_AND_REVERSALS',
  WithdrawalsAndPayments = 'WITHDRAWALS_AND_PAYMENTS'
}

export type NickelBalanceTransfer = {
  __typename?: 'NickelBalanceTransfer';
  amountCents: Scalars['Int']['output'];
  checkDepositPayerName?: Maybe<Scalars['String']['output']>;
  completedAt?: Maybe<Scalars['Float']['output']>;
  crossOrg?: Maybe<NickelBalanceCrossOrgTransfer>;
  externalTransferId?: Maybe<Scalars['String']['output']>;
  externalTransferType?: Maybe<Scalars['String']['output']>;
  failedAt?: Maybe<Scalars['Float']['output']>;
  failureReason?: Maybe<Scalars['String']['output']>;
  fromAccountId?: Maybe<Scalars['String']['output']>;
  fundingSource?: Maybe<NickelBalanceFundingSource>;
  id: Scalars['ID']['output'];
  initiatedAt: Scalars['Float']['output'];
  returnedAt?: Maybe<Scalars['Float']['output']>;
  statementDescriptor?: Maybe<Scalars['String']['output']>;
  status: NickelBalanceTransferStatus;
  toAccountId?: Maybe<Scalars['String']['output']>;
};

export enum NickelBalanceTransferStatus {
  Complete = 'COMPLETE',
  Failed = 'FAILED',
  Pending = 'PENDING',
  Returned = 'RETURNED'
}

/**
 * The payer block printed top-left on a voided check
 * (nickelBalanceVoidedCheckUrl), one field per printed line, in this order.
 * Every line is optional and blank lines are dropped; an input with no
 * non-blank line is the same as omitting it. Each line is at most 35
 * characters and may not contain emoji or other characters outside the Basic
 * Multilingual Plane (the bank's check-face limits); the mutation refuses a
 * line past either with a message naming the field.
 */
export type NickelBalanceVoidedCheckPayerInput = {
  addressLine1?: InputMaybe<Scalars['String']['input']>;
  addressLine2?: InputMaybe<Scalars['String']['input']>;
  addressLine3?: InputMaybe<Scalars['String']['input']>;
  name?: InputMaybe<Scalars['String']['input']>;
};

/**
 * What Pay would answer for a wire paid from Nickel Balance. A prediction:
 * today's limit can move before Pay, and a scheduled payment is checked again
 * when it runs.
 */
export type NickelBalanceWireCheck = {
  __typename?: 'NickelBalanceWireCheck';
  allowed: Scalars['Boolean']['output'];
  /** What Pay would say, for the merchant. Null when allowed. */
  refusal?: Maybe<Scalars['String']['output']>;
};

export enum NickelBalanceWithdrawalMethod {
  Ach = 'ACH',
  DomesticWire = 'DOMESTIC_WIRE'
}

export type Notification = {
  __typename?: 'Notification';
  email?: Maybe<Scalars['String']['output']>;
  name?: Maybe<Scalars['String']['output']>;
};

/**
 * One cell of the Team Notifications grid as an Administrator set it
 * (docs/workspace-roles/notifications.md §14): the value is stored on the role
 * and pushed into the setting of every current internal member of it.
 */
export type NotificationCellInput = {
  key: Scalars['String']['input'];
  subscribed: Scalars['Boolean']['input'];
  workspaceRoleId: Scalars['ID']['input'];
};

/**
 * A notification delivery channel. V1 delivers everything by email; the value
 * is channel-shaped from day one so a second channel adds a value, not a
 * shape migration (docs/workspace-roles/notifications.md §3).
 */
export enum NotificationChannel {
  Email = 'EMAIL'
}

/**
 * A default contact (Settings → Notifications): the switch and the addresses of
 * merchant.carbonCopyEmail or merchant.vendorCarbonCopyEmail. An omitted field
 * is left as stored.
 */
export type NotificationContactInput = {
  emails?: InputMaybe<Array<Scalars['String']['input']>>;
  enabled?: InputMaybe<Scalars['Boolean']['input']>;
};

/**
 * The org-level notification defaults the Settings tab edits (notifications.md
 * §9) — the parts of notificationSettings that are not role routing. Every
 * field is optional and an omitted one is left as stored; the JSON is merged
 * server-side, never replaced, so a client that sees part of it cannot clobber
 * the rest.
 */
export type NotificationDefaultsInput = {
  creatorNotifications?: InputMaybe<Array<CreatorNotificationInput>>;
  customerReplyTo?: InputMaybe<Array<CustomerReplyToInput>>;
  /** merchant.carbonCopyEmail — the Default Invoice Contact for Customers. */
  invoiceContact?: InputMaybe<NotificationContactInput>;
  recipients?: InputMaybe<Array<MerchantRecipientsInput>>;
  /** buyer.smsPaymentLinks.enabled — text the payment link to recipients with a phone number on file. */
  smsPaymentLinks?: InputMaybe<Scalars['Boolean']['input']>;
  /** merchant.vendorCarbonCopyEmail — the Default Vendor Contact for Bills. */
  vendorContact?: InputMaybe<NotificationContactInput>;
  /**
   * merchant.vendorPaymentEmails — one receipt email per payment, or one
   * summary per vendor at the end of the day.
   */
  vendorPaymentEmails?: InputMaybe<VendorPaymentEmailCadence>;
};

export type NotificationDefaultsResponse = {
  __typename?: 'NotificationDefaultsResponse';
  /** The same merge shape updateNotificationSettings returns. */
  accountInfo?: Maybe<AccountInfo>;
  error?: Maybe<Error>;
};

/**
 * An address added to a merchant notification beside the members subscribed to
 * it (docs/workspace-roles/notifications.md §16). Not a user: the address is
 * never matched against an account, so anyone may be added.
 */
export type NotificationEmailRecipient = {
  __typename?: 'NotificationEmailRecipient';
  /** Lowercased. */
  email: Scalars['String']['output'];
  id: Scalars['ID']['output'];
  /** A merchant key of the notification catalog in @nickeltechnology/types. */
  key: Scalars['String']['output'];
};

/**
 * One address on one merchant notification, as an Administrator set it:
 * `subscribed` adds it, off removes it. An address already so is left alone.
 */
export type NotificationEmailRecipientInput = {
  email: Scalars['String']['input'];
  key: Scalars['String']['input'];
  subscribed: Scalars['Boolean']['input'];
};

export type NotificationEmailRecipientsResponse = {
  __typename?: 'NotificationEmailRecipientsResponse';
  emailRecipients?: Maybe<Array<NotificationEmailRecipient>>;
  error?: Maybe<Error>;
};

export type NotificationSetting = {
  __typename?: 'NotificationSetting';
  emails?: Maybe<Array<Scalars['String']['output']>>;
  enabled?: Maybe<Scalars['Boolean']['output']>;
  /**
   * Merchant payment-scoped categories only: also notify the User who created
   * the payment link. Null/absent = off. Unused (always null) on buyer categories.
   */
  notifyCreator?: Maybe<Scalars['Boolean']['output']>;
};

export type NotificationSettings = {
  __typename?: 'NotificationSettings';
  buyer?: Maybe<BuyerNotificationSetting>;
  merchant?: Maybe<MerchantNotificationSetting>;
};

export type NotificationSubscription = {
  __typename?: 'NotificationSubscription';
  channel: NotificationChannel;
  key: Scalars['String']['output'];
};

export type NotificationSubscriptionInput = {
  channel: NotificationChannel;
  key: Scalars['String']['input'];
};

export type OAuthAuthorizationRequestResponse = {
  __typename?: 'OAuthAuthorizationRequestResponse';
  client?: Maybe<OAuthClientInfo>;
  error?: Maybe<Error>;
  fdxClient?: Maybe<Scalars['Boolean']['output']>;
  nickelBalanceActive?: Maybe<Scalars['Boolean']['output']>;
  pkceRequired?: Maybe<Scalars['Boolean']['output']>;
  stepUpRequired?: Maybe<Scalars['Boolean']['output']>;
};

export type OAuthClientInfo = {
  __typename?: 'OAuthClientInfo';
  clientId: Scalars['ID']['output'];
  clientUri?: Maybe<Scalars['String']['output']>;
  logoUri?: Maybe<Scalars['String']['output']>;
  name: Scalars['String']['output'];
};

export type OauthTokenCreateResponse = {
  __typename?: 'OauthTokenCreateResponse';
  error?: Maybe<Scalars['String']['output']>;
};

/**
 * Moments in the onboarding application only the browser sees, which the
 * dashboard reports (recordOnboardingApplicationEvent). The API reports the
 * rest itself where it saves them: the submission, approvals and denials,
 * deactivation, and requests for information and their answers.
 */
export enum OnboardingApplicationEvent {
  /** Clicked the application's close (X) button. */
  ApplicationClosed = 'APPLICATION_CLOSED',
  /** Opened the application, other than to view one already submitted. */
  ApplicationOpened = 'APPLICATION_OPENED',
  /** Clicked Save & exit. */
  ApplicationSavedAndExited = 'APPLICATION_SAVED_AND_EXITED',
  BankingCompleted = 'BANKING_COMPLETED',
  /** Left Plaid without connecting. */
  BankLinkAbandoned = 'BANK_LINK_ABANDONED',
  /** Connected a bank through Plaid. */
  BankLinkSucceeded = 'BANK_LINK_SUCCEEDED',
  /** Chose to enter bank details by hand. */
  BankManualSelected = 'BANK_MANUAL_SELECTED',
  BusinessDetailsCompleted = 'BUSINESS_DETAILS_COMPLETED',
  OwnerDetailsCompleted = 'OWNER_DETAILS_COMPLETED',
  /** Opened the request-for-information flow from the Get Activated card or banner. */
  RfiOpened = 'RFI_OPENED'
}

export type OnboardingFormInfo = {
  __typename?: 'OnboardingFormInfo';
  /**
   * Legacy acknowledgment from the Get Activated application's retired intro
   * screen ("Before we get started"). true: the merchant checked "I understand
   * and agree", the only way past that screen. false: the screen was never
   * rendered, the no-intro experiment arm (PostHog experiment 386496), always
   * alongside flowVariant "no-intro" in the stored JSON. Absent: written after
   * the screen was removed (2026-09) or the org never used the application.
   * It never means "declined".
   */
  acceptTerms?: Maybe<Scalars['Boolean']['output']>;
  accountHolder?: Maybe<Scalars['String']['output']>;
  accountName?: Maybe<Scalars['String']['output']>;
  accountNumber?: Maybe<Scalars['String']['output']>;
  accountType?: Maybe<Scalars['String']['output']>;
  additionalNotes?: Maybe<Scalars['String']['output']>;
  annualRevenue?: Maybe<Scalars['String']['output']>;
  attributer?: Maybe<Scalars['JSONObject']['output']>;
  averageTransactionAmount?: Maybe<Scalars['String']['output']>;
  averageTransactionsPerMonth?: Maybe<Scalars['String']['output']>;
  bankName?: Maybe<Scalars['String']['output']>;
  businessType?: Maybe<Scalars['String']['output']>;
  city?: Maybe<Scalars['String']['output']>;
  /**
   * Free-form band chosen at signup. A business answers headcount ("11–50
   * people"); an accounting firm answers its book of clients ("10–25 clients").
   * The unit in the string says which question was asked — there is no
   * separate discriminator, so bucket by parsing the unit, or join on the org's
   * isAccountingFirm.
   */
  companySize?: Maybe<Scalars['String']['output']>;
  contactPhoneNumber?: Maybe<Scalars['String']['output']>;
  country?: Maybe<Scalars['String']['output']>;
  dateOfBirth?: Maybe<Scalars['String']['output']>;
  description?: Maybe<Scalars['String']['output']>;
  documents?: Maybe<Array<Scalars['String']['output']>>;
  email?: Maybe<Scalars['String']['output']>;
  hasDateOfBirth?: Maybe<Scalars['Boolean']['output']>;
  hasOwnerSSN?: Maybe<Scalars['Boolean']['output']>;
  industry?: Maybe<Scalars['String']['output']>;
  /**
   * The merchant's legal name as entered (or as an admin corrected it through
   * updateOrganizationAdminControls): what Nickel Balance statements and the
   * merchant's Increase records are titled with.
   */
  legalName?: Maybe<Scalars['String']['output']>;
  manualBankDocuments?: Maybe<Array<Scalars['String']['output']>>;
  manualBankReviewRequested?: Maybe<Scalars['Boolean']['output']>;
  organizationId?: Maybe<Scalars['String']['output']>;
  ownerSSN?: Maybe<Scalars['String']['output']>;
  owners?: Maybe<Array<Maybe<Scalars['String']['output']>>>;
  phone?: Maybe<Scalars['String']['output']>;
  queryParams?: Maybe<Array<Array<Maybe<Scalars['String']['output']>>>>;
  referralSource?: Maybe<Scalars['String']['output']>;
  /** Earlier RFI rounds, oldest first. */
  rfiHistory?: Maybe<Array<RfiRequest>>;
  rfiRequest?: Maybe<RfiRequest>;
  routingNumber?: Maybe<Scalars['String']['output']>;
  state?: Maybe<Scalars['String']['output']>;
  stateIncorporated?: Maybe<Scalars['String']['output']>;
  statementDescriptor?: Maybe<Scalars['String']['output']>;
  street?: Maybe<Scalars['String']['output']>;
  street2?: Maybe<Scalars['String']['output']>;
  taxId?: Maybe<Scalars['String']['output']>;
  website?: Maybe<Scalars['String']['output']>;
  yearlyVolumeRange?: Maybe<Scalars['String']['output']>;
  zip?: Maybe<Scalars['String']['output']>;
};

export type OnboardingInfoPayload = {
  __typename?: 'OnboardingInfoPayload';
  error?: Maybe<Error>;
  organizationId?: Maybe<Scalars['String']['output']>;
};

export type OnboardingPayload = {
  __typename?: 'OnboardingPayload';
  error?: Maybe<Error>;
  payoutMethod?: Maybe<BankAccount>;
};

export type OnboardingReviewPayload = {
  __typename?: 'OnboardingReviewPayload';
  accountantId?: Maybe<Scalars['String']['output']>;
  /** The linked accounting firm's name, when accountantId is set. */
  accountantName?: Maybe<Scalars['String']['output']>;
  adminNotes?: Maybe<AdminNotes>;
  alloyJourney?: Maybe<AlloyJourney>;
  /** The org's own OrganizationInfo.approvalStatus, for the admin status buttons. */
  approvalStatus?: Maybe<AccountApprovalStatus>;
  /** White-label: Nickel branding hidden on the portal and emails. */
  brandingDisabled?: Maybe<Scalars['Boolean']['output']>;
  /** The firm's client orgs, by name. Empty unless the org is an accounting firm. */
  clients?: Maybe<Array<AccountantClient>>;
  conductorEndUserId?: Maybe<Scalars['String']['output']>;
  creditCardEnabled?: Maybe<Scalars['Boolean']['output']>;
  employees?: Maybe<Array<User>>;
  error?: Maybe<Error>;
  /** Follow-up calls about the application, newest first. */
  followUpCalls?: Maybe<Array<UnderwritingFollowUpCall>>;
  /** Staff's questions for the org; null while the AI underwriter's stand. Read phoneAgent instead. */
  followUpQuestionEdit?: Maybe<FollowUpQuestionEdit>;
  /** Whether staff can start an AI follow-up call in this environment. */
  followUpVoiceAvailable?: Maybe<Scalars['Boolean']['output']>;
  /**
   * AI call attempts that recorded no call: in progress, unanswered or failed,
   * newest first.
   */
  followUpVoiceCalls?: Maybe<Array<FollowUpVoiceCall>>;
  id?: Maybe<Scalars['String']['output']>;
  isPayoutMethodAlsoPaymentMethod?: Maybe<Scalars['Boolean']['output']>;
  name?: Maybe<Scalars['String']['output']>;
  /**
   * Nickel Balance application lifecycle for the admin review card — see
   * NickelBalanceAccountStatus. Null on error payloads; treat as NONE.
   */
  nickelBalanceAccountStatus?: Maybe<NickelBalanceAccountStatus>;
  /**
   * ISO timestamp of the merchant's latest Nickel Balance application submit;
   * null if they never applied.
   */
  nickelBalanceApplicationSubmittedAt?: Maybe<Scalars['String']['output']>;
  onboardingInfo?: Maybe<OnboardingFormInfo>;
  /**
   * The stored override of the checkout reference input's label
   * (OrganizationInfo.checkoutCopy); null when the default "Reason for payment"
   * applies. The raw override rather than the resolved string
   * OrganizationPageResponse carries, so the admin form can show "unset".
   */
  orderReferenceLabel?: Maybe<Scalars['String']['output']>;
  /** The stored override of that input's placeholder; null when the default "A123" applies. */
  orderReferencePlaceholder?: Maybe<Scalars['String']['output']>;
  organizationId?: Maybe<Scalars['String']['output']>;
  paymentMethodsPlaidData?: Maybe<Array<PaymentMethodPlaidData>>;
  paymentsDisabled?: Maybe<Scalars['Boolean']['output']>;
  payoutMethodAccountNumber?: Maybe<Scalars['String']['output']>;
  payoutMethodBankName?: Maybe<Scalars['String']['output']>;
  payoutMethodId?: Maybe<Scalars['String']['output']>;
  payoutMethodLastFour?: Maybe<Scalars['String']['output']>;
  payoutsDisabled?: Maybe<Scalars['Boolean']['output']>;
  /** What the Phone Agent asks on a call about the org, and the numbers it can call. */
  phoneAgent?: Maybe<PhoneAgent>;
  plaidAccountInfo?: Maybe<Array<PlaidAccountInfo>>;
  plaidTransactions?: Maybe<Array<PlaidTransaction>>;
  quickbooksCompanyId?: Maybe<Scalars['String']['output']>;
  referrerOrganizationId?: Maybe<Scalars['String']['output']>;
  referrerOrganizationName?: Maybe<Scalars['String']['output']>;
  /** The org's role, for the admin role control. */
  role?: Maybe<OrganizationRole>;
  /** The nickelpayments.com subdomain (OrganizationInfo.subdomain). */
  subdomain?: Maybe<Scalars['String']['output']>;
  /** The org-level 3DS default (OrganizationInfo.threeDSEnabled). */
  threeDSEnabled?: Maybe<Scalars['Boolean']['output']>;
  /**
   * Every recorded underwriting run on the org, oldest first, auto-approval
   * second opinions included. Empty for orgs underwritten before runs were
   * recorded; adminNotes.aiApprovalSummaries still holds their latest.
   */
  underwritingRuns?: Maybe<Array<AiApprovalSummaryEntry>>;
};

export type OperatingAdjustments = {
  __typename?: 'OperatingAdjustments';
  accountsPayable?: Maybe<Scalars['Float']['output']>;
  accountsReceivable?: Maybe<Scalars['Float']['output']>;
  inventoryAsset?: Maybe<Scalars['Float']['output']>;
  prepaidExpenses?: Maybe<Scalars['Float']['output']>;
  totalAdjustments?: Maybe<Scalars['Float']['output']>;
};

export type OptionalAnswerForm = {
  __typename?: 'OptionalAnswerForm';
  completedAt?: Maybe<Scalars['Float']['output']>;
  id?: Maybe<Scalars['String']['output']>;
  paymentId?: Maybe<Scalars['String']['output']>;
  questionForm?: Maybe<QuestionForm>;
  questionFormName?: Maybe<Scalars['String']['output']>;
  schema?: Maybe<Scalars['String']['output']>;
};

export type Organization = {
  __typename?: 'Organization';
  accountInfo?: Maybe<AccountInfo>;
  accountant?: Maybe<AccountantOrganization>;
  /**
   * What this org withholds from its accounting firm (setAccountantAccess).
   * Empty: the firm acts with the full grants of the member it enters as.
   */
  accountantRestrictions: Array<AccountantAccessGroup>;
  /**
   * Whether the AI underwriter approved this org: true when
   * OrganizationInfo.autoApprovedByRunId is set, which outlives a later return
   * to pending. Set only by `unapprovedAccounts`; null from every other
   * Organization producer.
   */
  aiApproved?: Maybe<Scalars['Boolean']['output']>;
  bankBalance?: Maybe<Scalars['String']['output']>;
  clients?: Maybe<Array<Organization>>;
  createdAt?: Maybe<Scalars['Float']['output']>;
  employees?: Maybe<Array<User>>;
  /**
   * Where the applicant's follow-up call stands, for the review queue. Set only
   * by `unapprovedAccounts`; null from every other Organization producer, and
   * for an org that answered no call.
   */
  followUpCallReview?: Maybe<FollowUpCallReview>;
  id: Scalars['ID']['output'];
  invites?: Maybe<Array<Invite>>;
  name?: Maybe<Scalars['String']['output']>;
  needsPlaidUpdate?: Maybe<Scalars['Boolean']['output']>;
  /**
   * Full Nickel Balance account lifecycle: NONE (never opened), ACTIVE (the
   * first account is open), CLOSED (opened then closed). IN_REVIEW and
   * REJECTED are the retired compliance-review phase (submission now opens the
   * account directly; no org is in either state) and are kept only so the enum
   * matches the Prisma one. Treat null as NONE.
   */
  nickelBalanceAccountStatus?: Maybe<NickelBalanceAccountStatus>;
  /**
   * Whether the org has an activated Nickel Balance — true when
   * OrganizationInfo.nickelBalanceAccountStatus is ACTIVE (the merchant opened
   * their first account). Exposed as a dedicated field so callers (e.g. the dashboard's
   * "not enrolled" gating) read a stable signal: the resolver can change how
   * activation is determined without consumers changing. Cheap DB read — no
   * Increase balance lookup.
   *
   * Nullable (matching the rest of Organization) so it isn't forced onto every
   * Organization object literal across the apps; the resolver always returns a
   * concrete boolean, so treat null as false.
   */
  nickelBalanceActivated?: Maybe<Scalars['Boolean']['output']>;
  openNickelBalanceOverdrafts?: Maybe<Array<NickelBalanceOverdraft>>;
  /**
   * The org's AR payout destination — ONE selection (Settings → Get Paid): a
   * linked bank payout method, or one of the org's Nickel Balance accounts'
   * `nickel_balance` payout methods (`payoutMethods` lists the options;
   * `selectPayoutMethod` changes it). A bank selection means the bank rail —
   * the row credited is re-resolved at payout time if this one expired. A
   * Nickel Balance selection persists; when the account is not payout-ready
   * the payout falls back to the bank rail. Null = nothing to pay out to.
   * Per-invoice QuestionForm.payoutMethodId pins override it.
   */
  payoutMethod?: Maybe<PayoutMethod>;
  payouts?: Maybe<Array<Payout>>;
  referralInvites?: Maybe<Array<ReferralInvite>>;
  referrals?: Maybe<Array<Referral>>;
  role?: Maybe<Scalars['String']['output']>;
  /**
   * What brought this signup — the ad click ids and campaign tags captured at
   * org creation, plus any referral. Set only by `unapprovedAccounts` (the
   * admin approvals table); null from every other Organization producer.
   */
  signupAttribution?: Maybe<SignupAttribution>;
  transactionCount?: Maybe<Scalars['Int']['output']>;
};

export type OrganizationInfo = {
  __typename?: 'OrganizationInfo';
  merchantId?: Maybe<Scalars['String']['output']>;
  provider?: Maybe<Scalars['String']['output']>;
};

export type OrganizationPageResponse = {
  __typename?: 'OrganizationPageResponse';
  achEnabled: Scalars['Boolean']['output'];
  approvalStatus?: Maybe<AccountApprovalStatus>;
  bannerKey?: Maybe<Scalars['String']['output']>;
  brandingDisabled?: Maybe<Scalars['Boolean']['output']>;
  checkoutPath?: Maybe<Scalars['String']['output']>;
  creditCardEnabled: Scalars['Boolean']['output'];
  customARCardRateBips?: Maybe<Scalars['Int']['output']>;
  customDomain?: Maybe<Scalars['String']['output']>;
  employees?: Maybe<Array<User>>;
  error?: Maybe<Error>;
  feePassthroughPercent: Scalars['Int']['output'];
  /**
   * True when this pending merchant may take this payment on the light path, so
   * the portal offers checkout though approvalStatus is not APPROVED. False for
   * every other merchant, whatever its approvalStatus.
   */
  lightPathCheckoutOpen?: Maybe<Scalars['Boolean']['output']>;
  logoKey?: Maybe<Scalars['String']['output']>;
  merchantInOnboarding?: Maybe<Scalars['Boolean']['output']>;
  onboardingInfo?: Maybe<OnboardingFormInfo>;
  /**
   * Label of the payer's reference input on the checkout amount step, resolved
   * from OrganizationInfo.checkoutCopy over the default ("Reason for payment").
   * Always populated by the API; nullable like the type's other fields so the
   * portal's hand-built page objects (its fallback page, e2e mocks) need not
   * carry it, and the portal falls back to the same default when absent.
   */
  orderReferenceLabel?: Maybe<Scalars['String']['output']>;
  /** Placeholder of that input, resolved the same way (default "A123"). */
  orderReferencePlaceholder?: Maybe<Scalars['String']['output']>;
  organizationId?: Maybe<Scalars['String']['output']>;
  organizationName?: Maybe<Scalars['String']['output']>;
  paymentAmountEditable?: Maybe<Scalars['Boolean']['output']>;
  questionForms?: Maybe<Array<QuestionForm>>;
  subdomain: Scalars['String']['output'];
  threeDSEnabled: Scalars['Boolean']['output'];
};

export type OrganizationPagesResponse = {
  __typename?: 'OrganizationPagesResponse';
  error?: Maybe<Error>;
  organizationPages?: Maybe<Array<Maybe<OrganizationPageResponse>>>;
};

export type OrganizationResponse = {
  __typename?: 'OrganizationResponse';
  error?: Maybe<Error>;
  organization?: Maybe<Organization>;
};

/**
 * An organization's role, value-for-value the Prisma enum. CUSTOMER exists on
 * the column but nothing sets or reads it; admins switch between the other
 * two through AdminOrganizationRole.
 */
export enum OrganizationRole {
  AccountingFirm = 'ACCOUNTING_FIRM',
  Customer = 'CUSTOMER',
  Retailer = 'RETAILER'
}

export enum OrganizationTier {
  EnterpriseTier = 'ENTERPRISE_TIER',
  FreeTier = 'FREE_TIER',
  FreeTierNoTrials = 'FREE_TIER_NO_TRIALS',
  FreeTierPlus = 'FREE_TIER_PLUS',
  FreeTierPlusNoTrials = 'FREE_TIER_PLUS_NO_TRIALS',
  TeamTier = 'TEAM_TIER'
}

export type OrganizationsResponse = {
  __typename?: 'OrganizationsResponse';
  error?: Maybe<Error>;
  organizations?: Maybe<Array<Maybe<Organization>>>;
};

/**
 * What an amount edit did to the series' outstanding (already-issued) invoice.
 * Populated only by updateRecurringInvoice, and only when an amount was passed
 * for an ACTIVE series.
 */
export enum OutstandingInvoiceRepriceOutcome {
  /** A customer has a live payment against it (or an external payment is recorded on it), so it kept its issued price; the series still re-prices from the next issue. */
  BlockedByLivePayment = 'BLOCKED_BY_LIVE_PAYMENT',
  /** No outstanding invoice in a re-priceable state. */
  NotApplicable = 'NOT_APPLICABLE',
  /** The outstanding invoice now carries the series' new amount. */
  Repriced = 'REPRICED'
}

export type ParseBillDocumentResponse = {
  __typename?: 'ParseBillDocumentResponse';
  error?: Maybe<Error>;
  parse?: Maybe<BillDocumentParse>;
};

export type ParseCheckDepositImageResponse = {
  __typename?: 'ParseCheckDepositImageResponse';
  error?: Maybe<Error>;
  parse?: Maybe<CheckImageParse>;
};

export type ParseInvoiceDocumentResponse = {
  __typename?: 'ParseInvoiceDocumentResponse';
  error?: Maybe<Error>;
  parse?: Maybe<InvoiceDocumentParse>;
};

export type Partner = {
  __typename?: 'Partner';
  id: Scalars['ID']['output'];
  name: Scalars['String']['output'];
  returnUrls: Array<Scalars['String']['output']>;
  webhooks: Array<PartnerWebhook>;
};

export type PartnerAccount = {
  __typename?: 'PartnerAccount';
  account: Scalars['ID']['output'];
  decline?: Maybe<PartnerAccountDecline>;
  linkedAt: Scalars['String']['output'];
  partnerAccountRef: Scalars['String']['output'];
  status: Scalars['String']['output'];
};

export type PartnerAccountDecline = {
  __typename?: 'PartnerAccountDecline';
  reason: Scalars['String']['output'];
};

export type PartnerAccountPayload = {
  __typename?: 'PartnerAccountPayload';
  account?: Maybe<PartnerAccount>;
  error?: Maybe<Error>;
};

export type PartnerAccountsPayload = {
  __typename?: 'PartnerAccountsPayload';
  accounts?: Maybe<Array<PartnerAccount>>;
  error?: Maybe<Error>;
  totalResults?: Maybe<Scalars['Int']['output']>;
};

export type PartnerEnrollment = {
  __typename?: 'PartnerEnrollment';
  account?: Maybe<Scalars['String']['output']>;
  expiresAt: Scalars['String']['output'];
  id: Scalars['ID']['output'];
  linkedAt?: Maybe<Scalars['String']['output']>;
  partnerAccountRef: Scalars['String']['output'];
  requirements: PartnerEnrollmentRequirements;
  returnUrl: Scalars['String']['output'];
};

export type PartnerEnrollmentAddressInput = {
  city?: InputMaybe<Scalars['String']['input']>;
  state?: InputMaybe<Scalars['String']['input']>;
  street?: InputMaybe<Scalars['String']['input']>;
  street2?: InputMaybe<Scalars['String']['input']>;
  zip?: InputMaybe<Scalars['String']['input']>;
};

export type PartnerEnrollmentBusinessInput = {
  address?: InputMaybe<PartnerEnrollmentAddressInput>;
  annualRevenue?: InputMaybe<Scalars['String']['input']>;
  ein?: InputMaybe<Scalars['String']['input']>;
  industry?: InputMaybe<Scalars['String']['input']>;
  legalName?: InputMaybe<Scalars['String']['input']>;
  phone?: InputMaybe<Scalars['String']['input']>;
  stateOfIncorporation?: InputMaybe<Scalars['String']['input']>;
  website?: InputMaybe<Scalars['String']['input']>;
};

export type PartnerEnrollmentFieldError = {
  __typename?: 'PartnerEnrollmentFieldError';
  field: Scalars['String']['output'];
  message: Scalars['String']['output'];
};

export type PartnerEnrollmentLinkPayload = {
  __typename?: 'PartnerEnrollmentLinkPayload';
  error?: Maybe<Error>;
  expiresAt?: Maybe<Scalars['String']['output']>;
  url?: Maybe<Scalars['String']['output']>;
};

export type PartnerEnrollmentOwnerInput = {
  address?: InputMaybe<PartnerEnrollmentAddressInput>;
  dateOfBirth?: InputMaybe<Scalars['String']['input']>;
  firstName?: InputMaybe<Scalars['String']['input']>;
  lastName?: InputMaybe<Scalars['String']['input']>;
  ownershipPercentage?: InputMaybe<Scalars['Int']['input']>;
  phone?: InputMaybe<Scalars['String']['input']>;
  ssn?: InputMaybe<Scalars['String']['input']>;
};

export type PartnerEnrollmentPayload = {
  __typename?: 'PartnerEnrollmentPayload';
  enrollment?: Maybe<PartnerEnrollment>;
  error?: Maybe<Error>;
  fieldErrors?: Maybe<Array<PartnerEnrollmentFieldError>>;
};

export type PartnerEnrollmentPrefillInput = {
  business?: InputMaybe<PartnerEnrollmentBusinessInput>;
  ownerEmail?: InputMaybe<Scalars['String']['input']>;
  owners?: InputMaybe<Array<PartnerEnrollmentOwnerInput>>;
};

export type PartnerEnrollmentRequirements = {
  __typename?: 'PartnerEnrollmentRequirements';
  hostedOnly: Array<Scalars['String']['output']>;
  missing: Array<Scalars['String']['output']>;
  provided: Array<Scalars['String']['output']>;
};

export type PartnerEnrollmentSession = {
  __typename?: 'PartnerEnrollmentSession';
  businessName?: Maybe<Scalars['String']['output']>;
  ownerEmail?: Maybe<Scalars['String']['output']>;
  partnerName: Scalars['String']['output'];
  returnUrl: Scalars['String']['output'];
  signedUp: Scalars['Boolean']['output'];
};

export type PartnerEnrollmentSessionPayload = {
  __typename?: 'PartnerEnrollmentSessionPayload';
  error?: Maybe<Error>;
  session?: Maybe<PartnerEnrollmentSession>;
};

export type PartnerEnrollmentsPayload = {
  __typename?: 'PartnerEnrollmentsPayload';
  enrollments?: Maybe<Array<PartnerEnrollment>>;
  error?: Maybe<Error>;
  totalResults?: Maybe<Scalars['Int']['output']>;
};

export type PartnerPayload = {
  __typename?: 'PartnerPayload';
  error?: Maybe<Error>;
  partner?: Maybe<Partner>;
};

export type PartnerWebhook = {
  __typename?: 'PartnerWebhook';
  createdAt: Scalars['String']['output'];
  id: Scalars['ID']['output'];
  url: Scalars['String']['output'];
};

/**
 * Filters that apply only to PAYABLE (accounts-payable) activities.
 * Cross-source filters live directly on the paymentActivities query; filters
 * exclusive to one activity source are grouped into per-source inputs like this.
 * Combining these with a non-PAYABLE `source` is rejected as a bad request.
 */
export type PayableActivityFilters = {
  /** Case-insensitive substring match on the bill number or bill reason. */
  billLabel?: InputMaybe<Scalars['String']['input']>;
};

export enum PayableStatus {
  Draft = 'Draft',
  Open = 'Open',
  Paid = 'Paid',
  PartiallyPaid = 'PartiallyPaid',
  Unknown = 'Unknown',
  Void = 'Void'
}

export type Payment = {
  __typename?: 'Payment';
  achReturn?: Maybe<Return>;
  activity?: Maybe<PaymentActivity>;
  amountInCents: Scalars['Int']['output'];
  answerForm?: Maybe<OptionalAnswerForm>;
  /** What the caller can do to this payment right now, and why not. */
  availableActions: Array<ActionAvailability>;
  charges?: Maybe<Array<Charge>>;
  check?: Maybe<Check>;
  clientSecret?: Maybe<Scalars['String']['output']>;
  companyName?: Maybe<Scalars['String']['output']>;
  createdAt?: Maybe<Scalars['Float']['output']>;
  /**
   * How a card charge on this payment pays the merchant. GROSS pays the invoice
   * amount and debits the merchant's share of the card fee separately; NET pays
   * the invoice less that share. It is the mode saved on the payment, or the
   * organization's current one for a payment created before it was saved: the
   * rule the payout ledger books the charge by (receivableChargeFeeMode). Null
   * only for a payment with no organization.
   */
  creditCardFeeMode?: Maybe<CreditCardFeeMode>;
  currency: Scalars['String']['output'];
  /** Set only by confirmPayment (the provider customer the method was vaulted under); null from every other producer. */
  customerId?: Maybe<Scalars['String']['output']>;
  dbStatus?: Maybe<Scalars['String']['output']>;
  debitAuthorization?: Maybe<DebitAuthorization>;
  estimatedDisbursementDate?: Maybe<Scalars['String']['output']>;
  externalPaymentIntentId: Scalars['String']['output'];
  /**
   * The merchant's share of this card payment's fee, which a GROSS payment
   * collects as its own debit from the merchant's payout account after paying
   * them the full amount. Null when there is none to collect: an ACH payment, a
   * card payment whose payer paid the whole fee, and a NET payment, whose fee
   * came out of the payout instead. Null too for a member who can't see payouts.
   */
  feeDebit?: Maybe<PaymentFeeDebit>;
  id: Scalars['String']['output'];
  linkToken?: Maybe<Scalars['String']['output']>;
  livenessLinkToken?: Maybe<Scalars['String']['output']>;
  notes?: Maybe<Scalars['String']['output']>;
  payee?: Maybe<User>;
  paymentAttempts?: Maybe<Array<PaymentAttempt>>;
  paymentMethod?: Maybe<PaymentMethod>;
  /**
   * Absolute URL to download this payment's receipt PDF, generated on demand by
   * GET /api/pdf. Sessionless: the URL carries a token signed for this payment,
   * so it can be shared directly (e.g. quoted in a Penny email reply). Null until
   * the payment has a receipt (no successful payment attempt yet), and for a
   * caller that may not read this payment's receipt (see receiptToken).
   */
  pdfUrl?: Maybe<Scalars['String']['output']>;
  platformFeeInCents: Scalars['Int']['output'];
  provider?: Maybe<Scalars['String']['output']>;
  /**
   * The token that opens this payment's receipt: the portal receipt page and the
   * receipt query take it, and GET /api/pdf takes it as `token` with
   * fileName=invoice. Never expires. Returned to the payment's organization, to
   * the createPayment call that created it, to the confirmPayment call that paid
   * it, and under an invoice's PaymentRequest whose checkout link was presented
   * (not a reusable link's, whose payments are other payers'); null to any other
   * caller, who reached the payment by its id alone.
   */
  receiptToken?: Maybe<Scalars['String']['output']>;
  reconciled?: Maybe<Scalars['Boolean']['output']>;
  /** Set only by togglePaymentState (off PaymentInfo); null from every other producer. */
  seen?: Maybe<Scalars['Boolean']['output']>;
  status?: Maybe<PaymentStatus>;
  submittedAmountInCents: Scalars['Int']['output'];
  updatedAt?: Maybe<Scalars['Float']['output']>;
};

export type PaymentActivitiesResponse = {
  __typename?: 'PaymentActivitiesResponse';
  error?: Maybe<Error>;
  paymentActivities?: Maybe<Array<PaymentActivity>>;
  totalResults?: Maybe<Scalars['Int']['output']>;
};

export type PaymentActivity = {
  __typename?: 'PaymentActivity';
  achReturn?: Maybe<Return>;
  achVendorReturn?: Maybe<VendorReturn>;
  billPayment?: Maybe<BillPayment>;
  chargeback?: Maybe<Chargeback>;
  chargebackWon?: Maybe<ChargebackWin>;
  createdAt: Scalars['Float']['output'];
  /**
   * The row's merchant-facing description — what the dashboard's Description
   * column shows — read straight off the activity. This is exactly the text the
   * `paymentActivities(description:)` filter matches, so a client that filters
   * can show why each row matched instead of re-deriving the string.
   *
   * Null when no description has been derived for the row (see the column
   * docstring in schema.prisma); an empty description is indistinguishable from
   * an underived one.
   *
   * A snapshot, like `businessName`/`counterPartyName`: it is stamped when the
   * activity is written and re-derived on each upsert, so an edit to the source
   * record lands here the next time the row moves, not at the moment of the
   * edit. The dashboard still derives its own copy client-side
   * (getActivityReference), so until that renderer switches to this field the
   * two can disagree for a row that has not been upserted since the edit.
   */
  description?: Maybe<Scalars['String']['output']>;
  /**
   * Where this activity's merchant-side money is expected to land once its
   * payout settles — the AR payout kernel's forecast: the invoice's pinned
   * payout method (RECEIVABLE only) ?? the org's selected one, with Nickel
   * Balance readiness re-checked. A `nickel_balance` method (with its
   * account) when the money settles into Nickel Balance; the selected bank
   * method on the bank rail; null when the org has no selection (the bank
   * rail then credits the newest live bank method) and for sources that don't
   * settle through an AR-side payout (PAYABLE, the NB ledger sources, FEE).
   * Forecast only: once the row's payout has resolved this is null and the
   * payout's payoutMethod is the authoritative destination — including when
   * it recorded none (settlements before 2026-05, the payable-return
   * reversal), which must never be re-forecast from today's selection. Null
   * here regardless of whether the viewer may read `payout`. Clients must not
   * re-derive this from pins or selections — filter and label on this field
   * so every surface agrees with the router.
   */
  expectedSettlementPayoutMethod?: Maybe<PayoutMethod>;
  /**
   * Which fee product a FEE activity is, resolved from the fee-debit
   * transfer's idempotency-key prefix (see nickelBalance/feeKinds.ts):
   * 'CARD_PROCESSING' for the NB card-processing fee debit, 'CARD_FEE_CREDIT'
   * for a platform fee given back into the balance (refundFeeCli). A String (not an
   * enum) so a new fee kind is not a frontend/backend deploy gate — `source`
   * set this precedent. Null for non-FEE activities, and for FEE rows whose
   * transfer is missing or carries an unrecognized prefix (clients fall back
   * to the card-processing display copy).
   */
  feeKind?: Maybe<Scalars['String']['output']>;
  /**
   * The receivable Payment whose card charge incurred a FEE activity's fee —
   * resolved through the fee-debit transfer's charge (transfer.chargeId →
   * Charge.paymentId). Deliberately a separate field from `payment` (which
   * stays RECEIVABLE-only) so feed dispatch on `payment` presence keeps
   * treating FEE rows as NB rows. Null for non-FEE activities, and for FEE
   * rows whose transfer/charge link is missing.
   */
  feePayment?: Maybe<Payment>;
  flags?: Maybe<Array<Scalars['String']['output']>>;
  id: Scalars['String']['output'];
  /**
   * Multi-account: which of the org's Nickel Balance accounts this row belongs
   * to — the same attribution `paymentActivities(nickelBalanceAccountId:)`
   * filters on, so a row resolves account X exactly when X's slice contains
   * it. NB ledger rows (DEPOSIT / WITHDRAWAL / INTEREST / FEE) by the account
   * their posting or transfer sits on; NB-funded bill payments by the bill's
   * funding account; AR settlements and clawbacks by the account their payout
   * settled into; and, while still PENDING and unpaid, receivables and vendor
   * reversals by the account the settlement forecast lands on (the same
   * forecast `expectedSettlementPayoutMethod` reports). Null for rows that
   * are not Nickel Balance money — bank-rail settlements, card- or bank-funded
   * bills, a chargeback not yet netted against a payout, a receivable that
   * has settled but not yet paid out.
   * An internal transfer between two of the org's accounts names the account
   * the money left.
   */
  nickelBalanceAccount?: Maybe<NickelBalanceAccountRef>;
  /**
   * The live card authorization behind an NB activity that is a card hold and
   * nothing else yet — no posting, no transfer. Render amount, merchant and
   * date from it until `nickelBalanceTransaction` resolves, at which point this
   * goes null and the posting is authoritative (the settled amount can differ
   * from the hold). Null for every other row.
   */
  nickelBalancePendingTransaction?: Maybe<NickelBalancePendingTransaction>;
  /** The underlying Nickel Balance transaction for an NB-sourced activity (INTEREST, DEPOSIT, or WITHDRAWAL); null for other sources and for transfer-anchored deposits that haven't settled yet. */
  nickelBalanceTransaction?: Maybe<NickelBalanceTransaction>;
  /** The underlying Nickel Balance transfer for a transfer-anchored NB activity (merchant-initiated deposits); null for other sources and for transaction-anchored NB activities. Pending deposits carry only this — render amount/status from it until nickelBalanceTransaction resolves. */
  nickelBalanceTransfer?: Maybe<NickelBalanceTransfer>;
  note?: Maybe<ActivityNote>;
  payment?: Maybe<Payment>;
  payout?: Maybe<Payout>;
  refund?: Maybe<Refund>;
  /**
   * Which feed source this row is — an ActivitySource value (RECEIVABLE,
   * PAYABLE, ..., INTEREST, DEPOSIT, WITHDRAWAL, FEE). A String (not the
   * PaymentActivitySource enum) because rows can carry sources the filter enum
   * doesn't accept (LOAN). Lets clients dispatch rendering without inferring
   * the type from which sub-entity resolved.
   */
  source: Scalars['String']['output'];
  status: Scalars['String']['output'];
};

export enum PaymentActivitySource {
  Chargeback = 'CHARGEBACK',
  ChargebackWon = 'CHARGEBACK_WON',
  Deposit = 'DEPOSIT',
  /**
   * A Nickel-charged fee debited from the merchant's Nickel Balance (today: the
   * card-processing fee on a merchant-absorbed-fee payment settling to NB).
   * Transfer-anchored — render the amount from `nickelBalanceTransfer.amountCents`.
   */
  Fee = 'FEE',
  /**
   * Nickel Balance money movements, with detail on the `nickelBalanceTransaction`
   * field. INTEREST = interest share paid to the merchant; DEPOSIT = funds in
   * (ACH push/pull, accepted check deposit); WITHDRAWAL = funds out (outbound ACH).
   *
   * These and FEE are the account's own ledger, read under
   * `banking.account.see_balance`: a caller without it is refused a page
   * filtered to one of them, and the unfiltered feed leaves their rows out.
   */
  Interest = 'INTEREST',
  Payable = 'PAYABLE',
  Receivable = 'RECEIVABLE',
  Refund = 'REFUND',
  Return = 'RETURN',
  Withdrawal = 'WITHDRAWAL'
}

export type PaymentAllocation = {
  __typename?: 'PaymentAllocation';
  allocation?: Maybe<InvoiceAllocation>;
  payment?: Maybe<InvoicePayment>;
};

export type PaymentAttempt = {
  __typename?: 'PaymentAttempt';
  attemptedBy?: Maybe<User>;
  companyName?: Maybe<Scalars['String']['output']>;
  externalChargeId?: Maybe<Scalars['String']['output']>;
  failureMessage?: Maybe<Scalars['String']['output']>;
  fullName?: Maybe<Scalars['String']['output']>;
  id: Scalars['String']['output'];
  notes?: Maybe<Scalars['String']['output']>;
  status?: Maybe<Scalars['String']['output']>;
};

export type PaymentFeeDebit = {
  __typename?: 'PaymentFeeDebit';
  /** What the debit takes, in cents: the merchant's share of the card fee. */
  amountInCents: Scalars['Int']['output'];
  /**
   * The transfer that collects it. Null while SCHEDULED, and for a debit
   * Nickel stopped collecting before sending one.
   */
  payout?: Maybe<Payout>;
  status: PaymentFeeDebitStatus;
};

export enum PaymentFeeDebitStatus {
  /** The debit didn't go through, and the next payout run sends it again. */
  Failed = 'FAILED',
  Paid = 'PAID',
  /** Sent, and not yet collected. */
  Pending = 'PENDING',
  /** Not sent yet: it goes out with the payout run after the payment settles. */
  Scheduled = 'SCHEDULED',
  /** Nickel stopped sending this debit, after a return it can't retry or too many returns. */
  Uncollected = 'UNCOLLECTED'
}

export type PaymentHistory = {
  __typename?: 'PaymentHistory';
  date: Scalars['String']['output'];
  totalSubmittedCents: Scalars['Int']['output'];
};

export type PaymentHistoryResponse = {
  __typename?: 'PaymentHistoryResponse';
  billPayments?: Maybe<Array<PaymentHistory>>;
  error?: Maybe<Error>;
  payments?: Maybe<Array<PaymentHistory>>;
  totalResults?: Maybe<Scalars['Int']['output']>;
};

export type PaymentMethod = {
  __typename?: 'PaymentMethod';
  achDebit?: Maybe<AchDetails>;
  billingDetails?: Maybe<BillingDetails>;
  card?: Maybe<CardDetails>;
  /**
   * The QuickBooks chart-of-account this method posts to, and the account name
   * as of the last link.
   */
  chartOfAccountId?: Maybe<Scalars['String']['output']>;
  chartOfAccountName?: Maybe<Scalars['String']['output']>;
  /**
   * Proof the payer may pay with this saved method at checkout: pass it to
   * updatePayment as paymentMethodCheckoutToken. Set only by
   * getAllPaymentMethods (the payer's remember-me list) and
   * createACHPaymentMethod (the method they just entered); null everywhere
   * else, so an externalPaymentMethodId read elsewhere charges nothing.
   */
  checkoutToken?: Maybe<Scalars['String']['output']>;
  companyName?: Maybe<Scalars['String']['output']>;
  createdAt?: Maybe<Scalars['Float']['output']>;
  externalPaymentMethodId: Scalars['String']['output'];
  id: Scalars['String']['output'];
  /**
   * Only populated by getAllMerchantPaymentMethods; null on other queries.
   * Marks the funding method the org chose as its bill-pay default
   * (setDefaultBillPaymentMethod), or the first method in the list (oldest
   * bank account, else oldest card) when it has chosen none or its choice is
   * gone (removed, or a Nickel Balance account closed or switched off for bill
   * pay). When the choice is a
   * Nickel Balance account the list leaves out (not asked for, not shown to
   * the caller, unable to fund bills right now), no method is marked and
   * clients take the first.
   */
  isDefault?: Maybe<Scalars['Boolean']['output']>;
  latestCharge?: Maybe<Charge>;
  name?: Maybe<Scalars['String']['output']>;
  nickelBalance?: Maybe<NickelBalanceDetails>;
  /**
   * Multi-account: the Nickel Balance account a `nickel_balance` method draws
   * from, as its label alone — the nickname the merchant gave it and the last
   * four. Null on card and bank methods. Unlike `nickelBalance` (populated on
   * one query, and only for its own picker) this resolves wherever a
   * PaymentMethod does, so every surface that names the funding account —
   * the bill-pay pickers, a paid bill's detail, a recurring series — names it
   * the same way the Nickel Balance pages do.
   */
  nickelBalanceAccount?: Maybe<NickelBalanceAccountRef>;
  nickname?: Maybe<Scalars['String']['output']>;
  provider?: Maybe<Scalars['String']['output']>;
  type: Scalars['String']['output'];
};

export type PaymentMethodDetail = {
  __typename?: 'PaymentMethodDetail';
  brand?: Maybe<Scalars['String']['output']>;
  expiresOn?: Maybe<Scalars['String']['output']>;
  lastFour?: Maybe<Scalars['String']['output']>;
};

export type PaymentMethodPlaidData = {
  __typename?: 'PaymentMethodPlaidData';
  accountNumber?: Maybe<Scalars['String']['output']>;
  bankName?: Maybe<Scalars['String']['output']>;
  lastFour?: Maybe<Scalars['String']['output']>;
  paymentMethodId?: Maybe<Scalars['String']['output']>;
  paymentMethodName?: Maybe<Scalars['String']['output']>;
  paymentMethodType?: Maybe<Scalars['String']['output']>;
  plaidAccountInfo?: Maybe<Array<PlaidAccountInfo>>;
  plaidTransactions?: Maybe<Array<PlaidTransaction>>;
};

export type PaymentMethodRequest = {
  __typename?: 'PaymentMethodRequest';
  ccTo: Array<Scalars['String']['output']>;
  /** When the customer saved the payment method, in epoch milliseconds. Set only on COMPLETED and REMOVED. */
  completedAt?: Maybe<Scalars['Float']['output']>;
  /** The request's debit authorization id */
  id: Scalars['String']['output'];
  memo?: Maybe<Scalars['String']['output']>;
  /** The rails the customer may authorize through the request's link */
  methodTypes: Array<DebitAuthorizationMethodType>;
  /** What the customer saved through it. Set only on COMPLETED and REMOVED. */
  paymentMethod?: Maybe<PaymentMethod>;
  /** Who sent the request; scoped as DebitAuthorization.requestedBy is */
  requestedBy?: Maybe<User>;
  retailCustomer: RetailCustomer;
  /** When the request was first sent, in epoch milliseconds */
  sentAt: Scalars['Float']['output'];
  /** Every address the request was emailed to. Empty when the merchant only copied the link. */
  sentTo: Array<Scalars['String']['output']>;
  status: PaymentMethodRequestStatus;
};

/**
 * Where a payment-method request stands. A request is a debit authorization
 * created by emailing (or copying) the customer a link to save a bank account or
 * card, as opposed to one the merchant entered directly.
 */
export enum PaymentMethodRequestStatus {
  /** Withdrawn before the customer completed it: cancelled by the merchant or declined by the customer, or replaced by a newer request to the same customer */
  Cancelled = 'CANCELLED',
  /** The customer saved a payment method through it, and it is still on file */
  Completed = 'COMPLETED',
  /** Sent, and waiting on the customer: not completed yet, or completed and then withdrawn by the customer to redo it */
  Pending = 'PENDING',
  /** The customer saved a payment method through it that is no longer on file: removed by the merchant or deleted by the customer, retired after a bank return, or past its expiration date */
  Removed = 'REMOVED'
}

export type PaymentMethodRequestsResponse = {
  __typename?: 'PaymentMethodRequestsResponse';
  error?: Maybe<Error>;
  requests?: Maybe<Array<PaymentMethodRequest>>;
  totalResults?: Maybe<Scalars['Int']['output']>;
};

export type PaymentMethodResponse = {
  __typename?: 'PaymentMethodResponse';
  error?: Maybe<Error>;
  paymentMethod?: Maybe<PaymentMethod>;
};

export type PaymentRequest = {
  __typename?: 'PaymentRequest';
  achEnabled?: Maybe<Scalars['Boolean']['output']>;
  approvalStatus?: Maybe<AccountApprovalStatus>;
  /**
   * Whether the payer may pay by ACH credit, domestic wire, RTP or FedNow. When
   * true and the invoice is open, bankTransferInstructions is non-null.
   */
  bankTransferEnabled: Scalars['Boolean']['output'];
  bankTransferInstructions?: Maybe<BankTransferInstructions>;
  /**
   * True only outside production: the checkout may fake the payer's bank for
   * testing (simulateBankTransferCredit).
   */
  bankTransferSimulationAvailable: Scalars['Boolean']['output'];
  checkoutPath: Scalars['String']['output'];
  completedAmountInCents: Scalars['Int']['output'];
  creditCardEnabled?: Maybe<Scalars['Boolean']['output']>;
  /**
   * The live debit memos on this request, oldest first, for the payment page to
   * list under the invoice amount: requestedAmount already includes them, so
   * without the list the customer sees a total that does not match the invoice
   * they were sent. See docs/external-payments/debit-memos.md. Always a list
   * (empty when there are none); nullable only so that clients whose types are
   * built from PaymentRequest itself do not all have to supply it.
   */
  debitMemos?: Maybe<Array<PaymentRequestDebitMemo>>;
  documents?: Maybe<Array<Scalars['String']['output']>>;
  dueDate?: Maybe<Scalars['Float']['output']>;
  feePassthroughPercent?: Maybe<Scalars['Int']['output']>;
  id: Scalars['String']['output'];
  initialPayment?: Maybe<Payment>;
  /**
   * True when this pending merchant may take payment on this request on the light
   * path, so the portal offers checkout though approvalStatus is not APPROVED.
   * False for every other merchant, whatever its approvalStatus.
   */
  lightPathCheckoutOpen?: Maybe<Scalars['Boolean']['output']>;
  memo?: Maybe<Scalars['String']['output']>;
  merchantInOnboarding?: Maybe<Scalars['Boolean']['output']>;
  name: Scalars['String']['output'];
  notification?: Maybe<Notification>;
  /** Requested minus paid minus processing, never below 0. */
  owedAmountInCents: Scalars['Int']['output'];
  /**
   * Derived from this invoice's payments on every read, not from
   * completedAmountInCents: SUCCEEDED payments, the kept share of a partly
   * refunded one (not on an invoice synced from accounting, whose requested
   * amount already excludes it), and non-voided external payment records. See
   * docs/payment-status/design.md § Phase 1.
   */
  paidAmountInCents: Scalars['Int']['output'];
  payments?: Maybe<Array<Payment>>;
  /**
   * PENDING payments with money on a rail. A PENDING payment with no charge
   * counts nowhere.
   */
  processingAmountInCents: Scalars['Int']['output'];
  recurringInvoice?: Maybe<RecurringInvoice>;
  requestedAmount: Scalars['Int']['output'];
  retailCustomer?: Maybe<RetailCustomer>;
  scheduledPayment?: Maybe<Payment>;
  schema: Scalars['String']['output'];
  status?: Maybe<QuestionFormStatus>;
  threeDSEnabled?: Maybe<Scalars['Boolean']['output']>;
  thumbnail?: Maybe<Scalars['String']['output']>;
  withdrawalDate?: Maybe<Scalars['Float']['output']>;
};

/**
 * A debit memo as the payment page shows it to the customer: what was added to
 * the invoice, and why. Deliberately narrower than ArDebitMemo — no reference,
 * no recording user, no voided memos.
 */
export type PaymentRequestDebitMemo = {
  __typename?: 'PaymentRequestDebitMemo';
  amountInCents: Scalars['Int']['output'];
  createdAt: Scalars['Float']['output'];
  description: Scalars['String']['output'];
  id: Scalars['String']['output'];
};

export type PaymentRequestResponse = {
  __typename?: 'PaymentRequestResponse';
  error?: Maybe<Error>;
  paymentRequest?: Maybe<PaymentRequest>;
};

export type PaymentResponse = {
  __typename?: 'PaymentResponse';
  error?: Maybe<Error>;
  payment?: Maybe<Payment>;
};

export type PaymentSettings = {
  __typename?: 'PaymentSettings';
  achEnabled: Scalars['Boolean']['output'];
  automaticQbInvoiceEmail?: Maybe<Scalars['Boolean']['output']>;
  creditCardEnabled: Scalars['Boolean']['output'];
  feePassthroughPercent: Scalars['Int']['output'];
  invoiceMemoTemplate?: Maybe<Scalars['String']['output']>;
  paymentAmountEditable: Scalars['Boolean']['output'];
  /**
   * Whether a payment link lists the customer's other unpaid invoices from the
   * organization ("You have N additional unpaid invoices"). When false a payer
   * receives none of them: RetailCustomer.questionForms is empty to every caller
   * outside the organization.
   */
  showOtherUnpaidInvoices: Scalars['Boolean']['output'];
  /** Always null. Two-factor login is the user's, not the organization's: read User.twoFactorRequired. */
  twoFactorRequired?: Maybe<Scalars['Boolean']['output']>;
};

export type PaymentStatus = {
  __typename?: 'PaymentStatus';
  status: Scalars['String']['output'];
  substatus?: Maybe<Scalars['String']['output']>;
};

export type PaymentSummary = {
  __typename?: 'PaymentSummary';
  total: Scalars['Int']['output'];
  totalSubmitted: Scalars['Int']['output'];
};

export type PaymentsSummaryResponse = {
  __typename?: 'PaymentsSummaryResponse';
  error?: Maybe<Error>;
  summary: PaymentSummary;
};

export type Payout = {
  __typename?: 'Payout';
  amountInCents: Scalars['Int']['output'];
  charges?: Maybe<Array<Charge>>;
  createdAt: Scalars['Float']['output'];
  currency?: Maybe<Scalars['String']['output']>;
  description?: Maybe<Scalars['String']['output']>;
  externalPayoutId?: Maybe<Scalars['String']['output']>;
  failureCode?: Maybe<Scalars['String']['output']>;
  /**
   * Human-readable description of `failureCode` (an ACH return code, e.g.
   * "R01"). Null when no code was recorded — payouts that failed before the
   * code was persisted, and transfers canceled before submission, carry no
   * reason.
   */
  failureMessage?: Maybe<Scalars['String']['output']>;
  fees?: Maybe<Array<Fee>>;
  id: Scalars['String']['output'];
  /** What the payout moves: a payment's deposit, or a debit taking money back. */
  kind: PayoutKind;
  paidAt?: Maybe<Scalars['String']['output']>;
  /**
   * The funding method of the bill payment this payout refunds, set only on
   * the merchant refund for a returned vendor payout. A bill funded by
   * Increase ACH is refunded by reversing that pay-in, so the money lands on
   * this method and `payoutMethod` is null; a card-funded bill has no pay-in
   * to reverse and is refunded to `payoutMethod`, which then names the
   * destination. Read `payoutMethod` first and this second.
   */
  payableReturnPaymentMethod?: Maybe<PaymentMethod>;
  payoutMethod?: Maybe<PayoutMethod>;
  refunds?: Maybe<Array<Refund>>;
  returns?: Maybe<Array<Return>>;
  status?: Maybe<Scalars['String']['output']>;
  updatedAt?: Maybe<Scalars['Float']['output']>;
};

export enum PayoutKind {
  /** A disputed card payment taken back from the merchant. */
  Chargeback = 'CHARGEBACK',
  /** A won dispute paid back to the merchant. */
  ChargebackWon = 'CHARGEBACK_WON',
  /** The merchant's share of a card payment's fee, debited separately. */
  FeeDebit = 'FEE_DEBIT',
  /** A returned vendor payout credited back to the merchant. */
  PayableReturn = 'PAYABLE_RETURN',
  /** A payment's deposit to the merchant. */
  Payment = 'PAYMENT',
  /** A refund taken back from the merchant. */
  Refund = 'REFUND',
  /** A returned ACH payment taken back from the merchant. */
  Return = 'RETURN'
}

/**
 * A destination AR payouts settle into: a linked bank account (`type` "ach")
 * or one of the org's Nickel Balance accounts (`type` "nickel_balance", one
 * row per account). Branch on `type` before reading the bank-detail fields.
 */
export type PayoutMethod = {
  __typename?: 'PayoutMethod';
  /** The last four of the bank account number (Plaid mask when linked through Plaid), or of the Nickel Balance account number. */
  accountNumberLastFour?: Maybe<Scalars['String']['output']>;
  accountNumberLastTwo?: Maybe<Scalars['String']['output']>;
  accountType?: Maybe<Scalars['String']['output']>;
  /** Multi-account: for nickel_balance methods, the Nickel Balance account settled into; null on bank methods. */
  balanceAccountId?: Maybe<Scalars['ID']['output']>;
  bankName?: Maybe<Scalars['String']['output']>;
  externalPayoutMethodId: Scalars['String']['output'];
  id: Scalars['String']['output'];
  /**
   * Whether the destination can still be credited: a bank link that has not
   * expired (Plaid revocation and number rotation expire the superseded rows),
   * or a Nickel Balance account that finished provisioning. A selected method
   * that is no longer live is skipped at payout time in favour of the newest
   * live bank row.
   */
  live?: Maybe<Scalars['Boolean']['output']>;
  name: Scalars['String']['output'];
  /** For nickel_balance methods, the account's nickname (null when unnamed); null on bank methods. */
  nickname?: Maybe<Scalars['String']['output']>;
  plaidMask?: Maybe<Scalars['String']['output']>;
  routingNumber?: Maybe<Scalars['String']['output']>;
  type: Scalars['String']['output'];
};

/**
 * Which surface initiated createPayoutMethodWithPlaid. SETTINGS and
 * NICKEL_BALANCE only tailor the admin-notification copy (NICKEL_BALANCE means
 * the merchant is ADDING an additional withdrawal destination, not replacing
 * their payout account); RFI also changes who may call and who is told.
 */
export enum PayoutMethodCreationSource {
  NickelBalance = 'NICKEL_BALANCE',
  /**
   * Answering an open RFI's PRIMARY_BANK_ACCOUNT item. Refused unless the org
   * has one open; otherwise any member may link (it is still onboarding), the
   * merchant gets no "bank account changed" notice, and #signups is told
   * instead of #payment-monitoring.
   */
  Rfi = 'RFI',
  Settings = 'SETTINGS'
}

export type PayoutResponse = {
  __typename?: 'PayoutResponse';
  error?: Maybe<Error>;
  payout: Payout;
};

export type PayoutsResponse = {
  __typename?: 'PayoutsResponse';
  error?: Maybe<Error>;
  payouts?: Maybe<Array<Payout>>;
  totalResults?: Maybe<Scalars['Int']['output']>;
};

export type PennyActiveWebVisit = {
  __typename?: 'PennyActiveWebVisit';
  computer?: Maybe<PennyComputerSession>;
  queuedAt: Scalars['Float']['output'];
  siteUrl: Scalars['String']['output'];
  status: PennyWebVisitStatus;
  stopRequested: Scalars['Boolean']['output'];
  visitId: Scalars['ID']['output'];
};

export type PennyActiveWebVisitResponse = {
  __typename?: 'PennyActiveWebVisitResponse';
  error?: Maybe<Error>;
  visit?: Maybe<PennyActiveWebVisit>;
};

export enum PennyAdminReplyStatus {
  Failed = 'FAILED',
  Queued = 'QUEUED',
  Sent = 'SENT'
}

export enum PennyChannel {
  Email = 'EMAIL',
  Sms = 'SMS',
  Web = 'WEB'
}

export enum PennyChatStatus {
  Active = 'ACTIVE',
  Cancelled = 'CANCELLED',
  Completed = 'COMPLETED',
  Failed = 'FAILED',
  Pending = 'PENDING'
}

export enum PennyComputerAction {
  ApprovePayment = 'APPROVE_PAYMENT',
  DeclinePayment = 'DECLINE_PAYMENT',
  HandBack = 'HAND_BACK',
  HandBackPaid = 'HAND_BACK_PAID',
  Stop = 'STOP',
  TakeOver = 'TAKE_OVER'
}

export type PennyComputerSession = {
  __typename?: 'PennyComputerSession';
  approvalAmountCents?: Maybe<Scalars['Int']['output']>;
  approvalId?: Maybe<Scalars['ID']['output']>;
  approvalPreviousCents?: Maybe<Scalars['Int']['output']>;
  paymentApprovedCents?: Maybe<Scalars['Int']['output']>;
  reason?: Maybe<Scalars['String']['output']>;
  siteUrl: Scalars['String']['output'];
  status: PennyComputerStatus;
  viewerUrl?: Maybe<Scalars['String']['output']>;
  visitId: Scalars['ID']['output'];
};

export enum PennyComputerStatus {
  ApprovalRequested = 'APPROVAL_REQUESTED',
  Ended = 'ENDED',
  TakeoverRequested = 'TAKEOVER_REQUESTED',
  UserControl = 'USER_CONTROL',
  Watching = 'WATCHING'
}

export type PennyConversationDetails = {
  __typename?: 'PennyConversationDetails';
  channel: PennyChannel;
  chatStatus: PennyChatStatus;
  createdAt: Scalars['Float']['output'];
  id: Scalars['ID']['output'];
  lastMessageAt: Scalars['Float']['output'];
  messages: Array<PennyMessage>;
  pendingProposal?: Maybe<PennyProposal>;
  plans: Array<PennyPendingPlan>;
  runInFlight: Scalars['Boolean']['output'];
  subject: Scalars['String']['output'];
  supportHandoffs: Array<PennySupportHandoff>;
};

export type PennyConversationEvent = {
  __typename?: 'PennyConversationEvent';
  computer?: Maybe<PennyComputerSession>;
  kind: PennyConversationEventKind;
  messageId?: Maybe<Scalars['ID']['output']>;
  planId?: Maybe<Scalars['ID']['output']>;
  text?: Maybe<Scalars['String']['output']>;
  tool?: Maybe<Scalars['String']['output']>;
  trigger?: Maybe<Scalars['String']['output']>;
};

export enum PennyConversationEventKind {
  Activity = 'ACTIVITY',
  Computer = 'COMPUTER',
  Delta = 'DELTA',
  Message = 'MESSAGE',
  Plan = 'PLAN',
  RunDone = 'RUN_DONE',
  RunError = 'RUN_ERROR',
  RunStarted = 'RUN_STARTED'
}

export type PennyConversationFilter = {
  search?: InputMaybe<Scalars['String']['input']>;
  statuses?: InputMaybe<Array<PennyChatStatus>>;
};

export type PennyConversationListItem = {
  __typename?: 'PennyConversationListItem';
  channel: PennyChannel;
  chatStatus: PennyChatStatus;
  createdAt: Scalars['Float']['output'];
  id: Scalars['ID']['output'];
  lastMessageAt: Scalars['Float']['output'];
  pendingPlanCount: Scalars['Int']['output'];
  preview: Scalars['String']['output'];
  subject: Scalars['String']['output'];
};

export type PennyConversationResponse = {
  __typename?: 'PennyConversationResponse';
  conversation?: Maybe<PennyConversationDetails>;
  error?: Maybe<Error>;
};

export type PennyMessage = {
  __typename?: 'PennyMessage';
  adminReplyStatus?: Maybe<PennyAdminReplyStatus>;
  adminSentBy?: Maybe<Scalars['String']['output']>;
  attachments: Array<PennyMessageAttachment>;
  body: Scalars['String']['output'];
  ccEmails: Array<Scalars['String']['output']>;
  channel: PennyChannel;
  createdAt: Scalars['Float']['output'];
  direction: PennyMessageDirection;
  fromEmail: Scalars['String']['output'];
  fromName: Scalars['String']['output'];
  id: Scalars['ID']['output'];
  role: PennyMessageRole;
  runActivity?: Maybe<PennyRunActivity>;
  toEmails: Array<Scalars['String']['output']>;
};

export type PennyMessageAttachment = {
  __typename?: 'PennyMessageAttachment';
  downloadUrl: Scalars['String']['output'];
  filename: Scalars['String']['output'];
  id: Scalars['ID']['output'];
  mimeType: Scalars['String']['output'];
  sizeBytes: Scalars['Int']['output'];
};

export type PennyMessageAttachmentInput = {
  filename: Scalars['String']['input'];
  mimeType: Scalars['String']['input'];
  sizeBytes: Scalars['Int']['input'];
  uploadKey: Scalars['String']['input'];
};

export enum PennyMessageDirection {
  Inbound = 'INBOUND',
  Outbound = 'OUTBOUND'
}

export enum PennyMessageRole {
  Assistant = 'ASSISTANT',
  User = 'USER'
}

export type PennyPendingPlan = {
  __typename?: 'PennyPendingPlan';
  confirmedAt?: Maybe<Scalars['Float']['output']>;
  conversationId: Scalars['ID']['output'];
  createdAt: Scalars['Float']['output'];
  /**
   * Scheduled execution time (epoch millis). When set, confirming the plan
   * does not run it immediately — the due-plan sweep enqueues execution once
   * this time passes. Null = executes immediately on confirmation.
   */
  executeAt?: Maybe<Scalars['Float']['output']>;
  executedAt?: Maybe<Scalars['Float']['output']>;
  expiresAt: Scalars['Float']['output'];
  id: Scalars['ID']['output'];
  result?: Maybe<PennyPlanResult>;
  status: PennyPendingPlanStatus;
  steps: Array<PennyPendingPlanStep>;
  summary: Scalars['String']['output'];
};

export type PennyPendingPlanResponse = {
  __typename?: 'PennyPendingPlanResponse';
  error?: Maybe<Error>;
  plan?: Maybe<PennyPendingPlan>;
};

export enum PennyPendingPlanStatus {
  Cancelled = 'CANCELLED',
  Confirmed = 'CONFIRMED',
  Executed = 'EXECUTED',
  Executing = 'EXECUTING',
  Expired = 'EXPIRED',
  Failed = 'FAILED',
  PartiallyFailed = 'PARTIALLY_FAILED',
  Pending = 'PENDING',
  Superseded = 'SUPERSEDED'
}

export type PennyPendingPlanStep = {
  __typename?: 'PennyPendingPlanStep';
  args: Scalars['String']['output'];
  description: Scalars['String']['output'];
  descriptionSegments: Array<PennyPlanStepSegment>;
  id: Scalars['ID']['output'];
  status: PennyPendingPlanStepStatus;
  stepIndex: Scalars['Int']['output'];
  tool: Scalars['String']['output'];
};

export enum PennyPendingPlanStepStatus {
  Executed = 'EXECUTED',
  Failed = 'FAILED',
  Pending = 'PENDING',
  Skipped = 'SKIPPED'
}

export type PennyPlanResult = {
  __typename?: 'PennyPlanResult';
  detail?: Maybe<Scalars['String']['output']>;
  links: Array<PennyPlanResultLink>;
  message: Scalars['String']['output'];
  status: PennyPlanResultStatus;
};

export type PennyPlanResultLink = {
  __typename?: 'PennyPlanResultLink';
  label: Scalars['String']['output'];
  /**
   * The `stepIndex` of the plan step that created or acted on the record, so a
   * client can show the link on that step. Null when that step is not on the plan.
   */
  stepIndex?: Maybe<Scalars['Int']['output']>;
  target: Scalars['String']['output'];
  url: Scalars['String']['output'];
};

export enum PennyPlanResultStatus {
  Failed = 'FAILED',
  Success = 'SUCCESS'
}

export type PennyPlanStepSegment = {
  __typename?: 'PennyPlanStepSegment';
  emphasis?: Maybe<Scalars['String']['output']>;
  text: Scalars['String']['output'];
};

export type PennyProposal = {
  __typename?: 'PennyProposal';
  chosenOptionIndex?: Maybe<Scalars['Int']['output']>;
  createdAt: Scalars['Float']['output'];
  expiresAt: Scalars['Float']['output'];
  id: Scalars['ID']['output'];
  messageId?: Maybe<Scalars['ID']['output']>;
  options: Array<PennyProposalOption>;
  status: PennyProposalStatus;
};

export type PennyProposalOption = {
  __typename?: 'PennyProposalOption';
  label: Scalars['String']['output'];
  optionIndex: Scalars['Int']['output'];
  plan?: Maybe<PennyPendingPlan>;
};

export type PennyProposalResponse = {
  __typename?: 'PennyProposalResponse';
  error?: Maybe<Error>;
  proposal?: Maybe<PennyProposal>;
};

export enum PennyProposalStatus {
  Decided = 'DECIDED',
  Declined = 'DECLINED',
  Expired = 'EXPIRED',
  Pending = 'PENDING',
  Superseded = 'SUPERSEDED',
  Withdrawn = 'WITHDRAWN'
}

export type PennyRunActivity = {
  __typename?: 'PennyRunActivity';
  completedAt: Scalars['Float']['output'];
  startedAt: Scalars['Float']['output'];
  steps: Array<PennyRunStep>;
};

export type PennyRunStep = {
  __typename?: 'PennyRunStep';
  at: Scalars['Float']['output'];
  tool: Scalars['String']['output'];
};

export type PennySupportHandoff = {
  __typename?: 'PennySupportHandoff';
  createdAt: Scalars['Float']['output'];
  id: Scalars['ID']['output'];
  kind: PennySupportHandoffKind;
  prefillMessage: Scalars['String']['output'];
  reason: Scalars['String']['output'];
};

export enum PennySupportHandoffKind {
  FeatureRequest = 'FEATURE_REQUEST',
  Support = 'SUPPORT'
}

export type PennyVisitPayer = {
  __typename?: 'PennyVisitPayer';
  city?: Maybe<Scalars['String']['output']>;
  email?: Maybe<Scalars['String']['output']>;
  name?: Maybe<Scalars['String']['output']>;
  state?: Maybe<Scalars['String']['output']>;
  street?: Maybe<Scalars['String']['output']>;
  zip?: Maybe<Scalars['String']['output']>;
};

export enum PennyVisitPaymentOutcome {
  Declined = 'DECLINED',
  NotSubmitted = 'NOT_SUBMITTED',
  Paid = 'PAID',
  PaidByMerchant = 'PAID_BY_MERCHANT',
  Uncertain = 'UNCERTAIN'
}

export type PennyWebVisitReport = {
  answer: Scalars['String']['input'];
  answered: Scalars['Boolean']['input'];
  approvalId?: InputMaybe<Scalars['ID']['input']>;
  chargedCents?: InputMaybe<Scalars['Int']['input']>;
  confirmationNumber?: InputMaybe<Scalars['String']['input']>;
  errorMessage?: InputMaybe<Scalars['String']['input']>;
  finalUrl?: InputMaybe<Scalars['String']['input']>;
  paymentOutcome?: InputMaybe<PennyVisitPaymentOutcome>;
  paymentSubmitted: Scalars['Boolean']['input'];
  receiptFileId?: InputMaybe<Scalars['ID']['input']>;
  screenshotFileId?: InputMaybe<Scalars['ID']['input']>;
  userTookOver: Scalars['Boolean']['input'];
};

export enum PennyWebVisitStatus {
  Cancelled = 'CANCELLED',
  Ended = 'ENDED',
  Queued = 'QUEUED',
  Running = 'RUNNING'
}

export type PennyWebVisitWantedResponse = {
  __typename?: 'PennyWebVisitWantedResponse';
  error?: Maybe<Error>;
  wanted?: Maybe<Scalars['Boolean']['output']>;
};

/**
 * One entry of a role's grant map: a catalog key and its scope. The catalog
 * itself does not cross GraphQL — the dashboard imports PERMISSION_CATALOG from
 * @nickeltechnology/types, the same constant the API validates against.
 */
export type PermissionGrant = {
  __typename?: 'PermissionGrant';
  key: Scalars['String']['output'];
  scope: PermissionScope;
};

export type PermissionGrantInput = {
  key: Scalars['String']['input'];
  scope: PermissionScope;
};

/**
 * A grant's scope (docs/workspace-roles/design.md §3.2). V1 writes only ALL;
 * OWNED arrives with the ownership axis and is rejected at write time until then.
 */
export enum PermissionScope {
  All = 'ALL',
  Owned = 'OWNED'
}

/**
 * What Nickel's Phone Agent asks on a call about the org, and the numbers on
 * file to call (docs/underwriting/follow-up-calls.md §5). Staff can call any org;
 * the applicant's own offer is decided apart, by the AI underwriter's hold.
 */
export type PhoneAgent = {
  __typename?: 'PhoneAgent';
  editedAt?: Maybe<Scalars['String']['output']>;
  /** The staff member who wrote them; null for the AI underwriter's. */
  editedByName?: Maybe<Scalars['String']['output']>;
  /** Whether the latest summary is a hold with questions of the AI underwriter's own, to go back to. */
  hasAiQuestions: Scalars['Boolean']['output'];
  /** The bank record's numbers first when the AI underwriter routed its call there, then the application's. */
  numbers: Array<PhoneAgentNumber>;
  /** The agent's first words on a call staff start to the applicant, at any number; null while there are no questions. */
  opener?: Maybe<Scalars['String']['output']>;
  /** What every call about the org asks, in order. Empty when there is nothing to ask. */
  questions: Array<UnderwritingFollowUpQuestion>;
  /** Who wrote them; null when there are none. */
  source?: Maybe<PhoneAgentQuestionSource>;
};

/** A number on file the Phone Agent can call. */
export type PhoneAgentNumber = {
  __typename?: 'PhoneAgentNumber';
  /** Where the number comes from, for staff. */
  label: Scalars['String']['output'];
  /** The agent's first words on a call staff start to this number; null while there are no questions. */
  opener?: Maybe<Scalars['String']['output']>;
  phoneNumber: Scalars['String']['output'];
  /** Whose number it is, which changes what the agent may say. */
  target: FollowUpCallTarget;
};

/** Who wrote the questions the Phone Agent asks. */
export enum PhoneAgentQuestionSource {
  /** The AI underwriter, for the hold in the latest summary. */
  AiUnderwriter = 'AI_UNDERWRITER',
  /** Nickel staff, in place of the AI underwriter's. */
  Staff = 'STAFF'
}

/** Increase PhysicalCard.shipment.status, stored as-is. Delivery is a tracking update (IssuedCardShipment.deliveredAt), not a status. */
export enum PhysicalCardShipmentStatus {
  Acknowledged = 'acknowledged',
  Canceled = 'canceled',
  Pending = 'pending',
  Rejected = 'rejected',
  RequiresAttention = 'requires_attention',
  Returned = 'returned',
  Shipped = 'shipped',
  Submitted = 'submitted'
}

export type PlaidAccountInfo = {
  __typename?: 'PlaidAccountInfo';
  balance?: Maybe<Scalars['Float']['output']>;
  name?: Maybe<Scalars['String']['output']>;
  owners?: Maybe<Array<Maybe<Scalars['String']['output']>>>;
};

export type PlaidTransaction = {
  __typename?: 'PlaidTransaction';
  amount?: Maybe<Scalars['Float']['output']>;
  date?: Maybe<Scalars['String']['output']>;
  name?: Maybe<Scalars['String']['output']>;
};

export type PlatformPartnerAdmin = {
  __typename?: 'PlatformPartnerAdmin';
  accessEndsAt?: Maybe<Scalars['String']['output']>;
  createdAt: Scalars['String']['output'];
  enrollmentsPausedAt?: Maybe<Scalars['String']['output']>;
  id: Scalars['ID']['output'];
  keys: Array<PlatformPartnerAdminKey>;
  linkedMerchants: Array<PlatformPartnerAdminLink>;
  name: Scalars['String']['output'];
  returnUrls: Array<Scalars['String']['output']>;
  status: Scalars['String']['output'];
  suspendedAt?: Maybe<Scalars['String']['output']>;
  suspensionReason?: Maybe<Scalars['String']['output']>;
  webhooks: Array<PartnerWebhook>;
};

export type PlatformPartnerAdminKey = {
  __typename?: 'PlatformPartnerAdminKey';
  allowedIps: Array<Scalars['String']['output']>;
  createdAt: Scalars['String']['output'];
  id: Scalars['ID']['output'];
  lastUsedAt?: Maybe<Scalars['String']['output']>;
  lastUsedIp?: Maybe<Scalars['String']['output']>;
  name?: Maybe<Scalars['String']['output']>;
};

export type PlatformPartnerAdminLink = {
  __typename?: 'PlatformPartnerAdminLink';
  enrolled: Scalars['Boolean']['output'];
  id: Scalars['ID']['output'];
  linkedAt: Scalars['String']['output'];
  organizationId: Scalars['ID']['output'];
  organizationName: Scalars['String']['output'];
  partnerAccountRef: Scalars['String']['output'];
  status: Scalars['String']['output'];
};

export type PlatformPartnerAdminPayload = {
  __typename?: 'PlatformPartnerAdminPayload';
  error?: Maybe<Error>;
  partner?: Maybe<PlatformPartnerAdmin>;
};

export type PlatformPartnerAdminSummary = {
  __typename?: 'PlatformPartnerAdminSummary';
  createdAt: Scalars['String']['output'];
  id: Scalars['ID']['output'];
  linkedMerchantCount: Scalars['Int']['output'];
  liveKeyCount: Scalars['Int']['output'];
  name: Scalars['String']['output'];
  returnUrlCount: Scalars['Int']['output'];
  status: Scalars['String']['output'];
  webhookCount: Scalars['Int']['output'];
};

export type PlatformPartnersAdminPayload = {
  __typename?: 'PlatformPartnersAdminPayload';
  error?: Maybe<Error>;
  partners?: Maybe<Array<PlatformPartnerAdminSummary>>;
};

export type PortalGetLinkTokenResponse = {
  __typename?: 'PortalGetLinkTokenResponse';
  error?: Maybe<Error>;
  linkToken?: Maybe<Scalars['String']['output']>;
};

export type PrepareChargebackEvidenceUploadResponse = {
  __typename?: 'PrepareChargebackEvidenceUploadResponse';
  error?: Maybe<Error>;
  uploadKey?: Maybe<Scalars['String']['output']>;
  /** Presigned S3 PUT (1h), bound to the exact byte length. */
  uploadUrl?: Maybe<Scalars['String']['output']>;
};

export type PrepareCheckDepositUploadResponse = {
  __typename?: 'PrepareCheckDepositUploadResponse';
  error?: Maybe<Error>;
  key?: Maybe<Scalars['String']['output']>;
  uploadUrl?: Maybe<Scalars['String']['output']>;
};

export type PrepareFileUploadResponse = {
  __typename?: 'PrepareFileUploadResponse';
  error?: Maybe<Error>;
  /** What the mutations that take file ids take. */
  fileId?: Maybe<Scalars['ID']['output']>;
  /** Presigned S3 PUT URL, valid for one hour, bound to the declared length. */
  uploadUrl?: Maybe<Scalars['String']['output']>;
};

export type PreparePennyAttachmentUploadResponse = {
  __typename?: 'PreparePennyAttachmentUploadResponse';
  error?: Maybe<Error>;
  uploadKey?: Maybe<Scalars['String']['output']>;
  uploadUrl?: Maybe<Scalars['String']['output']>;
};

export type PreparePennyVisitCardResponse = {
  __typename?: 'PreparePennyVisitCardResponse';
  error?: Maybe<Error>;
  expirationMonth?: Maybe<Scalars['Int']['output']>;
  expirationYear?: Maybe<Scalars['Int']['output']>;
  payer?: Maybe<PennyVisitPayer>;
  revealUrl?: Maybe<Scalars['String']['output']>;
};

export type PreparePennyWebVisitCaptureResponse = {
  __typename?: 'PreparePennyWebVisitCaptureResponse';
  error?: Maybe<Error>;
  fileId?: Maybe<Scalars['ID']['output']>;
  uploadUrl?: Maybe<Scalars['String']['output']>;
};

export type ProcessUnsyncedTransactionsResponse = {
  __typename?: 'ProcessUnsyncedTransactionsResponse';
  error?: Maybe<Error>;
  ignoreCount?: Maybe<Scalars['Int']['output']>;
  success?: Maybe<Scalars['Boolean']['output']>;
  syncCount?: Maybe<Scalars['Int']['output']>;
};

export type ProfitAndLossReportSummary = {
  __typename?: 'ProfitAndLossReportSummary';
  expenses?: Maybe<Scalars['Float']['output']>;
  grossProfit?: Maybe<Scalars['Float']['output']>;
  income?: Maybe<Scalars['Float']['output']>;
  netIncome?: Maybe<Scalars['Float']['output']>;
  netOpIncome?: Maybe<Scalars['Float']['output']>;
};

/** What provisionAccount minted: the approved organization and its owner user. */
export type ProvisionAccountResponse = {
  __typename?: 'ProvisionAccountResponse';
  error?: Maybe<Error>;
  organization?: Maybe<Organization>;
  user?: Maybe<User>;
};

export type Query = {
  __typename?: 'Query';
  accountingSyncStatus?: Maybe<AccountingSyncStatusResponse>;
  achReturn?: Maybe<ReturnResponse>;
  achVendorReturn?: Maybe<VendorReturnResponse>;
  adminNickelBalanceAccounts: AdminNickelBalanceAccountsResponse;
  adminPennyChats: AdminPennyChatsResponse;
  adminPennyConversation: AdminPennyConversationResponse;
  answerForms?: Maybe<AnswerFormsResponse>;
  apiFile?: Maybe<ApiFileResponse>;
  apiTokens?: Maybe<ApiTokensResponse>;
  /** The org's active approval rules, for every action, as sentences. */
  approvalRuleSummaries?: Maybe<ApprovalRuleSummariesResponse>;
  /**
   * The org's current approval rules with each one's problems, for the rule
   * editor.
   */
  approvalRules?: Maybe<ApprovalRulesResponse>;
  /**
   * The org's bill approval requests, newest first. `approvalStatus` is one
   * status (PENDING, APPROVED, REJECTED, FAILED), or CLOSED for every decided
   * one (APPROVED, REJECTED, FAILED); omitted, all of those.
   */
  approvals?: Maybe<ApprovalsResponse>;
  /**
   * Nickel staff: the completed payment authorizations an id names, for the
   * authorization-records tool. `id` is an authorization id or a retail
   * customer id (every completed authorization of that customer).
   */
  authorizationRecordLookup: AuthorizationRecordLookupResponse;
  bankRoutingNumber?: Maybe<BankRoutingNumberResponse>;
  bill?: Maybe<BillResponse>;
  billPayable?: Maybe<BillPayableResponse>;
  billPayables?: Maybe<BillPayablesResponse>;
  billPayment?: Maybe<BillPaymentResponse>;
  bills?: Maybe<BillsResponse>;
  billsById?: Maybe<BillsResponse>;
  chargeback?: Maybe<ChargebackResponse>;
  /** The Disputes page. Kind CHARGEBACK only; optional status filter. */
  chargebacks: ChargebacksResponse;
  /**
   * The org's QuickBooks chart of accounts, filtered for a picker.
   * accountType: "bankAccount" (default — bank + other current asset),
   * "creditCard", "expense" (the fee-account picker, and on Xero the account
   * for AccountInfo.billExpenseChartOfAccount; in QuickBooks Online,
   * Classification 'Expense', which spans Expense, Other Expense and Cost of
   * Goods Sold), "unappliedPayment" (Xero only: income and liability
   * accounts, for AccountInfo.unappliedPaymentChartOfAccount), or
   * "invoiceSales" (Xero only: income accounts, for
   * AccountInfo.invoiceSalesChartOfAccount).
   */
  chartOfAccounts?: Maybe<ChartOfAccountsResponse>;
  checkApproval?: Maybe<CheckApprovalResponse>;
  checkApprovals?: Maybe<CheckApprovalsResponse>;
  checkCustomDomainAvailability?: Maybe<CheckCustomDomainAvailabilityResponse>;
  /** Who in the firm may enter one of its clients. Role managers only. */
  clientAccess: ClientAccessPage;
  conductorAuthenticate?: Maybe<ConductorAuthenticateResponse>;
  connectorCatalog?: Maybe<ConnectorCatalogResponse>;
  connectorConnections?: Maybe<ConnectorConnectionsResponse>;
  connectorRead?: Maybe<ConnectorReadResponse>;
  customDomainVerificationStatus?: Maybe<CustomDomainVerificationStatusResponse>;
  debitAuthorization?: Maybe<DebitAuthorizationResponse>;
  /**
   * The caller org's enterprise SSO config with live per-domain verification
   * state. Readable by any member of the org (only the SSO mutations require
   * `team_org.security.manage_sso`); null when the caller has no org or nothing
   * is configured.
   */
  enterpriseSsoConfig?: Maybe<EnterpriseSsoConfig>;
  enterpriseSsoForEmail?: Maybe<EnterpriseSsoInfo>;
  entriToken?: Maybe<EntriTokenResponse>;
  existingUserFromEmail?: Maybe<ExistingUserResponse>;
  /**
   * The firm's clients, for its Clients page and the client switcher, from a firm
   * session or an accountant session.
   */
  firmClients: FirmClientsPayload;
  /**
   * The follow-up call the caller's held application can take, for a faster
   * review; offer is null when there is none. Never says why: most reasons are
   * risk signals.
   */
  followUpCallOffer?: Maybe<FollowUpCallOfferPayload>;
  getAllMerchantPaymentMethods?: Maybe<AllPaymentMethodsResponse>;
  getAllPaymentMethods?: Maybe<AllPaymentMethodsResponse>;
  getClientToken?: Maybe<GetClientTokenResponse>;
  getDebitAuthorizationFromShortId?: Maybe<DebitAuthorizationResponse>;
  getPaymentMethod?: Maybe<PaymentMethodResponse>;
  getPaymentRequest?: Maybe<PaymentRequestResponse>;
  getQuickBooksClasses?: Maybe<QuickBooksClassesResponse>;
  getQuickBooksExpenseAccountsAndItems?: Maybe<QuickBooksExpenseAccountsAndItemsResponse>;
  /**
   * What a line of an invoice can be booked to in the organization's accounting
   * system: QuickBooks Online's revenue accounts and items, or for an
   * organization on Xero, its income accounts and the sold items whose sales
   * account is one of them, each by Xero's own id.
   */
  getQuickBooksRevenueAccountsAndItems?: Maybe<QuickBooksRevenueAccountsAndItemsResponse>;
  /**
   * Nickel staff only. Each org's HubSpot deal status, read live from the
   * warehouse mart (a daily batch, so up to a day old), for the approvals
   * table badge and the deny-flow second look. Nothing is stored.
   */
  hubspotDealStatuses?: Maybe<HubspotDealStatusesResponse>;
  /**
   * The organization's inbound bank transfers, newest first; `status` filters
   * (default: UNAPPLIED only).
   */
  inboundBankTransfers?: Maybe<InboundBankTransfersResponse>;
  integrationConnectionStatus?: Maybe<IntegrationConnectionStatusResponse>;
  /**
   * The org's held invoice actions, newest first: those the caller can decide,
   * those they asked for, and those on invoices inside their invoice view.
   */
  invoiceApprovalRequests?: Maybe<InvoiceApprovalRequestsResponse>;
  /**
   * An agent's tool call waiting for dashboard approval. Only the person whose
   * connection asked can read it.
   */
  mcpActionApproval?: Maybe<McpActionApprovalResponse>;
  /**
   * How many of the caller's agent tool calls wait for them to approve or
   * decline, and whether they have an agent connected. Only from a dashboard
   * session.
   */
  mcpActionApprovalSummary?: Maybe<McpActionApprovalSummaryResponse>;
  /**
   * The caller's agent tool calls still open: waiting for them to decide, or
   * approved and not yet run, and not expired. Newest first, at most 200. With
   * recentDays (1 to 30), followed by the calls asked in that many days that are
   * closed (run, declined or expired), newest first, at most 100. Only from a
   * dashboard session.
   */
  mcpActionApprovals?: Maybe<McpActionApprovalsResponse>;
  /**
   * The org's provisioned Nickel Balance accounts, oldest first; empty when
   * nothing is provisioned. `includeClosed` appends the accounts the merchant
   * has closed (oldest first, `closedAt` set, no full numbers); every other
   * read treats a closed account as gone.
   */
  nickelBalanceAccounts: Array<NickelBalanceAccount>;
  /** Trailing daily balance series, oldest first, ending with a live now-point: one account's when `balanceAccountId` is named, else every provisioned account's summed point-by-point (the overview chart). Empty when nothing is provisioned. The now-point is the live signed balance — unfloored, unlike the summary's availableBalanceCents. `days` is clamped to [1, 365]. */
  nickelBalanceAvailableHistory: Array<NickelBalanceAvailableHistoryPoint>;
  nickelBalanceCard?: Maybe<IssuedCard>;
  nickelBalanceCards: Array<IssuedCard>;
  nickelBalanceCheckDeposit?: Maybe<NickelBalanceCheckDeposit>;
  nickelBalanceCheckDeposits?: Maybe<Array<NickelBalanceCheckDeposit>>;
  /** An account's active allowed-company rules for Debit Protection, newest first. */
  nickelBalanceDebitAllowRules: Array<NickelBalanceDebitAllowRule>;
  /**
   * Companies that have debited the account (held, blocked or posted) and are
   * not already allowed, for picking one to always allow. `search` matches the
   * company name (contains) or Company ID (prefix), case-insensitively.
   */
  nickelBalanceDebitOriginators?: Maybe<NickelBalanceDebitOriginatorSearch>;
  /** Inbound ACH/wire instructions for one of the org's accounts (the oldest provisioned one when omitted). */
  nickelBalanceDepositInstructions?: Maybe<NickelBalanceDepositInstructions>;
  nickelBalanceFundingSources: Array<NickelBalanceFundingSource>;
  /**
   * The org's held paper checks, newest first (at most 200): every status by
   * default, one account's when `balanceAccountId` is given, and only the named
   * statuses when `status` is.
   */
  nickelBalanceInboundCheckReviews: Array<NickelBalanceInboundCheckReview>;
  /**
   * The org's Debit Protection holds, newest first (at most 200): every
   * status by default, one account's when `balanceAccountId` is given, and
   * only the named statuses when `status` is. The account page reads all of
   * an account's so a stale deep link can say what became of its debit.
   */
  nickelBalanceInboundDebitReviews: Array<NickelBalanceInboundDebitReview>;
  /**
   * Whether a bill paid from Nickel Balance to this vendor delivery method, for
   * this amount, is expected to reach the vendor instantly over RTP or FedNow
   * at no fee, with a 15-minute cancellation window. A prediction made with
   * the gates the payment will run; it can still go out by standard ACH. Null
   * when signed out, not permitted, or the method is not the org's.
   */
  nickelBalanceInstantPayout?: Maybe<NickelBalanceInstantPayout>;
  /**
   * The balance card's interest and balance figures. `balanceAccountId` names
   * one of the org's accounts (multi-account): its balance, in-flight and held
   * figures, and its own interest owed/paid. Omitted = the ORG-WIDE figures:
   * every provisioned account's balance summed (floored at 0 as a total), the
   * org's in-flight, held and pending-out cents, and org-wide interest totals
   * — what the overview hero shows above the per-account list. A withdrawal
   * form must name its source account: the org total is not a spendable cap.
   */
  nickelBalanceInterestSummary?: Maybe<NickelBalanceInterestSummary>;
  /** Onboarding-data readiness for Nickel Balance provisioning; null when signed out or not permitted. */
  nickelBalanceOnboardingReadiness?: Maybe<NickelBalanceOnboardingReadiness>;
  /**
   * Which Nickel Balance payouts the bill-pay flow should offer at all, before
   * there is a vendor, a method or an amount: the org's own gates on each pass.
   * Says nothing about whether the org has Nickel Balance. Null when signed out
   * or not permitted.
   */
  nickelBalancePayoutOffers?: Maybe<NickelBalancePayoutOffers>;
  /** Statements for one of the org's accounts, or every account when omitted. */
  nickelBalanceStatements: Array<NickelBalanceStatement>;
  /**
   * Whether a wire paid from Nickel Balance to this vendor delivery method, for
   * this amount (the vendor's, without the wire fee), would be refused at Pay,
   * and why: the eligibility checks Pay runs, read without sending anything.
   * Allowed for a method that is not a wire. Null when signed out, not
   * permitted, or the method is not the org's.
   */
  nickelBalanceWireCheck?: Maybe<NickelBalanceWireCheck>;
  notificationEmailRecipients?: Maybe<NotificationEmailRecipientsResponse>;
  oauthAuthorizationRequest?: Maybe<OAuthAuthorizationRequestResponse>;
  onboardingInfo?: Maybe<OnboardingReviewPayload>;
  organization?: Maybe<OrganizationResponse>;
  organizationPage?: Maybe<OrganizationPageResponse>;
  organizationPages?: Maybe<OrganizationPagesResponse>;
  organizations?: Maybe<OrganizationsResponse>;
  /**
   * Nickel-admin org lookup for the become-user page. `organizationId` is an
   * exact match and wins over `organizationName` (a substring search) when both
   * are sent; with neither, returns pending/approved orgs.
   */
  organizationsForAdmin?: Maybe<OrganizationsResponse>;
  partner?: Maybe<PartnerPayload>;
  partnerAccount?: Maybe<PartnerAccountPayload>;
  partnerAccounts?: Maybe<PartnerAccountsPayload>;
  partnerEnrollment?: Maybe<PartnerEnrollmentPayload>;
  partnerEnrollmentSession?: Maybe<PartnerEnrollmentSessionPayload>;
  partnerEnrollments?: Maybe<PartnerEnrollmentsPayload>;
  payment?: Maybe<PaymentResponse>;
  paymentActivities?: Maybe<PaymentActivitiesResponse>;
  /**
   * A single activity-feed row by id, scoped to the caller's org — null when the
   * id doesn't exist, belongs to another org, or names a row the caller's own
   * feed would not list (a Nickel Balance ledger row without
   * `banking.account.see_balance`; another member's row under an OWNED view).
   * Powers detail overlays that deep-link to one row (e.g. the Nickel Balance
   * transaction overlay).
   */
  paymentActivity?: Maybe<PaymentActivity>;
  paymentHistory?: Maybe<PaymentHistoryResponse>;
  /**
   * The organization's payment-method requests in every state, newest first.
   * Customers that have been deleted are left out.
   */
  paymentMethodRequests: PaymentMethodRequestsResponse;
  paymentsSummary?: Maybe<PaymentsSummaryResponse>;
  payout?: Maybe<PayoutResponse>;
  /**
   * The org's selectable AR payout destinations, in picker order: the
   * provisioned Nickel Balance accounts' methods oldest-first (only while the
   * org's Nickel Balance is ACTIVE), then the live bank methods newest-first.
   * Every row is a valid `selectPayoutMethod` / `payoutMethodId` target. Empty
   * without a session.
   */
  payoutMethods: Array<PayoutMethod>;
  payouts?: Maybe<PayoutsResponse>;
  pennyActiveWebVisit?: Maybe<PennyActiveWebVisitResponse>;
  pennyConversation?: Maybe<PennyConversationResponse>;
  pennyConversations: Array<PennyConversationListItem>;
  /**
   * How many of the caller's own Penny chats have a plan waiting for their
   * decision, the count pennyConversations(filter: { statuses: [PENDING] })
   * would list. Null when the caller cannot read their Penny chats.
   */
  pennyPendingChatCount?: Maybe<Scalars['Int']['output']>;
  platformPartnerForAdmin?: Maybe<PlatformPartnerAdminPayload>;
  platformPartnersForAdmin?: Maybe<PlatformPartnersAdminPayload>;
  questionForm?: Maybe<QuestionFormResponse>;
  questionForms?: Maybe<QuestionFormsResponse>;
  questionFormsById?: Maybe<QuestionFormsResponse>;
  quickBooksUnderwrite?: Maybe<QuickBooksUnderwriteResponse>;
  quickbooksAuthenticate?: Maybe<Scalars['String']['output']>;
  /**
   * A payment's receipt, for the portal receipt page. `token` is the payment's
   * receiptToken, and is required: without it the receipt is not found.
   */
  receipt?: Maybe<ReceiptResponse>;
  recurringBill?: Maybe<RecurringBillResponse>;
  recurringBills?: Maybe<RecurringBillsResponse>;
  recurringInvoice?: Maybe<RecurringInvoiceResponse>;
  recurringInvoices?: Maybe<RecurringInvoicesResponse>;
  refund?: Maybe<RefundResponse>;
  /**
   * The account a refund of `amount` cents would be debited from: the account
   * the payment settled into, or the linked bank when that is a Nickel Balance
   * that cannot cover it. The refund dialog names it before the merchant
   * confirms.
   */
  refundFundingSource?: Maybe<RefundFundingSourceResponse>;
  refunds?: Maybe<RefundsResponse>;
  reminderRules?: Maybe<ReminderRulesResponse>;
  retailCustomer?: Maybe<RetailCustomerResponse>;
  retailCustomerArAging?: Maybe<ArAgingReportResponse>;
  retailCustomers?: Maybe<RetailCustomersResponse>;
  ssoProviderForEmail?: Maybe<SsoProvider>;
  subscriptionDowngradePreview?: Maybe<SubscriptionDowngradePreviewResponse>;
  subscriptionPayments?: Maybe<SubscriptionPaymentsResponse>;
  subscriptionUpgradePreview?: Maybe<SubscriptionUpgradePreviewResponse>;
  /**
   * Signs the support chat widget in as the person at the keyboard: the
   * accountant in an accountant session, otherwise the signed-in user. Also
   * queues a push of their contact and company records to the support vendor,
   * which is how support learns who they are; the token carries identity only.
   * `session` is null under admin impersonation, for an API token, and when
   * no support vendor is configured.
   */
  supportMessenger?: Maybe<SupportMessengerResponse>;
  tagsForOrg?: Maybe<Array<Tag>>;
  unapprovedAccounts?: Maybe<UnapprovedAccountsResponse>;
  unsyncedAccountingTransactions?: Maybe<UnsyncedAccountingTransactionsResponse>;
  uploadUrl?: Maybe<S3SignedUrlResponse>;
  user?: Maybe<UserResponse>;
  vendor?: Maybe<VendorResponse>;
  vendorPayout?: Maybe<VendorPayoutResponse>;
  vendorPayouts?: Maybe<VendorPayoutsResponse>;
  vendors?: Maybe<VendorsResponse>;
  viewUrl?: Maybe<S3ViewUrlResponse>;
  webhooks?: Maybe<WebhooksResponse>;
  workspaceRoles?: Maybe<WorkspaceRolesResponse>;
  youtubePlaylistVideos?: Maybe<YouTubePlaylistResponse>;
};


export type QueryAchReturnArgs = {
  returnId: Scalars['String']['input'];
};


export type QueryAchVendorReturnArgs = {
  returnId: Scalars['String']['input'];
};


export type QueryAdminNickelBalanceAccountsArgs = {
  name?: InputMaybe<Scalars['String']['input']>;
  page?: InputMaybe<Scalars['Int']['input']>;
  pageSize?: InputMaybe<Scalars['Int']['input']>;
  sortBy?: InputMaybe<AdminNickelBalanceAccountsSort>;
  status?: InputMaybe<NickelBalanceAccountStatus>;
};


export type QueryAdminPennyChatsArgs = {
  organizationId?: InputMaybe<Scalars['ID']['input']>;
};


export type QueryAdminPennyConversationArgs = {
  id: Scalars['ID']['input'];
};


export type QueryAnswerFormsArgs = {
  orderBy?: InputMaybe<Scalars['String']['input']>;
  page?: InputMaybe<Scalars['Int']['input']>;
  pageSize?: InputMaybe<Scalars['Int']['input']>;
  questionFormId: Scalars['String']['input'];
};


export type QueryApiFileArgs = {
  fileId: Scalars['String']['input'];
};


export type QueryApiTokensArgs = {
  scope?: InputMaybe<ApiTokenScope>;
};


export type QueryApprovalsArgs = {
  approvalStatus?: InputMaybe<Scalars['String']['input']>;
  page?: InputMaybe<Scalars['Int']['input']>;
  pageSize?: InputMaybe<Scalars['Int']['input']>;
};


export type QueryAuthorizationRecordLookupArgs = {
  id: Scalars['String']['input'];
};


export type QueryBankRoutingNumberArgs = {
  bankRoutingNumber: Scalars['String']['input'];
};


export type QueryBillArgs = {
  billId?: InputMaybe<Scalars['String']['input']>;
};


export type QueryBillPayableArgs = {
  invoiceId: Scalars['String']['input'];
};


export type QueryBillPayablesArgs = {
  page?: InputMaybe<Scalars['Int']['input']>;
  pageSize?: InputMaybe<Scalars['Int']['input']>;
  status?: InputMaybe<Array<Scalars['String']['input']>>;
};


export type QueryBillPaymentArgs = {
  billPaymentId: Scalars['String']['input'];
};


export type QueryBillsArgs = {
  amountDueEndValue?: InputMaybe<Scalars['String']['input']>;
  amountDueStartValue?: InputMaybe<Scalars['String']['input']>;
  billAmountEndValue?: InputMaybe<Scalars['String']['input']>;
  billAmountStartValue?: InputMaybe<Scalars['String']['input']>;
  billReferenceId?: InputMaybe<Scalars['String']['input']>;
  dueDateEndDate?: InputMaybe<Scalars['String']['input']>;
  dueDateStartDate?: InputMaybe<Scalars['String']['input']>;
  excludeTagIds?: InputMaybe<Array<Scalars['String']['input']>>;
  issuedOnEndDate?: InputMaybe<Scalars['String']['input']>;
  issuedOnStartDate?: InputMaybe<Scalars['String']['input']>;
  lastModifiedAfter?: InputMaybe<Scalars['String']['input']>;
  lastModifiedBefore?: InputMaybe<Scalars['String']['input']>;
  lastSyncedAfter?: InputMaybe<Scalars['Float']['input']>;
  lastSyncedBefore?: InputMaybe<Scalars['Float']['input']>;
  nickelBillStatus?: InputMaybe<Scalars['String']['input']>;
  orderBy?: InputMaybe<Scalars['String']['input']>;
  page?: InputMaybe<Scalars['Int']['input']>;
  pageSize?: InputMaybe<Scalars['Int']['input']>;
  paidAmountEndValue?: InputMaybe<Scalars['String']['input']>;
  paidAmountStartValue?: InputMaybe<Scalars['String']['input']>;
  paidOnEndDate?: InputMaybe<Scalars['String']['input']>;
  paidOnStartDate?: InputMaybe<Scalars['String']['input']>;
  payableQuery?: InputMaybe<Array<PayableStatus>>;
  references?: InputMaybe<Array<Scalars['String']['input']>>;
  searchQuery?: InputMaybe<Scalars['String']['input']>;
  sortOrder?: InputMaybe<Scalars['String']['input']>;
  synced?: InputMaybe<Scalars['Boolean']['input']>;
  tagIds?: InputMaybe<Array<Scalars['String']['input']>>;
  vendorDeliveryMethods?: InputMaybe<Array<VendorDeliveryMethodFilter>>;
  vendorName?: InputMaybe<Scalars['String']['input']>;
};


export type QueryBillsByIdArgs = {
  billIds: Array<Scalars['String']['input']>;
};


export type QueryChargebackArgs = {
  chargebackId: Scalars['String']['input'];
};


export type QueryChargebacksArgs = {
  organizationId: Scalars['String']['input'];
  status?: InputMaybe<Array<ChargebackStatus>>;
};


export type QueryChartOfAccountsArgs = {
  accountType?: InputMaybe<Scalars['String']['input']>;
  query?: InputMaybe<Scalars['String']['input']>;
};


export type QueryCheckApprovalArgs = {
  paymentMethodId: Scalars['String']['input'];
  submittedAmountCents: Scalars['Int']['input'];
  vendorId: Scalars['String']['input'];
};


export type QueryCheckApprovalsArgs = {
  checkApprovalInput: Array<CheckApprovalInput>;
};


export type QueryCheckCustomDomainAvailabilityArgs = {
  root: Scalars['String']['input'];
  subdomain: Scalars['String']['input'];
};


export type QueryClientAccessArgs = {
  clientOrganizationId: Scalars['ID']['input'];
};


export type QueryConnectorConnectionsArgs = {
  includeDisconnected?: InputMaybe<Scalars['Boolean']['input']>;
};


export type QueryConnectorReadArgs = {
  capabilityId: Scalars['String']['input'];
  connectionId: Scalars['ID']['input'];
  input: Scalars['JSONObject']['input'];
};


export type QueryDebitAuthorizationArgs = {
  debitAuthorizationId: Scalars['String']['input'];
};


export type QueryEnterpriseSsoForEmailArgs = {
  email: Scalars['String']['input'];
};


export type QueryExistingUserFromEmailArgs = {
  email: Scalars['String']['input'];
};


export type QueryGetAllMerchantPaymentMethodsArgs = {
  includeNickelBalance?: InputMaybe<Scalars['Boolean']['input']>;
};


export type QueryGetAllPaymentMethodsArgs = {
  customerIds?: InputMaybe<Array<Scalars['String']['input']>>;
};


export type QueryGetClientTokenArgs = {
  basisTheoryFallbackDetail?: InputMaybe<Scalars['String']['input']>;
  basisTheoryFallbackReason?: InputMaybe<Scalars['String']['input']>;
  paymentId?: InputMaybe<Scalars['String']['input']>;
  retailCustomerId?: InputMaybe<Scalars['String']['input']>;
};


export type QueryGetDebitAuthorizationFromShortIdArgs = {
  retailCustomerId: Scalars['String']['input'];
  shortId: Scalars['String']['input'];
};


export type QueryGetPaymentMethodArgs = {
  paymentMethodId: Scalars['String']['input'];
};


export type QueryGetPaymentRequestArgs = {
  organizationId: Scalars['String']['input'];
  paymentRequestId: Scalars['String']['input'];
};


export type QueryHubspotDealStatusesArgs = {
  organizationIds: Array<Scalars['String']['input']>;
};


export type QueryInboundBankTransfersArgs = {
  status?: InputMaybe<InboundBankTransferStatus>;
};


export type QueryIntegrationConnectionStatusArgs = {
  forceRefresh?: InputMaybe<Scalars['Boolean']['input']>;
};


export type QueryInvoiceApprovalRequestsArgs = {
  statuses?: InputMaybe<Array<ApprovalRequestStatus>>;
};


export type QueryMcpActionApprovalArgs = {
  id: Scalars['ID']['input'];
};


export type QueryMcpActionApprovalsArgs = {
  recentDays?: InputMaybe<Scalars['Int']['input']>;
};


export type QueryNickelBalanceAccountsArgs = {
  includeClosed?: InputMaybe<Scalars['Boolean']['input']>;
};


export type QueryNickelBalanceAvailableHistoryArgs = {
  balanceAccountId?: InputMaybe<Scalars['ID']['input']>;
  days?: InputMaybe<Scalars['Int']['input']>;
};


export type QueryNickelBalanceCardArgs = {
  id: Scalars['ID']['input'];
};


export type QueryNickelBalanceCardsArgs = {
  balanceAccountId?: InputMaybe<Scalars['ID']['input']>;
  limit?: InputMaybe<Scalars['Int']['input']>;
  offset?: InputMaybe<Scalars['Int']['input']>;
  status?: InputMaybe<IssuedCardStatus>;
};


export type QueryNickelBalanceCheckDepositArgs = {
  id: Scalars['ID']['input'];
};


export type QueryNickelBalanceCheckDepositsArgs = {
  balanceAccountId?: InputMaybe<Scalars['ID']['input']>;
  limit?: InputMaybe<Scalars['Int']['input']>;
  status?: InputMaybe<Array<NickelBalanceCheckDepositStatus>>;
  unappliedOnly?: InputMaybe<Scalars['Boolean']['input']>;
};


export type QueryNickelBalanceDebitAllowRulesArgs = {
  balanceAccountId: Scalars['ID']['input'];
};


export type QueryNickelBalanceDebitOriginatorsArgs = {
  balanceAccountId: Scalars['ID']['input'];
  search?: InputMaybe<Scalars['String']['input']>;
};


export type QueryNickelBalanceDepositInstructionsArgs = {
  balanceAccountId?: InputMaybe<Scalars['ID']['input']>;
};


export type QueryNickelBalanceInboundCheckReviewsArgs = {
  balanceAccountId?: InputMaybe<Scalars['ID']['input']>;
  status?: InputMaybe<Array<NickelBalanceInboundCheckReviewStatus>>;
};


export type QueryNickelBalanceInboundDebitReviewsArgs = {
  balanceAccountId?: InputMaybe<Scalars['ID']['input']>;
  status?: InputMaybe<Array<NickelBalanceInboundDebitReviewStatus>>;
};


export type QueryNickelBalanceInstantPayoutArgs = {
  amountInCents: Scalars['Int']['input'];
  vendorPayoutMethodId: Scalars['ID']['input'];
};


export type QueryNickelBalanceInterestSummaryArgs = {
  balanceAccountId?: InputMaybe<Scalars['ID']['input']>;
};


export type QueryNickelBalanceStatementsArgs = {
  balanceAccountId?: InputMaybe<Scalars['ID']['input']>;
};


export type QueryNickelBalanceWireCheckArgs = {
  amountInCents: Scalars['Int']['input'];
  vendorPayoutMethodId: Scalars['ID']['input'];
};


export type QueryOauthAuthorizationRequestArgs = {
  clientId: Scalars['ID']['input'];
  redirectUri: Scalars['String']['input'];
};


export type QueryOnboardingInfoArgs = {
  organizationId: Scalars['String']['input'];
  plaidTransactionStartDate?: InputMaybe<Scalars['String']['input']>;
};


export type QueryOrganizationPageArgs = {
  checkoutPath: Scalars['String']['input'];
  subdomain: Scalars['String']['input'];
};


export type QueryOrganizationPagesArgs = {
  organizationName?: InputMaybe<Scalars['String']['input']>;
  persistentLink?: InputMaybe<Scalars['Boolean']['input']>;
  queryKey?: InputMaybe<Scalars['String']['input']>;
  subdomain?: InputMaybe<Scalars['String']['input']>;
};


export type QueryOrganizationsArgs = {
  organizationName?: InputMaybe<Scalars['String']['input']>;
};


export type QueryOrganizationsForAdminArgs = {
  organizationId?: InputMaybe<Scalars['String']['input']>;
  organizationName?: InputMaybe<Scalars['String']['input']>;
};


export type QueryPartnerAccountArgs = {
  account: Scalars['ID']['input'];
};


export type QueryPartnerAccountsArgs = {
  page?: InputMaybe<Scalars['Int']['input']>;
  pageSize?: InputMaybe<Scalars['Int']['input']>;
  partnerAccountRef?: InputMaybe<Scalars['String']['input']>;
};


export type QueryPartnerEnrollmentArgs = {
  id: Scalars['ID']['input'];
};


export type QueryPartnerEnrollmentsArgs = {
  page?: InputMaybe<Scalars['Int']['input']>;
  pageSize?: InputMaybe<Scalars['Int']['input']>;
  partnerAccountRef?: InputMaybe<Scalars['String']['input']>;
};


export type QueryPaymentArgs = {
  paymentId: Scalars['String']['input'];
};


export type QueryPaymentActivitiesArgs = {
  customerName?: InputMaybe<Scalars['String']['input']>;
  description?: InputMaybe<Scalars['String']['input']>;
  email?: InputMaybe<Scalars['String']['input']>;
  endDate?: InputMaybe<Scalars['String']['input']>;
  issuedCardId?: InputMaybe<Scalars['ID']['input']>;
  maxAmountCents?: InputMaybe<Scalars['Int']['input']>;
  minAmountCents?: InputMaybe<Scalars['Int']['input']>;
  nickelBalanceAccountId?: InputMaybe<Scalars['ID']['input']>;
  nickelBalanceOnly?: InputMaybe<Scalars['Boolean']['input']>;
  page?: InputMaybe<Scalars['Int']['input']>;
  pageSize?: InputMaybe<Scalars['Int']['input']>;
  payableFilters?: InputMaybe<PayableActivityFilters>;
  receivableFilters?: InputMaybe<ReceivableActivityFilters>;
  sortBy?: InputMaybe<SortOrder>;
  source?: InputMaybe<PaymentActivitySource>;
  startDate?: InputMaybe<Scalars['String']['input']>;
  status?: InputMaybe<ActivityStatus>;
  transactionId?: InputMaybe<Scalars['String']['input']>;
};


export type QueryPaymentActivityArgs = {
  id: Scalars['ID']['input'];
};


export type QueryPaymentHistoryArgs = {
  interval?: InputMaybe<HistoryInterval>;
};


export type QueryPaymentMethodRequestsArgs = {
  page?: InputMaybe<Scalars['Int']['input']>;
  pageSize?: InputMaybe<Scalars['Int']['input']>;
  retailCustomerId?: InputMaybe<Scalars['String']['input']>;
  sentAfter?: InputMaybe<Scalars['String']['input']>;
  sentBefore?: InputMaybe<Scalars['String']['input']>;
  statuses?: InputMaybe<Array<PaymentMethodRequestStatus>>;
};


export type QueryPaymentsSummaryArgs = {
  customer?: InputMaybe<Scalars['String']['input']>;
  endDateEpochMillis?: InputMaybe<Scalars['Float']['input']>;
  paymentId?: InputMaybe<Scalars['String']['input']>;
  startDateEpochMillis?: InputMaybe<Scalars['Float']['input']>;
  statuses?: InputMaybe<Array<Scalars['String']['input']>>;
};


export type QueryPayoutArgs = {
  payoutId: Scalars['String']['input'];
};


export type QueryPayoutsArgs = {
  page?: InputMaybe<Scalars['Int']['input']>;
  pageSize?: InputMaybe<Scalars['Int']['input']>;
};


export type QueryPennyActiveWebVisitArgs = {
  conversationId: Scalars['ID']['input'];
};


export type QueryPennyConversationArgs = {
  id: Scalars['ID']['input'];
};


export type QueryPennyConversationsArgs = {
  filter?: InputMaybe<PennyConversationFilter>;
};


export type QueryPlatformPartnerForAdminArgs = {
  id: Scalars['ID']['input'];
};


export type QueryQuestionFormArgs = {
  questionFormId: Scalars['String']['input'];
};


export type QueryQuestionFormsArgs = {
  activePaymentMethodTypes?: InputMaybe<Array<DebitAuthorizationMethodType>>;
  customerName?: InputMaybe<Scalars['String']['input']>;
  excludeTagIds?: InputMaybe<Array<Scalars['String']['input']>>;
  hasDebitAuthorization?: InputMaybe<Scalars['Boolean']['input']>;
  lastModifiedAfter?: InputMaybe<Scalars['String']['input']>;
  lastModifiedBefore?: InputMaybe<Scalars['String']['input']>;
  lastSyncedAfter?: InputMaybe<Scalars['Float']['input']>;
  lastSyncedBefore?: InputMaybe<Scalars['Float']['input']>;
  maxCompletedDate?: InputMaybe<Scalars['String']['input']>;
  maxCreatedDate?: InputMaybe<Scalars['String']['input']>;
  maxDueDate?: InputMaybe<Scalars['String']['input']>;
  maxIssueDate?: InputMaybe<Scalars['String']['input']>;
  maxPaymentRequestSentDate?: InputMaybe<Scalars['String']['input']>;
  maxReceivedAmount?: InputMaybe<Scalars['Int']['input']>;
  maxRequestedAmount?: InputMaybe<Scalars['Int']['input']>;
  minCompletedDate?: InputMaybe<Scalars['String']['input']>;
  minCreatedDate?: InputMaybe<Scalars['String']['input']>;
  minDueDate?: InputMaybe<Scalars['String']['input']>;
  minIssueDate?: InputMaybe<Scalars['String']['input']>;
  minPaymentRequestSentDate?: InputMaybe<Scalars['String']['input']>;
  minReceivedAmount?: InputMaybe<Scalars['Int']['input']>;
  minRequestedAmount?: InputMaybe<Scalars['Int']['input']>;
  modifiedAfterLastRequest?: InputMaybe<Scalars['Boolean']['input']>;
  name?: InputMaybe<Scalars['String']['input']>;
  orderBy?: InputMaybe<Scalars['String']['input']>;
  page?: InputMaybe<Scalars['Int']['input']>;
  pageSize?: InputMaybe<Scalars['Int']['input']>;
  pastDue?: InputMaybe<Scalars['Boolean']['input']>;
  query?: InputMaybe<Scalars['String']['input']>;
  sent?: InputMaybe<Scalars['Boolean']['input']>;
  sortOrder?: InputMaybe<QuestionFormSortOrder>;
  status?: InputMaybe<QuestionFormStatus>;
  statuses?: InputMaybe<Array<QuestionFormStatus>>;
  synced?: InputMaybe<Scalars['Boolean']['input']>;
  tagIds?: InputMaybe<Array<Scalars['String']['input']>>;
  unsent?: InputMaybe<Scalars['Boolean']['input']>;
};


export type QueryQuestionFormsByIdArgs = {
  questionFormIds: Array<Scalars['String']['input']>;
};


export type QueryQuickBooksUnderwriteArgs = {
  organizationId: Scalars['String']['input'];
};


export type QueryReceiptArgs = {
  paymentId: Scalars['String']['input'];
  token?: InputMaybe<Scalars['String']['input']>;
};


export type QueryRecurringBillArgs = {
  recurringBillId: Scalars['String']['input'];
};


export type QueryRecurringBillsArgs = {
  endDateEndDate?: InputMaybe<Scalars['String']['input']>;
  endDateStartDate?: InputMaybe<Scalars['String']['input']>;
  orderBy?: InputMaybe<Scalars['String']['input']>;
  page?: InputMaybe<Scalars['Int']['input']>;
  pageSize?: InputMaybe<Scalars['Int']['input']>;
  sortOrder?: InputMaybe<Scalars['String']['input']>;
  startDateEndDate?: InputMaybe<Scalars['String']['input']>;
  startDateStartDate?: InputMaybe<Scalars['String']['input']>;
  vendorName?: InputMaybe<Scalars['String']['input']>;
};


export type QueryRecurringInvoiceArgs = {
  recurringInvoiceId: Scalars['String']['input'];
};


export type QueryRecurringInvoicesArgs = {
  customerName?: InputMaybe<Scalars['String']['input']>;
  endDateEndDate?: InputMaybe<Scalars['String']['input']>;
  endDateStartDate?: InputMaybe<Scalars['String']['input']>;
  orderBy?: InputMaybe<Scalars['String']['input']>;
  page?: InputMaybe<Scalars['Int']['input']>;
  pageSize?: InputMaybe<Scalars['Int']['input']>;
  sortOrder?: InputMaybe<Scalars['String']['input']>;
  startDateEndDate?: InputMaybe<Scalars['String']['input']>;
  startDateStartDate?: InputMaybe<Scalars['String']['input']>;
};


export type QueryRefundArgs = {
  refundId: Scalars['String']['input'];
};


export type QueryRefundFundingSourceArgs = {
  amount: Scalars['Int']['input'];
  paymentId: Scalars['String']['input'];
};


export type QueryRefundsArgs = {
  endDateEpochMillis?: InputMaybe<Scalars['Float']['input']>;
  page?: InputMaybe<Scalars['Int']['input']>;
  pageSize?: InputMaybe<Scalars['Int']['input']>;
  sortBy?: InputMaybe<SortOrder>;
  startDateEpochMillis?: InputMaybe<Scalars['Float']['input']>;
  status?: InputMaybe<Array<Scalars['String']['input']>>;
};


export type QueryRetailCustomerArgs = {
  retailCustomerId: Scalars['String']['input'];
  shortId?: InputMaybe<Scalars['String']['input']>;
};


export type QueryRetailCustomerArAgingArgs = {
  retailCustomerId: Scalars['String']['input'];
};


export type QueryRetailCustomersArgs = {
  activePaymentMethodTypes?: InputMaybe<Array<DebitAuthorizationMethodType>>;
  customerEmail?: InputMaybe<Scalars['String']['input']>;
  customerName?: InputMaybe<Scalars['String']['input']>;
  emails?: InputMaybe<Array<Scalars['String']['input']>>;
  excludeIfLastAgingReportSentAfter?: InputMaybe<Scalars['String']['input']>;
  excludeIfLastAgingReportSentBefore?: InputMaybe<Scalars['String']['input']>;
  excludeTagIds?: InputMaybe<Array<Scalars['String']['input']>>;
  hasOverdueInvoices?: InputMaybe<Scalars['Boolean']['input']>;
  hasSavedPaymentMethod?: InputMaybe<Scalars['Boolean']['input']>;
  lastAgingReportSentAfter?: InputMaybe<Scalars['String']['input']>;
  lastAgingReportSentBefore?: InputMaybe<Scalars['String']['input']>;
  orderBy?: InputMaybe<Scalars['String']['input']>;
  page?: InputMaybe<Scalars['Int']['input']>;
  pageSize?: InputMaybe<Scalars['Int']['input']>;
  paymentMethodStatuses?: InputMaybe<Array<RetailCustomerPaymentMethodStatus>>;
  search?: InputMaybe<Scalars['String']['input']>;
  tagIds?: InputMaybe<Array<Scalars['String']['input']>>;
};


export type QuerySsoProviderForEmailArgs = {
  email: Scalars['String']['input'];
};


export type QuerySubscriptionDowngradePreviewArgs = {
  planName: Scalars['String']['input'];
};


export type QuerySubscriptionUpgradePreviewArgs = {
  planName: Scalars['String']['input'];
};


export type QueryTagsForOrgArgs = {
  type?: InputMaybe<TagType>;
};


export type QueryUnapprovedAccountsArgs = {
  aiApproved?: InputMaybe<Scalars['Boolean']['input']>;
  approvalStatus?: InputMaybe<AccountApprovalStatus>;
  name?: InputMaybe<Scalars['String']['input']>;
  organizationId?: InputMaybe<Scalars['String']['input']>;
  page?: InputMaybe<Scalars['Int']['input']>;
  pageSize?: InputMaybe<Scalars['Int']['input']>;
};


export type QueryUnsyncedAccountingTransactionsArgs = {
  page?: InputMaybe<Scalars['Int']['input']>;
  pageSize?: InputMaybe<Scalars['Int']['input']>;
  transactionType?: InputMaybe<AccountingTransactionType>;
};


export type QueryUploadUrlArgs = {
  isPrivate?: InputMaybe<Scalars['Boolean']['input']>;
  key: Scalars['String']['input'];
  organizationId?: InputMaybe<Scalars['String']['input']>;
};


export type QueryVendorArgs = {
  token?: InputMaybe<Scalars['String']['input']>;
  vendorId?: InputMaybe<Scalars['String']['input']>;
};


export type QueryVendorPayoutArgs = {
  vendorPayoutId: Scalars['String']['input'];
};


export type QueryVendorPayoutsArgs = {
  page?: InputMaybe<Scalars['Int']['input']>;
  pageSize?: InputMaybe<Scalars['Int']['input']>;
};


export type QueryVendorsArgs = {
  deliveryMethods?: InputMaybe<Array<VendorDeliveryMethodFilter>>;
  excludeTagIds?: InputMaybe<Array<Scalars['String']['input']>>;
  hasDeliveryMethod?: InputMaybe<Scalars['Boolean']['input']>;
  orderBy?: InputMaybe<Scalars['String']['input']>;
  page?: InputMaybe<Scalars['Int']['input']>;
  pageSize?: InputMaybe<Scalars['Int']['input']>;
  tagIds?: InputMaybe<Array<Scalars['String']['input']>>;
  vendorEmail?: InputMaybe<Scalars['String']['input']>;
  vendorName?: InputMaybe<Scalars['String']['input']>;
};


export type QueryViewUrlArgs = {
  url: Scalars['String']['input'];
};

export type QuestionForm = {
  __typename?: 'QuestionForm';
  achEnabled?: Maybe<Scalars['Boolean']['output']>;
  answerForms?: Maybe<Array<BaseAnswerForm>>;
  /**
   * What the caller can do to this invoice right now, and why not. Always a list;
   * nullable only so clients typed against the whole `QuestionForm` need not select it.
   */
  availableActions?: Maybe<Array<ActionAvailability>>;
  bankTransferEnabled: Scalars['Boolean']['output'];
  /** The invoice's receiving account number, while it accepts credits. */
  bankTransferInstructions?: Maybe<BankTransferInstructions>;
  checkoutActive: Scalars['Boolean']['output'];
  checkoutPath: Scalars['String']['output'];
  completedAmountInCents?: Maybe<Scalars['Int']['output']>;
  completedAt?: Maybe<Scalars['Float']['output']>;
  completedCount: Scalars['Int']['output'];
  createdAt: Scalars['Float']['output'];
  /**
   * The team member whose account created this payment request. Null for
   * records with no creator on file (integration-created or pre-dating the
   * column). This user is shown as the visible sender inside the invoice email
   * unless the org's Contact Email notification setting is enabled — then the
   * configured address is shown instead (see
   * MerchantNotificationSetting.carbonCopyEmail); whoever clicks send stands in
   * only when this is null.
   */
  createdBy?: Maybe<User>;
  creditCardEnabled?: Maybe<Scalars['Boolean']['output']>;
  /**
   * The ISO 4217 code, uppercase, that every amount on the invoice is in: its
   * request, and what its payments, external payments and debit memos count.
   * Always USD today.
   */
  currency: Scalars['String']['output'];
  /**
   * Sum of non-voided debit memos (amounts added after issue — an NSF fee, a
   * finance charge), which is INCLUDED in requestedAmount: the invoice's own
   * amount is requestedAmount minus this. See
   * docs/external-payments/debit-memos.md §3.2.
   */
  debitMemoAmountInCents?: Maybe<Scalars['Int']['output']>;
  /**
   * Debit memos recorded against this invoice, newest first, voided ones
   * included. Only non-voided memos count toward requestedAmount.
   */
  debitMemos?: Maybe<Array<ArDebitMemo>>;
  documents?: Maybe<Array<Scalars['String']['output']>>;
  /**
   * Total raw bytes of `documents`, for checking how much of the email-attachment
   * budget (`MAX_RAW_ATTACHMENT_BYTES`) this record has already spent before
   * accepting another upload.
   *
   * Resolving it costs one S3 HEAD per document (issued in parallel, so one round
   * trip per record, not per document) and is not cached. Select it only where an
   * attachment can actually be added, in its own narrow operation — never
   * alongside a record's general fields, and never across a list. Documents whose
   * size cannot be read count as 0.
   */
  documentsTotalSizeBytes?: Maybe<Scalars['Int']['output']>;
  dueDate?: Maybe<Scalars['Float']['output']>;
  /**
   * Sum of non-voided external payment records ("mark as paid"), which is
   * INCLUDED in completedAmountInCents — subtract it to get Nickel-processed
   * money. See docs/external-payments/design.md §3.2.
   */
  externalAmountInCents?: Maybe<Scalars['Int']['output']>;
  externalArPayments?: Maybe<Array<ExternalArPayment>>;
  feePassthroughPercent?: Maybe<Scalars['Int']['output']>;
  id: Scalars['String']['output'];
  invoice?: Maybe<Invoice>;
  issueDate?: Maybe<Scalars['Float']['output']>;
  kind?: Maybe<QuestionFormKind>;
  /**
   * When the invoice's pay page was last opened in the portal (epoch ms); null
   * if never, and for every invoice that predates view recording. An open is
   * anyone loading the link — the customer, the merchant checking their own
   * link, or an email scanner that renders the page — so render it as "last
   * viewed", never as "the customer read it". Null unless the caller is a
   * signed-in member of the invoice's organization.
   */
  lastViewedAt?: Maybe<Scalars['Float']['output']>;
  memo?: Maybe<Scalars['String']['output']>;
  name: Scalars['String']['output'];
  /** Requested minus paid minus processing, never below 0. */
  owedAmountInCents: Scalars['Int']['output'];
  /**
   * Derived from this invoice's payments on every read, not from
   * completedAmountInCents: SUCCEEDED payments, the kept share of a partly
   * refunded one (not on an invoice synced from accounting, whose requested
   * amount already excludes it), and non-voided external payment records. See
   * docs/payment-status/design.md § Phase 1.
   */
  paidAmountInCents: Scalars['Int']['output'];
  /**
   * Per-invoice AR payout destination override: one of the org's payout
   * methods (`payoutMethods`), bank or Nickel Balance account; null = the org's
   * selection (Organization.payoutMethod).
   */
  payoutMethodId?: Maybe<Scalars['ID']['output']>;
  /**
   * PENDING payments with money on a rail. A PENDING payment with no charge
   * counts nowhere.
   */
  processingAmountInCents: Scalars['Int']['output'];
  recipients?: Maybe<Array<RecipientType>>;
  recurringInvoice?: Maybe<RecurringInvoice>;
  remindersDisabled?: Maybe<Scalars['Boolean']['output']>;
  requestedAmount?: Maybe<Scalars['Int']['output']>;
  retailCustomer?: Maybe<RetailCustomer>;
  schema: Scalars['String']['output'];
  /**
   * When the invoice's first send goes out, or went out (epoch ms): the 9am US
   * Eastern send time of the date set with `scheduleInvoiceSend`, or the moment
   * another route sent it first. Null when no date was set, and for anyone but
   * a member of the invoice's organization.
   */
  sendAt?: Maybe<Scalars['Float']['output']>;
  /**
   * Where that send stands. Null when no date was set, and for anyone but a
   * member of the invoice's organization.
   */
  sendStatus?: Maybe<QuestionFormSendStatus>;
  status?: Maybe<QuestionFormStatus>;
  successfulPayments?: Maybe<Array<Payment>>;
  tags?: Maybe<Array<Tag>>;
  threeDSEnabled?: Maybe<Scalars['Boolean']['output']>;
  thumbnail?: Maybe<Scalars['String']['output']>;
  updatedAt: Scalars['Float']['output'];
  url?: Maybe<Scalars['String']['output']>;
  /**
   * Recorded opens of the pay page — a FLOOR, not a click count, and not
   * merchant-facing as a number without saying so. Opens of one invoice within
   * a few minutes of each other are folded into one
   * (recordPaymentRequestView), so several people opening the same emailed link
   * at once count once, and a link shared with many customers undercounts the
   * most. Null unless the caller is a signed-in member of the invoice's
   * organization.
   */
  viewCount?: Maybe<Scalars['Int']['output']>;
  /**
   * Whether opens were being recorded when this invoice was created, so a null
   * lastViewedAt means "not opened yet" rather than "not watched". Recording is
   * taken to begin at the earliest recorded open in this environment, which can
   * only be on or after the real start, so an invoice created in between reads
   * as untracked rather than unopened. Null unless the caller is a signed-in
   * member of the invoice's organization.
   */
  viewsTracked?: Maybe<Scalars['Boolean']['output']>;
  withdrawalDate?: Maybe<Scalars['Float']['output']>;
};

export type QuestionFormError = {
  __typename?: 'QuestionFormError';
  error: Error;
  questionForm: QuestionForm;
};

/**
 * What a QuestionForm IS, orthogonal to its lifecycle status. INVOICE is a
 * single-use payment request that completes when satisfied; REUSABLE_LINK is a
 * standing, repeatedly-payable checkout page (the dashboard's "Payment Links"
 * product), whose status stays null except ARCHIVED.
 */
export enum QuestionFormKind {
  Invoice = 'INVOICE',
  ReusableLink = 'REUSABLE_LINK'
}

export type QuestionFormPaymentAndWithdrawalDateInput = {
  amountCents?: InputMaybe<Scalars['Int']['input']>;
  questionFormId: Scalars['String']['input'];
  withdrawalDateEpochMillis?: InputMaybe<Scalars['Float']['input']>;
};

export type QuestionFormResponse = {
  __typename?: 'QuestionFormResponse';
  error?: Maybe<Error>;
  questionForm?: Maybe<QuestionForm>;
};

/**
 * Where an invoice's first send stands once a merchant set a date for it
 * (`scheduleInvoiceSend`). Null on an invoice with no date set.
 */
export enum QuestionFormSendStatus {
  /** Waiting to go out at `sendAt`. */
  Due = 'DUE',
  /** An approval rule holds the send. The approved request sends it; a rejected or withdrawn request, or a rule no one can approve, leaves it held until the date is set again or the invoice is sent another way. */
  HeldForApproval = 'HELD_FOR_APPROVAL',
  /** Sent: by the scheduled send, by its approved request, or by any other route. */
  Sent = 'SENT',
  /** The send did not go out when it fired: the invoice was paid or closed, its customer was removed, it had no unsent recipient, or the send was refused (the send limit for an organization not yet approved, or a malformed address). */
  Skipped = 'SKIPPED'
}

export enum QuestionFormSortOrder {
  CompletedAtAsc = 'COMPLETED_AT_ASC',
  CompletedAtDesc = 'COMPLETED_AT_DESC',
  CreatedDateAsc = 'CREATED_DATE_ASC',
  CreatedDateDesc = 'CREATED_DATE_DESC',
  DueDateAsc = 'DUE_DATE_ASC',
  DueDateDesc = 'DUE_DATE_DESC'
}

export enum QuestionFormStatus {
  Active = 'ACTIVE',
  Archived = 'ARCHIVED',
  Completed = 'COMPLETED',
  /** Legacy: no record has it. createQuestionForm still reads it as a reusable link (kind REUSABLE_LINK). */
  Persistent = 'PERSISTENT',
  Scheduled = 'SCHEDULED'
}

export type QuestionFormsResponse = {
  __typename?: 'QuestionFormsResponse';
  error?: Maybe<Error>;
  questionForms?: Maybe<Array<QuestionForm>>;
  totalResults?: Maybe<Scalars['Int']['output']>;
};

export type QuickBooksClass = {
  __typename?: 'QuickBooksClass';
  id: Scalars['String']['output'];
  name: Scalars['String']['output'];
};

export type QuickBooksClassesResponse = {
  __typename?: 'QuickBooksClassesResponse';
  classes?: Maybe<Array<QuickBooksClass>>;
  error?: Maybe<Error>;
};

export type QuickBooksExpenseAccount = {
  __typename?: 'QuickBooksExpenseAccount';
  accountSubType: Scalars['String']['output'];
  accountType: Scalars['String']['output'];
  description?: Maybe<Scalars['String']['output']>;
  id: Scalars['String']['output'];
  name: Scalars['String']['output'];
};

export type QuickBooksExpenseAccountsAndItemsResponse = {
  __typename?: 'QuickBooksExpenseAccountsAndItemsResponse';
  accounts?: Maybe<Array<QuickBooksExpenseAccount>>;
  error?: Maybe<Error>;
  items?: Maybe<Array<QuickBooksExpenseItem>>;
};

export type QuickBooksExpenseItem = {
  __typename?: 'QuickBooksExpenseItem';
  description?: Maybe<Scalars['String']['output']>;
  id: Scalars['String']['output'];
  name: Scalars['String']['output'];
  taxable: Scalars['Boolean']['output'];
  type: Scalars['String']['output'];
  unitPrice: Scalars['Float']['output'];
};

export type QuickBooksRevenueAccount = {
  __typename?: 'QuickBooksRevenueAccount';
  accountSubType: Scalars['String']['output'];
  accountType: Scalars['String']['output'];
  description?: Maybe<Scalars['String']['output']>;
  id: Scalars['String']['output'];
  name: Scalars['String']['output'];
};

export type QuickBooksRevenueAccountsAndItemsResponse = {
  __typename?: 'QuickBooksRevenueAccountsAndItemsResponse';
  accounts?: Maybe<Array<QuickBooksRevenueAccount>>;
  error?: Maybe<Error>;
  items?: Maybe<Array<QuickBooksRevenueItem>>;
};

export type QuickBooksRevenueItem = {
  __typename?: 'QuickBooksRevenueItem';
  description?: Maybe<Scalars['String']['output']>;
  id: Scalars['String']['output'];
  name: Scalars['String']['output'];
  taxable: Scalars['Boolean']['output'];
  type: Scalars['String']['output'];
  unitPrice: Scalars['Float']['output'];
};

export type QuickBooksSummaryInfo = {
  __typename?: 'QuickBooksSummaryInfo';
  cashFlowReport?: Maybe<CashFlowReportSummary>;
  profitAndLossReport?: Maybe<ProfitAndLossReportSummary>;
};

export type QuickBooksTransaction = {
  __typename?: 'QuickBooksTransaction';
  account?: Maybe<Scalars['String']['output']>;
  amount?: Maybe<Scalars['Float']['output']>;
  date?: Maybe<Scalars['String']['output']>;
  method?: Maybe<Scalars['String']['output']>;
  reference?: Maybe<Scalars['String']['output']>;
  status?: Maybe<Scalars['String']['output']>;
  type?: Maybe<Scalars['String']['output']>;
  typeId?: Maybe<Scalars['String']['output']>;
};

export type QuickBooksUnderwriteResponse = {
  __typename?: 'QuickBooksUnderwriteResponse';
  billData?: Maybe<Array<BillData>>;
  error?: Maybe<Error>;
  invoices?: Maybe<Array<Invoice>>;
  summaryInfo?: Maybe<QuickBooksSummaryInfo>;
  transactions?: Maybe<Array<QuickBooksTransaction>>;
};

export type QuickbooksCategoryDetails = {
  __typename?: 'QuickbooksCategoryDetails';
  amountCents?: Maybe<Scalars['Int']['output']>;
  description?: Maybe<Scalars['String']['output']>;
  id: Scalars['String']['output'];
  quantity?: Maybe<Scalars['Float']['output']>;
  rate?: Maybe<Scalars['Float']['output']>;
};

export type QuickbooksCategoryDetailsInput = {
  amountCents: Scalars['Int']['input'];
  description?: InputMaybe<Scalars['String']['input']>;
  id: Scalars['String']['input'];
  quantity?: InputMaybe<Scalars['Float']['input']>;
  rate?: InputMaybe<Scalars['Float']['input']>;
};

/**
 * The connected accounting company and the account payouts are recorded to.
 * Despite the name it also describes a Xero connection, where companyId and
 * companyName are Xero's own and chartOfAccountId is Xero's account id.
 */
export type QuickbooksCompanyInfo = {
  __typename?: 'QuickbooksCompanyInfo';
  chartOfAccountId?: Maybe<Scalars['String']['output']>;
  chartOfAccountName?: Maybe<Scalars['String']['output']>;
  companyId?: Maybe<Scalars['String']['output']>;
  companyName?: Maybe<Scalars['String']['output']>;
  isDesktop?: Maybe<Scalars['Boolean']['output']>;
  /**
   * The payout method whose QuickBooks link chartOfAccountId/Name describe: the
   * org's selected AR payout destination (AccountInfo.payoutMethod) — a bank row
   * or one of its Nickel Balance accounts' rows — falling back to the newest
   * bank row when nothing is selected. Null when the org has no payout method.
   * Pass it to updateOrCreateChartOfAccount(payoutMethodId: ...) to change the
   * link. Every other Nickel Balance account's link lives on
   * NickelBalanceAccount.quickbooksChartOfAccount.
   */
  payoutMethodId?: Maybe<Scalars['String']['output']>;
};

export type QuickbooksItemDetails = {
  __typename?: 'QuickbooksItemDetails';
  amountCents?: Maybe<Scalars['Int']['output']>;
  description?: Maybe<Scalars['String']['output']>;
  id: Scalars['String']['output'];
  quantity?: Maybe<Scalars['Float']['output']>;
  rate?: Maybe<Scalars['Float']['output']>;
};

export type QuickbooksItemDetailsInput = {
  amountCents: Scalars['Int']['input'];
  description?: InputMaybe<Scalars['String']['input']>;
  id: Scalars['String']['input'];
  quantity: Scalars['Float']['input'];
  rate: Scalars['Float']['input'];
};

/**
 * Read-side mirror of `QuickbooksLinesInput`: a stored QuickBooks line
 * template, e.g. a recurring invoice schedule's `qbLines`. Subfields other
 * than the ids are nullable because the template is persisted JSON —
 * validated on write, tolerated on read.
 */
export type QuickbooksLines = {
  __typename?: 'QuickbooksLines';
  categories?: Maybe<Array<QuickbooksCategoryDetails>>;
  /** QuickBooks Class applied to every line of the synced invoice. */
  classId?: Maybe<Scalars['String']['output']>;
  items?: Maybe<Array<QuickbooksItemDetails>>;
};

export type QuickbooksLinesInput = {
  categories?: InputMaybe<Array<QuickbooksCategoryDetailsInput>>;
  /**
   * QuickBooks Class applied to every line of the synced invoice. Optional;
   * only meaningful for orgs with class tracking enabled in QuickBooks.
   */
  classId?: InputMaybe<Scalars['String']['input']>;
  items?: InputMaybe<Array<QuickbooksItemDetailsInput>>;
};

export type QuickbooksPreferences = {
  __typename?: 'QuickbooksPreferences';
  customTxnNumbers?: Maybe<Scalars['Boolean']['output']>;
};

export type ReactivateSubscriptionResponse = {
  __typename?: 'ReactivateSubscriptionResponse';
  error?: Maybe<Error>;
};

export type Receipt = {
  __typename?: 'Receipt';
  amountSubmitted?: Maybe<Scalars['String']['output']>;
  billedTo?: Maybe<Scalars['String']['output']>;
  contactEmail?: Maybe<Scalars['String']['output']>;
  contactName?: Maybe<Scalars['String']['output']>;
  feesCollected?: Maybe<Scalars['String']['output']>;
  hideCta?: Maybe<Scalars['Boolean']['output']>;
  logo?: Maybe<Scalars['String']['output']>;
  orderReference?: Maybe<Scalars['String']['output']>;
  organizationName?: Maybe<Scalars['String']['output']>;
  paymentDate?: Maybe<Scalars['String']['output']>;
  paymentId?: Maybe<Scalars['String']['output']>;
  paymentMethod?: Maybe<PaymentMethod>;
  totalAmount?: Maybe<Scalars['String']['output']>;
  totalCharged?: Maybe<Scalars['String']['output']>;
};

export type ReceiptResponse = {
  __typename?: 'ReceiptResponse';
  error?: Maybe<Error>;
  receipt?: Maybe<Receipt>;
};

/**
 * Filters that apply only to RECEIVABLE (accounts-receivable) activities.
 * Cross-source filters live directly on the paymentActivities query; filters
 * exclusive to one activity source are grouped into per-source inputs like this.
 * Combining these with a non-RECEIVABLE `source` is rejected as a bad request.
 */
export type ReceivableActivityFilters = {
  /**
   * Case-insensitive substring match on the AR invoice name (the QuestionForm
   * name the merchant gave the invoice / payment link).
   */
  invoiceLabel?: InputMaybe<Scalars['String']['input']>;
};

export type Recipient = {
  email?: InputMaybe<Scalars['String']['input']>;
  name?: InputMaybe<Scalars['String']['input']>;
  phoneNumber?: InputMaybe<Scalars['String']['input']>;
};

export type RecipientType = {
  __typename?: 'RecipientType';
  email?: Maybe<Scalars['String']['output']>;
  name: Scalars['String']['output'];
  phoneNumber?: Maybe<Scalars['String']['output']>;
  sentAt?: Maybe<Scalars['Float']['output']>;
};

export type RecommendedControls = {
  __typename?: 'RecommendedControls';
  maxTransactionSize?: Maybe<Scalars['String']['output']>;
  monthlyProcessingCap?: Maybe<Scalars['String']['output']>;
  payoutSpeed?: Maybe<Scalars['String']['output']>;
  reserve?: Maybe<Scalars['String']['output']>;
};

export type ReconcilePaymentResponse = {
  __typename?: 'ReconcilePaymentResponse';
  error?: Maybe<Error>;
  payment?: Maybe<Payment>;
};

export type RecordExternalBillPaymentResponse = {
  __typename?: 'RecordExternalBillPaymentResponse';
  bill?: Maybe<Bill>;
  billPayable?: Maybe<BillPayable>;
  error?: Maybe<Error>;
};

export type RecordExternalInvoicePaymentResponse = {
  __typename?: 'RecordExternalInvoicePaymentResponse';
  error?: Maybe<Error>;
  externalArPayment?: Maybe<ExternalArPayment>;
  questionForm?: Maybe<QuestionForm>;
};

export type RecordInvoiceDebitMemoResponse = {
  __typename?: 'RecordInvoiceDebitMemoResponse';
  debitMemo?: Maybe<ArDebitMemo>;
  error?: Maybe<Error>;
  questionForm?: Maybe<QuestionForm>;
};

export type RecordNickelBankingApplicationOpenedResponse = {
  __typename?: 'RecordNickelBankingApplicationOpenedResponse';
  error?: Maybe<Error>;
};

export type RecordOnboardingApplicationEventResponse = {
  __typename?: 'RecordOnboardingApplicationEventResponse';
  error?: Maybe<Error>;
};

export type RecordPaymentRequestViewResponse = {
  __typename?: 'RecordPaymentRequestViewResponse';
  error?: Maybe<Error>;
  /** False when the open was folded into one recorded moments earlier. */
  recorded?: Maybe<Scalars['Boolean']['output']>;
};

export type RecordPennyVisitPaymentOutcomeResponse = {
  __typename?: 'RecordPennyVisitPaymentOutcomeResponse';
  error?: Maybe<Error>;
  recorded?: Maybe<Scalars['Boolean']['output']>;
};

export type RecordUnderwritingFollowUpCallInput = {
  answers: Array<UnderwritingFollowUpAnswerInput>;
  notes?: InputMaybe<Scalars['String']['input']>;
  organizationId: Scalars['String']['input'];
  outcome: FollowUpCallOutcome;
  phoneNumber: Scalars['String']['input'];
  target: FollowUpCallTarget;
  /** AiApprovalSummaryEntry.runId of the summary the call followed up on; the org's latest when left out. */
  underwritingRunId?: InputMaybe<Scalars['String']['input']>;
};

export type RecordUnderwritingFollowUpCallPayload = {
  __typename?: 'RecordUnderwritingFollowUpCallPayload';
  error?: Maybe<Error>;
  followUpCall?: Maybe<UnderwritingFollowUpCall>;
  /** False when the call was saved but the underwriter's re-run was not queued. */
  reassessmentQueued?: Maybe<Scalars['Boolean']['output']>;
};

export type RecurringBill = {
  __typename?: 'RecurringBill';
  active?: Maybe<Scalars['Boolean']['output']>;
  amountCents: Scalars['Int']['output'];
  approvals?: Maybe<Array<Approval>>;
  /** What the caller can do to this series right now, and why not. */
  availableActions: Array<ActionAvailability>;
  bills?: Maybe<Array<Bill>>;
  createdAt: Scalars['Float']['output'];
  currentBillPayable?: Maybe<BillPayable>;
  endDate?: Maybe<Scalars['Float']['output']>;
  frequency: RecurringBillFrequency;
  id: Scalars['String']['output'];
  nextPaymentDate?: Maybe<Scalars['Float']['output']>;
  organizationId: Scalars['String']['output'];
  paymentMethod?: Maybe<PaymentMethod>;
  paymentMode: RecurringBillPaymentMode;
  reason?: Maybe<Scalars['String']['output']>;
  /** Set exactly when reminderTiming is DAYS_BEFORE_DUE. */
  reminderDaysBeforeDue?: Maybe<Scalars['Int']['output']>;
  /** Read only for a PAY_MANUALLY series. */
  reminderTiming: RecurringBillReminderTiming;
  startDate: Scalars['Float']['output'];
  status?: Maybe<RecurringStatus>;
  updatedAt: Scalars['Float']['output'];
  vendor?: Maybe<Vendor>;
  /** Null outside the caller's bill view. */
  vendorPayoutMethod?: Maybe<VendorPayoutMethod>;
};

export enum RecurringBillFrequency {
  Fortnightly = 'FORTNIGHTLY',
  Monthly = 'MONTHLY',
  Quarterly = 'QUARTERLY',
  Weekly = 'WEEKLY',
  Yearly = 'YEARLY'
}

/** Whether a recurring bill series pays each bill it creates. */
export enum RecurringBillPaymentMode {
  /**
   * Each bill is paid on its due date from the series' payment method to the
   * vendor's payout method.
   */
  PayAutomatically = 'PAY_AUTOMATICALLY',
  /**
   * Each bill is created unpaid, and the merchant pays it as any bill is paid.
   * The series is active from creation and needs no approval.
   */
  PayManually = 'PAY_MANUALLY'
}

/** When a PAY_MANUALLY series emails the merchant that a bill is ready to pay. */
export enum RecurringBillReminderTiming {
  /** reminderDaysBeforeDue days before the bill is due. */
  DaysBeforeDue = 'DAYS_BEFORE_DUE',
  Never = 'NEVER',
  /** The day the bill is created, one period before it is due. */
  OnCreation = 'ON_CREATION'
}

export type RecurringBillResponse = {
  __typename?: 'RecurringBillResponse';
  error?: Maybe<Error>;
  recurringBill?: Maybe<RecurringBill>;
};

export type RecurringBillsResponse = {
  __typename?: 'RecurringBillsResponse';
  error?: Maybe<Error>;
  recurringBills?: Maybe<Array<RecurringBill>>;
  totalResults?: Maybe<Scalars['Int']['output']>;
};

export type RecurringInvoice = {
  __typename?: 'RecurringInvoice';
  active?: Maybe<Scalars['Boolean']['output']>;
  amountCents: Scalars['Int']['output'];
  /** What the caller can do to this series right now, and why not. */
  availableActions: Array<ActionAvailability>;
  collectionMethod: RecurringInvoiceCollectionMethod;
  createdAt: Scalars['Float']['output'];
  creditCardEnabled?: Maybe<Scalars['Boolean']['output']>;
  /** The ISO 4217 code, uppercase, of amountCents and of every generated invoice. Always USD today. */
  currency: Scalars['String']['output'];
  currentQuestionForm?: Maybe<QuestionForm>;
  documents?: Maybe<Array<Scalars['String']['output']>>;
  endDate?: Maybe<Scalars['Float']['output']>;
  feePassthroughPercent?: Maybe<Scalars['Float']['output']>;
  frequency: RecurringBillFrequency;
  id: Scalars['String']['output'];
  invoiceNumber?: Maybe<Scalars['String']['output']>;
  memo?: Maybe<Scalars['String']['output']>;
  nextInvoiceDate?: Maybe<Scalars['Float']['output']>;
  organizationId?: Maybe<Scalars['String']['output']>;
  paymentMethod?: Maybe<PaymentMethod>;
  /**
   * The series' AR payout destination override, copied onto every generated
   * invoice: one of the org's payout methods; null = the org's selection.
   */
  payoutMethodId?: Maybe<Scalars['ID']['output']>;
  /**
   * The schedule's stored QuickBooks line template — every occurrence's QBO
   * mirror is built from it (see `recurringQbLines.ts`), and edits that pass
   * `lines` replace it wholesale. Null for a Nickel-only schedule.
   */
  qbLines?: Maybe<QuickbooksLines>;
  questionForms?: Maybe<Array<QuestionForm>>;
  retailCustomer?: Maybe<RetailCustomer>;
  /** Set exactly when sendTiming is DAYS_BEFORE_DUE. */
  sendDaysBeforeDue?: Maybe<Scalars['Int']['output']>;
  /** Read only for a SEND_INVOICE series. */
  sendTiming: RecurringInvoiceSendTiming;
  startDate: Scalars['Float']['output'];
  status?: Maybe<RecurringStatus>;
  updatedAt: Scalars['Float']['output'];
};

/** How a recurring invoice series collects each invoice. */
export enum RecurringInvoiceCollectionMethod {
  /**
   * Autopay: the series activates once the customer pays its first invoice and
   * saves a payment method, and each later invoice is charged to it.
   */
  ChargeAutomatically = 'CHARGE_AUTOMATICALLY',
  /**
   * Active from creation and never charged: each invoice after the first is
   * emailed to the customer when the series issues it, to pay through its link.
   */
  SendInvoice = 'SEND_INVOICE'
}

export type RecurringInvoiceResponse = {
  __typename?: 'RecurringInvoiceResponse';
  error?: Maybe<Error>;
  outstandingInvoiceReprice?: Maybe<OutstandingInvoiceRepriceOutcome>;
  recurringInvoice?: Maybe<RecurringInvoice>;
};

/** When a SEND_INVOICE series emails each invoice after the first. */
export enum RecurringInvoiceSendTiming {
  /** sendDaysBeforeDue days before the invoice is due. */
  DaysBeforeDue = 'DAYS_BEFORE_DUE',
  /** Never to the customer: the merchant is emailed that it is ready to send. */
  Manual = 'MANUAL',
  /** The day the invoice is created, one period before it is due. */
  OnCreation = 'ON_CREATION'
}

export type RecurringInvoicesResponse = {
  __typename?: 'RecurringInvoicesResponse';
  error?: Maybe<Error>;
  recurringInvoices?: Maybe<Array<RecurringInvoice>>;
  totalResults?: Maybe<Scalars['Int']['output']>;
};

export enum RecurringStatus {
  Active = 'ACTIVE',
  Archived = 'ARCHIVED',
  Completed = 'COMPLETED',
  Deleted = 'DELETED',
  Draft = 'DRAFT',
  Paused = 'PAUSED',
  Pending = 'PENDING'
}

export type RedeemMcpActionApprovalResponse = {
  __typename?: 'RedeemMcpActionApprovalResponse';
  error?: Maybe<Error>;
  /** True when an approval was found and spent: the call may run, once. */
  redeemed: Scalars['Boolean']['output'];
};

export type Referral = {
  __typename?: 'Referral';
  activeAt?: Maybe<Scalars['Float']['output']>;
  approvedAt?: Maybe<Scalars['Float']['output']>;
  createdAt?: Maybe<Scalars['Float']['output']>;
  email?: Maybe<Scalars['String']['output']>;
  name?: Maybe<Scalars['String']['output']>;
  role?: Maybe<Scalars['String']['output']>;
  status?: Maybe<ReferralStatus>;
};

export type ReferralInvite = {
  __typename?: 'ReferralInvite';
  acceptedAt?: Maybe<Scalars['Float']['output']>;
  createdAt: Scalars['Float']['output'];
  email: Scalars['String']['output'];
  id: Scalars['ID']['output'];
  lastSentAt: Scalars['Float']['output'];
};

export enum ReferralStatus {
  AccountClosed = 'ACCOUNT_CLOSED',
  Active = 'ACTIVE',
  NotApproved = 'NOT_APPROVED',
  Pending = 'PENDING',
  Qualifying = 'QUALIFYING'
}

export type Refund = {
  __typename?: 'Refund';
  activity?: Maybe<PaymentActivity>;
  amountCents?: Maybe<Scalars['Int']['output']>;
  charge?: Maybe<Charge>;
  createdAt?: Maybe<Scalars['Float']['output']>;
  externalRefundId?: Maybe<Scalars['String']['output']>;
  id: Scalars['String']['output'];
  payout?: Maybe<Payout>;
  /**
   * Absolute URL to download this refund's receipt PDF, generated on demand by
   * GET /api/pdf. Sessionless: the URL carries a token signed for this refund, so
   * it can be shared directly. Null for failed or canceled refunds, and for a
   * caller outside the refund's organization.
   */
  pdfUrl?: Maybe<Scalars['String']['output']>;
  status?: Maybe<Scalars['String']['output']>;
  voided?: Maybe<Scalars['Boolean']['output']>;
};

/** An account the merchant may pick to fund a refund. */
export type RefundFundingOption = {
  __typename?: 'RefundFundingOption';
  payoutMethod: PayoutMethod;
  rail: RefundFundingRail;
  /**
   * Why this account cannot fund the refund now; null when it can. A bank's
   * balance is read when the refund is debited, so a bank is only marked when
   * it could never be confirmed.
   */
  unavailableReason?: Maybe<RefundFundingUnavailableReason>;
};

export enum RefundFundingRail {
  Bank = 'BANK',
  NickelBalance = 'NICKEL_BALANCE'
}

export enum RefundFundingReason {
  /** The payment has not been paid out yet; this is where the organization's payouts go. */
  PayoutDestination = 'PAYOUT_DESTINATION',
  /** The account the payment settled into cannot fund the refund (no live bank link, or a Nickel Balance that is short or frozen). */
  SettledAccountUnavailable = 'SETTLED_ACCOUNT_UNAVAILABLE',
  /** The payment's proceeds settled into this account. */
  SettledHere = 'SETTLED_HERE',
  /** Only on `staffOverride`: the account Nickel staff may redirect the refund to. */
  StaffRedirect = 'STAFF_REDIRECT'
}

export type RefundFundingSource = {
  __typename?: 'RefundFundingSource';
  payoutMethod: PayoutMethod;
  rail: RefundFundingRail;
  reason: RefundFundingReason;
};

export type RefundFundingSourceResponse = {
  __typename?: 'RefundFundingSourceResponse';
  error?: Maybe<Error>;
  /**
   * Every account `refundPayment(fundingPayoutMethodId:)` accepts from this
   * caller, the default (`source`) first. Null only with `error`.
   */
  options?: Maybe<Array<RefundFundingOption>>;
  /** Null when the organization has no account a refund could be debited from. */
  source?: Maybe<RefundFundingSource>;
  /**
   * Nickel staff impersonating the organization only: the other rail's account,
   * which `refundPayment(fundingSource:)` would redirect the refund to. Null
   * for a merchant, and when the organization has no such account.
   */
  staffOverride?: Maybe<RefundFundingSource>;
};

export enum RefundFundingUnavailableReason {
  /**
   * A bank with no Plaid link, other than the one the payment was deposited
   * into: its balance cannot be confirmed, so a refund may not be debited from it.
   */
  BalanceUnverifiable = 'BALANCE_UNVERIFIABLE',
  /** The organization's Nickel Banking is frozen. */
  Frozen = 'FROZEN',
  /** The Nickel Banking account's available balance is less than the refund. */
  InsufficientFunds = 'INSUFFICIENT_FUNDS'
}

/**
 * One invoice line of a refund: how much the refund takes off one invoice the
 * payment is applied to.
 */
export type RefundLineInput = {
  /**
   * Cents to take off the invoice, in invoice terms: the customer's share of
   * the platform fee is not part of a line. More than zero, and at most what
   * the payment still has applied to the invoice.
   */
  amountCents: Scalars['Int']['input'];
  /** The invoice the line comes off. The payment must be applied to it. */
  questionFormId: Scalars['ID']['input'];
};

export type RefundPaymentResponse = {
  __typename?: 'RefundPaymentResponse';
  error?: Maybe<Error>;
  payment?: Maybe<Payment>;
};

export type RefundResponse = {
  __typename?: 'RefundResponse';
  error?: Maybe<Error>;
  refund?: Maybe<Refund>;
};

export type RefundsResponse = {
  __typename?: 'RefundsResponse';
  error?: Maybe<Error>;
  refunds?: Maybe<Array<Refund>>;
  totalResults?: Maybe<Scalars['Int']['output']>;
};

export enum ReminderDirection {
  AfterDueDate = 'AFTER_DUE_DATE',
  BeforeDueDate = 'BEFORE_DUE_DATE'
}

export type ReminderRule = {
  __typename?: 'ReminderRule';
  bodyTemplate: Scalars['String']['output'];
  /**
   * Whether this rule's reminders CC the org's Default Invoice Contact
   * (MerchantNotificationSetting.carbonCopyEmail), as invoice sends do. Nobody
   * is CC'd while that contact setting is off.
   */
  ccInvoiceContact: Scalars['Boolean']['output'];
  count: Scalars['Int']['output'];
  createdAt: Scalars['Float']['output'];
  direction: ReminderDirection;
  id: Scalars['String']['output'];
  isActive: Scalars['Boolean']['output'];
  subjectTemplate: Scalars['String']['output'];
  unit: ReminderUnit;
  updatedAt: Scalars['Float']['output'];
};

export type ReminderRuleResponse = {
  __typename?: 'ReminderRuleResponse';
  error?: Maybe<Error>;
  reminderRule?: Maybe<ReminderRule>;
};

export type ReminderRulesResponse = {
  __typename?: 'ReminderRulesResponse';
  error?: Maybe<Error>;
  reminderRules?: Maybe<Array<ReminderRule>>;
};

export enum ReminderUnit {
  Days = 'DAYS',
  Months = 'MONTHS',
  Weeks = 'WEEKS'
}

export type RemoveInviteResponse = {
  __typename?: 'RemoveInviteResponse';
  error?: Maybe<Error>;
};

export type RemoveRetailCustomerResponse = {
  __typename?: 'RemoveRetailCustomerResponse';
  error?: Maybe<Error>;
};

export type RemoveSubscriptionResponse = {
  __typename?: 'RemoveSubscriptionResponse';
  error?: Maybe<Error>;
  subscription?: Maybe<SubscriptionDetail>;
};

export type RemoveTagResponse = {
  __typename?: 'RemoveTagResponse';
  error?: Maybe<Error>;
};

export type RemoveTeammateResponse = {
  __typename?: 'RemoveTeammateResponse';
  error?: Maybe<Error>;
};

export type RemoveVendorResponse = {
  __typename?: 'RemoveVendorResponse';
  error?: Maybe<Error>;
};

export type ReorderTagsResponse = {
  __typename?: 'ReorderTagsResponse';
  error?: Maybe<Error>;
  tags?: Maybe<Array<Tag>>;
};

export type RequestAlloyApplicationResponse = {
  __typename?: 'RequestAlloyApplicationResponse';
  error?: Maybe<Error>;
  success?: Maybe<Scalars['Boolean']['output']>;
};

export type RequestCustomerDebitAuthorizationResponse = {
  __typename?: 'RequestCustomerDebitAuthorizationResponse';
  debitAuthorization?: Maybe<DebitAuthorization>;
  error?: Maybe<Error>;
  url?: Maybe<Scalars['String']['output']>;
};

export type RequestEmail = {
  __typename?: 'RequestEmail';
  debitAuthorizationId?: Maybe<Scalars['String']['output']>;
  shortId: Scalars['String']['output'];
};

export type RequestPennyVisitPaymentApprovalResponse = {
  __typename?: 'RequestPennyVisitPaymentApprovalResponse';
  approvalId?: Maybe<Scalars['ID']['output']>;
  error?: Maybe<Error>;
};

export type RequestSignInLinkPayload = {
  __typename?: 'RequestSignInLinkPayload';
  error?: Maybe<Error>;
};

export type RequestVendorDeliveryDetailsResponse = {
  __typename?: 'RequestVendorDeliveryDetailsResponse';
  error?: Maybe<Error>;
  /**
   * The vendor's self-service link, the one emailed to them. Null unless the
   * caller holds bill_pay.vendors.manage_payment_accounts,
   * bill_pay.vendors.edit_check_addresses and
   * bill_pay.vendors.edit_international_wire_info: the link's token sets the
   * vendor's bank account, check address and wire details on its own.
   */
  url?: Maybe<Scalars['String']['output']>;
};

export type ResendRadarChallengePayload = {
  __typename?: 'ResendRadarChallengePayload';
  error?: Maybe<Error>;
};

export type ResendSsoVerificationCodePayload = {
  __typename?: 'ResendSsoVerificationCodePayload';
  error?: Maybe<Error>;
};

export type RetailCustomer = {
  __typename?: 'RetailCustomer';
  accountingSource?: Maybe<AccountingSource>;
  achEnabled?: Maybe<Scalars['Boolean']['output']>;
  /** What the caller can do to this customer right now, and why not. */
  availableActions?: Maybe<Array<ActionAvailability>>;
  creditCardEnabled?: Maybe<Scalars['Boolean']['output']>;
  customFeePassthroughPercent?: Maybe<Scalars['Int']['output']>;
  debitAuthorizations?: Maybe<Array<DebitAuthorization>>;
  emails?: Maybe<Array<Scalars['String']['output']>>;
  externalId?: Maybe<Scalars['String']['output']>;
  id: Scalars['String']['output'];
  integrationEmails?: Maybe<Array<Scalars['String']['output']>>;
  /** Summed over the invoices in the caller's invoice view, as questionForms lists them. */
  invoiceSummary?: Maybe<InvoiceSummary>;
  lastAgingReportSentAt?: Maybe<Scalars['Float']['output']>;
  name: Scalars['String']['output'];
  pendingDebitAuthorizations?: Maybe<Array<DebitAuthorization>>;
  phoneNumbers?: Maybe<Array<Scalars['String']['output']>>;
  /**
   * The customer's invoices. Empty to a caller outside the customer's
   * organization — a payer reaching the customer through a payment link — when
   * the organization has turned off PaymentSettings.showOtherUnpaidInvoices.
   */
  questionForms?: Maybe<Array<QuestionForm>>;
  tags?: Maybe<Array<Tag>>;
};

/** Column mapping for a retail-customer CSV bulk import (0-based column indices). */
export type RetailCustomerCsvColumnMappingInput = {
  /** 0-based index of the column holding the customer's email. Required — every created customer needs an email, so an import without an email column would fail 100% of its rows. */
  emails: Scalars['Int']['input'];
  /** 0-based index of the column holding the customer's display name. */
  name: Scalars['Int']['input'];
  /** 0-based index of the column holding the customer's phone number, if present. */
  phoneNumbers?: InputMaybe<Scalars['Int']['input']>;
};

/**
 * Saved-payment-method state of a retail customer, derived from its debit
 * authorizations. ACTIVE and PENDING_REQUEST can overlap (an active method
 * plus a newer open request); NONE means neither.
 */
export enum RetailCustomerPaymentMethodStatus {
  /** Has an active saved payment method authorized for charges */
  Active = 'ACTIVE',
  /** No active payment method and no pending authorization request */
  None = 'NONE',
  /** Has an open authorization request the customer has not completed */
  PendingRequest = 'PENDING_REQUEST'
}

export type RetailCustomerResponse = {
  __typename?: 'RetailCustomerResponse';
  error?: Maybe<Error>;
  retailCustomer?: Maybe<RetailCustomer>;
};

export type RetailCustomersResponse = {
  __typename?: 'RetailCustomersResponse';
  error?: Maybe<Error>;
  retailCustomers?: Maybe<Array<RetailCustomer>>;
  totalResults?: Maybe<Scalars['Int']['output']>;
};

export type Return = {
  __typename?: 'Return';
  activity?: Maybe<PaymentActivity>;
  amountCents: Scalars['Int']['output'];
  charge: Charge;
  createdAt: Scalars['Float']['output'];
  externalReturnId: Scalars['String']['output'];
  id: Scalars['String']['output'];
  payout?: Maybe<Payout>;
};

export type ReturnResponse = {
  __typename?: 'ReturnResponse';
  achReturn?: Maybe<Return>;
  error?: Maybe<Error>;
};

export type RevokeApiTokenResponse = {
  __typename?: 'RevokeApiTokenResponse';
  error?: Maybe<Error>;
  success?: Maybe<Scalars['Boolean']['output']>;
};

/**
 * The outcome of revokeSessions: whose outstanding sessions were just rejected.
 * `revokedUserIds` is what actually changed, so a caller can tell an org-wide
 * revocation from a single member without re-reading the org.
 */
export type RevokeSessionsResponse = {
  __typename?: 'RevokeSessionsResponse';
  error?: Maybe<Error>;
  organizationId?: Maybe<Scalars['String']['output']>;
  /** ISO-8601. Every session JWT minted before this instant is now rejected. */
  revokedAt?: Maybe<Scalars['String']['output']>;
  revokedUserIds?: Maybe<Array<Scalars['String']['output']>>;
};

/**
 * What a request for information asks the merchant for. The merchant's RFI
 * flow renders one step per item; the request itself is recorded on the org's
 * onboardingInfo (rfiRequest), so adding a value here is a schema change and
 * a renderer, never a migration. An org RFI'd before requests were recorded is
 * asked for IDENTITY + PROOF_OF_ADDRESS + CERTIFICATE_OF_INCORPORATION.
 */
export enum RfiItem {
  /** A document: articles/certificate of incorporation or formation. */
  CertificateOfIncorporation = 'CERTIFICATE_OF_INCORPORATION',
  /** Plaid Identity Verification: photo ID + liveness, proving the applicant holds the account. */
  Identity = 'IDENTITY',
  /**
   * The business's primary operating account, linked through Plaid or entered
   * manually with statements. Becomes the AR payout destination; the previous
   * method stays on file for review.
   */
  PrimaryBankAccount = 'PRIMARY_BANK_ACCOUNT',
  /** A document: utility bill, lease or similar at the business address. */
  ProofOfAddress = 'PROOF_OF_ADDRESS'
}

/** The open or last-answered request for information on an org (onboardingInfo.rfiRequest). */
export type RfiRequest = {
  __typename?: 'RfiRequest';
  /** How PRIMARY_BANK_ACCOUNT was answered, when it was requested. */
  bankAccountMethod?: Maybe<Scalars['String']['output']>;
  items: Array<RfiItem>;
  /** Staff's note to the merchant, shown at the top of the RFI flow. */
  note?: Maybe<Scalars['String']['output']>;
  requestedAt: Scalars['String']['output'];
  /** Staff email; null when the blacklist match opened the request. */
  requestedBy?: Maybe<Scalars['String']['output']>;
  /** The merchant's written answer to the note, when there was one. */
  response?: Maybe<Scalars['String']['output']>;
  /** ISO timestamp of the merchant's answer; null while open. */
  submittedAt?: Maybe<Scalars['String']['output']>;
};

export type S3SignedUrlResponse = {
  __typename?: 'S3SignedURLResponse';
  error?: Maybe<Error>;
  url?: Maybe<Scalars['String']['output']>;
};

export type S3ViewUrlResponse = {
  __typename?: 'S3ViewURLResponse';
  error?: Maybe<Error>;
  url?: Maybe<Scalars['String']['output']>;
};

export enum SsoProvider {
  Google = 'GOOGLE',
  Intuit = 'INTUIT',
  Microsoft = 'MICROSOFT'
}

export type SaveApprovalRuleResponse = {
  __typename?: 'SaveApprovalRuleResponse';
  error?: Maybe<Error>;
  /** Every rule's cross-rule issues after the save, for the editor to show. */
  issues?: Maybe<Array<ApprovalRuleIssue>>;
  rule?: Maybe<ApprovalRule>;
};

export type ScheduleSubscriptionDowngradeResponse = {
  __typename?: 'ScheduleSubscriptionDowngradeResponse';
  error?: Maybe<Error>;
};

export type ScheduledPlanChange = {
  __typename?: 'ScheduledPlanChange';
  billingMonths: Scalars['Int']['output'];
  /** Epoch milliseconds: the renewal at which the new plan takes over. */
  effectiveAt: Scalars['Float']['output'];
  name: Scalars['String']['output'];
  planName: Scalars['String']['output'];
  priceCents: Scalars['Int']['output'];
};

export type Scorecard = {
  __typename?: 'Scorecard';
  account: ScorecardDimension;
  business: ScorecardDimension;
  transactions: ScorecardDimension;
};

export type ScorecardDimension = {
  __typename?: 'ScorecardDimension';
  notes: Array<ScorecardNote>;
  score?: Maybe<Scalars['Float']['output']>;
};

export type ScorecardNote = {
  __typename?: 'ScorecardNote';
  text: Scalars['String']['output'];
  type: Scalars['String']['output'];
};

export type SendArAgingReportEmailResponse = {
  __typename?: 'SendArAgingReportEmailResponse';
  error?: Maybe<Error>;
  success?: Maybe<Scalars['Boolean']['output']>;
};

export type SendNickelVerificationCodeViaEmailResponse = {
  __typename?: 'SendNickelVerificationCodeViaEmailResponse';
  error?: Maybe<Error>;
};

export type SendPaymentLinkToRecipientsResponse = {
  __typename?: 'SendPaymentLinkToRecipientsResponse';
  error?: Maybe<Error>;
  /**
   * Set when an approval rule held the send: nothing was sent, and the invoice
   * goes to its recipients once the request is approved. Sending again while it
   * waits returns the same request.
   */
  heldForApproval?: Maybe<InvoiceApprovalRequest>;
  questionForm?: Maybe<QuestionForm>;
};

export type SendPennyMessageResponse = {
  __typename?: 'SendPennyMessageResponse';
  conversationId?: Maybe<Scalars['ID']['output']>;
  error?: Maybe<Error>;
  message?: Maybe<PennyMessage>;
};

export type SendReferralInviteResponse = {
  __typename?: 'SendReferralInviteResponse';
  error?: Maybe<Error>;
  invite?: Maybe<ReferralInvite>;
};

export type SendTestReminderResponse = {
  __typename?: 'SendTestReminderResponse';
  error?: Maybe<Error>;
  sentCount?: Maybe<Scalars['Int']['output']>;
};

export type SetAccountantAccessResponse = {
  __typename?: 'SetAccountantAccessResponse';
  error?: Maybe<Error>;
  organization?: Maybe<Organization>;
};

export type SetCustomDomainResponse = {
  __typename?: 'SetCustomDomainResponse';
  /**
   * The domain that was submitted. This is the pending candidate while
   * verification is in flight — it is not yet committed to the org
   * record. Poll customDomainVerificationStatus to observe the terminal
   * state (COMPLETED commits the domain; FAILED rolls it back).
   */
  customDomain?: Maybe<Scalars['String']['output']>;
  error?: Maybe<Error>;
};

export type SetNickelBalanceAccountChartOfAccountResponse = {
  __typename?: 'SetNickelBalanceAccountChartOfAccountResponse';
  account?: Maybe<NickelBalanceAccount>;
  error?: Maybe<Error>;
};

export type SetNickelBalanceInterestRateResponse = {
  __typename?: 'SetNickelBalanceInterestRateResponse';
  error?: Maybe<Error>;
  /** Null when the global-default rate was set. */
  organizationId?: Maybe<Scalars['String']['output']>;
  rateBips?: Maybe<Scalars['Int']['output']>;
};

/**
 * The outcome of setOrganizationAccountant: the org's firm link as it now
 * stands, both null when it was unlinked.
 */
export type SetOrganizationAccountantResponse = {
  __typename?: 'SetOrganizationAccountantResponse';
  accountantId?: Maybe<Scalars['String']['output']>;
  accountantName?: Maybe<Scalars['String']['output']>;
  error?: Maybe<Error>;
  organizationId?: Maybe<Scalars['String']['output']>;
};

export type SetPayoutMethodWireAccountDetailsResponse = {
  __typename?: 'SetPayoutMethodWireAccountDetailsResponse';
  error?: Maybe<Error>;
  fundingSource?: Maybe<NickelBalanceFundingSource>;
};

export type SetPayoutMethodWireBeneficiaryNameResponse = {
  __typename?: 'SetPayoutMethodWireBeneficiaryNameResponse';
  error?: Maybe<Error>;
  fundingSource?: Maybe<NickelBalanceFundingSource>;
};

export type SignUpThroughPartnerEnrollmentPayload = {
  __typename?: 'SignUpThroughPartnerEnrollmentPayload';
  account?: Maybe<Scalars['String']['output']>;
  error?: Maybe<Error>;
  expiresAt?: Maybe<Scalars['String']['output']>;
  token?: Maybe<Scalars['String']['output']>;
};

/**
 * The OrganizationAttribution row (docs/ad-conversions/design.md §4.1) as
 * captured, plus the referral records: each click id is one ad platform, so a
 * non-null value is that platform's badge. No classification happens here —
 * the client maps field to icon.
 */
export type SignupAttribution = {
  __typename?: 'SignupAttribution';
  /** Meta. */
  fbclid?: Maybe<Scalars['String']['output']>;
  firstTouchReferrer?: Maybe<Scalars['String']['output']>;
  gbraid?: Maybe<Scalars['String']['output']>;
  /** Google Ads click ids (web, iOS app-to-web, iOS web-to-app). */
  gclid?: Maybe<Scalars['String']['output']>;
  /** LinkedIn. */
  liFatId?: Maybe<Scalars['String']['output']>;
  /** Microsoft Ads. */
  msclkid?: Maybe<Scalars['String']['output']>;
  /** OpenAI / ChatGPT ads. */
  oppref?: Maybe<Scalars['String']['output']>;
  /** PartnerStack partner key when a partner link brought this org. */
  partnerStackPartnerKey?: Maybe<Scalars['String']['output']>;
  /** Name of the merchant whose referral code or invite brought this org. */
  referrerOrganizationName?: Maybe<Scalars['String']['output']>;
  /** TikTok. */
  ttclid?: Maybe<Scalars['String']['output']>;
  utmCampaign?: Maybe<Scalars['String']['output']>;
  utmMedium?: Maybe<Scalars['String']['output']>;
  utmSource?: Maybe<Scalars['String']['output']>;
  wbraid?: Maybe<Scalars['String']['output']>;
};

export type SignupPayload = {
  __typename?: 'SignupPayload';
  error?: Maybe<Error>;
  id?: Maybe<Scalars['ID']['output']>;
  token?: Maybe<Scalars['String']['output']>;
};

export type SimulateBankTransferCreditResponse = {
  __typename?: 'SimulateBankTransferCreditResponse';
  error?: Maybe<Error>;
  /**
   * The posted transaction whose `transaction.created` event was queued for the
   * worker; the invoice updates once it runs.
   */
  transactionId?: Maybe<Scalars['String']['output']>;
  /**
   * The simulated Increase inbound transfer (`inbound_ach_transfer_…`,
   * `inbound_wire_transfer_…`, `inbound_real_time_payments_transfer_…` or
   * `inbound_fednow_transfer_…`).
   */
  transferId?: Maybe<Scalars['String']['output']>;
};

export enum SortOrder {
  AmountAsc = 'AMOUNT_ASC',
  AmountDesc = 'AMOUNT_DESC',
  DateAsc = 'DATE_ASC',
  DateDesc = 'DATE_DESC'
}

export type SsoCompleteResult = {
  __typename?: 'SsoCompleteResult';
  error?: Maybe<Error>;
  registrationToken?: Maybe<Scalars['String']['output']>;
};

export type StagedSubscriptionDetail = {
  __typename?: 'StagedSubscriptionDetail';
  billingFrequency?: Maybe<Scalars['Int']['output']>;
  /**
   * Set when the subscription has been voluntarily canceled but is still in its
   * paid grace window (access retained until nextBillingDate, then downgraded).
   * Null when the subscription is active. The frontend uses this to show an
   * "expiring" state + resubscribe option instead of "next charge".
   */
  canceledAt?: Maybe<Scalars['Float']['output']>;
  initiated?: Maybe<Scalars['Boolean']['output']>;
  name?: Maybe<Scalars['String']['output']>;
  nextBillingDate?: Maybe<Scalars['Float']['output']>;
  numberOfBillingCycles?: Maybe<Scalars['Int']['output']>;
  organizationId?: Maybe<Scalars['String']['output']>;
  paymentMethod?: Maybe<PaymentMethod>;
  planId?: Maybe<Scalars['String']['output']>;
  price?: Maybe<Scalars['String']['output']>;
  /**
   * A plan change chosen for the next renewal. While it is pending, `planId`
   * and `name` are the plan in effect and `price` is what the renewal charges.
   */
  scheduledPlanChange?: Maybe<ScheduledPlanChange>;
  startDate?: Maybe<Scalars['Float']['output']>;
};

export type StagedSubscriptionResponse = {
  __typename?: 'StagedSubscriptionResponse';
  error?: Maybe<Error>;
  stagedSubscription?: Maybe<StagedSubscriptionDetail>;
};

export type StartConnectorConnectionResponse = {
  __typename?: 'StartConnectorConnectionResponse';
  connection?: Maybe<ConnectorConnection>;
  error?: Maybe<Error>;
  /** Where to send the person to finish connecting: the provider's Connect Link. */
  redirectUrl?: Maybe<Scalars['String']['output']>;
};

export type StartFollowUpVoiceCallInput = {
  organizationId: Scalars['String']['input'];
  phoneNumber: Scalars['String']['input'];
  target: FollowUpCallTarget;
  /** AiApprovalSummaryEntry.runId of the summary the call follows up on; the org's latest when left out. */
  underwritingRunId?: InputMaybe<Scalars['String']['input']>;
};

export type StartFollowUpVoiceCallPayload = {
  __typename?: 'StartFollowUpVoiceCallPayload';
  error?: Maybe<Error>;
  voiceCall?: Maybe<FollowUpVoiceCall>;
};

export type StartPartnerEnrollmentSessionPayload = {
  __typename?: 'StartPartnerEnrollmentSessionPayload';
  error?: Maybe<Error>;
  expiresAt?: Maybe<Scalars['String']['output']>;
  token?: Maybe<Scalars['String']['output']>;
};

export type StopCheckPaymentResponse = {
  __typename?: 'StopCheckPaymentResponse';
  billPayment?: Maybe<BillPayment>;
  error?: Maybe<Error>;
};

export type SubmitNickelBalanceApplicationResponse = {
  __typename?: 'SubmitNickelBalanceApplicationResponse';
  /** The first account, now open (also on an idempotent re-submit while ACTIVE). */
  account?: Maybe<NickelBalanceAccount>;
  error?: Maybe<Error>;
  organization?: Maybe<Organization>;
};

export type Subscription = {
  __typename?: 'Subscription';
  pennyConversationEvents: PennyConversationEvent;
};


export type SubscriptionPennyConversationEventsArgs = {
  conversationId: Scalars['ID']['input'];
};

export type SubscriptionDetail = {
  __typename?: 'SubscriptionDetail';
  billingDayOfMonth?: Maybe<Scalars['Int']['output']>;
  billingEndDate?: Maybe<Scalars['String']['output']>;
  billingStartDate?: Maybe<Scalars['String']['output']>;
  cycleNumber?: Maybe<Scalars['Int']['output']>;
  id?: Maybe<Scalars['String']['output']>;
  nextBillingAmount?: Maybe<Scalars['Int']['output']>;
  nextBillingDate?: Maybe<Scalars['String']['output']>;
  paymentMethod?: Maybe<PaymentMethodDetail>;
  planId?: Maybe<Scalars['String']['output']>;
  planName?: Maybe<Scalars['String']['output']>;
  status?: Maybe<Scalars['String']['output']>;
  totalCycleNumbers?: Maybe<Scalars['Int']['output']>;
};

/**
 * A move from Pro to Plus, effective at the next renewal: nothing is charged or
 * refunded now, and the renewal charges the new plan's price. The client passes
 * `nextChargeCents` back to `scheduleSubscriptionDowngrade` as
 * `nextChargeAcknowledgedCents`.
 */
export type SubscriptionDowngradePreview = {
  __typename?: 'SubscriptionDowngradePreview';
  billingMonths: Scalars['Int']['output'];
  /** ISO timestamp of the renewal at which the new plan takes over. */
  effectiveDate: Scalars['String']['output'];
  nextChargeCents: Scalars['Int']['output'];
  planName: Scalars['String']['output'];
};

export type SubscriptionDowngradePreviewResponse = {
  __typename?: 'SubscriptionDowngradePreviewResponse';
  error?: Maybe<Error>;
  preview?: Maybe<SubscriptionDowngradePreview>;
};

export type SubscriptionPayment = {
  __typename?: 'SubscriptionPayment';
  amountInCents?: Maybe<Scalars['Int']['output']>;
  charges?: Maybe<Array<Charge>>;
  createdAt?: Maybe<Scalars['Float']['output']>;
  externalId?: Maybe<Scalars['String']['output']>;
  id?: Maybe<Scalars['String']['output']>;
  paymentMethod?: Maybe<PaymentMethod>;
  /**
   * Absolute URL to download this subscription payment's receipt PDF, signed like
   * Payment.pdfUrl. Null for a caller outside its organization.
   */
  pdfUrl?: Maybe<Scalars['String']['output']>;
  status?: Maybe<Scalars['String']['output']>;
  updatedAt?: Maybe<Scalars['Float']['output']>;
};

export type SubscriptionPaymentsResponse = {
  __typename?: 'SubscriptionPaymentsResponse';
  error?: Maybe<Error>;
  subscriptionPayments?: Maybe<Array<SubscriptionPayment>>;
};

export type SubscriptionResponse = {
  __typename?: 'SubscriptionResponse';
  error?: Maybe<Error>;
  subscription?: Maybe<SubscriptionDetail>;
};

/**
 * Quote for an effective-now upgrade (engine-billed orgs only), charged
 * immediately: a same-cadence tier upgrade bills the day-prorated price
 * difference with the renewal anniversary unchanged, and a monthly-to-annual
 * cadence upgrade bills the new plan's full price minus credit for the
 * unused monthly days, starting a fresh period today. The client passes
 * `chargeTodayCents` back to `addSubscription` as
 * `prorationAcknowledgedCents` — the mutation refuses unless it matches
 * the live quote, so a UI that never displayed the amount cannot trigger
 * the charge.
 */
export type SubscriptionUpgradePreview = {
  __typename?: 'SubscriptionUpgradePreview';
  /** True for a monthly-to-annual upgrade: a fresh billing period starts today and nextRenewalDate is one new-plan period out. False when the anniversary is unchanged. */
  anniversaryResets: Scalars['Boolean']['output'];
  chargeTodayCents: Scalars['Int']['output'];
  nextRenewalAmountCents: Scalars['Int']['output'];
  /** ISO timestamp of the next renewal: the unchanged anniversary for tier upgrades (trial end for trials), or one new-plan period from now when the upgrade resets the anniversary. */
  nextRenewalDate: Scalars['String']['output'];
  planName: Scalars['String']['output'];
  remainingDays: Scalars['Int']['output'];
  totalDays: Scalars['Int']['output'];
  unusedCreditCents: Scalars['Int']['output'];
};

export type SubscriptionUpgradePreviewResponse = {
  __typename?: 'SubscriptionUpgradePreviewResponse';
  error?: Maybe<Error>;
  preview?: Maybe<SubscriptionUpgradePreview>;
};

export type SupplierRef = {
  __typename?: 'SupplierRef';
  id?: Maybe<Scalars['String']['output']>;
  supplierName?: Maybe<Scalars['String']['output']>;
};

export type SupportMessengerResponse = {
  __typename?: 'SupportMessengerResponse';
  error?: Maybe<Error>;
  session?: Maybe<SupportMessengerSession>;
};

export type SupportMessengerSession = {
  __typename?: 'SupportMessengerSession';
  /** The vendor's public app id for its widget. */
  appId: Scalars['String']['output'];
  /** ISO 8601. After this the widget refuses `token`. */
  expiresAt: Scalars['String']['output'];
  /** The vendor whose widget to load, such as `intercom`. */
  provider: Scalars['String']['output'];
  /** Signed identity for the widget's boot call. Fetch a fresh one per boot. */
  token: Scalars['String']['output'];
};

export type SurveyResponse = {
  __typename?: 'SurveyResponse';
  error?: Maybe<Error>;
  feedback?: Maybe<Scalars['String']['output']>;
  rating?: Maybe<Scalars['String']['output']>;
};

export type SyncAccountingCustomersResponse = {
  __typename?: 'SyncAccountingCustomersResponse';
  error?: Maybe<Error>;
};

export type Tag = {
  __typename?: 'Tag';
  assignmentCount: Scalars['Int']['output'];
  id: Scalars['String']['output'];
  name: Scalars['String']['output'];
  position: Scalars['Int']['output'];
  type: TagType;
};

export type TagAssignmentResponse = {
  __typename?: 'TagAssignmentResponse';
  entityId?: Maybe<Scalars['String']['output']>;
  error?: Maybe<Error>;
  tag?: Maybe<Tag>;
};

export type TagResponse = {
  __typename?: 'TagResponse';
  error?: Maybe<Error>;
  tag?: Maybe<Tag>;
};

export enum TagType {
  Bill = 'BILL',
  QuestionForm = 'QUESTION_FORM',
  RetailCustomer = 'RETAIL_CUSTOMER',
  Vendor = 'VENDOR'
}

export type TeamNotificationsResponse = {
  __typename?: 'TeamNotificationsResponse';
  /**
   * Present when the org JSON was written — customer switches, or a merchant
   * category switched back on by its first subscription — the same merge shape
   * updateNotificationSettings returns.
   */
  accountInfo?: Maybe<AccountInfo>;
  /**
   * Every address on the org's notifications after the write, present when the
   * call sent `emailRecipients`.
   */
  emailRecipients?: Maybe<Array<NotificationEmailRecipient>>;
  error?: Maybe<Error>;
  /**
   * The users whose maps were written — the members a push reached and the
   * users in `members` — once each, so the client merges by id.
   */
  members?: Maybe<Array<User>>;
  /** The roles whose cells were written (notifications.md §14). */
  workspaceRoles?: Maybe<Array<WorkspaceRole>>;
};

/**
 * The issuer wants the cardholder to complete a 3DS challenge before the
 * charge (external 3DS on the Basis Theory frame). The portal hands these to
 * the frame's `startThreeDSChallenge`, then calls confirmPayment again with
 * `threeDSChallengeCompleted: true`. The payment is back in CREATED meanwhile:
 * nothing was captured or charged.
 */
export type ThreeDsChallenge = {
  __typename?: 'ThreeDSChallenge';
  acsChallengeUrl: Scalars['String']['output'];
  acsTransactionId: Scalars['String']['output'];
  sessionId: Scalars['String']['output'];
  threeDSVersion: Scalars['String']['output'];
};

export type TwoFactorAuthPayload = {
  __typename?: 'TwoFactorAuthPayload';
  error?: Maybe<Error>;
  /**
   * Short-lived, single-use proof that the caller just passed this factor's
   * challenge. Required by setTwoFactorMethodEnabled / first-time
   * enrollSmsTwoFactor — distinct from `token`, which is a session JWT and
   * deliberately unusable as proof.
   */
  proofToken?: Maybe<Scalars['String']['output']>;
  status?: Maybe<Scalars['String']['output']>;
  token?: Maybe<Scalars['String']['output']>;
};

export enum TwoFactorMethod {
  Email = 'EMAIL',
  Phone = 'PHONE'
}

/**
 * The user's 2FA settings after a change, so the Security page can re-render from
 * the mutation result without a refetch.
 */
export type TwoFactorSettingsPayload = {
  __typename?: 'TwoFactorSettingsPayload';
  emailTwoFactorEnabled?: Maybe<Scalars['Boolean']['output']>;
  error?: Maybe<Error>;
  smsTwoFactorEnabled?: Maybe<Scalars['Boolean']['output']>;
  twoFactorPhoneNumber?: Maybe<Scalars['String']['output']>;
};

export type UnapprovedAccountsResponse = {
  __typename?: 'UnapprovedAccountsResponse';
  error?: Maybe<Error>;
  totalResults?: Maybe<Scalars['Int']['output']>;
  unapprovedAccounts?: Maybe<Array<Organization>>;
};

export type UnderwritingFollowUpAnswer = {
  __typename?: 'UnderwritingFollowUpAnswer';
  /** Null when the question went unanswered. */
  answer?: Maybe<Scalars['String']['output']>;
  lookFor: Scalars['String']['output'];
  question: Scalars['String']['output'];
  questionId: Scalars['String']['output'];
  reason: Scalars['String']['output'];
};

export type UnderwritingFollowUpAnswerInput = {
  answer?: InputMaybe<Scalars['String']['input']>;
  questionId: Scalars['String']['input'];
};

export type UnderwritingFollowUpCall = {
  __typename?: 'UnderwritingFollowUpCall';
  answers: Array<UnderwritingFollowUpAnswer>;
  /** The staff member who called; null for an agent, or a user since deleted. */
  calledByName?: Maybe<Scalars['String']['output']>;
  caller: FollowUpCallCaller;
  createdAt: Scalars['String']['output'];
  id: Scalars['String']['output'];
  notes?: Maybe<Scalars['String']['output']>;
  outcome: FollowUpCallOutcome;
  phoneNumber: Scalars['String']['output'];
  /** The first run that read the call's answers; null until it has run. */
  scoreAfter?: Maybe<UnderwritingRunScore>;
  /** The run whose questions the call answered. */
  scoreBefore?: Maybe<UnderwritingRunScore>;
  target: FollowUpCallTarget;
  underwritingRunId: Scalars['String']['output'];
  /** The AI phone call that made this record; null for a reviewer's call. */
  voiceCall?: Maybe<FollowUpVoiceCall>;
};

export type UnderwritingFollowUpCallPlan = {
  __typename?: 'UnderwritingFollowUpCallPlan';
  /** The numbers on file for the target, best first. Empty when there are none. */
  phoneNumbers: Array<Scalars['String']['output']>;
  target: FollowUpCallTarget;
};

export type UnderwritingFollowUpQuestion = {
  __typename?: 'UnderwritingFollowUpQuestion';
  id: Scalars['String']['output'];
  /** For the reviewer only: the answer that settles it either way. */
  lookFor: Scalars['String']['output'];
  /** Read aloud to the person called. */
  question: Scalars['String']['output'];
  /** For the reviewer only: the finding the question tests. */
  reason: Scalars['String']['output'];
};

/** An underwriting run's verdict as the auto-approve rule reads it. */
export type UnderwritingRunScore = {
  __typename?: 'UnderwritingRunScore';
  confidence?: Maybe<Scalars['String']['output']>;
  decision: Scalars['String']['output'];
  /** Whether the run met the auto-approve rule: APPROVE above 0.80, no blocker, bank data seen, research complete. */
  inAutoApproveRange: Scalars['Boolean']['output'];
  riskRating: Scalars['Float']['output'];
};

export type UnlinkOrganizationFromAccountantResponse = {
  __typename?: 'UnlinkOrganizationFromAccountantResponse';
  error?: Maybe<Error>;
};

export type UnsyncedAccountingTransaction = {
  __typename?: 'UnsyncedAccountingTransaction';
  accountingSource: Scalars['String']['output'];
  amountInCents?: Maybe<Scalars['Int']['output']>;
  attemptCount: Scalars['Int']['output'];
  billNumber?: Maybe<Scalars['String']['output']>;
  createdAt: Scalars['Float']['output'];
  customerName?: Maybe<Scalars['String']['output']>;
  errorMessage?: Maybe<Scalars['String']['output']>;
  id: Scalars['String']['output'];
  invoiceName?: Maybe<Scalars['String']['output']>;
  lastAttemptedAt?: Maybe<Scalars['Float']['output']>;
  sourceId: Scalars['String']['output'];
  syncInitiatedAt?: Maybe<Scalars['Float']['output']>;
  transactionType: Scalars['String']['output'];
  vendorName?: Maybe<Scalars['String']['output']>;
};

export type UnsyncedAccountingTransactionsResponse = {
  __typename?: 'UnsyncedAccountingTransactionsResponse';
  error?: Maybe<Error>;
  totalResults?: Maybe<Scalars['Int']['output']>;
  unsyncedAccountingTransactions?: Maybe<Array<UnsyncedAccountingTransaction>>;
};

export type UnsyncedTransactionAction = {
  action: UnsyncedTransactionActionType;
  id: Scalars['String']['input'];
};

export enum UnsyncedTransactionActionType {
  Ignore = 'IGNORE',
  Sync = 'SYNC'
}

export type UpdateAdminNotesResponse = {
  __typename?: 'UpdateAdminNotesResponse';
  error?: Maybe<Error>;
  /** Null when `error` is set (the resolver rejects before writing anything). */
  onboardingReviewPayload?: Maybe<OnboardingReviewPayload>;
};

export type UpdateBankAccountResponse = {
  __typename?: 'UpdateBankAccountResponse';
  bankAccount?: Maybe<BankAccount>;
  error?: Maybe<Error>;
};

export type UpdateBillExpenseChartOfAccountResponse = {
  __typename?: 'UpdateBillExpenseChartOfAccountResponse';
  billExpenseChartOfAccount?: Maybe<FeeChartOfAccount>;
  error?: Maybe<Error>;
};

export type UpdateBrandingResponse = {
  __typename?: 'UpdateBrandingResponse';
  /** The updated parent AccountInfo, so clients can merge the write into their cached entity. */
  accountInfo?: Maybe<AccountInfo>;
  error?: Maybe<Error>;
};

export type UpdateFeeChartOfAccountResponse = {
  __typename?: 'UpdateFeeChartOfAccountResponse';
  error?: Maybe<Error>;
  feeChartOfAccount?: Maybe<FeeChartOfAccount>;
};

export type UpdateInvoiceSalesChartOfAccountResponse = {
  __typename?: 'UpdateInvoiceSalesChartOfAccountResponse';
  error?: Maybe<Error>;
  invoiceSalesChartOfAccount?: Maybe<FeeChartOfAccount>;
};

export type UpdateNickelBalanceAccountResponse = {
  __typename?: 'UpdateNickelBalanceAccountResponse';
  account?: Maybe<NickelBalanceAccount>;
  error?: Maybe<Error>;
};

export type UpdateNickelBalanceCardInput = {
  /** Merchant-facing label; empty clears it. Omit to leave unchanged. */
  nickname?: InputMaybe<Scalars['String']['input']>;
  /** The card's full set of limits, replacing the current one (at most one per interval). [] clears them. Omit to leave unchanged. */
  spendingLimits?: InputMaybe<Array<CardSpendingLimitInput>>;
};

export type UpdateNickelBalanceCardResponse = {
  __typename?: 'UpdateNickelBalanceCardResponse';
  card?: Maybe<IssuedCard>;
  error?: Maybe<Error>;
};

export type UpdateNotificationSettingsResponse = {
  __typename?: 'UpdateNotificationSettingsResponse';
  /** The updated parent AccountInfo, so clients can merge the write into their cached entity. */
  accountInfo?: Maybe<AccountInfo>;
  error?: Maybe<Error>;
  notificationSettings?: Maybe<NotificationSettings>;
};

export type UpdateOrCreateChartOfAccountResponse = {
  __typename?: 'UpdateOrCreateChartOfAccountResponse';
  error?: Maybe<Error>;
};

/**
 * The org's admin-controlled kill switches after updateOrganizationAdminControls
 * ran — the same three values onboardingInfo reports, so the admin page can
 * render the result without a refetch.
 */
export type UpdateOrganizationAdminControlsResponse = {
  __typename?: 'UpdateOrganizationAdminControlsResponse';
  /** The business address in the org's onboarding info after the write. */
  address?: Maybe<AdminOrganizationAddress>;
  brandingDisabled?: Maybe<Scalars['Boolean']['output']>;
  creditCardEnabled?: Maybe<Scalars['Boolean']['output']>;
  error?: Maybe<Error>;
  /** The legal name in the org's onboarding info after the write; null when none is on file. */
  legalName?: Maybe<Scalars['String']['output']>;
  name?: Maybe<Scalars['String']['output']>;
  /** Stored checkout-copy overrides after the write; null = default (see OnboardingReviewPayload). */
  orderReferenceLabel?: Maybe<Scalars['String']['output']>;
  orderReferencePlaceholder?: Maybe<Scalars['String']['output']>;
  organizationId?: Maybe<Scalars['String']['output']>;
  paymentsDisabled?: Maybe<Scalars['Boolean']['output']>;
  payoutsDisabled?: Maybe<Scalars['Boolean']['output']>;
  role?: Maybe<Scalars['String']['output']>;
  subdomain?: Maybe<Scalars['String']['output']>;
  threeDSEnabled?: Maybe<Scalars['Boolean']['output']>;
};

export type UpdatePaymentResponse = {
  __typename?: 'UpdatePaymentResponse';
  error?: Maybe<Error>;
  payment?: Maybe<Payment>;
  /**
   * Set only when a SAVED card was selected on a checkout that requires 3DS:
   * the Basis Theory token the card-entry frame must open a 3DS session on
   * before the confirm (sent back as confirmPayment's
   * basisTheoryThreeDSSessionId). Opaque; null everywhere else.
   */
  threeDSTokenId?: Maybe<Scalars['String']['output']>;
};

export type UpdatePaymentSettingsResponse = {
  __typename?: 'UpdatePaymentSettingsResponse';
  /** The updated parent AccountInfo, so clients can merge the write into their cached entity. */
  accountInfo?: Maybe<AccountInfo>;
  error?: Maybe<Error>;
  /** Null when `error` is set (the resolver rejects before writing anything). */
  paymentSettings?: Maybe<PaymentSettings>;
};

export type UpdateRetailCustomerPaymentMethodResponse = {
  __typename?: 'UpdateRetailCustomerPaymentMethodResponse';
  debitAuthorization?: Maybe<DebitAuthorization>;
  error?: Maybe<Error>;
  /**
   * The issuer wants a challenge before this card can be enrolled: run it in
   * the Basis Theory frame, then call again with
   * `cardDetails.threeDSChallengeCompleted: true`. Nothing was vaulted yet.
   */
  threeDSChallenge?: Maybe<ThreeDsChallenge>;
};

export type UpdateRetailCustomerResponse = {
  __typename?: 'UpdateRetailCustomerResponse';
  error?: Maybe<Error>;
  retailCustomer?: Maybe<RetailCustomer>;
};

export type UpdateSubdomainResponse = {
  __typename?: 'UpdateSubdomainResponse';
  error?: Maybe<Error>;
  organization?: Maybe<Organization>;
};

export type UpdateSubscriptionPaymentMethodResponse = {
  __typename?: 'UpdateSubscriptionPaymentMethodResponse';
  error?: Maybe<Error>;
  organizationInfo?: Maybe<OrganizationInfo>;
  subscriptionPayment?: Maybe<SubscriptionPayment>;
};

export type UpdateTeammateResponse = {
  __typename?: 'UpdateTeammateResponse';
  error?: Maybe<Error>;
  user?: Maybe<User>;
};

export type UpdateUnappliedPaymentChartOfAccountResponse = {
  __typename?: 'UpdateUnappliedPaymentChartOfAccountResponse';
  error?: Maybe<Error>;
  unappliedPaymentChartOfAccount?: Maybe<FeeChartOfAccount>;
};

export type UpdateVendorPayoutMethodResponse = {
  __typename?: 'UpdateVendorPayoutMethodResponse';
  error?: Maybe<Error>;
  vendor?: Maybe<Vendor>;
};

export type User = {
  __typename?: 'User';
  createdAt?: Maybe<Scalars['Float']['output']>;
  /**
   * Every grant the member effectively holds — staff bypass and legacy shim
   * already applied server-side — so the dashboard never re-implements
   * resolution. The resolver always returns a list; the field is nullable like
   * every other User field because the dashboard types partial selections as
   * the full User, and a required field would break each of those sites.
   */
  effectiveGrants?: Maybe<Array<PermissionGrant>>;
  email?: Maybe<Scalars['String']['output']>;
  /**
   * Per-method state for the Security settings toggles: 2FA is switched on AND a
   * verified record exists for that method. Signup stamps a verified EMAIL record
   * on every user, so the record alone does not mean the method is active.
   */
  emailTwoFactorEnabled?: Maybe<Scalars['Boolean']['output']>;
  /**
   * The factors this user has ENROLLED -- a verified record per method -- which
   * is what every proof rule in the API reads, and what a 2FA settings change may
   * be vouched for (setTwoFactorMethodEnabled's enrolled-factor rule). Strongest
   * first.
   *
   * Distinct from emailTwoFactorEnabled / smsTwoFactorEnabled, which are the
   * other question: record AND login 2FA switched on. The two come apart -- a
   * user can hold a verified phone with 2FA off -- so a challenge picked off
   * those fields can be one the API refuses. Read this to decide which
   * verification to open before a 2FA settings change.
   *
   * Answered for the signed-in user only; null for anyone else, for the same
   * reason as stepUpTwoFactorMethod above -- and enrolment while login 2FA is OFF
   * is not derivable from emailTwoFactorEnabled / smsTwoFactorEnabled, so this
   * would otherwise be a new disclosure on a read every member can make.
   *
   * Nullable for the same reason as stepUpTwoFactorMethod above: a non-null
   * field that throws would null the whole User.
   */
  enrolledTwoFactorMethods?: Maybe<Array<TwoFactorMethod>>;
  /**
   * An email-only member (docs/workspace-roles/design.md §10.4): holds a role so
   * the notifications tied to it reach them, but has no login until an admin
   * invites them to Nickel. Nullable like every other User field.
   */
  external?: Maybe<Scalars['Boolean']['output']>;
  firstName?: Maybe<Scalars['String']['output']>;
  /**
   * True when this person has left the business or its accounting firm since
   * acting on the record, so only their name is shown.
   */
  formerMember: Scalars['Boolean']['output'];
  id: Scalars['ID']['output'];
  lastName?: Maybe<Scalars['String']['output']>;
  /**
   * The notification categories this member has on, keyed by the code-defined
   * catalog in @nickeltechnology/types (docs/workspace-roles/notifications.md
   * §13, §14), in catalog order: the member's own map once seeded, else what
   * their role gives them today. A category the member may not pick can still
   * be listed — the send path checks eligibility and skips it. Null while the
   * org is not on role routing, not "receives nothing".
   */
  notificationSubscriptions?: Maybe<Array<NotificationSubscription>>;
  organization?: Maybe<Organization>;
  passwordChangeRequired?: Maybe<Scalars['Boolean']['output']>;
  plaidIdentityVerificationId?: Maybe<Scalars['String']['output']>;
  role?: Maybe<UserRole>;
  shadowRemoved?: Maybe<Scalars['Boolean']['output']>;
  smsTwoFactorEnabled?: Maybe<Scalars['Boolean']['output']>;
  ssoProvider?: Maybe<SsoProvider>;
  /**
   * Which factor a two-factor step-up will demand of this user: PHONE once they
   * have a verified SMS record, else EMAIL (apps/api/src/lib/adminGrantStepUp.ts,
   * which resolves this from the same function it enforces). In an accountant
   * session it is EMAIL: the proof is the acting accountant's, and only the
   * emailed code reaches them there.
   *
   * Read this -- not smsTwoFactorEnabled -- to decide which verification modal to
   * open before granting admin access. smsTwoFactorEnabled additionally requires
   * login 2FA to be switched ON, which the step-up does not: a user who enrolled
   * a phone and left 2FA off reads false there, and picking the modal off it
   * emailed them a code the API then refused for want of a texted one.
   *
   * Answered for the signed-in user only; null for anyone else. User is listed as
   * an org's employees, and which factor a colleague would be challenged on is
   * not something every member should be able to read off that list.
   *
   * Nullable although the resolver always answers for you: this hangs off User,
   * and a non-null field that throws nulls the nearest nullable parent -- the
   * whole User -- so a transient failure reading 2FA records would log every
   * caller out rather than degrade one modal. The client reads a null as "ask me
   * again", never as EMAIL.
   */
  stepUpTwoFactorMethod?: Maybe<TwoFactorMethod>;
  /**
   * Legacy, predating per-method 2FA state: true when ANY PHONE record exists
   * for the user -- verified or not, 2FA switched on or not (the resolver
   * applies no filters). Existing dashboard call sites read it as "is SMS 2FA
   * on", which it only approximates: it stays true for an unverified or stale
   * record even when 2FA is off. New code should use smsTwoFactorEnabled
   * (verified record AND 2FA on); migrating the remaining readers is a
   * follow-up.
   */
  twoFactorEnabled?: Maybe<Scalars['Boolean']['output']>;
  /**
   * The enrolled 2FA phone number, masked for display. Null when the user has no
   * verified PHONE record.
   */
  twoFactorPhoneNumber?: Maybe<Scalars['String']['output']>;
  twoFactorRequired?: Maybe<Scalars['Boolean']['output']>;
  /**
   * The member's workspace role once backfilled; null resolves through the
   * legacy-tier shim (docs/workspace-roles/design.md §6.1).
   */
  workspaceRole?: Maybe<WorkspaceRole>;
};

export type UserResponse = {
  __typename?: 'UserResponse';
  error?: Maybe<Error>;
  user?: Maybe<User>;
};

export enum UserRole {
  Admin = 'ADMIN',
  Manager = 'MANAGER',
  User = 'USER',
  UserAdmin = 'USER_ADMIN'
}

export type ValidateData = {
  __typename?: 'ValidateData';
  encryptionKey?: Maybe<Scalars['String']['output']>;
  /**
   * Short-lived, single-use proof that the signed-in caller just verified a
   * code sent to their own email. Required by setTwoFactorMethodEnabled /
   * first-time enrollSmsTwoFactor. Absent for portal/accountant/other-email
   * verifications.
   */
  proofToken?: Maybe<Scalars['String']['output']>;
  success?: Maybe<Scalars['Boolean']['output']>;
  token?: Maybe<Scalars['String']['output']>;
};

export type ValidateEmailVerificationResponse = {
  __typename?: 'ValidateEmailVerificationResponse';
  data?: Maybe<ValidateData>;
  error?: Maybe<Error>;
};

export type Vendor = {
  __typename?: 'Vendor';
  accountingSource?: Maybe<AccountingSource>;
  address?: Maybe<Scalars['String']['output']>;
  /**
   * What the caller can do to this vendor right now, and why not. Always a list;
   * nullable only so clients typed against the whole `Vendor` need not select it.
   */
  availableActions?: Maybe<Array<ActionAvailability>>;
  billPayables?: Maybe<Array<BillPayable>>;
  /**
   * Payout methods the merchant has blocked for this vendor; empty means none
   * is blocked. "Require ACH" blocks CHECK: the vendor's onboarding links cannot
   * add a check address, and the QuickBooks sync does not import one. Set
   * through the achOnly argument of requestVendorDeliveryDetails and
   * batchRequestVendorDeliveryDetails.
   */
  blockedPayoutMethods?: Maybe<Array<VendorPayoutMethodType>>;
  city?: Maybe<Scalars['String']['output']>;
  emails?: Maybe<Array<Scalars['String']['output']>>;
  externalVendorId?: Maybe<Scalars['String']['output']>;
  id: Scalars['String']['output'];
  integrationEmails?: Maybe<Array<Scalars['String']['output']>>;
  /** The last payment on a bill in the caller's bill view; null when the view holds no bills. */
  lastPaidDate?: Maybe<Scalars['Float']['output']>;
  name?: Maybe<Scalars['String']['output']>;
  organization?: Maybe<Organization>;
  recipients?: Maybe<Array<RecipientType>>;
  state?: Maybe<Scalars['String']['output']>;
  tags?: Maybe<Array<Tag>>;
  taxId?: Maybe<Scalars['String']['output']>;
  /** Summed over the bills in the caller's bill view, as unpaidBills lists them. */
  transactionsSummary?: Maybe<VendorTransactionsSummary>;
  type?: Maybe<VendorType>;
  /** The open bills in the caller's bill view: under an own-records view, the ones they created. */
  unpaidBills?: Maybe<Array<Bill>>;
  /** Null, not empty, for a member who holds neither a bill view nor the vendor view: withheld rather than none. */
  vendorPayoutMethods?: Maybe<Array<Maybe<VendorPayoutMethod>>>;
  zip?: Maybe<Scalars['String']['output']>;
};

/**
 * Column mapping for a vendor CSV bulk import. Values are 0-based column
 * indices into the file's rows, so the same mapping works whether or not the
 * file has a header row (hasHeader on the mutation controls whether row 0 is
 * skipped).
 */
export type VendorCsvColumnMappingInput = {
  /** 0-based index of the column holding the vendor's contact email, if the file has one. */
  emails?: InputMaybe<Scalars['Int']['input']>;
  /** 0-based index of the column holding the vendor's display name. */
  name: Scalars['Int']['input'];
};

/**
 * Delivery-method values the vendors query can filter on. ACH / CHECK / WIRE /
 * DOMESTIC_WIRE are the four types a merchant can put on a vendor (the only ones
 * the vendor page renders); NONE is their complement, so the five values
 * partition the vendor list. A vendor whose only active method is one of
 * VendorPayoutMethodType's other two values therefore reads as NONE — which is
 * also what the vendor page shows it as. Those two are INCREASE_ACCOUNT, the
 * system-created virtual-card wallet, and RTP, which nothing writes today
 * (expedites reuse the bill's ACH method and record the rail on
 * VendorPayout.paymentRail). WIRE is the international SWIFT wire.
 */
export enum VendorDeliveryMethodFilter {
  Ach = 'ACH',
  Check = 'CHECK',
  DomesticWire = 'DOMESTIC_WIRE',
  None = 'NONE',
  Wire = 'WIRE'
}

/** When vendor "You were sent …" receipt emails go out. */
export enum VendorPaymentEmailCadence {
  /**
   * One email per vendor each evening, listing the payments whose receipt
   * became due that day.
   */
  DailySummary = 'DAILY_SUMMARY',
  /** One email per bill payment, when its merchant cancellation window closes. */
  PerPayment = 'PER_PAYMENT'
}

export type VendorPayout = {
  __typename?: 'VendorPayout';
  achVendorReturn?: Maybe<VendorReturn>;
  amountInCents?: Maybe<Scalars['Int']['output']>;
  charges?: Maybe<Array<Charge>>;
  checkDeliveredAt?: Maybe<Scalars['String']['output']>;
  checkMailedAt?: Maybe<Scalars['String']['output']>;
  checkNumber?: Maybe<Scalars['String']['output']>;
  checkReturnedAt?: Maybe<Scalars['String']['output']>;
  /**
   * Physical-check lifecycle (CHECK payouts only; null on other rails and on
   * checks issued before lifecycle tracking): issued | mailed | delivered |
   * deposited | returned | stopped | canceled | rejected. `returned` means USPS
   * sent the envelope back to Nickel — the vendor never received it.
   */
  checkStatus?: Maybe<CheckStatus>;
  /**
   * Increase stop reason when checkStatus is `stopped`, e.g. mail_delivery_failed
   * (USPS return), valid_until_date_passed (expired), not_authorized (merchant stop).
   */
  checkStopReason?: Maybe<Scalars['String']['output']>;
  checkStoppedAt?: Maybe<Scalars['String']['output']>;
  createdAt?: Maybe<Scalars['Float']['output']>;
  currency?: Maybe<Scalars['String']['output']>;
  description?: Maybe<Scalars['String']['output']>;
  externalPayoutId?: Maybe<Scalars['String']['output']>;
  fees?: Maybe<Array<Fee>>;
  id: Scalars['String']['output'];
  paidAt?: Maybe<Scalars['String']['output']>;
  replacedByVendorPayout?: Maybe<VendorPayout>;
  /**
   * Reissue provenance: the stopped check payout this one replaces (set on the
   * replacement), and the replacement that superseded this one (set on the
   * stopped original). Both null outside a reissue.
   */
  replacesVendorPayout?: Maybe<VendorPayout>;
  status?: Maybe<Scalars['String']['output']>;
  updatedAt?: Maybe<Scalars['Float']['output']>;
  vendor: Vendor;
};

export type VendorPayoutMethod = {
  __typename?: 'VendorPayoutMethod';
  accountHolderName?: Maybe<Scalars['String']['output']>;
  accountNumber?: Maybe<Scalars['String']['output']>;
  accountType?: Maybe<Scalars['String']['output']>;
  bankCode?: Maybe<Scalars['String']['output']>;
  bankCountry?: Maybe<Scalars['String']['output']>;
  bankName?: Maybe<Scalars['String']['output']>;
  canadianAccountNumber?: Maybe<Scalars['String']['output']>;
  chineseAccountNumber?: Maybe<Scalars['String']['output']>;
  city?: Maybe<Scalars['String']['output']>;
  correspondentSwiftCode?: Maybe<Scalars['String']['output']>;
  country?: Maybe<Scalars['String']['output']>;
  countryCode?: Maybe<Scalars['String']['output']>;
  /**
   * When these delivery details were added, in epoch milliseconds. Changing a
   * vendor's bank account or wire details deactivates the old method and adds a
   * new one, so an active ACH or wire method's createdAt is when its current
   * details were entered. Editing a check address updates the method in place
   * and does not change it.
   */
  createdAt?: Maybe<Scalars['Float']['output']>;
  createdSource?: Maybe<VendorPayoutMethodSource>;
  currency?: Maybe<Scalars['String']['output']>;
  externalPayoutMethodId?: Maybe<Scalars['String']['output']>;
  hongKongAccountNumber?: Maybe<Scalars['String']['output']>;
  iban?: Maybe<Scalars['String']['output']>;
  id: Scalars['String']['output'];
  ifscCode?: Maybe<Scalars['String']['output']>;
  indianAccountNumber?: Maybe<Scalars['String']['output']>;
  name?: Maybe<Scalars['String']['output']>;
  provider?: Maybe<Scalars['String']['output']>;
  recipientAddress?: Maybe<Scalars['String']['output']>;
  recipientAddressLine2?: Maybe<Scalars['String']['output']>;
  recipientCity?: Maybe<Scalars['String']['output']>;
  recipientState?: Maybe<Scalars['String']['output']>;
  recipientZip?: Maybe<Scalars['String']['output']>;
  routingNumber?: Maybe<Scalars['String']['output']>;
  state?: Maybe<Scalars['String']['output']>;
  street?: Maybe<Scalars['String']['output']>;
  street2?: Maybe<Scalars['String']['output']>;
  swiftCode?: Maybe<Scalars['String']['output']>;
  transitNumber?: Maybe<Scalars['String']['output']>;
  type?: Maybe<Scalars['String']['output']>;
  vendor?: Maybe<Vendor>;
  zip?: Maybe<Scalars['String']['output']>;
};

/**
 * Provenance of a vendor's delivery details.
 *
 * VENDOR_SELF_SERVE means they were entered through the vendor's onboarding
 * link. The merchant also receives that link back from
 * requestVendorDeliveryDetails, so it means "entered through the vendor's
 * form", not "provably typed by the vendor".
 *
 * SYSTEM_NOC marks a row the Increase notification-of-change handler wrote
 * whose predecessor had no recorded source; when the predecessor does have
 * one, the replacement copies it forward instead.
 *
 * PENNY means penny entered them while a merchant user was talking to it.
 *
 * ACCOUNTANT means a member of the merchant's accounting firm entered them inside
 * the merchant's workspace, in an accountant session or with a token minted in
 * one.
 */
export enum VendorPayoutMethodSource {
  Accountant = 'ACCOUNTANT',
  MerchantApi = 'MERCHANT_API',
  MerchantDashboard = 'MERCHANT_DASHBOARD',
  NickelAdmin = 'NICKEL_ADMIN',
  Penny = 'PENNY',
  QboSync = 'QBO_SYNC',
  System = 'SYSTEM',
  SystemNoc = 'SYSTEM_NOC',
  VendorSelfServe = 'VENDOR_SELF_SERVE'
}

export enum VendorPayoutMethodType {
  Ach = 'ACH',
  Check = 'CHECK',
  /**
   * Domestic Fedwire, sent from the merchant's own Nickel Balance account
   * with a $20 fee on top. Payable only from Nickel Balance, and only from the
   * dashboard: API-token callers are refused.
   */
  DomesticWire = 'DOMESTIC_WIRE',
  /** The vendor's virtual-card wallet. */
  IncreaseAccount = 'INCREASE_ACCOUNT',
  Rtp = 'RTP',
  /** International SWIFT wire, $20 fee. Not a domestic Fedwire: see DOMESTIC_WIRE. */
  Wire = 'WIRE'
}

export type VendorPayoutResponse = {
  __typename?: 'VendorPayoutResponse';
  error?: Maybe<Error>;
  vendorPayout?: Maybe<VendorPayout>;
};

export type VendorPayoutsResponse = {
  __typename?: 'VendorPayoutsResponse';
  error?: Maybe<Error>;
  totalResults?: Maybe<Scalars['Int']['output']>;
  vendorPayouts: Array<VendorPayout>;
};

export type VendorResponse = {
  __typename?: 'VendorResponse';
  error?: Maybe<Error>;
  vendor?: Maybe<Vendor>;
};

export type VendorReturn = {
  __typename?: 'VendorReturn';
  activity?: Maybe<PaymentActivity>;
  amountCents: Scalars['Int']['output'];
  createdAt: Scalars['Float']['output'];
  externalReturnId: Scalars['String']['output'];
  /**
   * Machine-readable code for the failure `failureMessage` describes — the ACH
   * return code the vendor's bank sent back (e.g. "R01" = insufficient funds).
   * Null when the provider supplied no code.
   */
  failureCode?: Maybe<Scalars['String']['output']>;
  failureMessage?: Maybe<Scalars['String']['output']>;
  id: Scalars['String']['output'];
  payout?: Maybe<Payout>;
  vendorPayout?: Maybe<VendorPayout>;
};

export type VendorReturnResponse = {
  __typename?: 'VendorReturnResponse';
  achVendorReturn?: Maybe<VendorReturn>;
  error?: Maybe<Error>;
};

export type VendorTransactionsSummary = {
  __typename?: 'VendorTransactionsSummary';
  remainingBalanceInCents?: Maybe<Scalars['Int']['output']>;
  totalPaidInCents?: Maybe<Scalars['Int']['output']>;
};

export enum VendorType {
  Business = 'BUSINESS',
  Individual = 'INDIVIDUAL'
}

export type VendorsResponse = {
  __typename?: 'VendorsResponse';
  error?: Maybe<Error>;
  totalResults?: Maybe<Scalars['Int']['output']>;
  vendors?: Maybe<Array<Vendor>>;
};

export type VoidExternalBillPaymentResponse = {
  __typename?: 'VoidExternalBillPaymentResponse';
  bill?: Maybe<Bill>;
  billPayable?: Maybe<BillPayable>;
  error?: Maybe<Error>;
};

export type VoidExternalInvoicePaymentResponse = {
  __typename?: 'VoidExternalInvoicePaymentResponse';
  error?: Maybe<Error>;
  externalArPayment?: Maybe<ExternalArPayment>;
  questionForm?: Maybe<QuestionForm>;
};

export type VoidInvoiceDebitMemoResponse = {
  __typename?: 'VoidInvoiceDebitMemoResponse';
  debitMemo?: Maybe<ArDebitMemo>;
  error?: Maybe<Error>;
  /**
   * What the customer has already paid beyond the invoice's new total — the
   * memo was paid before it was voided. 0 when nothing was over-collected.
   * Returning the money is a refund, the merchant's call.
   */
  overCollectedAmountInCents?: Maybe<Scalars['Int']['output']>;
  questionForm?: Maybe<QuestionForm>;
};

export type WebSearchFindings = {
  __typename?: 'WebSearchFindings';
  businessFound?: Maybe<Scalars['Boolean']['output']>;
  notes: Array<WebSearchNote>;
  searched: Scalars['Boolean']['output'];
};

export type WebSearchNote = {
  __typename?: 'WebSearchNote';
  text: Scalars['String']['output'];
  type: Scalars['String']['output'];
};

export type Webhook = {
  __typename?: 'Webhook';
  id: Scalars['String']['output'];
  /**
   * HMAC-SHA256 signing secret for deliveries to this webhook. Null for
   * webhooks created before signing shipped; recreate the webhook to get one.
   */
  secret?: Maybe<Scalars['String']['output']>;
  url: Scalars['String']['output'];
};

export enum WebhookType {
  ApiWebhook = 'API_WEBHOOK',
  Legacy = 'LEGACY'
}

export type WebhooksResponse = {
  __typename?: 'WebhooksResponse';
  error?: Maybe<Error>;
  webhooks: Array<Webhook>;
};

/**
 * An org-defined role: a name and a per-key grant map (docs/workspace-roles/
 * design.md §3.1). systemKey is non-null for the three seeded presets (ADMIN,
 * MANAGER, MEMBER), which stay renameable — the key is the stable handle.
 */
export type WorkspaceRole = {
  __typename?: 'WorkspaceRole';
  description?: Maybe<Scalars['String']['output']>;
  grants: Array<PermissionGrant>;
  id: Scalars['ID']['output'];
  memberCount: Scalars['Int']['output'];
  name: Scalars['String']['output'];
  /**
   * The role's cells on the Team Notifications grid (docs/workspace-roles/
   * notifications.md §14): an Administrator's last bulk value for the role,
   * which a member joining it starts from. Never read to decide who is emailed
   * — that is each member's own map (User.notificationSubscriptions). Null
   * until the org's notification defaults have been seeded.
   */
  notificationSubscriptions?: Maybe<Array<NotificationSubscription>>;
  systemKey?: Maybe<Scalars['String']['output']>;
};

/**
 * A role's whole map — the pre-§14 shape of the grid write, retired
 * (docs/workspace-roles/notifications.md §14). Still accepted so a dashboard
 * built against it keeps compiling; a non-empty list is refused, and the grid
 * sends `cells`.
 */
export type WorkspaceRoleNotificationsInput = {
  subscriptions: Array<NotificationSubscriptionInput>;
  workspaceRoleId: Scalars['ID']['input'];
};

export type WorkspaceRoleResponse = {
  __typename?: 'WorkspaceRoleResponse';
  error?: Maybe<Error>;
  workspaceRole?: Maybe<WorkspaceRole>;
};

export type WorkspaceRolesResponse = {
  __typename?: 'WorkspaceRolesResponse';
  error?: Maybe<Error>;
  workspaceRoles?: Maybe<Array<WorkspaceRole>>;
};

export type XeroConnectionCompleteResponse = {
  __typename?: 'XeroConnectionCompleteResponse';
  error?: Maybe<Error>;
  organization?: Maybe<Organization>;
};

export type XeroConnectionReconnectResponse = {
  __typename?: 'XeroConnectionReconnectResponse';
  error?: Maybe<Error>;
  /**
   * Where to send the merchant to sign in to Xero again. As with
   * xeroConnectionStart, Xero's login returns them to the dashboard's
   * integrations settings with a public_token for xeroConnectionComplete, which
   * moves the same connection back to CONNECTED.
   */
  linkUrl?: Maybe<Scalars['String']['output']>;
};

export type XeroConnectionStartResponse = {
  __typename?: 'XeroConnectionStartResponse';
  error?: Maybe<Error>;
  /**
   * Where to send the merchant to log in to Xero. Once they have, Xero's login
   * returns them to the dashboard's integrations settings with a public_token
   * query parameter, to be passed to xeroConnectionComplete.
   */
  linkUrl?: Maybe<Scalars['String']['output']>;
};

export type YouTubePlaylistResponse = {
  __typename?: 'YouTubePlaylistResponse';
  error?: Maybe<Error>;
  videos: Array<YouTubeVideo>;
};

export type YouTubeVideo = {
  __typename?: 'YouTubeVideo';
  duration: Scalars['String']['output'];
  thumbnailUrl: Scalars['String']['output'];
  title: Scalars['String']['output'];
  videoId: Scalars['String']['output'];
  videoUrl: Scalars['String']['output'];
};
